import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { buildLessonFromCalendarFlow } from "../utils/classroomLesson";
import ClassroomWorkspace from "../components/classroom/workspace/ClassroomWorkspace";
import ClassroomNotesPanel from "../components/classroom/workspace/ClassroomNotesPanel";
import { api } from "../lib/api";
import { queueGradingSettingsSync, saveClassroomEvents, waitForClassroomSync } from "../lib/classroomEventSync";
import { publishReviewedAssignment } from "../lib/academicReports";
import { classToDoForDay, nextClassDate, questsForClass } from "../lib/classroomSchedule";
import { classroomEventOnDate } from "../lib/dashboardGadgets";
import { AttachmentEditor } from "../components/GadgetParts";
import AssignmentSeriesEditor from "../components/AssignmentSeriesEditor";
import AssignmentSeriesPreview from "../components/AssignmentSeriesPreview";
import { prepareAssignmentCopies } from "../lib/assignmentSeries";
import AssignmentTimingOptions from "../components/AssignmentTimingOptions";
import { assignmentTimerSections, validAssignmentTiming } from "../lib/assignmentTiming";
import DeleteAssignmentDialog from "../components/DeleteAssignmentDialog";
import { deleteSharedAssignment } from "../lib/assignmentDeletion";
import { schoolCalendarForChild } from "../lib/classroomEventSync";
import useClassroomDashboard from "../lib/useClassroomDashboard";
import LessonStagePanel, { AssignmentLessonLinks, LessonCalendarDetails } from "../components/LessonStagePanel";
import { sessionDates, sessionWindow, defaultClassDays } from "../lib/sessionSchedule";
import { groupLessonCalendarEvents } from "../lib/lessonStage";
import { NextClassButton } from "../components/SessionTimer";
import HomeroomMessages from "../components/HomeroomMessages";
import { classroomCalendarColor, classroomCalendarStyle } from "../lib/classroomCalendarStyle";
import { shadowCalendarShell, shadowCalendarToolbar, shadowCalendarGrid } from "../shadowGrove/ui/shadowStyles";
import { useClassroom } from "../context/ClassroomProvider";
import {
    ArrowLeft,
    BookOpen,
    CalendarDays,
    ChevronRight,
    Clock,
    FileText,
    Folder,
    FolderPlus,
    Link as LinkIcon,
    Mic,
    Plus,
    Presentation,
    Upload,
    Video,
} from "lucide-react";

const purpleActionBtn =
    "rounded-2xl border border-[#ffd166]/45 bg-black px-4 py-2 text-sm font-semibold text-[#ffd166] shadow-[0_0_16px_rgba(255,209,102,0.18)] transition-all duration-200 hover:border-[#ffd166] hover:bg-[#ffd166]/10 hover:shadow-[0_0_28px_rgba(255,209,102,0.35)] active:scale-[0.98]";

const inputClass =
    "w-full rounded-2xl border border-[#ffd166]/25 bg-black px-3 py-2 text-[#fff3c4] outline-none transition focus:border-[#ffd166]/70 focus:shadow-[0_0_16px_rgba(255,209,102,0.25)]";


const textareaClass =
    "w-full rounded-2xl border border-[#ffd166]/25 bg-black p-3 text-[#fff3c4] outline-none transition focus:border-[#ffd166]/70 focus:shadow-[0_0_16px_rgba(255,209,102,0.25)]";


const DAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];
const DEFAULT_ITEM_TYPE_COLORS = {
    lesson: "#ffd166",
    assignment: "#60a5fa",
    practice: "#4ade80",
    quiz: "#c084fc",
    test: "#f87171",
    project: "#fb923c",
    field_trip: "#22d3ee",
    reading: "#38bdf8",
    review: "#a78bfa",
    break: "#7cc1ff",
    lunch: "#fb923c",
};

const DEFAULT_SCALE_LEVELS = [
    {
        id: "1",
        score: 1,
        label: "Beginning",
        description: "",
        color: "#ef4444",
    },
    {
        id: "2",
        score: 2,
        label: "Developing",
        description: "",
        color: "#fb923c",
    },
    {
        id: "3",
        score: 3,
        label: "Meets Expectations",
        description: "",
        color: "#facc15",
    },
    {
        id: "4",
        score: 4,
        label: "Proficient",
        description: "",
        color: "#4ade80",
    },
];

const DEFAULT_MATRIX_COLUMNS = [
    { id: "5", score: 5, label: "Excellent", color: "#4ade80" },
    { id: "4", score: 4, label: "Good", color: "#22c55e" },
    { id: "3", score: 3, label: "Decent", color: "#facc15" },
    { id: "2", score: 2, label: "Needs Work", color: "#fb923c" },
    { id: "1", score: 1, label: "Terrible", color: "#ef4444" },
];

const CHECKLIST_COMPLETE_COLOR = "#4ade80";
const CHECKLIST_INCOMPLETE_COLOR = "#ef4444";
const CHECKLIST_OPTIONAL_COLOR = "#facc15";

const createEmptyRubricRow = () => ({
    id: `${Date.now()}-${Math.random()}`,
    criteria: "",
    maxPoints: 25,
    earnedPoints: 0,
    selectedScaleScore: "",
    checklistChecked: false,
    checklistRequired: true,
    checklistCategory: "Content & Ideas",
    checklistSectionPoints: 5,
    checklistColor: CHECKLIST_COMPLETE_COLOR,
    checklistNote: "",
    selectedMatrixScore: "",
    matrixCells: DEFAULT_MATRIX_COLUMNS.reduce((cells, column) => {
        cells[column.score] = "";
        return cells;
    }, {}),
    scaleLevels: DEFAULT_SCALE_LEVELS.map((level) => ({
        ...level,
    })),
});

const normalizeRubricRows = (rows = []) => {
    return rows
        .filter((row) => row.criteria?.trim() !== "")
        .map((row) => {
            const legacyScaleDescriptions = row.scaleDescriptions || {};
            const legacyScaleColors = row.scaleColors || {};

            return {
                id: row.id || `${Date.now()}-${Math.random()}`,
                criteria: row.criteria || "",
                maxPoints: Number(row.maxPoints) || 0,
                earnedPoints: Number(row.earnedPoints) || 0,
                selectedScaleScore: row.selectedScaleScore || row.scaleScore || "",
                checklistChecked: Boolean(row.checklistChecked || row.checked),
                checklistRequired: row.checklistRequired ?? row.required ?? true,
                checklistCategory: row.checklistCategory || row.category || "Content & Ideas",
                checklistSectionPoints: Number(row.checklistSectionPoints || row.sectionPoints || row.maxPoints) || 5,
                checklistColor: row.checklistColor || CHECKLIST_COMPLETE_COLOR,
                checklistNote: row.checklistNote || row.note || "",
                selectedMatrixScore: row.selectedMatrixScore || "",
                matrixCells: row.matrixCells || DEFAULT_MATRIX_COLUMNS.reduce((cells, column) => {
                    cells[column.score] = "";
                    return cells;
                }, {}),
                scaleLevels:
                    Array.isArray(row.scaleLevels) && row.scaleLevels.length
                        ? row.scaleLevels.map((level) => ({
                              id: level.id || String(level.score),
                              score: Number(level.score) || 1,
                              label: level.label || `Score ${level.score}`,
                              description: level.description || "",
                              color: level.color || "#ffd166",
                          }))
                        : DEFAULT_SCALE_LEVELS.map((level) => ({
                              ...level,
                              description:
                                  legacyScaleDescriptions[level.score] || "",
                              color:
                                  legacyScaleColors[level.score] ||
                                  level.color,
                          })),
            };
        });
};

const getRubricEarnedTotal = (rows = []) => {
    return rows.reduce(
        (total, row) => total + (Number(row.earnedPoints) || 0),
        0
    );
};

const getRubricMaxTotal = (rows = []) => {
    return rows.reduce(
        (total, row) => total + (Number(row.maxPoints) || 0),
        0
    );
};

const getRubricPercentage = (earned = 0, total = 100) => {
    if (!total) return 0;

    return Math.round((earned / total) * 100);
};

const getScaleAverage = (rows = []) => {
    const scoredRows = rows.filter((row) => row.selectedScaleScore);

    if (scoredRows.length === 0) return 0;

    const total = scoredRows.reduce(
        (sum, row) => sum + Number(row.selectedScaleScore || 0),
        0
    );

    return Number((total / scoredRows.length).toFixed(2));
};

const getChecklistScoreSummary = (rows = [], scoringMode = "point_per_item") => {
    const requiredRows = rows.filter((row) => row.checklistRequired !== false);

    if (scoringMode === "weighted_sections") {
        const sections = {};

        requiredRows.forEach((row) => {
            const category = row.checklistCategory || "Checklist";

            if (!sections[category]) {
                sections[category] = {
                    category,
                    points: Number(row.checklistSectionPoints) || 5,
                    rows: [],
                };
            }

            sections[category].rows.push(row);
        });

        const sectionList = Object.values(sections);
        const total = sectionList.reduce((sum, section) => sum + section.points, 0);
        const earned = sectionList.reduce((sum, section) => {
            const complete = section.rows.every((row) => row.checklistChecked);
            return complete ? sum + section.points : sum;
        }, 0);

        return {
            earned,
            total,
            completed: sectionList.filter((section) =>
                section.rows.every((row) => row.checklistChecked)
            ).length,
            count: sectionList.length,
            percentage: total ? Math.round((earned / total) * 100) : 0,
            sections: sectionList,
        };
    }

    const total = requiredRows.length;
    const earned = requiredRows.filter((row) => row.checklistChecked).length;

    return {
        earned,
        total,
        completed: earned,
        count: total,
        percentage: total ? Math.round((earned / total) * 100) : 0,
        sections: [],
    };
};

const getChecklistCompletedTotal = (rows = [], scoringMode = "point_per_item") => {
    return getChecklistScoreSummary(rows, scoringMode).completed;
};

const getChecklistTotal = (rows = [], scoringMode = "point_per_item") => {
    return getChecklistScoreSummary(rows, scoringMode).count;
};

const getChecklistPercentage = (rows = [], scoringMode = "point_per_item") => {
    return getChecklistScoreSummary(rows, scoringMode).percentage;
};

const getMatrixScoreSummary = (rows = [], columns = DEFAULT_MATRIX_COLUMNS, assignmentPoints = 100) => {
    const activeRows = rows.filter((row) => row.criteria?.trim() !== "");
    const activeColumns = Array.isArray(columns) && columns.length ? columns : DEFAULT_MATRIX_COLUMNS;
    const highestScore = Math.max(...activeColumns.map((column) => Number(column.score) || 0), 1);
    const earnedScore = activeRows.reduce(
        (sum, row) => sum + (Number(row.selectedMatrixScore) || 0),
        0
    );
    const maxScore = activeRows.length * highestScore;
    const percentage = maxScore ? Math.round((earnedScore / maxScore) * 100) : 0;
    const earnedPoints = Math.round((percentage / 100) * (Number(assignmentPoints) || 100));

    return {
        earnedScore,
        maxScore,
        percentage,
        assignmentPoints: Number(assignmentPoints) || 100,
        earnedPoints,
        highestScore,
        rowCount: activeRows.length,
    };
};

const getGradeFromPercentage = (percentage) => {
    if (percentage >= 90) return "A";
    if (percentage >= 80) return "B";
    if (percentage >= 70) return "C";
    if (percentage >= 60) return "D";

    return "F";
};

const getGradeColor = (grade) => {
    if (grade === "A") return "#4ade80";
    if (grade === "B") return "#22c55e";
    if (grade === "C") return "#facc15";
    if (grade === "D") return "#fb923c";

    return "#ef4444";
};

const getRubricModeLabel = (mode) => {
    if (mode === "scale") return "1-4 Scale Rubric";
    if (mode === "custom") return "Custom Scale Rubric";
    if (mode === "checklist") return "Simple Checklist";
    if (mode === "matrix") return "Rubric Matrix";

    return "Points Based Rubric";
};

const getScaleMaxScore = (rows = [], fallback = 4) => {
    const scores = rows.flatMap((row) =>
        Array.isArray(row.scaleLevels)
            ? row.scaleLevels.map((level) => Number(level.score) || 0)
            : []
    );

    return Math.max(Number(fallback) || 4, ...scores, 1);
};

const getSuggestedGradeForRubric = (event = {}) => {
    if (!event.rubric_enabled) return "A";

    const rows = event.rubric_rows || [];

    if (event.rubric_mode === "matrix") {
        const summary = getMatrixScoreSummary(
            rows,
            event.rubric_matrix_columns || DEFAULT_MATRIX_COLUMNS,
            event.rubric_matrix_assignment_points || event.rubric_total_points || 100
        );
        return getGradeFromPercentage(summary.percentage);
    }

    if (event.rubric_mode === "checklist") {
        const percentage = getChecklistPercentage(
            rows,
            event.rubric_checklist_scoring_mode || "point_per_item"
        );
        return getGradeFromPercentage(percentage);
    }

    if (event.rubric_mode === "scale" || event.rubric_mode === "custom") {
        const average = getScaleAverage(rows);
        const maxScore = getScaleMaxScore(rows, event.custom_scale_max || 4);
        const percentage = getRubricPercentage(average, maxScore);
        return getGradeFromPercentage(percentage);
    }

    const earned = getRubricEarnedTotal(rows);
    const total =
        Number(event.rubric_total_points) ||
        getRubricMaxTotal(rows) ||
        100;

    return getGradeFromPercentage(getRubricPercentage(earned, total));
};
export const ParentClassroom = ({ generatorOnly = false, editExisting = false, childId: scopedChildId, subjectId: scopedSubjectId, initialDraft }) => {
    const navigate = useNavigate();
    const routeParams = useParams();
    const childId = scopedChildId || routeParams.childId;
    const subjectId = scopedSubjectId || routeParams.subjectId;
    const {
        setActiveLesson,
        setActiveSection,
        layout,
        setLayout,
    } = useClassroom();
    const [isClassroomNotesOpen, setIsClassroomNotesOpen] =
        React.useState(true);
    const classroomLayoutStorageKey =
        `archivumClassroomLayout_${childId}_${subjectId}`;

    React.useEffect(() => {
        try {
            const savedLayout = JSON.parse(
                localStorage.getItem(
                    classroomLayoutStorageKey
                ) || "null"
            );

            if (!savedLayout) {
                return;
            }

            setLayout((prev) => ({
                ...prev,

                workspaceWidth:
                    Number(savedLayout.workspaceWidth) ||
                    prev.workspaceWidth,

                showWorkspace:
                    savedLayout.showWorkspace !== false,
            }));
        } catch (error) {
            console.error(
                "Unable to load classroom layout:",
                error
            );
        }
    }, [
        classroomLayoutStorageKey,
        setLayout,
    ]);

    React.useEffect(() => {
        try {
            localStorage.setItem(
                classroomLayoutStorageKey,
                JSON.stringify({
                    workspaceWidth:
                        layout.workspaceWidth,

                    showWorkspace:
                        isClassroomNotesOpen,
                })
            );
        } catch (error) {
            console.error(
                "Unable to save classroom layout:",
                error
            );
        }
    }, [
        classroomLayoutStorageKey,
        layout.workspaceWidth,
        isClassroomNotesOpen,
    ]);    
    const startClassroomWorkspaceResize = (event) => {
        event.preventDefault();

        const handlePointerMove = (moveEvent) => {
            const nextWidth =
                window.innerWidth - moveEvent.clientX;

            const clampedWidth = Math.min(
                window.innerWidth * 0.72,
                Math.max(260, nextWidth)
            );

            setLayout((prev) => ({
                ...prev,
                workspaceWidth: clampedWidth,
            }));
        };

        const handlePointerUp = () => {
            document.removeEventListener(
                "pointermove",
                handlePointerMove
            );

            document.removeEventListener(
                "pointerup",
                handlePointerUp
            );

            document.body.style.removeProperty(
                "user-select"
            );

            document.body.style.removeProperty(
                "cursor"
            );
        };

        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";

        document.addEventListener(
            "pointermove",
            handlePointerMove
        );

        document.addEventListener(
            "pointerup",
            handlePointerUp
        );
    };
    const [
        backendChild,
        setBackendChild,
    ] = React.useState(null);

    const [
        backendSubject,
        setBackendSubject,
    ] = React.useState(null);

    const [
        classroomDataLoading,
        setClassroomDataLoading,
    ] = React.useState(true);

    const [
        classroomDataError,
        setClassroomDataError,
    ] = React.useState("");

    const child = backendChild;
    const subject = backendSubject;
    useClassroomDashboard({ role: "parent", children: child ? [child] : [], subjects: subject ? [subject] : [] });
    React.useEffect(() => {
    let isActive = true;

    const loadClassroomData = async () => {
        setClassroomDataLoading(true);
        setClassroomDataError("");

        try {
            const [
                childrenResponse,
                subjectsResponse,
            ] = await Promise.all([
                api.get("/children"),
                api.get(
                    `/children/${encodeURIComponent(
                        childId
                    )}/subjects`
                ),
            ]);

            if (!isActive) {
                return;
            }

            const actualChildren =
                Array.isArray(
                    childrenResponse.data
                )
                    ? childrenResponse.data
                    : [];

            const actualSubjects =
                Array.isArray(
                    subjectsResponse.data
                )
                    ? subjectsResponse.data
                    : [];

            const matchedChild =
                actualChildren.find(
                    (currentChild) =>
                        String(
                            currentChild.child_id
                        ) === String(childId)
                ) || null;

            const matchedSubject =
                actualSubjects.find(
                    (currentSubject) =>
                        String(
                            currentSubject.subject_id ||
                                currentSubject.id
                        ) === String(subjectId)
                ) || null;

            setBackendChild(matchedChild);
            setBackendSubject(
                matchedSubject
            );

            if (
                !matchedChild ||
                !matchedSubject
            ) {
                setClassroomDataError(
                    "This classroom could not be found."
                );
            }
        } catch (error) {
            if (!isActive) {
                return;
            }

            console.error(
                "Failed to load classroom:",
                error
            );

            setClassroomDataError(
                error?.response?.data?.detail ||
                    "Could not load this classroom."
            );
        } finally {
            if (isActive) {
                setClassroomDataLoading(
                    false
                );
            }
        }
    };

    loadClassroomData();

    return () => {
        isActive = false;
    };
}, [childId, subjectId]);

    const eventsKey = "shadowGroveEvents";
    const getShadowGroveEvents = () => {
    try {
        return JSON.parse(localStorage.getItem(eventsKey) || "[]");
    } catch {
        return [];
    }
};

const saveShadowGroveEvents = (nextEvents) => {
    return saveClassroomEvents(nextEvents, { childId, subjectId });
};

const upsertShadowGroveEvent = (eventToSave) => {
    const storedEvents = getShadowGroveEvents();
    const eventId = eventToSave.id || `${Date.now()}-${Math.random()}`;

    const eventWithMeta = {
        ...eventToSave,
        id: eventId,
        created_at: eventToSave.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString(),
    };

    const exists = storedEvents.some((event) => event.id === eventId);

    const updatedEvents = exists
        ? storedEvents.map((event) =>
              event.id === eventId
                  ? {
                        ...event,
                        ...eventWithMeta,
                    }
                  : event
          )
        : [...storedEvents, eventWithMeta];

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);

    return updatedEvents;
};

const deleteShadowGroveEvent = (eventId) => {
    const updatedEvents = getShadowGroveEvents().filter(
        (event) => event.id !== eventId
    );

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);

    return updatedEvents;
};
    const classroomStyleKey = `shadowGroveClassroomStyle_${subjectId}`;
    const classDisplayNameKey = `shadowGroveClassDisplayName_${subjectId}`;

const [classDisplayName, setClassDisplayName] = React.useState(
    () =>
        localStorage.getItem(
            classDisplayNameKey
        ) || ""
);

React.useEffect(() => {
    if (!subject?.name) {
        return;
    }

    const savedClassDisplayName =
        localStorage.getItem(
            classDisplayNameKey
        );

    setClassDisplayName(
        savedClassDisplayName ||
            subject.name
    );
}, [
    classDisplayNameKey,
    subject?.name,
]);

const saveClassDisplayName = () => {
    localStorage.setItem(
        classDisplayNameKey,
        classDisplayName.trim() || subject.name
    );

    alert("Class banner name saved.");
};
    const [classScheduleOpen, setClassScheduleOpen] = React.useState(false);
    const [calendarOpen, setCalendarOpen] = React.useState(false);
    const [shiftScheduleOpen, setShiftScheduleOpen] = React.useState(false);

    const [shiftScheduleForm, setShiftScheduleForm] = React.useState(() => ({
    startDate: new Date().toISOString().slice(0, 10),
    dayOffset: 1,
    direction: "forward",
    scope: "class",
    itemScope: "both",
    includeCompleted: false,
}));
    const classScheduleKey = `shadowGroveClassSchedule_${subjectId}`;
    const [schoolCalendar, setSchoolCalendar] = React.useState(() => {
        try {
            return (
                JSON.parse(
                    localStorage.getItem("shadowGroveSchoolCalendar") || "null"
                ) || {
                    holidays: [],
                    vacations: [],
                    daysOff: [],
                }
            );
        } catch {
            return {
                holidays: [],
                vacations: [],
                daysOff: [],
            };
        }
    });
React.useEffect(() => {
    const refreshSchoolCalendar = () => {
        try {
            const updatedCalendar =
                JSON.parse(
                    localStorage.getItem(
                        "shadowGroveSchoolCalendar"
                    ) || "null"
                ) || {
                    holidays: [],
                    vacations: [],
                    daysOff: [],
                };

            setSchoolCalendar(updatedCalendar);
        } catch (error) {
            console.error(
                "Unable to refresh school calendar:",
                error
            );
        }
    };

    window.addEventListener(
        "storage",
        refreshSchoolCalendar
    );

    window.addEventListener(
        "shadowGroveSchoolCalendarUpdated",
        refreshSchoolCalendar
    );

    return () => {
        window.removeEventListener(
            "storage",
            refreshSchoolCalendar
        );

        window.removeEventListener(
            "shadowGroveSchoolCalendarUpdated",
            refreshSchoolCalendar
        );
    };
}, []);    
    const [classSchedule, setClassSchedule] = React.useState(() =>
        JSON.parse(localStorage.getItem(classScheduleKey) || "null") || {
            days: [],
            allDay: true,
            
        }
    );

    React.useEffect(() => {
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(classScheduleKey) || "null"); } catch { /* Use the classroom's saved schedule. */ }
        const canonical = subject?.class_schedule;
        setClassSchedule(canonical ? {
            ...saved, ...canonical,
            days: canonical.days || [],
            startTime: canonical.start_time || saved?.startTime || "",
            endTime: canonical.end_time || saved?.endTime || "",
            allDay: canonical.all_day ?? !(canonical.start_time && canonical.end_time),
        } : saved || { days: [], allDay: true, startTime: "", endTime: "" });
    }, [classScheduleKey, subject?.class_schedule]);

   const [events, setEvents] = React.useState(() => {
    try {
        return JSON.parse(localStorage.getItem(eventsKey) || "[]");
    } catch {
        return [];
    }
});
React.useEffect(() => {
    const refreshEvents = () => {
        setEvents(getShadowGroveEvents());
    };

    window.addEventListener("shadowGroveEventsUpdated", refreshEvents);
    window.addEventListener("storage", refreshEvents);

    return () => {
        window.removeEventListener("shadowGroveEventsUpdated", refreshEvents);
        window.removeEventListener("storage", refreshEvents);
    };
}, []);
        const toggleScheduleDay = (day) => {
    setClassSchedule((prev) => ({
        ...prev,
        days: prev.days.includes(day)
            ? prev.days.filter((d) => d !== day)
            : [...prev.days, day],
    }));
};

const saveClassSchedule = async () => {
    setDraggedQuestId(null);
    setCalendarWarning("");
    try {
        const response = await api.put(`/subjects/${subjectId}`, {
            class_schedule: {
                ...subject?.class_schedule,
                mode: subject?.class_schedule?.mode || "days",
                days: classSchedule.days,
                all_day: classSchedule.allDay,
                start_time: classSchedule.startTime || "",
                end_time: classSchedule.endTime || "",
            },
        });
        setBackendSubject(response.data);
        localStorage.setItem(classScheduleKey, JSON.stringify(classSchedule));
        toast.success("Class schedule saved.");
    } catch {
        toast.error("The class schedule could not synchronize. Please try again.");
    }
};
const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
};
const shiftDateString = (dateString, dayOffset) => {
    if (!dateString) return dateString;

    const date = parseLocalDate(dateString);
    date.setDate(date.getDate() + dayOffset);

    return formatLocalDate(date);
};

const getShiftEventDate = (event) =>
    event.date_start ||
    event.date ||
    event.due_date ||
    event.scheduled_date;

const isShiftableSession = (event) =>
    Boolean(
        event?.is_calendar_session ||
        event?.is_timed_session ||
        event?.created_from === "class_timer_lesson_flow"
    );

const isShiftableQuest = (event) => !isShiftableSession(event);

const shouldShiftCalendarEvent = (event, form) => {
    const eventDate = getShiftEventDate(event);

    if (!eventDate) return false;
    if (parseLocalDate(eventDate) < parseLocalDate(form.startDate)) return false;

    if (!form.includeCompleted && event.completed) return false;

    if (form.scope === "class") {
        if (event.child_id !== childId) return false;
        if (event.subject_id !== subjectId) return false;
    }

    if (form.scope === "child") {
        if (event.child_id !== childId) return false;
    }

    if (form.itemScope === "quests" && !isShiftableQuest(event)) return false;
    if (form.itemScope === "sessions" && !isShiftableSession(event)) return false;

    return true;
};

const getShiftDayOffset = () => {
    const baseOffset = Number(shiftScheduleForm.dayOffset) || 1;
    return shiftScheduleForm.direction === "backward"
        ? -Math.abs(baseOffset)
        : Math.abs(baseOffset);
};

const getShiftPreviewCount = () => {
    return events.filter((event) =>
        shouldShiftCalendarEvent(event, shiftScheduleForm)
    ).length;
};

const applyShiftSchedule = () => {
    if (!shiftScheduleForm.startDate) {
        toast.error("Choose a start date first.");
        return;
    }

    const dayOffset = getShiftDayOffset();

    const updatedEvents = events.map((event) =>
        shouldShiftCalendarEvent(event, shiftScheduleForm)
            ? {
                  ...event,
                  date: shiftDateString(event.date, dayOffset),
                  date_start: shiftDateString(event.date_start, dayOffset),
                  date_end: shiftDateString(event.date_end, dayOffset),
                  due_date: shiftDateString(event.due_date, dayOffset),
                  scheduled_date: shiftDateString(event.scheduled_date, dayOffset),
                  updated_at: new Date().toISOString(),
              }
            : event
    );

    const shiftedCount = events.filter((event) =>
        shouldShiftCalendarEvent(event, shiftScheduleForm)
    ).length;

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);
    setShiftScheduleOpen(false);

    toast.success(`${shiftedCount} calendar item${shiftedCount === 1 ? "" : "s"} shifted.`);
};
const isVacationDay = (dateString) => {
    const vacationBlocks = (
        schoolCalendar.vacations || []
    ).some((vacation) => {
        const start =
            vacation.start ||
            vacation.date_start;

        const end =
            vacation.end ||
            vacation.date_end ||
            start;

        return (
            Boolean(start) &&
            dateString >= start &&
            dateString <= end
        );
    });

    if (vacationBlocks) {
        return true;
    }

    const holidayBlocks = (
        schoolCalendar.holidays || []
    ).some((holiday) => {
        const start =
            holiday.start ||
            holiday.date ||
            holiday.date_start;

        const end =
            holiday.end ||
            holiday.date_end ||
            start;

        return (
            Boolean(start) &&
            dateString >= start &&
            dateString <= end
        );
    });

    if (holidayBlocks) {
        return true;
    }

    const dayOffBlocks = (
        schoolCalendar.daysOff || []
    ).some((dayOff) => {
        const start =
            dayOff.start ||
            dayOff.date ||
            dayOff.date_start;

        const end =
            dayOff.end ||
            dayOff.date_end ||
            start;

        return (
            Boolean(start) &&
            dateString >= start &&
            dateString <= end
        );
    });

    if (dayOffBlocks) {
        return true;
    }

    return (
        schoolCalendar.items || []
    ).some((item) => {
        if (!item.blocksSchool) {
            return false;
        }

        const start =
            item.start ||
            item.date ||
            item.date_start;

        const end =
            item.end ||
            item.date_end ||
            start;

        return (
            Boolean(start) &&
            dateString >= start &&
            dateString <= end
        );
    });
};

const parseLocalDate = (dateString) => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
};

const isWeekend = (dateString) => {
    const day = parseLocalDate(dateString).getDay();
    return day === 0 || day === 6;
};

const isClassScheduledDay = (dateString) => {
    const schedule = subject?.class_schedule;

    if (!schedule) {
        return true;
    }

    const scheduleMode =
        schedule.mode === "flexible" ||
        schedule.mode === "block"
            ? "block"
            : "days";

    if (scheduleMode === "block") {
        return true;
    }

    const weekday = parseLocalDate(
        dateString
    ).toLocaleDateString("en-US", {
        weekday: "long",
    });

    const scheduledDays = Array.isArray(
        schedule.days
    )
        ? schedule.days
        : [];

    if (scheduledDays.length === 0) {
        return true;
    }

    return scheduledDays.includes(weekday);
};

const isValidSchoolDay = (dateString) => {
    return (
        !isVacationDay(dateString) &&
        isClassScheduledDay(dateString)
    );
};
const generateSequentialDates = (startDate, totalDays) => {
    if (!startDate) return [];

    const dates = [];
    let current = parseLocalDate(startDate);

    while (dates.length < totalDays) {
        const formatted = formatLocalDate(current);

        if (isValidSchoolDay(formatted)) {
            dates.push(formatted);
        }

        current.setDate(current.getDate() + 1);
    }

    return dates;
};
    const generateRepeatQuestDates = (startDate, repeatType, repeatDays = [], total = 12) => {
    if (!startDate || repeatType === "none") return [startDate].filter(Boolean);

    const dates = [];
    let current = parseLocalDate(startDate);

    const shouldUseDate = (dateString) => {
        if (!isValidSchoolDay(dateString)) return false;

        const weekday = parseLocalDate(dateString).toLocaleDateString("en-US", {
            weekday: "long",
        });

        if (repeatType === "daily") return true;

        if (repeatType === "weekly") {
            return dateString === startDate || weekday === parseLocalDate(startDate).toLocaleDateString("en-US", {
                weekday: "long",
            });
        }

        if (repeatType === "custom") {
            return repeatDays.includes(weekday);
        }

        if (repeatType === "monthly") {
            return parseLocalDate(dateString).getDate() === parseLocalDate(startDate).getDate();
        }

        return false;
    };

    for (let guard = 0; guard < 370 && dates.length < total; guard++) {
        const dateString = formatLocalDate(current);

        if (shouldUseDate(dateString)) {
            dates.push(dateString);
        }

        current.setDate(current.getDate() + 1);
    }

    return dates;
};

    const [classroomStyle, setClassroomStyle] = React.useState(() =>
        JSON.parse(localStorage.getItem(classroomStyleKey) || "null") || {
            icon: "",
            banner: "",
        }
    );


    const classTimerKey = `shadowGroveClassTimer_${childId}_${subjectId}`;
    const classSessionsKey = "shadowGroveClassSessions";

const getSavedClassSessions = () => {
    return JSON.parse(localStorage.getItem(classSessionsKey) || "[]");
};

const getCurrentClassSessions = () => {
    return getSavedClassSessions().filter(
        (session) =>
            session.child_id === childId &&
            session.subject_id === subjectId
    );
};

const getSessionHoursTotal = (sessions) => {
    const totalSeconds = sessions.reduce(
        (total, session) => total + (Number(session.total_seconds) || 0),
        0
    );

    return (totalSeconds / 3600).toFixed(2);
};
    const savedSessionLessonsKey = `shadowGroveSessionLessons_${childId}_${subjectId}`;
const activeSessionLessonKey = `shadowGroveActiveSessionLesson_${childId}_${subjectId}`;

const [sessionLessonName, setSessionLessonName] = React.useState("");
const [sessionCalendarDate, setSessionCalendarDate] = React.useState(
    formatLocalDate(new Date())
);
const [sessionDayMode, setSessionDayMode] = React.useState("all");
const [sessionSelectedDays, setSessionSelectedDays] = React.useState([]);
const [sessionFlowStartTime, setSessionFlowStartTime] = React.useState("");
const [sessionTimeWarning, setSessionTimeWarning] = React.useState(null);
const sessionOutsideConfirmedRef = React.useRef(false);
const [sessionRepeatMode, setSessionRepeatMode] =
    React.useState("none");

const [sessionRepeatInterval, setSessionRepeatInterval] =
    React.useState(1);

const [sessionRepeatEndDate, setSessionRepeatEndDate] =
    React.useState("");

const [sessionRepeatWeekdays, setSessionRepeatWeekdays] =
    React.useState([]);
const [savedSessionLessons, setSavedSessionLessons] = React.useState(() =>
    JSON.parse(localStorage.getItem(savedSessionLessonsKey) || "[]")
);
const [selectedCalendarLessonId, setSelectedCalendarLessonId] =
    React.useState("");
const [activeSessionLessonId, setActiveSessionLessonId] = React.useState(() =>
    localStorage.getItem(activeSessionLessonKey) || ""

);
const hasLoadedActiveSessionLesson = React.useRef(false);
const weekScrollRef = React.useRef(null);
const weekScrollLeftRef = React.useRef(0);

const rememberWeekScroll = () => {
    if (weekScrollRef.current) {
        weekScrollLeftRef.current = weekScrollRef.current.scrollLeft;
    }
};

const restoreWeekScroll = () => {
    requestAnimationFrame(() => {
        if (weekScrollRef.current) {
            weekScrollRef.current.scrollLeft = weekScrollLeftRef.current;
        }
    });
};
    const [renameSessionLessonModal, setRenameSessionLessonModal] = React.useState(null);
const [renameSessionLessonValue, setRenameSessionLessonValue] = React.useState("");
const [pendingSessionLessonSwitch, setPendingSessionLessonSwitch] = React.useState(null);    
const [showSessionFlowTools, setShowSessionFlowTools] = React.useState(true);
const [showSessionFlowBuilder, setShowSessionFlowBuilder] = React.useState(true);
const [calendarMoreModal, setCalendarMoreModal] = React.useState(null);
const [showSavedSessionLessons, setShowSavedSessionLessons] =
    React.useState(true);
const getSavedTimer = React.useCallback(
    () =>
        JSON.parse(
            localStorage.getItem(
                classTimerKey
            ) || "null"
        ),
    [classTimerKey]
);

const calculateTimerSeconds = React.useCallback(() => {
    const saved = getSavedTimer();

    if (!saved) return 0;

    if (saved.running && saved.started_at) {
        const elapsed = Math.floor(
            (Date.now() -
                new Date(
                    saved.started_at
                ).getTime()) /
                1000
        );

        return (
            (Number(
                saved.base_seconds
            ) || 0) + elapsed
        );
    }

    return Number(
        saved.base_seconds
    ) || 0;
}, [getSavedTimer]);

const [timerSeconds, setTimerSeconds] = React.useState(calculateTimerSeconds);
const [sectionStartedAtMainSeconds, setSectionStartedAtMainSeconds] =
    React.useState(0);
const [timerRunning, setTimerRunning] = React.useState(
    () => Boolean(getSavedTimer()?.running)
);

React.useEffect(() => {
    if (!timerRunning) return;

    const interval = setInterval(() => {
        setTimerSeconds(
            calculateTimerSeconds()
        );
    }, 1000);

    return () =>
        clearInterval(interval);
}, [
    timerRunning,
    calculateTimerSeconds,
]);
const adjustClassTimerSeconds = (amount) => {
    const currentSeconds = timerRunning
        ? calculateTimerSeconds()
        : timerSeconds;

    const nextSeconds = Math.max(
        0,
        currentSeconds + amount
    );

    setTimerSeconds(nextSeconds);

    localStorage.setItem(
        classTimerKey,
        JSON.stringify({
            running: timerRunning,
            base_seconds: nextSeconds,
            started_at: timerRunning
                ? new Date().toISOString()
                : null,
            child_id: childId,
            subject_id: subjectId,
            subject_name: subject?.name || "Class",
            updated_at: new Date().toISOString(),
        })
    );
};
const handleTimerStartPause = async (
    requestedStepId = null
) => {
    const selectedStepId =
        typeof requestedStepId === "string"
            ? requestedStepId
            : activeSectionId || null;

    const savedTimer = getSavedTimer();

    if (timerRunning) {
        const currentSeconds =
            calculateTimerSeconds();

        const previousBaseSeconds =
            Math.max(
                0,
                Number(
                    savedTimer?.base_seconds
                ) || 0
            );

        const previousStartedAt =
            savedTimer?.started_at ||
            new Date().toISOString();

        localStorage.setItem(
            classTimerKey,
            JSON.stringify({
                running: false,
                base_seconds:
                    currentSeconds,
                started_at: null,
                child_id: childId,
                subject_id: subjectId,
                subject_name:
                    subject?.name ||
                    "Class",
                updated_at:
                    new Date().toISOString(),
            })
        );

        setTimerRunning(false);
        setTimerSeconds(
            currentSeconds
        );

        setClassTimerSections(
            (previousSections) =>
                previousSections.map(
                    (section) => ({
                        ...section,
                        running: false,
                    })
                )
        );

        await sendSharedSessionCommand(
            "pause",
            {
                stateOverrides: {
                    status: "running",

                    active_step_id:
                        selectedStepId,

                    active_step_started_at:
                        null,

                    base_seconds:
                        previousBaseSeconds,

                    started_at:
                        previousStartedAt,

                    session_started_at:
                        classSessionStartedAt ||
                        null,
                },
            }
        );

        return;
    }

    const startedAt =
        new Date().toISOString();

    const command =
        classSessionStartedAt
            ? "resume"
            : "start";

    localStorage.setItem(
        classTimerKey,
        JSON.stringify({
            running: true,
            base_seconds:
                timerSeconds,
            started_at: startedAt,
            child_id: childId,
            subject_id: subjectId,
            subject_name:
                subject?.name ||
                "Class",
            updated_at: startedAt,
        })
    );

    setTimerRunning(true);

    if (selectedStepId) {
        setClassTimerSections(
            (previousSections) =>
                previousSections.map(
                    (section) => ({
                        ...section,

                        running:
                            section.id ===
                            selectedStepId,
                    })
                )
        );
    }

    await sendSharedSessionCommand(
        command,
        {
            stateOverrides: {
                status:
                    command === "resume"
                        ? "paused"
                        : "waiting",

                active_step_id:
                    selectedStepId,

                active_step_started_at:
                    null,

                base_seconds:
                    Math.max(
                        0,
                        Number(
                            timerSeconds
                        ) || 0
                    ),

                started_at: null,

                session_started_at:
                    classSessionStartedAt ||
                    null,
            },
        }
    );
};

    const handleTimerReset = () => {
    localStorage.setItem(
        classTimerKey,
        JSON.stringify({
            running: false,
            base_seconds: 0,
            started_at: null,
            child_id: childId,
            subject_id: subjectId,
            subject_name: subject?.name || "Class",
            updated_at: new Date().toISOString(),
        })
    );

    setTimerRunning(false);
    setTimerSeconds(0);
    setActiveSectionId(null);
    setClassSessionStartedAt(null);
    setClassTimerSections((prev) =>
        prev.map((section) => ({
            ...section,
            actualSeconds: 0,
            running: false,
            completed: false,
        }))
    );
};
    const [classSessionStartedAt, setClassSessionStartedAt] = React.useState(null);
    const [showSessionCompleteModal, setShowSessionCompleteModal] = React.useState(false);
    const [completedSessionSummary, setCompletedSessionSummary] = React.useState(null);
    const [workspaceTab, setWorkspaceTab] = React.useState("notebook");

    const [showWorkspace, setShowWorkspace] = React.useState(true);

    const [showLessonStage, setShowLessonStage] = React.useState(true);

    const [showGadgets, setShowGadgets] = React.useState(true);

    const [workspaceWidth, setWorkspaceWidth] = React.useState(34);
    const [showLessonFlowModal, setShowLessonFlowModal] = React.useState(false);
    const [classTimerSections, setClassTimerSections] = React.useState([
    
    {
        id: Date.now().toString(),
        title: "Class Lecture",
        type: "lesson",
        description: "",
        materials: [
            {
                id: `${Date.now()}-${Math.random()}`,
                name: "",
                checked: false,
            },
        ],
        plannedMinutes: 20,
        actualSeconds: 0,
        running: false,
        completed: false,
    }
]);
    
    const [activeSectionId, setActiveSectionId] = React.useState(null);    

    const [autoStartNextSection, setAutoStartNextSection] =
    React.useState(false);

    const [
        autoEndClassAfterLastSection,
        setAutoEndClassAfterLastSection,
    ] = React.useState(false);

    const [
        childSessionPermissions,
        setChildSessionPermissions,
    ] = React.useState({
        child_can_start: false,
        child_can_pause: false,
        child_can_skip: false,
        child_can_reorder: false,
        child_can_end: false,
    });

    const sharedSessionStatusRef = React.useRef(null);
    const sharedSessionRevisionRef =
        React.useRef(0);

    const applySharedSessionState = React.useCallback(
        (session) => {
            if (!session) return;

        const remoteSteps = Array.isArray(session.steps)
            ? session.steps
            : [];

        const isRunning = session.status === "running";

        const elapsedSeconds = Math.max(
            0,
            Number(
                session.elapsed_seconds ??
                    session.base_seconds ??
                    0
            )
        );

        const previousStatus = sharedSessionStatusRef.current;
        sharedSessionStatusRef.current = session.status;
        if (session.status === "completed" && previousStatus && previousStatus !== "completed") {
            setCompletedSessionSummary({totalSeconds: elapsedSeconds, endedAt: session.updated_at || new Date().toISOString(), sections: remoteSteps.map(step => ({id: step.step_id, title: step.title, type: step.step_type, plannedMinutes: step.planned_minutes || 0, actualSeconds: step.actual_seconds || 0, completed: Boolean(step.completed), skipped: Boolean(step.skipped), materials: step.materials || []}))});
            setShowSessionCompleteModal(true);
        }
        setTimerRunning(isRunning);
        setTimerSeconds(elapsedSeconds);

        setClassSessionStartedAt(
            session.session_started_at || null
        );

        setActiveSectionId(
            session.active_step_id || null
        );

        setSectionStartedAtMainSeconds(
            elapsedSeconds
        );

        setAutoStartNextSection(
            Boolean(session.auto_start_next_step)
        );

setAutoEndClassAfterLastSection(
    Boolean(session.auto_end_after_last_step)
);

setChildSessionPermissions({
    child_can_start: Boolean(
        session.child_can_start
    ),
    child_can_pause: Boolean(
        session.child_can_pause
    ),
    child_can_skip: Boolean(
        session.child_can_skip
    ),
    child_can_reorder: Boolean(
        session.child_can_reorder
    ),
    child_can_end: Boolean(
        session.child_can_end
    ),
});

if (session.active_lesson_id) {
            hasLoadedActiveSessionLesson.current = true;

            setActiveSessionLessonId(
                session.active_lesson_id
            );

            localStorage.setItem(
                activeSessionLessonKey,
                session.active_lesson_id
            );
        } else {
            setActiveSessionLessonId("");
            localStorage.removeItem(
                activeSessionLessonKey
            );
        }

        setSessionLessonName(
            session.active_lesson_name || ""
        );

        if (remoteSteps.length > 0) {
            setClassTimerSections(
                remoteSteps.map((step) => ({
                    id: step.step_id,
                    title:
                        step.title ||
                        "Untitled Section",
                    type:
                        step.step_type ||
                        "lesson",
                    description:
                        step.description || "",
                    materials: Array.isArray(
                        step.materials
                    )
                        ? step.materials
                        : [],
                    plannedMinutes:
                        Number(
                            step.planned_minutes
                        ) || 0,
                    actualSeconds:
                        Number(
                            step.actual_seconds
                        ) || 0,
                    running: Boolean(
                        step.running
                    ),
                    completed: Boolean(
                        step.completed
                    ),
                    skipped: Boolean(
                        step.skipped
                    ),
                    questId:
                        step.quest_id || null,
                }))
            );
        }

        localStorage.setItem(
            classTimerKey,
            JSON.stringify({
                running: isRunning,
                base_seconds: elapsedSeconds,
                started_at: isRunning
                    ? new Date().toISOString()
                    : null,
                child_id: childId,
                subject_id: subjectId,
                subject_name:
                    session.subject_name ||
                    subject?.name ||
                    "Class",
                updated_at:
                    session.updated_at ||
                    new Date().toISOString(),
            })
        );
    },
    [
        activeSessionLessonKey,
        childId,
        classTimerKey,
        subject?.name,
        subjectId,
    ]
);

React.useEffect(() => {
    let cancelled = false;

    const loadSharedSession = async () => {
        try {
            const response = await api.get(
                `/children/${childId}/classes/${subjectId}/session`,
                {
                    params: {
                        subject_name:
                            subject?.name ||
                            "Class",
                    },
                }
            );

            if (cancelled) return;

            const session = response.data;

            const revision =
                Number(session?.revision) || 0;

            const hasRemoteSession =
                session?.status !== "waiting" ||
                Boolean(
                    session?.session_started_at
                ) ||
                Boolean(
                    Array.isArray(
                        session?.steps
                    ) &&
                        session.steps.length > 0
                );

            if (
                hasRemoteSession &&
                revision !==
                    sharedSessionRevisionRef.current
            ) {
                sharedSessionRevisionRef.current =
                    revision;

                applySharedSessionState(
                    session
                );

                return;
            }

            if (
                sharedSessionRevisionRef.current ===
                0
            ) {
                sharedSessionRevisionRef.current =
                    revision;
            }
        } catch (error) {
            console.error(
                "Unable to load shared class session:",
                error
            );
        }
    };

    loadSharedSession();
    window.addEventListener("shadowGroveLessonUpdated", loadSharedSession);

    const interval = window.setInterval(
        loadSharedSession,
        2000
    );

    return () => {
        cancelled = true;
        window.clearInterval(interval);
        window.removeEventListener("shadowGroveLessonUpdated", loadSharedSession);
    };
}, [
    applySharedSessionState,
    childId,
    subject?.name,
    subjectId,
]);

const buildSharedSessionPayload = (
    overrides = {}
) => {
    const savedTimer = getSavedTimer();

    const currentSeconds = timerRunning
        ? calculateTimerSeconds()
        : timerSeconds;

    const defaultStatus = timerRunning
        ? "running"
        : classSessionStartedAt
          ? "paused"
          : "waiting";

    return {
        session_start_time: sessionFlowStartTime || classSchedule.startTime || null,
        outside_class_time_confirmed: sessionOutsideConfirmedRef.current,
        subject_name:
            subject?.name || "Class",

        status:
            overrides.status ??
            defaultStatus,

        active_lesson_id:
            overrides.active_lesson_id ??
            activeSessionLessonId ??
            null,

        active_lesson_name:
            overrides.active_lesson_name ??
            sessionLessonName ??
            "",

        active_step_id:
            overrides.active_step_id ??
            activeSectionId ??
            null,

        active_step_started_at:
            overrides.active_step_started_at ??
            null,

base_seconds:
    overrides.base_seconds ??
    Math.max(
        0,
        Number(
            timerRunning
                ? savedTimer?.base_seconds ??
                  timerSeconds
                : timerSeconds
        ) || 0
    ),

started_at:
    overrides.started_at !== undefined
        ? overrides.started_at
        : timerRunning
          ? savedTimer?.started_at ||
            new Date().toISOString()
          : null,

        session_started_at:
            overrides.session_started_at ??
            classSessionStartedAt ??
            null,

        steps: classTimerSections.map(
            (section) => ({
                step_id: String(section.id),

                title:
                    section.title ||
                    "Untitled Section",

                step_type:
                    section.type ||
                    "lesson",

                description:
                    section.description || "",

                materials: Array.isArray(
                    section.materials
                )
                    ? section.materials
                    : [],

                planned_minutes:
                    Math.max(
                        0,
                        Number(
                            section.plannedMinutes
                        ) || 0
                    ),

                actual_seconds:
                    Math.max(
                        0,
                        Number(
                            section.actualSeconds
                        ) || 0
                    ),

                running:
                    Boolean(
                        section.running
                    ),

                completed:
                    Boolean(
                        section.completed
                    ),

                skipped:
                    Boolean(
                        section.skipped
                    ),

                quest_id:
                    section.questId ||
                    section.quest_id ||
                    null,
            })
        ),

        auto_start_next_step:
            Boolean(autoStartNextSection),

        auto_end_after_last_step:
            Boolean(
                autoEndClassAfterLastSection
            ),

child_can_start:
    childSessionPermissions.child_can_start,

child_can_pause:
    childSessionPermissions.child_can_pause,

child_can_skip:
    childSessionPermissions.child_can_skip,

child_can_reorder:
    childSessionPermissions.child_can_reorder,

child_can_end:
    childSessionPermissions.child_can_end,

controlled_by: "parent",

        ...overrides,
    };
};

const saveSharedSessionSnapshot = async (
    overrides = {}
) => {
    const response = await api.put(
        `/children/${childId}/classes/${subjectId}/session`,
        buildSharedSessionPayload(overrides)
    );

    sharedSessionRevisionRef.current =
        Number(
            response.data?.revision
        ) || 0;

    return response.data;
};

const saveSessionToLessonStage = async (options = {}) => {
    if (!sessionLessonName.trim()) {toast.error("Name this session lesson first."); return;}
    if (sessionCalendarDate !== formatLocalDate(new Date())) {toast.error("Use Send Lesson Flow to Calendar for a future session date."); return;}
    const windowCheck = sessionWindow({classStart: classSchedule.startTime, classEnd: classSchedule.endTime, start: sessionFlowStartTime, sections: classTimerSections});
    if (windowCheck.invalid) {toast.error("Adjust the session start time or minutes."); return;}
    if (windowCheck.outside && !options.confirmOutside && !sessionOutsideConfirmedRef.current) {setSessionTimeWarning({action: "save", repeating: false}); return;}
    try {await waitForClassroomSync(); const saved = await saveSharedSessionSnapshot(); applySharedSessionState(saved); window.dispatchEvent(new CustomEvent("shadowGroveLessonUpdated")); toast.success("Today's session is ready on Lesson Stage.");}
    catch (error) {toast.error(error.response?.data?.detail || "The session could not be saved.");}
};
const updateChildSessionPermission =
    async (permissionName, allowed) => {
        const nextPermissions = {
            ...childSessionPermissions,
            [permissionName]: allowed,
        };

        setChildSessionPermissions(
            nextPermissions
        );

        try {
            const savedSession =
                await saveSharedSessionSnapshot(
                    nextPermissions
                );

            applySharedSessionState(
                savedSession
            );

            toast.success(
                allowed
                    ? "Child session control enabled."
                    : "Child session control disabled."
            );
        } catch (error) {
            console.error(
                "Unable to update child session permission:",
                error
            );

            setChildSessionPermissions(
                childSessionPermissions
            );

            toast.error(
                error?.response?.data?.detail ||
                    "The child session permission could not be saved."
            );
        }
    };

const sendSharedSessionCommand = async (
    command,
    {
        stepId = null,
        stateOverrides = {},
    } = {}
) => {
    try {
        await saveSharedSessionSnapshot(
            stateOverrides
        );

        const response = await api.post(
            `/children/${childId}/classes/${subjectId}/session/command`,
            {
                command,
                actor_role: "parent",
                step_id: stepId,
            }
        );

        sharedSessionRevisionRef.current =
            Number(
                response.data?.revision
            ) || 0;

        applySharedSessionState(
            response.data
        );

        return response.data;
    } catch (error) {
        console.error(
            `Unable to send class-session command "${command}":`,
            error
        );

        toast.error(
            error?.response?.data?.detail ||
                "The shared class session could not be updated."
        );

        return null;
    }
};
    React.useEffect(() => {
        if (timerRunning) return;
        if (String(activeSessionLessonId || "").startsWith("lesson_")) return;
        if (!Array.isArray(events)) return;
        if (!Array.isArray(savedSessionLessons)) return;

        const todayDate = formatLocalDate(new Date());

        const todayCalendarSections = assignmentTimerSections(events, childId, subjectId, todayDate).filter((event) => event.status !== "completed");

        if (todayCalendarSections.length === 0) {
            setSessionLessonName("");
            setActiveSessionLessonId("");
            setActiveSectionId(null);
            setClassTimerSections([]);
            setActiveLesson(null);
            setActiveSection(null);
            localStorage.removeItem(activeSessionLessonKey);
            return;
        }

        const firstCalendarSection =
            todayCalendarSections[0];

        const scheduledTemplate =
            savedSessionLessons.find(
                (template) =>
                    template.id ===
                    firstCalendarSection.session_lesson_template_id
            ) || null;
        
        const activeLessonModel =
            buildLessonFromCalendarFlow({
                calendarSections: todayCalendarSections,
                savedTemplate: scheduledTemplate,
            });

        const loadedSections =
            todayCalendarSections.map(
                (calendarSection, index) => {
                    const templateSection =
                        scheduledTemplate?.sections?.[index] ||
                        null;

                    const sourceSection =
                        templateSection ||
                        calendarSection;

                    const sourceMaterials =
                        Array.isArray(sourceSection.materials)
                            ? sourceSection.materials
                            : Array.isArray(
                                calendarSection.materials_needed
                            )
                            ? calendarSection.materials_needed
                            : [];

                    return {
                        ...sourceSection,

                        id: calendarSection.id,

                        calendar_session_event_id:
                            calendarSection.id,

                        session_flow_id:
                            calendarSection.session_flow_id ||
                            null,

                        session_occurrence_date:
                            todayDate,

                        session_section_order:
                            Number(
                                calendarSection.session_order ||
                                index + 1
                            ),

                        session_lesson_template_id:
                            firstCalendarSection.session_lesson_template_id ||
                            null,

                        title:
                            sourceSection.title ||
                            calendarSection.title ||
                            `Session ${index + 1}`,

                        description:
                            sourceSection.description ||
                            calendarSection.description ||
                            "",

                        materials:
                            sourceMaterials.map(
                                (material, materialIndex) => ({
                                    id:
                                        material.id ||
                                        `${calendarSection.id}-material-${materialIndex}`,

                                    name:
                                        typeof material === "string"
                                            ? material
                                            : material.name || "",

                                    checked:
                                        Boolean(
                                            typeof material === "object" &&
                                                material.checked
                                        ),
                                })
                            ),

                        plannedMinutes:
                            Number(sourceSection.plannedMinutes) ||
                            Number(
                                calendarSection.duration_minutes
                            ) ||
                            15,

                        actualSeconds:
                            Number(
                                calendarSection.actual_seconds
                            ) || 0,

                        running: false,

                        completed:
                            calendarSection.completed === true ||
                            calendarSection.status === "completed",

                        skipped:
                            Boolean(calendarSection.skipped),
                    };
                }
            );

        setClassTimerSections(loadedSections);
        setActiveSectionId(null);
        setActiveLesson(activeLessonModel);
        setActiveSection(null);
        setActiveLesson(activeLessonModel);

        setSessionLessonName(
            scheduledTemplate?.name ||
            firstCalendarSection.session_lesson_name ||
            "Today’s Session"
        );

        setActiveSessionLessonId(
            scheduledTemplate?.id ||
            firstCalendarSection.session_lesson_template_id ||
            ""
        );

        setAutoStartNextSection(
            Boolean(
                scheduledTemplate?.autoStartNextSection
            )
        );

        setAutoEndClassAfterLastSection(
            Boolean(
                scheduledTemplate?.autoEndClassAfterLastSection
            )
        );

        if (scheduledTemplate?.id) {
            localStorage.setItem(
                activeSessionLessonKey,
                scheduledTemplate.id
            );
        }
    }, [
        events,
        savedSessionLessons,
        childId,
        subjectId,
        timerRunning,
        activeSessionLessonKey,
        activeSessionLessonId,
        setActiveLesson,
        setActiveSection,
    ]);
    const formatTimer = (seconds) => {
    
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(
        2,
        "0"
    )}:${String(secs).padStart(2, "0")}`;
};
    const timeToMinutes = (time) => {
    if (!time || !time.includes(":")) return null;

    const [hours, minutes] = time.split(":").map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    return hours * 60 + minutes;
};
    const getTimedItemLayout = (items = []) => {
    const sorted = [...items].sort((a, b) => {
        const aStart = timeToMinutes(a.startTime || a.start_time || a.time || "00:00");
        const bStart = timeToMinutes(b.startTime || b.start_time || b.time || "00:00");
        return aStart - bStart;
    });

    const columns = [];

    return sorted.map((item) => {
        const start = timeToMinutes(item.startTime || item.start_time || item.time || "00:00");
        const end = timeToMinutes(item.endTime || item.end_time || item.due_time || item.startTime || item.time || "00:30");

        let columnIndex = columns.findIndex((columnEnd) => columnEnd <= start);

        if (columnIndex === -1) {
            columnIndex = columns.length;
            columns.push(end);
        } else {
            columns[columnIndex] = end;
        }

        const overlapCount = Math.max(columns.length, 1);

        return {
            ...item,
            overlapIndex: columnIndex,
            overlapCount,
        };
    });
};

const minutesToTimeLabel = (minutes) => {
    if (minutes == null) return "";

    const hours24 = Math.floor(minutes / 60);
    const mins = minutes % 60;

    const suffix = hours24 >= 12 ? "PM" : "AM";
    const hours12 = hours24 % 12 || 12;

    return `${hours12}:${String(mins).padStart(2, "0")} ${suffix}`;
};

const getSessionStartMinutes = (session) => {
    return timeToMinutes(
        session.scheduled_start_time ||
        session.start_time
    );
};
const minutesToInputTime = (minutes) => {
    if (minutes == null) return "";

    const safeMinutes = Math.max(0, Math.min(minutes, 23 * 60 + 59));

    const hours = Math.floor(safeMinutes / 60);
    const mins = safeMinutes % 60;

    return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};
const getSessionEndMinutes = (session) => {
    const explicitEnd = timeToMinutes(
        session.scheduled_end_time ||
        session.end_time
    );

    if (explicitEnd != null) return explicitEnd;

    const start = getSessionStartMinutes(session);
    const duration =
        Number(session.duration_minutes) ||
        Number(session.plannedMinutes) ||
        30;

    return start == null ? null : start + duration;
};

const getCalendarItemColor = (item) => classroomCalendarColor(item, subject?.color);

const isTimedSession = (item) => {
    if (!item) return false;

    if (
        item.all_day === true ||
        item.is_all_day === true ||
        item.unscheduled === true
    ) {
        return false;
    }

    const startMinutes =
        getSessionStartMinutes(item) ??
        timeToMinutes(item.start_time);

    const endMinutes =
        getSessionEndMinutes(item) ??
        timeToMinutes(item.end_time);

    return (
        item.is_timed_session === true ||
        (startMinutes != null && endMinutes != null)
    );
};

const getTimedSessionsForDate = (items, date) => {
    return items
        .filter((item) => {
            const itemDate = item.date || item.date_start || item.due_date;
            return itemDate === date && isTimedSession(item);
        })
        .sort(
            (a, b) =>
                (getSessionStartMinutes(a) ?? 9999) -
                (getSessionStartMinutes(b) ?? 9999)
        );
};

const getUnscheduledItemsForDate = (items, date) => {
    return items.filter((item) => {
        const itemDate = item.date || item.date_start || item.due_date;
        return itemDate === date && !isTimedSession(item);
    });
};

const BLOCK_DAY_START = 7 * 60;
const BLOCK_DAY_END = 20 * 60;
const PIXELS_PER_MINUTE = 4;

const getBlockTop = (startMinutes) => {
    return Math.max(0, (startMinutes - BLOCK_DAY_START) * PIXELS_PER_MINUTE);
};

const getBlockHeight = (startMinutes, endMinutes) => {
    const duration = Math.max(15, endMinutes - startMinutes);
    return duration * PIXELS_PER_MINUTE;
};
    const formatDisplayTime = (timeString) => {
    if (!timeString) return "";

    const [hourPart, minutePart] = timeString.split(":");
    const hour = Number(hourPart);
    const minute = minutePart || "00";

    if (Number.isNaN(hour)) return timeString;

    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;

    return `${displayHour}:${minute} ${suffix}`;
};

    const getSectionProgressColor = (section) => {
    const plannedSeconds = (Number(section.plannedMinutes) || 0) * 60;
    const actualSeconds = Number(section.actualSeconds) || 0;

    if (!plannedSeconds || plannedSeconds <= 0) return "#ffd166";

    const progress = actualSeconds / plannedSeconds;

    if (progress >= 0.9) return "#ef4444"; // red
    if (progress >= 0.75) return "#fb923c"; // orange
    if (progress >= 0.5) return "#facc15"; // yellow

    return "#4ade80"; // green
};
const getActiveSection = React.useCallback(() => {
    return classTimerSections.find(
        (section) => section.id === activeSectionId
    );
}, [
    classTimerSections,
    activeSectionId,
]);
const getNextSection = () => {
    const currentIndex = classTimerSections.findIndex(
        (section) => section.id === activeSectionId
    );

    if (currentIndex === -1) return null;

    return classTimerSections[currentIndex + 1] || null;
};
const getActiveSectionColor = () => {
    const activeSection = getActiveSection();

    if (!activeSection) return "#ffd166";

    return getSectionProgressColor(activeSection);
};
      const getOverallClassProgress = () => {
    const totalPlannedSeconds = classTimerSections.reduce(
        (total, section) =>
            total + (Number(section.plannedMinutes) || 0) * 60,
        0
    );

    if (!totalPlannedSeconds) return 0;

    const completedOrSkippedSeconds = classTimerSections.reduce(
        (total, section) => {
            const plannedSeconds =
                (Number(section.plannedMinutes) || 0) * 60;

            if (section.completed) {
                return total + plannedSeconds;
            }

            return (
                total +
                Math.min(
                    Number(section.actualSeconds) || 0,
                    plannedSeconds
                )
            );
        },
        0
    );

    return Math.min(
        100,
        Math.round(
            (completedOrSkippedSeconds / totalPlannedSeconds) * 100
        )
    );
};
    const getActiveSectionRemainingSeconds = () => {
    const activeSection = getActiveSection();

    if (!activeSection) return 0;

    const plannedSeconds =
        (Number(activeSection.plannedMinutes) || 0) * 60;

    const actualSeconds = Number(activeSection.actualSeconds) || 0;

    return Math.max(plannedSeconds - actualSeconds, 0);
};
const sessionMinutesSignature = classTimerSections.map(section => section.plannedMinutes).join(",");
React.useEffect(() => {sessionOutsideConfirmedRef.current = false;}, [sessionFlowStartTime, sessionCalendarDate, sessionMinutesSignature]);
const getSessionRepeatDates = () => sessionDates({start: sessionCalendarDate, end: sessionRepeatEndDate, repeat: sessionRepeatMode, classDays: classSchedule.days?.length ? classSchedule.days : defaultClassDays, mode: sessionDayMode, selected: sessionSelectedDays, interval: sessionRepeatInterval}, isValidSchoolDay);
const commitCalendarSessionFlow = ({
    newSessions,
    repeatDates,
    replaceExisting = false,
}) => {
    const repeatDateSet = new Set(repeatDates);

    let baseEvents = events;

    if (replaceExisting) {
        baseEvents = events.filter((event) => {
            const eventDate =
                event.date_start ||
                event.date ||
                event.due_date;

            const matchesExistingFlow =
                event.child_id === childId &&
                event.subject_id === subjectId &&
                repeatDateSet.has(eventDate) &&
                event.created_from ===
                    "class_timer_lesson_flow";

            return !matchesExistingFlow;
        });
    }

    newSessions = groupLessonCalendarEvents(newSessions).map(session => {
        const canonical = baseEvents.find(event => event.created_from === "lesson_stage" && event.child_id === childId && event.subject_id === subjectId && event.date_start === session.date_start);
        return canonical ? {...canonical, ...session, id: canonical.id, event_id: canonical.event_id || canonical.id, resources: canonical.resources || []} : session;
    });
    const incomingIds = new Set(newSessions.map(event => event.event_id || event.id));
    const updatedEvents = [...newSessions, ...baseEvents.filter(event => !incomingIds.has(event.event_id || event.id))];

    saveShadowGroveEvents(updatedEvents);

    const confirmedEvents =
        getShadowGroveEvents();

    const confirmedIds = new Set(
        confirmedEvents.map(
            (event) => event.id
        )
    );

    const everySessionWasSaved =
        newSessions.every(
            (session) =>
                confirmedIds.has(session.id)
        );

    if (!everySessionWasSaved) {
        toast.error(
            "The lesson flow could not be confirmed in the calendar. The builder was not reset."
        );
        return false;
    }

    setEvents(confirmedEvents);

    const firstSessionDate =
        repeatDates[0];

    setSelectedPlannerDate(
        firstSessionDate
    );

    setCalendarCursorDate(
        firstSessionDate
    );

    setPlannerViewMode("day");

    localStorage.setItem(
        plannerViewModeKey,
        "day"
    );

    createNewSessionFlow();

    setSessionCalendarDate("");
    setSessionRepeatMode("none");
    setSessionRepeatInterval(1);
    setSessionRepeatEndDate("");
    setSessionRepeatWeekdays([]);
    setSelectedCalendarLessonId("");
    setPendingSessionFlowConflict(null);

    selectClassroomPanel(
        "calendar"
    );

    toast.success(
        `${newSessions.length} session block${
            newSessions.length === 1
                ? ""
                : "s"
        } saved across ${repeatDates.length} class date${
            repeatDates.length === 1
                ? ""
                : "s"
        }.`
    );

    return true;
};
const createCalendarSessionsFromLessonFlow = (options = {}) => {
    if (!sessionCalendarDate) {
        toast.error("Choose a session date first.");
        return;
    }

    if (!Array.isArray(classTimerSections) || classTimerSections.length === 0) {
        toast.error("Add at least one session section first.");
        return;
    }

    if (!isValidSchoolDay(sessionCalendarDate)) {
        toast.error(
            "That date is not a valid school/class day. Choose a scheduled class day or update the school calendar."
        );
        return;
    }

    if (
        sessionRepeatMode !== "none" &&
        !sessionRepeatEndDate
    ) {
        toast.error("Choose a Repeat Until date.");
        return;
    }

    if (
        sessionDayMode === "custom" &&
        sessionSelectedDays.length === 0
    ) {
        toast.error("Choose at least one custom repeat day.");
        return;
    }

const isFlexibleSessionFlow =
    !sessionFlowStartTime && (classSchedule.allDay || !classSchedule.startTime);

const classStartMinutes =
    isFlexibleSessionFlow
        ? null
        : timeToMinutes(sessionFlowStartTime || classSchedule.startTime);

if (
    !isFlexibleSessionFlow &&
    classStartMinutes == null
) {
    toast.error("Class start time is invalid.");
    return;
}

    const windowCheck = sessionWindow({classStart: classSchedule.startTime, classEnd: classSchedule.endTime, start: sessionFlowStartTime, sections: classTimerSections});
    if (windowCheck.invalid) {toast.error("Session sections must finish before midnight. Adjust the start time or minutes."); return;}
    if (windowCheck.outside && !options.confirmOutside && !sessionOutsideConfirmedRef.current) {setSessionTimeWarning({action: "calendar", repeating: sessionRepeatMode !== "none"}); return;}
    const repeatDates =
        getSessionRepeatDates();

    if (windowCheck.outside && (options.confirmOutside || sessionOutsideConfirmedRef.current)) repeatDates.splice(1);
    if (repeatDates.length === 0) {
        toast.error(
            "No valid class dates were found for these repeat settings."
        );
        return;
    }

    const createdAt =
        new Date().toISOString();

    const repeatGroupId =
        `${Date.now()}-${Math.random()
            .toString(36)
            .slice(2, 9)}`;

    const newSessions = repeatDates.flatMap(
        (occurrenceDate, occurrenceIndex) => {
            let cursorMinutes =
                isFlexibleSessionFlow
                    ? null
                    : classStartMinutes;

            const occurrenceFlowId =
                `${repeatGroupId}-${occurrenceDate}`;

            return classTimerSections.map(
                (section, sectionIndex) => {
                    const duration =
                        Number(section.plannedMinutes) || 15;

                    const startMinutes =
                        isFlexibleSessionFlow
                            ? null
                            : cursorMinutes;

                    const endMinutes =
                        isFlexibleSessionFlow
                            ? null
                            : cursorMinutes + duration;

                    if (!isFlexibleSessionFlow) {
                        cursorMinutes =
                            endMinutes;
                    }

                    const isBreak =
                        section.type === "break" ||
                        section.type === "lunch" ||
                        section.title
                            ?.toLowerCase()
                            .includes("break") ||
                        section.title
                            ?.toLowerCase()
                            .includes("lunch");

                    const cleanMaterials =
                        Array.isArray(section.materials)
                            ? section.materials
                                  .map(
                                      (
                                          material,
                                          materialIndex
                                      ) => ({
                                          id:
                                              material?.id ||
                                              `${occurrenceFlowId}-material-${sectionIndex}-${materialIndex}`,
                                          name:
                                              typeof material ===
                                              "string"
                                                  ? material.trim()
                                                  : String(
                                                        material?.name ||
                                                            ""
                                                    ).trim(),
                                          checked: false,
                                      })
                                  )
                                  .filter(
                                      (material) =>
                                          material.name
                                  )
                            : [];

                    return {
                        id:
                            `${occurrenceFlowId}-session-${sectionIndex}`,

                        title:
                            cleanSessionTitle(
                                section.title ||
                                    `Session ${
                                        sectionIndex + 1
                                    }`
                            ),

                        description:
                            section.description || "",

                        materials_needed:
                            cleanMaterials,

                        materials:
                            cleanMaterials,

                        child_id:
                            childId,

                        subject_id:
                            subjectId,

                        subject_name:
                            subject?.name || "Class",

                        child_name:
                            child?.name || "",

                        // The unified School Calendar uses this for the border and glow.
                        child_color:
                            child?.calendarColor || "#7c3aed",

                        // The class color is used for the inside/background tint.
                        color:
                            subject?.color || "#ffd166",

                        class_color:
                            subject?.color || "#ffd166",

                        calendar_color:
                            subject?.color || "#ffd166",

                        type:
                            isBreak
                                ? "break"
                                : section.type ||
                                  "lesson",

                        status: "open",
                        archived: false,
                        completed: false,

                        date:
                            occurrenceDate,

                        date_start:
                            occurrenceDate,

                        date_end:
                            occurrenceDate,

                        due_date:
                            occurrenceDate,

                        scheduled_start_time:
                            isFlexibleSessionFlow
                                ? null
                                : `${String(
                                    Math.floor(
                                        startMinutes / 60
                                    )
                                ).padStart(
                                    2,
                                    "0"
                                )}:${String(
                                    startMinutes % 60
                                ).padStart(2, "0")}`,

                        scheduled_end_time:
                            isFlexibleSessionFlow
                                ? null
                                : `${String(
                                    Math.floor(
                                        endMinutes / 60
                                    )
                                ).padStart(
                                    2,
                                    "0"
                                )}:${String(
                                    endMinutes % 60
                                ).padStart(2, "0")}`,

                        start_time:
                            isFlexibleSessionFlow
                                ? null
                                : `${String(
                                    Math.floor(
                                        startMinutes / 60
                                    )
                                ).padStart(
                                    2,
                                    "0"
                                )}:${String(
                                    startMinutes % 60
                                ).padStart(2, "0")}`,

                        end_time:
                            isFlexibleSessionFlow
                                ? null
                                : `${String(
                                    Math.floor(
                                        endMinutes / 60
                                    )
                                ).padStart(
                                    2,
                                    "0"
                                )}:${String(
                                    endMinutes % 60
                                ).padStart(2, "0")}`,

                        duration_minutes:
                            duration,

                        all_day:
                            isFlexibleSessionFlow,

                        is_all_day:
                            isFlexibleSessionFlow,

                        unscheduled:
                            isFlexibleSessionFlow,

                        is_calendar_session: true,

                        is_timed_session:
                            !isFlexibleSessionFlow,
                        is_instructional_time:
                            !isBreak,
                        is_break:
                            isBreak,

                        grading_mode:
                            "not_graded",

                        graded: false,

                        session_lesson_template_id:
                            activeSessionLessonId || null,

                        session_lesson_name:
                            sessionLessonName?.trim() || null,

                        quest_id: section.questId || section.quest_id || null,
                        session_order:
                            sectionIndex + 1,

                        session_flow_id:
                            occurrenceFlowId,

                        recurrence_group_id:
                            repeatGroupId,

                        recurrence_occurrence:
                            occurrenceIndex + 1,

                        repeat:
                            windowCheck.outside ? "none" : sessionRepeatMode,

                        repeat_interval:
                            Number(
                                sessionRepeatInterval
                            ) || 1,

                        repeat_end_date:
                            windowCheck.outside ? null : sessionRepeatEndDate || null,

                        repeat_weekdays:
                            sessionRepeatMode ===
                            "custom"
                                ? [
                                      ...sessionRepeatWeekdays,
                                  ]
                                : [],

                        outside_class_time_confirmed: Boolean(windowCheck.outside && (options.confirmOutside || sessionOutsideConfirmedRef.current)),
                        session_start_time: sessionFlowStartTime || classSchedule.startTime || null,
                        created_from:
                            "class_timer_lesson_flow",

                        created_at:
                            createdAt,

                        updated_at:
                            createdAt,
                    };
                }
            );
        }
    );

    const repeatDateSet =
        new Set(repeatDates);

    const existingFlowDates =
        new Set(
            events
                .filter((event) => {
                    const eventDate =
                        event.date_start ||
                        event.date ||
                        event.due_date;

                    return (
                        event.child_id === childId &&
                        event.subject_id === subjectId &&
                        repeatDateSet.has(eventDate) &&
                        event.created_from ===
                            "class_timer_lesson_flow"
                    );
                })
                .map(
                    (event) =>
                        event.date_start ||
                        event.date ||
                        event.due_date
                )
        );

    if (existingFlowDates.size > 0) {
        setPendingSessionFlowConflict({
            newSessions,
            repeatDates,
            existingFlowDates: Array.from(
                existingFlowDates
            ).sort(),
        });

        return;
    }

commitCalendarSessionFlow({
    newSessions,
    repeatDates,
    replaceExisting: false,
});

return;
};
const cleanSessionTitle = (title) => {
    return String(title || "Untitled Section")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/(Class)(Lecture)(Vocabulary)/g, "$1 $2 • $3")
        .replace(/(Lecture)(Vocabulary)/g, "$1 • $2")
        .trim();
};
const saveSessionLessonTemplate = () => {
    if (!sessionLessonName.trim()) {
        toast.error("Name this session lesson first.");
        return;
    }

    const cleanSections = classTimerSections.map((section) => ({
        id: `${Date.now()}-${Math.random()}`,
        title: cleanSessionTitle(section.title || "Untitled Section"),
        type: section.type || "lesson",
        description: section.description || "",
        materials: Array.isArray(section.materials)
            ? section.materials.map((item) => ({
                  id: `${Date.now()}-${Math.random()}`,
                  name: item.name || "",
                  checked: Boolean(item.checked),
              }))
            : section.materials
            ? String(section.materials)
                  .split("\n")
                  .map((item) => ({
                      id: `${Date.now()}-${Math.random()}`,
                      name: item.trim(),
                      checked: false,
                  }))
                  .filter((item) => item.name)
            : [
                  {
                      id: `${Date.now()}-${Math.random()}`,
                      name: "",
                      checked: false,
                  },
              ],
        plannedMinutes: Number(section.plannedMinutes) || 15,
        actualSeconds: 0,
        running: false,
        completed: false,
    }));

    const newTemplate = {
        id: `${Date.now()}-${Math.random()}`,
        name: sessionLessonName.trim(),
        sections: cleanSections,
        autoStartNextSection,
        autoEndClassAfterLastSection,
        created_at: new Date().toISOString(),
    };
    
    const updatedTemplates = [newTemplate, ...savedSessionLessons];

    localStorage.setItem(
        savedSessionLessonsKey,
        JSON.stringify(updatedTemplates)
    );

    localStorage.setItem(activeSessionLessonKey, newTemplate.id);

    setSavedSessionLessons(updatedTemplates);
    setActiveSessionLessonId(newTemplate.id);

    toast.success("Session lesson saved.");
};
    const updateActiveSessionLessonTemplate = () => {
    if (!activeSessionLessonId) {
        toast.error("Load a saved session lesson first.");
        return;
    }

    const cleanSections = classTimerSections.map((section) => ({
        id: section.id || `${Date.now()}-${Math.random()}`,
        title: section.title || "Untitled Section",
        type: section.type || "lesson",
        description: section.description || "",
        materials: Array.isArray(section.materials)
            ? section.materials.map((item) => ({
                  id: item.id || `${Date.now()}-${Math.random()}`,
                  name: item.name || "",
                  checked: Boolean(item.checked),
              }))
            : [],
        plannedMinutes: Number(section.plannedMinutes) || 15,
        actualSeconds: 0,
        running: false,
        completed: false,
    }));

    const updatedTemplates = savedSessionLessons.map((template) =>
        template.id === activeSessionLessonId
            ? {
                  ...template,
                  name: sessionLessonName.trim() || template.name,
                  sections: cleanSections,
                  autoStartNextSection,
                  autoEndClassAfterLastSection,
                  updated_at: new Date().toISOString(),
              }
            : template
    );

    localStorage.setItem(
        savedSessionLessonsKey,
        JSON.stringify(updatedTemplates)
    );

    setSavedSessionLessons(updatedTemplates);

    toast.success("Saved session lesson updated.");
};
const applySessionLessonTemplate = (template, shouldRestartTimer = true) => {
    const loadedSections = template.sections.map((section, index) => ({
        ...section,
        id: `${Date.now()}-${Math.random()}-${index}`,
        description: section.description || "",
        materials: Array.isArray(section.materials)
            ? section.materials.map((item) => ({
                  id: `${Date.now()}-${Math.random()}`,
                  name: item.name || "",
                  checked: Boolean(item.checked),
              }))
            : section.materials
            ? String(section.materials)
                  .split("\n")
                  .map((item) => ({
                      id: `${Date.now()}-${Math.random()}`,
                      name: item.trim(),
                      checked: false,
                  }))
                  .filter((item) => item.name)
            : [
                  {
                      id: `${Date.now()}-${Math.random()}`,
                      name: "",
                      checked: false,
                  },
              ],
        actualSeconds: 0,
        running: !shouldRestartTimer && index === 0,
        completed: false,
    }));

setClassTimerSections(loadedSections);

    setAutoStartNextSection(Boolean(template.autoStartNextSection));
    setAutoEndClassAfterLastSection(Boolean(template.autoEndClassAfterLastSection));

    localStorage.setItem(activeSessionLessonKey, template.id);
    setActiveSessionLessonId(template.id);
    setSessionLessonName(template.name || "");
    if (loadedSections.length > 0) {
    setTimeout(() => {
        setActiveSectionId(loadedSections[0].id);
    }, 0);
} else {
    setActiveSectionId(null);
}

    if (shouldRestartTimer) {
        setTimerRunning(false);
        setTimerSeconds(0);

        localStorage.setItem(
            classTimerKey,
            JSON.stringify({
                running: false,
                base_seconds: 0,
                started_at: null,
                child_id: childId,
                subject_id: subjectId,
                subject_name: subject?.name || "Class",
                updated_at: new Date().toISOString(),
            })
        );
    }

    toast.success(`${template.name} loaded.`);
};

const loadSessionLessonTemplate = (template) => {
    if (timerRunning) {
        setPendingSessionLessonSwitch(template);
        return;
    }

    applySessionLessonTemplate(template, true);
};
       const openRenameSessionLessonModal = (template) => {
    setRenameSessionLessonModal(template);
    setRenameSessionLessonValue(template.name || "");
};

const saveRenamedSessionLesson = () => {
    if (!renameSessionLessonModal) return;

    if (!renameSessionLessonValue.trim()) {
        toast.error("Enter a session lesson name.");
        return;
    }

    const updatedTemplates = savedSessionLessons.map((template) =>
        template.id === renameSessionLessonModal.id
            ? {
                  ...template,
                  name: renameSessionLessonValue.trim(),
              }
            : template
    );

    localStorage.setItem(
        savedSessionLessonsKey,
        JSON.stringify(updatedTemplates)
    );

    setSavedSessionLessons(updatedTemplates);

    if (activeSessionLessonId === renameSessionLessonModal.id) {
        setSessionLessonName(renameSessionLessonValue.trim());
    }

    setRenameSessionLessonModal(null);
    setRenameSessionLessonValue("");

    toast.success("Session lesson renamed.");
};
   
const deleteSessionLessonTemplate = (templateId) => {
    const updatedTemplates = savedSessionLessons.filter(
        (template) => template.id !== templateId
    );

    localStorage.setItem(
        savedSessionLessonsKey,
        JSON.stringify(updatedTemplates)
    );

    if (activeSessionLessonId === templateId) {
        localStorage.removeItem(activeSessionLessonKey);
        setActiveSessionLessonId("");
    }

    setSavedSessionLessons(updatedTemplates);
};
   
   const createNewSessionFlow = () => {
    setClassTimerSections([
        {
            id: `${Date.now()}-${Math.random()}`,
            title: "Class Lecture",
            description: "",
            materials: [
                {
                    id: `${Date.now()}-${Math.random()}`,
                    name: "",
                    checked: false,
                },
            ],
            plannedMinutes: 20,
            actualSeconds: 0,
            running: false,
            completed: false,
        },
    ]);

    setActiveSectionId(null);
    setTimerRunning(false);
    setTimerSeconds(0);
    setClassSessionStartedAt(null);
    setSessionLessonName("");
    setActiveSessionLessonId("");
    setActiveLesson(null);

    localStorage.removeItem(activeSessionLessonKey);

    localStorage.setItem(
        classTimerKey,
        JSON.stringify({
            running: false,
            base_seconds: 0,
            started_at: null,
            child_id: childId,
            subject_id: subjectId,
            subject_name: subject?.name || "Class",
            updated_at: new Date().toISOString(),
        })
    );

    
};
    const savedSessionMaterialListsKey = `shadowGroveSessionMaterialLists_${subjectId}`;

const saveSessionMaterialList = (section) => {
    const cleanMaterials = Array.isArray(section.materials)
        ? section.materials.filter((item) => item.name?.trim())
        : [];

    if (cleanMaterials.length === 0) {
        toast.error("Add at least one material first.");
        return;
    }

   const listName = `${section.title || "Session"} Materials`;
    const savedLists = JSON.parse(
        localStorage.getItem(savedSessionMaterialListsKey) || "[]"
    );

    const newList = {
        id: `${Date.now()}-${Math.random()}`,
        name: listName.trim(),
        materials: cleanMaterials.map((item) => ({
            id: `${Date.now()}-${Math.random()}`,
            name: item.name,
            checked: false,
        })),
        created_at: new Date().toISOString(),
    };

    localStorage.setItem(
        savedSessionMaterialListsKey,
        JSON.stringify([newList, ...savedLists])
    );

    toast.success(`${listName} saved.`);
};
   const addClassTimerSection = () => {
    setClassTimerSections((prev) => [
        ...prev,
        {
            id: Date.now().toString(),
            title: "Class Lecture",
            description: "",
            materials: [
                {
                    id: Date.now().toString(),
                    name: "",
                    checked: false,
                },
            ],
            plannedMinutes: 20, 
            actualSeconds: 0,
            running: false,
            completed: false,
        },
    ]);

    if (classSessionStartedAt) {
        toast.success("Section added to this active session.");
    }
};

const updateClassTimerSection = (sectionId, field, value) => {
    setClassTimerSections((prev) =>
        prev.map((section) =>
            section.id === sectionId
                ? {
                      ...section,
                      [field]: value,
                  }
                : section
        )
    );
};

    const removeClassTimerSection = (sectionId) => {
    setClassTimerSections((prev) =>
        prev.length > 1
            ? prev.filter((section) => section.id !== sectionId)
            : prev
    );
};
    const moveClassTimerSection = (sectionId, direction) => {
    setClassTimerSections((prev) => {
        const currentIndex = prev.findIndex(
            (section) => section.id === sectionId
        );

        if (currentIndex === -1) return prev;

        const nextIndex = currentIndex + direction;

        if (nextIndex < 0 || nextIndex >= prev.length) return prev;

        const reorderedSections = [...prev];
        const [movedSection] = reorderedSections.splice(currentIndex, 1);

        reorderedSections.splice(nextIndex, 0, movedSection);

        return reorderedSections;
    });
};
        const startClassTimerSection = (sectionId) => {
    setActiveSectionId(sectionId);
setSectionStartedAtMainSeconds(timerSeconds);
    setClassTimerSections((prev) =>
        prev.map((section) => ({
            ...section,
            running: section.id === sectionId,
        }))
    );

if (!timerRunning) {
    handleTimerStartPause(
        sectionId
    );
}
};

const startClassSession = (options = {}) => {
    const windowCheck = sessionWindow({classStart: classSchedule.startTime, classEnd: classSchedule.endTime, start: sessionFlowStartTime, sections: classTimerSections});
    if (windowCheck.invalid) {toast.error("Adjust the session start time or section minutes."); return;}
    if (windowCheck.outside && !options.confirmOutside && !sessionOutsideConfirmedRef.current) {setSessionTimeWarning({action: "start", repeating: false}); return;}
    if (!classTimerSections.length) {
        toast.error("No class is scheduled for today.");
        return;
    }

    if (classSessionStartedAt) {
        toast.info("This class session has already been started.");
        return;
    }

    const firstIncompleteSection =
        classTimerSections.find(
            (section) => !section.completed
        ) || classTimerSections[0];

    if (!firstIncompleteSection) {
        toast.error("No lesson sections are available.");
        return;
    }

    setClassSessionStartedAt(
        new Date().toISOString()
    );

    startClassTimerSection(
        firstIncompleteSection.id
    );

    toast.success(
        `${sessionLessonName || "Today's Lesson"} started.`
    );
};

const completeClassTimerSection = (sectionId) => {
    setClassTimerSections((prev) =>
        prev.map((section) =>
            section.id === sectionId
                ? {
                      ...section,
                      running: false,
                      completed: true,
                  }
                : section
        )
    );

    setActiveSectionId(null);
};

const startNextClassTimerSection = () => {
    const currentIndex = classTimerSections.findIndex(
        (section) => section.id === activeSectionId
    );

    if (currentIndex === -1) {
        const firstIncompleteSection =
            classTimerSections.find((section) => !section.completed) ||
            classTimerSections[0];

        if (!firstIncompleteSection) {
            toast.error("Add at least one section first.");
            return;
        }

        setActiveSectionId(firstIncompleteSection.id);

        setClassTimerSections((prev) =>
            prev.map((section) => ({
                ...section,
                running:
                    section.id === firstIncompleteSection.id &&
                    timerRunning,
            }))
        );

        toast.info("Moved to the next available section.");
        return;
    }

    const nextSection = classTimerSections[currentIndex + 1];

    if (!nextSection) {
        toast.success("No more sections.");
        return;
    }

    setClassTimerSections((prev) =>
        prev.map((section) => {
            if (section.id === activeSectionId) {
                return {
                    ...section,
                    running: false,
                    completed: true,
                };
            }

            if (section.id === nextSection.id) {
                return {
                    ...section,
                    running: timerRunning,
                    completed: false,
                };
            }

            return section;
        })
    );

    setActiveSectionId(nextSection.id);
    setSectionStartedAtMainSeconds(timerSeconds);
    toast.success("Moved to next section.");
};
    const autoAdvanceSectionIfNeeded = React.useCallback((updatedSections) => {
    if (!autoStartNextSection || !activeSectionId) return updatedSections;

    const activeIndex = updatedSections.findIndex(
        (section) => section.id === activeSectionId
    );

    const activeSection = updatedSections[activeIndex];

    if (!activeSection) return updatedSections;

    const plannedSeconds =
        (Number(activeSection.plannedMinutes) || 0) * 60;

    if (!plannedSeconds || plannedSeconds <= 0) return updatedSections;

    if ((Number(activeSection.actualSeconds) || 0) < plannedSeconds) {
        return updatedSections;
    }

    const nextSection = updatedSections[activeIndex + 1];

    if (!nextSection) {
        setActiveSectionId(null);

        if (autoEndClassAfterLastSection) {
            const completedSections = updatedSections.map((section) =>
                section.id === activeSectionId
                    ? {
                          ...section,
                          running: false,
                          completed: true,
                      }
                    : section
            );

            setTimerRunning(false);

            localStorage.setItem(
                classTimerKey,
                JSON.stringify({
                    running: false,
                    base_seconds: timerSeconds,
                    started_at: null,
                    child_id: childId,
                    subject_id: subjectId,
                    subject_name: subject?.name || "Class",
                    updated_at: new Date().toISOString(),
                })
            );

            setCompletedSessionSummary({
                totalSeconds: timerSeconds,
                sections: completedSections,
                endedAt: new Date().toISOString(),
            });

            setShowSessionCompleteModal(true);
            toast.success("Class completed.");

            return completedSections;
        }

        return updatedSections.map((section) =>
            section.id === activeSectionId
                ? {
                      ...section,
                      running: false,
                      completed: true,
                  }
                : section
        );
    }

    const liveMainSeconds = calculateTimerSeconds();

setActiveSectionId(nextSection.id);
setSectionStartedAtMainSeconds(liveMainSeconds);

return updatedSections.map((section) => {
        if (section.id === activeSectionId) {
            return {
                ...section,
                running: false,
                completed: true,
            };
        }

        if (section.id === nextSection.id) {
    return {
        ...section,
        actualSeconds: 0,
        running: true,
        completed: false,
    };
}

                return section;
    });
}, [
    autoStartNextSection,
    activeSectionId,
    autoEndClassAfterLastSection,
    classTimerKey,
    timerSeconds,
    childId,
    subjectId,
    subject?.name,
    calculateTimerSeconds,
]);
    React.useEffect(() => {
        const currentSection = getActiveSection();

        setActiveSection(currentSection || null);
    }, [
        activeSectionId,
        classTimerSections,
        setActiveSection,
        getActiveSection,
    ]);

   React.useEffect(() => {
    if (!activeSectionId) return;
    if (!timerRunning) return;

    const sectionTimer = setInterval(() => {
        const currentMainSeconds = calculateTimerSeconds();
        const elapsedSinceSectionStart =
            currentMainSeconds - sectionStartedAtMainSeconds;

        setClassTimerSections((prev) => {
            const updatedSections = prev.map((section) =>
                section.id === activeSectionId && section.running
                    ? {
                          ...section,
                          actualSeconds: Math.max(
                              Number(section.actualSeconds) || 0,
                              elapsedSinceSectionStart
                          ),
                      }
                    : section
            );

            return autoAdvanceSectionIfNeeded(updatedSections);
        });
    }, 1000);

    return () => clearInterval(sectionTimer);
}, [
    activeSectionId,
    timerRunning,
    sectionStartedAtMainSeconds,
    autoStartNextSection,
    autoEndClassAfterLastSection,
    calculateTimerSeconds,
    autoAdvanceSectionIfNeeded,
]);
    const itemTypeColorsKey = `shadowGroveItemTypeColors_${subjectId}`;

    const [itemTypeColors, setItemTypeColors] = React.useState(() =>
    JSON.parse(localStorage.getItem(itemTypeColorsKey) || "null") ||
    DEFAULT_ITEM_TYPE_COLORS
);

    const saveItemTypeColors = () => {
    localStorage.setItem(
        itemTypeColorsKey,
        JSON.stringify(itemTypeColors)
    );

    toast.success("Planner colors saved.");
};
    const [itemType, setItemType] = React.useState("lesson");
    

const [projectSize, setProjectSize] = React.useState(10);
    const [itemGradingMode, setItemGradingMode] = React.useState("not_graded");
    const [assignmentMaxPoints, setAssignmentMaxPoints] = React.useState(100);
    const [assignmentGradeType, setAssignmentGradeType] = React.useState("");
    const [lessonTitle, setLessonTitle] = React.useState("");
    const [lessonDescription, setLessonDescription] = React.useState("");
    const [assignmentAttachments, setAssignmentAttachments] = React.useState({ links: [], pictures: [] });
    const [assignmentAttachmentBusy, setAssignmentAttachmentBusy] = React.useState(false);
    const [assignmentTiming, setAssignmentTiming] = React.useState({ assignment_timing: { mode: "auto", minutes: 15 }, include_in_class_timer: false });
 const [lessonMaterials, setLessonMaterials] = React.useState([
    { id: Date.now().toString(), name: "", checked: false },
]);
        const savedMaterialListsKey = `shadowGroveMaterialLists_${subjectId}`;

const [materialListName, setMaterialListName] = React.useState("");

const [savedMaterialLists, setSavedMaterialLists] = React.useState(() =>
    JSON.parse(localStorage.getItem(savedMaterialListsKey) || "[]")
);

const saveMaterialList = () => {
    if (!materialListName.trim()) {
        alert("Name this materials list first.");
        return;
    }

    const cleanMaterials = lessonMaterials.filter(
        (item) => item.name.trim() !== ""
    );

    if (cleanMaterials.length === 0) {
        alert("Add at least one material.");
        return;
    }

    const newList = {
        id: Date.now().toString(),
        name: materialListName.trim(),
        materials: cleanMaterials.map((item) => ({
    id: `${Date.now()}-${Math.random()}`,
    name: item.name,
    checked: item.checked || false,
})),
        created_at: new Date().toISOString(),
    };

   const updatedLists = [
    newList,
    ...savedMaterialLists.map((list) => ({
        ...list,
        materials: list.materials.map((item) => ({
            ...item,
        })),
    })),
];

    localStorage.setItem(
        savedMaterialListsKey,
        JSON.stringify(updatedLists)
    );

    setSavedMaterialLists(JSON.parse(JSON.stringify(updatedLists)));
    alert("Materials list saved.");
};

const loadMaterialList = (list) => {
    setMaterialListName(list.name);

    setLessonMaterials(
        list.materials.map((item) => ({
            id: `${Date.now()}-${Math.random()}`,
            name: item.name,
            checked: true,
        }))
    );
};

const deleteMaterialList = (listId) => {
    const updatedLists = savedMaterialLists.filter(
        (list) => list.id !== listId
    );

    localStorage.setItem(
        savedMaterialListsKey,
        JSON.stringify(updatedLists)
    );

    setSavedMaterialLists(JSON.parse(JSON.stringify(updatedLists)));
};
    const [lessonEndOfDayNotes, setLessonEndOfDayNotes] = React.useState("");
    const [useQuestBreakdown, setUseQuestBreakdown] = React.useState(false);
const [useRubric, setUseRubric] = React.useState(false);
const [rubricTotalPoints, setRubricTotalPoints] = React.useState(100);
const [rubricMode, setRubricMode] = React.useState("points");
const [customScaleMax, setCustomScaleMax] = React.useState(4);
const [rubricChecklistScoringMode, setRubricChecklistScoringMode] = React.useState("point_per_item");
const [rubricMatrixAssignmentPoints, setRubricMatrixAssignmentPoints] = React.useState(100);
const [rubricMatrixColumns, setRubricMatrixColumns] = React.useState(DEFAULT_MATRIX_COLUMNS);
const [rubricTemplateName, setRubricTemplateName] = React.useState("");

const savedRubricTemplatesKey = `shadowGroveRubricTemplates_${subjectId}`;

const [savedRubricTemplates, setSavedRubricTemplates] = React.useState(() =>
    JSON.parse(localStorage.getItem(savedRubricTemplatesKey) || "[]")
);

const [rubricRows, setRubricRows] = React.useState([
    createEmptyRubricRow(),
]);

const saveRubricTemplate = () => {
    if (!rubricTemplateName.trim()) {
        alert("Name this rubric first.");
        return;
    }

    const cleanRows = normalizeRubricRows(rubricRows);

    if (cleanRows.length === 0) {
        alert("Add at least one rubric criteria.");
        return;
    }

    const newTemplate = {
        id: `${Date.now()}-${Math.random()}`,
        name: rubricTemplateName.trim(),
        rubric_mode: rubricMode,
        rubric_total_points: Number(rubricTotalPoints) || 100,
        custom_scale_max: Number(customScaleMax) || 4,
        rubric_checklist_scoring_mode: rubricChecklistScoringMode,
        rubric_matrix_assignment_points: Number(rubricMatrixAssignmentPoints) || 100,
        rubric_matrix_columns: rubricMatrixColumns,
        rubric_rows: cleanRows,
        created_at: new Date().toISOString(),
    };

    const updatedTemplates = [newTemplate, ...savedRubricTemplates];

    localStorage.setItem(
        savedRubricTemplatesKey,
        JSON.stringify(updatedTemplates)
    );

    setSavedRubricTemplates(updatedTemplates);
    alert("Rubric template saved.");
};

const loadRubricTemplate = (template) => {
    setUseRubric(true);
    setRubricTemplateName(template.name || "");
    setRubricMode(template.rubric_mode || "points");
    setRubricTotalPoints(template.rubric_total_points || 100);
    setCustomScaleMax(template.custom_scale_max || 4);
    setRubricChecklistScoringMode(template.rubric_checklist_scoring_mode || "point_per_item");
    setRubricMatrixAssignmentPoints(template.rubric_matrix_assignment_points || template.rubric_total_points || 100);
    setRubricMatrixColumns(
        Array.isArray(template.rubric_matrix_columns) && template.rubric_matrix_columns.length
            ? template.rubric_matrix_columns
            : DEFAULT_MATRIX_COLUMNS
    );
    setRubricRows(
        Array.isArray(template.rubric_rows) && template.rubric_rows.length
            ? normalizeRubricRows(template.rubric_rows)
            : [createEmptyRubricRow()]
    );
};

const deleteRubricTemplate = (templateId) => {
    const updatedTemplates = savedRubricTemplates.filter(
        (template) => template.id !== templateId
    );

    localStorage.setItem(
        savedRubricTemplatesKey,
        JSON.stringify(updatedTemplates)
    );

    setSavedRubricTemplates(updatedTemplates);
};

const createCustomScaleLevels = (maxScore) => {
    const count = Math.max(2, Number(maxScore) || 4);

    return Array.from({ length: count }).map((_, index) => {
        const score = index + 1;

        return {
            id: String(score),
            score,
            label: `Score ${score}`,
            description: "",
            color:
                score === 1
                    ? "#ef4444"
                    : score === count
                    ? "#4ade80"
                    : score <= Math.ceil(count / 2)
                    ? "#fb923c"
                    : "#facc15",
        };
    });
};

const [questBreakdownSteps, setQuestBreakdownSteps] = React.useState([
    {
        id: Date.now().toString(),
        title: "",
        notes: "",
        due_date: "",
        completed: false,
    },
]);

    const [editingLessonId, setEditingLessonId] = React.useState(null);
    const [lessonDateStart, setLessonDateStart] = React.useState(() => nextClassDate(subject, undefined, schoolCalendar));
    React.useEffect(() => {
        if (subject && !editingLessonId && !generatorOnly) setLessonDateStart(nextClassDate(subject, undefined, schoolCalendar));
    }, [subject, schoolCalendar, editingLessonId, generatorOnly]);
    const [lessonDateEnd, setLessonDateEnd] = React.useState("");
    const [questDueDate, setQuestDueDate] = React.useState("");
    const [questDueTime, setQuestDueTime] = React.useState("");

    const [questSessionAttachmentMode, setQuestSessionAttachmentMode] =
    React.useState("standalone");

    const [selectedQuestSessionId, setSelectedQuestSessionId] =
        React.useState("");

    const [selectedQuestSessionFlowId, setSelectedQuestSessionFlowId] =
        React.useState("");

    const [selectedQuestSessionSectionId, setSelectedQuestSessionSectionId] =
        React.useState("");

    const [selectedQuestSessionSectionOrder, setSelectedQuestSessionSectionOrder] =
    React.useState(null);

    const availableQuestSessionSections = React.useMemo(() => {
        return events
            .filter((event) => {
                return (
                    event.child_id === childId &&
                    event.subject_id === subjectId &&
                    event.created_from === "class_timer_lesson_flow" &&
                    event.is_calendar_session === true &&
                    !event.archived &&
                    event.status !== "completed"
                );
            })
            .sort((a, b) => {
                const dateA =
                    a.date_start ||
                    a.date ||
                    a.due_date ||
                    "";

                const dateB =
                    b.date_start ||
                    b.date ||
                    b.due_date ||
                    "";

                if (dateA !== dateB) {
                    return dateA.localeCompare(dateB);
                }

                return (
                    Number(a.session_order || 0) -
                    Number(b.session_order || 0)
                );
            });
    }, [events, childId, subjectId]);

    const selectedQuestSessionSection =
        availableQuestSessionSections.find(
            (session) =>
                session.id === selectedQuestSessionId
        ) || null;

    const [lessonAllDay, setLessonAllDay] = React.useState(true);
    const [lessonStartTime, setLessonStartTime] = React.useState("");
    const [lessonEndTime, setLessonEndTime] = React.useState("");

    const [scheduleType, setScheduleType] = React.useState("one_time");
    const [lessonRepeat, setLessonRepeat] = React.useState("none");
    const [lessonRepeatDays, setLessonRepeatDays] = React.useState([]);

    const [plannerMode, setPlannerMode] = React.useState("one_time");
    const [curriculumPreview, setCurriculumPreview] = React.useState(null);
    const [curriculumUploading, setCurriculumUploading] = React.useState(false);

const [sectionTitles, setSectionTitles] = React.useState([""]);
const [useSectionTitles, setUseSectionTitles] = React.useState(false);
const [loopSteps, setLoopSteps] = React.useState([
    {
        id: Date.now().toString(),
        label: "Read",
        type: "assignment",
    },
]);
const savedLoopTemplatesKey = `shadowGroveLoopTemplates_${subjectId}`;

const [loopName, setLoopName] = React.useState("");

const [savedLoopTemplates, setSavedLoopTemplates] = React.useState(() =>
    JSON.parse(localStorage.getItem(savedLoopTemplatesKey) || "[]")
);

const saveLoopTemplate = () => {
    if (!loopName.trim()) {
        alert("Name this loop first.");
        return;
    }

    const cleanSteps = loopSteps.filter((step) => step.label.trim() !== "");

    if (cleanSteps.length === 0) {
        alert("Add at least one loop step.");
        return;
    }

    const newTemplate = {
        id: Date.now().toString(),
        name: loopName.trim(),
        steps: cleanSteps,
        created_at: new Date().toISOString(),
    };

    const updatedTemplates = [newTemplate, ...savedLoopTemplates];

    localStorage.setItem(
        savedLoopTemplatesKey,
        JSON.stringify(updatedTemplates)
    );

    setSavedLoopTemplates(updatedTemplates);
    alert("Loop saved.");
};

const loadLoopTemplate = (template) => {
    setLoopName(template.name);
    setLoopSteps(template.steps);
};

const deleteLoopTemplate = (templateId) => {
    const updatedTemplates = savedLoopTemplates.filter(
        (template) => template.id !== templateId
    );

    localStorage.setItem(
        savedLoopTemplatesKey,
        JSON.stringify(updatedTemplates)
    );

    setSavedLoopTemplates(updatedTemplates);
};
const [buildQuizHere, setBuildQuizHere] = React.useState(false);
    const [lessonSections, setLessonSections] = React.useState(1);
    const [lessonCount, setLessonCount] = React.useState(10);

const [customPatternSteps, setCustomPatternSteps] = React.useState([
    "Read",
    "Practice",
    "Quiz",
]);
const [showCreator, setShowCreator] = React.useState(false);

const [activePanel, setActivePanel] = React.useState(() => new URLSearchParams(window.location.search).get("panel") === "lessonStage" ? "lessonStage" : "today");
const [openClassroomMenu, setOpenClassroomMenu] = React.useState(null);
const selectClassroomPanel = (panelName) => {
    setActivePanel(panelName);
    setOpenClassroomMenu(null);
};

const toggleClassroomMenu = (menuName) => {
    setOpenClassroomMenu((current) =>
        current === menuName ? null : menuName
    );
};

const compactClassroomButtonClass = (isActive) =>
    [
        "inline-flex items-center justify-center gap-2",
        "rounded-xl border px-3 py-2",
        "text-sm font-semibold",
        "transition-all duration-200",
        isActive
            ? [
                  "border-[#c084fc]/80",
                  "bg-[#2e1065]/35",
                  "text-[#e9d5ff]",
                  "shadow-[0_0_22px_rgba(192,132,252,0.42)]",
              ].join(" ")
            : [
                  "border-[#ffd166]/40",
                  "bg-black",
                  "text-[#ffd166]",
                  "shadow-[0_0_12px_rgba(255,209,102,0.10)]",
                  "hover:border-[#ffd166]/75",
                  "hover:bg-[#ffd166]/10",
              ].join(" "),
    ].join(" ");
const classroomTabClass = (isActive) =>
    [
        "inline-flex items-center justify-center gap-1.5",
        "rounded-2xl border px-3 py-2",
        "text-xs font-semibold whitespace-nowrap",
        "transition-all duration-200",
        isActive
            ? [
                  "border-[#ffd166]/80",
                  "bg-[#2e1065]/65",
                  "text-[#ffd166]",
                  "shadow-[0_0_16px_rgba(192,132,252,0.18)]",
              ].join(" ")
            : [
                  "border-[#ffd166]/30",
                  "bg-black",
                  "text-[#fff3c4]",
                  "hover:border-[#ffd166]/60",
                  "hover:bg-[#ffd166]/5",
                  "hover:text-[#ffd166]",
              ].join(" "),
    ].join(" ");
const compactDropdownItemClass = (isActive) =>
    [
        "flex w-full items-center gap-2",
        "rounded-xl border px-3 py-2.5",
        "text-left text-sm transition-all duration-200",
        isActive
            ? [
                  "border-[#c084fc]/70",
                  "bg-[#2e1065]/45",
                  "text-[#e9d5ff]",
                  "shadow-[0_0_16px_rgba(192,132,252,0.30)]",
              ].join(" ")
            : [
                  "border-transparent",
                  "text-[#fff3c4]",
                  "hover:border-[#ffd166]/35",
                  "hover:bg-[#ffd166]/10",
              ].join(" "),
    ].join(" ");
//collapsing
const [showCalendar, setShowCalendar] = React.useState(true);
const [showGrading, setShowGrading] = React.useState(false);
const [showOpenItems, setShowOpenItems] = React.useState(false);
const [showPendingApproval, setShowPendingApproval] = React.useState(false);
const [showCompletedToday, setShowCompletedToday] = React.useState(false);
const [showClassSchedule, setShowClassSchedule] = React.useState(false);
const [showSavedLessons, setShowSavedLessons] = React.useState(false);
const [showOpenLessons, setShowOpenLessons] = React.useState(true);
const [showCompletedLessons, setShowCompletedLessons] = React.useState(false);
const [showGradingSystem, setShowGradingSystem] = React.useState(false);
const [showProgressReports, setShowProgressReports] = React.useState(false);
const [showResources, setShowResources] = React.useState(false);
const normalizeArchivumFolder = (
    folder
) => ({
    ...folder,
    id:
        folder?.folder_id ||
        folder?.id,
    folder_id:
        folder?.folder_id ||
        folder?.id,
    description:
        folder?.description || "",
    parent_folder_id:
        folder?.parent_folder_id ||
        null,
    sticky_notes: Array.isArray(
        folder?.sticky_notes
    )
        ? folder.sticky_notes
        : [],
    resources: Array.isArray(
        folder?.resources
    )
        ? folder.resources
        : [],
});

const [classArchivum, setClassArchivum] =
    React.useState({
        folders: [
            {
                id: "section-1",
                folder_id: "section-1",
                name: "Section I",
                parent_folder_id: null,
                sort_order: 0,
                resources: [],
            },
        ],
    });

const [
    newArchivumFolderName,
    setNewArchivumFolderName,
] = React.useState("");

const [
    openArchivumFolderId,
    setOpenArchivumFolderId,
] = React.useState(null);

const [
    archivumInstructionTitle,
    setArchivumInstructionTitle,
] = React.useState("");

const [
    archivumInstructionBody,
    setArchivumInstructionBody,
] = React.useState("");

const [
    archivumInstructionColor,
    setArchivumInstructionColor,
] = React.useState("parchment");

const [
    editingArchivumInstructionId,
    setEditingArchivumInstructionId,
] = React.useState(null);

const [
    isSavingArchivumInstruction,
    setIsSavingArchivumInstruction,
] = React.useState(false);

const selectedArchivumFolder =
    React.useMemo(
        () =>
            (
                classArchivum.folders ||
                []
            ).find(
                (folder) =>
                    folder.id ===
                    openArchivumFolderId
            ) || null,
        [
            classArchivum.folders,
            openArchivumFolderId,
        ]
    );

const visibleArchivumFolders =
    React.useMemo(
        () =>
            (
                classArchivum.folders ||
                []
            )
                .filter(
                    (folder) =>
                        (
                            folder.parent_folder_id ||
                            null
                        ) ===
                        (
                            openArchivumFolderId ||
                            null
                        )
                )
                .sort(
                    (firstFolder, secondFolder) =>
                        (
                            firstFolder.sort_order ||
                            0
                        ) -
                        (
                            secondFolder.sort_order ||
                            0
                        )
                ),
        [
            classArchivum.folders,
            openArchivumFolderId,
        ]
    );

React.useEffect(() => {
    let requestCancelled = false;

    const loadClassArchivum =
        async () => {
            if (
                !childId ||
                !subjectId
            ) {
                return;
            }

            try {
                const response =
                    await api.get(
                        `/children/${childId}/classes/${subjectId}/archivum`
                    );

                if (requestCancelled) {
                    return;
                }

                const folders =
                    Array.isArray(
                        response.data
                            ?.folders
                    )
                        ? response.data.folders.map(
                              normalizeArchivumFolder
                          )
                        : [];

                setClassArchivum({
                    ...response.data,
                    folders,
                });

setOpenArchivumFolderId(
    (currentFolderId) => {
        if (!currentFolderId) {
            return null;
        }

        const currentExists =
            folders.some(
                (folder) =>
                    folder.id ===
                    currentFolderId
            );

        return currentExists
            ? currentFolderId
            : null;
    }
);
            } catch (error) {
                console.error(
                    "Unable to load class Archivum:",
                    error
                );

                if (!requestCancelled) {
                    toast.error(
                        "Unable to load the Archivum."
                    );
                }
            }
        };

    loadClassArchivum();

    return () => {
        requestCancelled = true;
    };
}, [childId, subjectId]);

const addArchivumFolder =
    async () => {
        const cleanFolderName =
            newArchivumFolderName.trim();

        if (!cleanFolderName) {
            toast.error(
                "Enter a folder name first."
            );
            return;
        }

        const existingFolders =
            Array.isArray(
                classArchivum.folders
            )
                ? classArchivum.folders
                : [];

const siblingFolders =
    existingFolders.filter(
        (folder) =>
            (
                folder.parent_folder_id ||
                null
            ) ===
            (
                openArchivumFolderId ||
                null
            )
    );

const folderAlreadyExists =
    siblingFolders.some(
        (folder) =>
            folder.name
                ?.trim()
                .toLowerCase() ===
            cleanFolderName.toLowerCase()
    );
        if (folderAlreadyExists) {
            toast.error(
                "A folder with that name already exists."
            );
            return;
        }

        try {
            const response =
                await api.post(
                    `/children/${childId}/classes/${subjectId}/archivum/folders`,
{
    name: cleanFolderName,
    description: "",
    parent_folder_id:
        openArchivumFolderId ||
        null,
    sort_order:
        siblingFolders.length,
}
                );

            const newFolder =
                normalizeArchivumFolder(
                    response.data
                );

            setClassArchivum(
                (
                    previousArchivum
                ) => ({
                    ...previousArchivum,
                    folders: [
                        ...(
                            Array.isArray(
                                previousArchivum.folders
                            )
                                ? previousArchivum.folders
                                : []
                        ),
                        newFolder,
                    ],
                })
            );

            setOpenArchivumFolderId(
                newFolder.id
            );

            setNewArchivumFolderName(
                ""
            );

            toast.success(
                "Archivum folder created."
            );
        } catch (error) {
            console.error(
                "Unable to create Archivum folder:",
                error
            );

            toast.error(
                error.response?.data
                    ?.detail ||
                    "Unable to create the Archivum folder."
            );
        }
    };
const resetArchivumInstructionForm =
    () => {
        setArchivumInstructionTitle("");
        setArchivumInstructionBody("");
        setArchivumInstructionColor(
            "parchment"
        );
        setEditingArchivumInstructionId(
            null
        );
    };

const startEditingArchivumInstruction =
    (note) => {
        setArchivumInstructionTitle(
            note.title || ""
        );
        setArchivumInstructionBody(
            note.body || ""
        );
        setArchivumInstructionColor(
            note.note_color || "parchment"
        );
        setEditingArchivumInstructionId(
            note.note_id
        );
    };

const saveArchivumInstruction =
    async () => {
        if (!selectedArchivumFolder) {
            toast.error(
                "Open an Archivum folder first."
            );
            return;
        }

        const cleanBody =
            archivumInstructionBody.trim();

        if (!cleanBody) {
            toast.error(
                "Enter the instructions first."
            );
            return;
        }

        const currentNotes =
            selectedArchivumFolder
                .sticky_notes || [];

        const existingNote =
            currentNotes.find(
                (note) =>
                    note.note_id ===
                    editingArchivumInstructionId
            );

        const payload = {
            title:
                archivumInstructionTitle.trim() ||
                "Instructions",
            body: cleanBody,
            note_color:
                archivumInstructionColor,
            sort_order:
                existingNote?.sort_order ??
                currentNotes.length,
        };

        setIsSavingArchivumInstruction(
            true
        );

        try {
            let response;

            if (
                editingArchivumInstructionId
            ) {
                response = await api.patch(
                    `/children/${childId}` +
                        `/classes/${subjectId}` +
                        `/archivum/folders/` +
                        `${selectedArchivumFolder.id}` +
                        `/sticky-notes/` +
                        `${editingArchivumInstructionId}`,
                    payload
                );
            } else {
                response = await api.post(
                    `/children/${childId}` +
                        `/classes/${subjectId}` +
                        `/archivum/folders/` +
                        `${selectedArchivumFolder.id}` +
                        `/sticky-notes`,
                    payload
                );
            }

            const savedNote =
                response.data;

            setClassArchivum(
                (previousArchivum) => ({
                    ...previousArchivum,
                    folders: (
                        previousArchivum.folders ||
                        []
                    ).map((folder) => {
                        if (
                            folder.id !==
                            selectedArchivumFolder.id
                        ) {
                            return folder;
                        }

                        const oldNotes =
                            folder.sticky_notes ||
                            [];

                        const nextNotes =
                            editingArchivumInstructionId
                                ? oldNotes.map(
                                      (
                                          note
                                      ) =>
                                          note.note_id ===
                                          editingArchivumInstructionId
                                              ? {
                                                    ...note,
                                                    ...savedNote,
                                                }
                                              : note
                                  )
                                : [
                                      ...oldNotes,
                                      savedNote,
                                  ];

                        return {
                            ...folder,
                            sticky_notes:
                                nextNotes,
                        };
                    }),
                })
            );

            toast.success(
                editingArchivumInstructionId
                    ? "Instructions updated."
                    : "Instructions added."
            );

            resetArchivumInstructionForm();
        } catch (error) {
            console.error(
                "Unable to save Archivum instructions:",
                error
            );

            toast.error(
                error.response?.data
                    ?.detail ||
                    "Unable to save the instructions."
            );
        } finally {
            setIsSavingArchivumInstruction(
                false
            );
        }
    };

const deleteArchivumInstruction =
    async (noteId) => {
        if (!selectedArchivumFolder) {
            return;
        }

        const shouldDelete =
            window.confirm(
                "Delete these instructions?"
            );

        if (!shouldDelete) {
            return;
        }

        try {
            await api.delete(
                `/children/${childId}` +
                    `/classes/${subjectId}` +
                    `/archivum/folders/` +
                    `${selectedArchivumFolder.id}` +
                    `/sticky-notes/${noteId}`
            );

            setClassArchivum(
                (previousArchivum) => ({
                    ...previousArchivum,
                    folders: (
                        previousArchivum.folders ||
                        []
                    ).map((folder) =>
                        folder.id ===
                        selectedArchivumFolder.id
                            ? {
                                  ...folder,
                                  sticky_notes: (
                                      folder.sticky_notes ||
                                      []
                                  ).filter(
                                      (
                                          note
                                      ) =>
                                          note.note_id !==
                                          noteId
                                  ),
                              }
                            : folder
                    ),
                })
            );

            if (
                editingArchivumInstructionId ===
                noteId
            ) {
                resetArchivumInstructionForm();
            }

            toast.success(
                "Instructions deleted."
            );
        } catch (error) {
            console.error(
                "Unable to delete Archivum instructions:",
                error
            );

            toast.error(
                error.response?.data
                    ?.detail ||
                    "Unable to delete the instructions."
            );
        }
    };    
const [showWeeklyReport, setShowWeeklyReport] = React.useState(false);
const [showFieldTrip, setShowFieldTrip] = React.useState(false);
const [showBookList, setShowBookList] = React.useState(false);
const [showClassCustomize, setShowClassCustomize] = React.useState(false);

const gradingSettingsKey = `shadowGroveGrading_${subjectId}`;

const [gradingSettings, setGradingSettings] = React.useState(() =>
    JSON.parse(
        localStorage.getItem(gradingSettingsKey) || "null"
    ) || {
        gradingScale: "A-F",

        assignmentWeight: 25,
        quizWeight: 20,
        testWeight: 30,
        projectWeight: 25,
    }
);

React.useEffect(() => {
    const refresh = () => setGradingSettings(JSON.parse(localStorage.getItem(`shadowGroveGrading_${subjectId}`) || "null") || { gradingScale: "A-F", assignmentWeight: 25, quizWeight: 20, testWeight: 30, projectWeight: 25 });
    refresh();
    window.addEventListener("shadowGroveGradingSettingsUpdated", refresh);
    return () => window.removeEventListener("shadowGroveGradingSettingsUpdated", refresh);
}, [subjectId]);

const [isGraded, setIsGraded] =
    React.useState(true);


const [quizQuestions, setQuizQuestions] =
    React.useState([
        {
            id: Date.now().toString(),
            question: "",
            choices: ["", "", "", ""],
            correctAnswer: 0,
        },
    ]);


    const [lessonLinkTitle, setLessonLinkTitle] = React.useState("");
    const [lessonLinkUrl, setLessonLinkUrl] = React.useState("");


    const [lessonFile, setLessonFile] = React.useState(null);


    const [selectedPlannerEvent, setSelectedPlannerEvent] = React.useState(null);
    const [selectedSessionModal, setSelectedSessionModal] = React.useState(null);
    const [
        pendingSessionFlowConflict,
        setPendingSessionFlowConflict,
    ] = React.useState(null);
    const lastCalendarScrollY = React.useRef(0);
    const openSessionModalWithoutScrollJump = (session) => {
    rememberWeekScroll();

    lastCalendarScrollY.current = window.scrollY;
    setSelectedSessionModal(session);
};
    const closeSessionModalWithoutScrollJump = () => {
    const savedWeekScrollLeft = weekScrollLeftRef.current;

    setSelectedSessionModal(null);

    requestAnimationFrame(() => {
        window.scrollTo({
            top: lastCalendarScrollY.current,
            behavior: "auto",
        });

        requestAnimationFrame(() => {
            if (weekScrollRef.current) {
                weekScrollRef.current.scrollLeft = savedWeekScrollLeft;
            }
        });

        setTimeout(() => {
            if (weekScrollRef.current) {
                weekScrollRef.current.scrollLeft = savedWeekScrollLeft;
            }
        }, 50);
    });
};
    const openPlannerEventModalWithoutWeekJump = (event) => {
    rememberWeekScroll();
    lastCalendarScrollY.current = window.scrollY;
    setSelectedPlannerEvent(event);
};

const closePlannerEventModalWithoutWeekJump = () => {
    const savedWeekScrollLeft = weekScrollLeftRef.current;

    setSelectedPlannerEvent(null);

    requestAnimationFrame(() => {
        window.scrollTo({
            top: lastCalendarScrollY.current,
            behavior: "auto",
        });

        requestAnimationFrame(() => {
            if (weekScrollRef.current) {
                weekScrollRef.current.scrollLeft = savedWeekScrollLeft;
            }
        });

        setTimeout(() => {
            if (weekScrollRef.current) {
                weekScrollRef.current.scrollLeft = savedWeekScrollLeft;
            }
        }, 50);
    });
};
    const [editingSessionModal, setEditingSessionModal] = React.useState(null);
    const [deleteSessionModal, setDeleteSessionModal] = React.useState(null);
    const [editingSessionTitle, setEditingSessionTitle] = React.useState("");
    const [editingSessionDescription, setEditingSessionDescription] = React.useState("");
   const [editingSessionMaterials, setEditingSessionMaterials] = React.useState([
    {
        id: Date.now().toString(),
        name: "",
        checked: false,
    },
]);
    const [editingSessionStartTime, setEditingSessionStartTime] = React.useState("");
    const [editingSessionEndTime, setEditingSessionEndTime] = React.useState("");
    const openEditSessionModal = (session) => {
    setEditingSessionModal(session);
    setEditingSessionTitle(session.title || "");
    setEditingSessionDescription(session.description || "");
    setEditingSessionMaterials(
    Array.isArray(session.materials_needed)
        ? session.materials_needed
        : session.materials_needed
        ? session.materials_needed
              .split("\n")
              .map((item) => ({
                  id: `${Date.now()}-${Math.random()}`,
                  name: item.trim(),
                  checked: false,
              }))
              .filter((item) => item.name)
        : [
              {
                  id: Date.now().toString(),
                  name: "",
                  checked: false,
              },
          ]
);
    setEditingSessionStartTime(session.scheduled_start_time || "");
    setEditingSessionEndTime(session.scheduled_end_time || "");
};

const startTimedSession = (session) => {
    if (!session) return;

    const sessionDate =
        session.date_start ||
        session.date ||
        session.due_date ||
        "";

    const flowSessions = assignmentTimerSections(events, session.child_id, session.subject_id, sessionDate)
        .filter((event) => {
            const eventDate =
                event.date_start ||
                event.date ||
                event.due_date ||
                "";

            return (
                event.child_id === session.child_id &&
                event.subject_id === session.subject_id &&
                eventDate === sessionDate &&
                event.created_from === "class_timer_lesson_flow" &&
                (
                    event.session_flow_id === session.session_flow_id ||
                    event.id === session.id
                )
            );
        })
        .sort(
            (a, b) =>
                Number(a.session_order || 0) -
                Number(b.session_order || 0)
        );

    const sourceCalendarSections =
        flowSessions.length > 0
            ? flowSessions
            : [session];

    const linkedTemplate =
        savedSessionLessons.find(
            (template) =>
                template.id ===
                session.session_lesson_template_id
        ) ||
        savedSessionLessons.find((template) => {
            if (
                !Array.isArray(template.sections) ||
                template.sections.length !==
                    sourceCalendarSections.length
            ) {
                return false;
            }

            return template.sections.every(
                (templateSection, index) => {
                    const calendarSection =
                        sourceCalendarSections[index];

                    return (
                        cleanSessionTitle(
                            templateSection.title
                        ).toLowerCase() ===
                        cleanSessionTitle(
                            calendarSection?.title
                        ).toLowerCase()
                    );
                }
            );
        }) ||
        null;

    const selectedSectionIndex = Math.max(
        0,
        sourceCalendarSections.findIndex(
            (event) => event.id === session.id
        )
    );

    const loadedSections =
        sourceCalendarSections.map(
            (calendarSection, index) => {
                const templateSection =
                    linkedTemplate?.sections?.[index] ||
                    null;

                const sourceSection =
                    templateSection ||
                    calendarSection;

                const sourceMaterials =
                    Array.isArray(
                        sourceSection.materials
                    )
                        ? sourceSection.materials
                        : Array.isArray(
                              calendarSection.materials_needed
                          )
                        ? calendarSection.materials_needed
                        : Array.isArray(
                              calendarSection.materials
                          )
                        ? calendarSection.materials
                        : [];

                return {
                    ...sourceSection,

                    id:
                        calendarSection.id,

                    calendar_session_event_id:
                        calendarSection.id,

                    session_lesson_template_id:
                        linkedTemplate?.id ||
                        session.session_lesson_template_id ||
                        null,

                    session_lesson_name:
                        linkedTemplate?.name ||
                        session.session_lesson_name ||
                        "",

                    session_flow_id:
                        calendarSection.session_flow_id ||
                        session.session_flow_id ||
                        null,

                    session_occurrence_date:
                        calendarSection.date_start ||
                        calendarSection.date ||
                        calendarSection.due_date ||
                        sessionDate ||
                        null,

                    session_section_order:
                        Number(
                            calendarSection.session_order ||
                            index + 1
                        ),

                    title:
                        sourceSection.title ||
                        calendarSection.title ||
                        `Session ${index + 1}`,

                    type:
                        sourceSection.type ||
                        calendarSection.type ||
                        "lesson",

                    description:
                        sourceSection.description ||
                        calendarSection.description ||
                        "",

                    materials:
                        sourceMaterials.map(
                            (
                                material,
                                materialIndex
                            ) => ({
                                id:
                                    material.id ||
                                    `${calendarSection.id}-material-${materialIndex}`,

                                name:
                                    typeof material ===
                                    "string"
                                        ? material
                                        : material.name ||
                                          "",

                                checked:
                                    Boolean(
                                        typeof material ===
                                            "object" &&
                                            material.checked
                                    ),
                            })
                        ),

                    plannedMinutes:
                        Number(
                            sourceSection.plannedMinutes
                        ) ||
                        Number(
                            calendarSection.duration_minutes
                        ) ||
                        15,

                    actualSeconds:
                        Number(
                            calendarSection.actual_seconds
                        ) ||
                        0,

                    running:
                        index ===
                        selectedSectionIndex,

                    completed:
                        calendarSection.completed ===
                            true ||
                        calendarSection.status ===
                            "completed",

                    skipped:
                        Boolean(
                            calendarSection.skipped
                        ),

                    questId:
                        calendarSection.quest_id ||
                        null,
                };
            }
        );

    const activeSection =
        loadedSections[
            selectedSectionIndex
        ];

    const now =
        new Date().toISOString();

    setClassTimerSections(
        loadedSections
    );

    setActiveSessionLessonId(
        linkedTemplate?.id ||
        session.session_lesson_template_id ||
        ""
    );

    setSessionLessonName(
        linkedTemplate?.name ||
        session.session_lesson_name ||
        ""
    );

    if (linkedTemplate) {
        setAutoStartNextSection(
            Boolean(
                linkedTemplate.autoStartNextSection
            )
        );

        setAutoEndClassAfterLastSection(
            Boolean(
                linkedTemplate.autoEndClassAfterLastSection
            )
        );

        localStorage.setItem(
            activeSessionLessonKey,
            linkedTemplate.id
        );
    }

    setActiveSectionId(
        activeSection?.id || null
    );

    setSectionStartedAtMainSeconds(0);
    setTimerSeconds(0);
    setTimerRunning(true);
    setClassSessionStartedAt(now);

    localStorage.setItem(
        classTimerKey,
        JSON.stringify({
            running: true,
            base_seconds: 0,
            started_at: now,
            child_id: childId,
            subject_id: subjectId,
            subject_name:
                subject?.name ||
                "Class",
            active_section_id:
                activeSection?.id ||
                null,
            session_flow_id:
                session.session_flow_id ||
                null,
            session_date:
                sessionDate ||
                null,
            session_lesson_template_id:
                linkedTemplate?.id ||
                session.session_lesson_template_id ||
                null,
            updated_at: now,
        })
    );

    toast.success(
        `${
            linkedTemplate?.name ||
            activeSection?.title ||
            "Session"
        } started.`
    );
};
const completeTimedSession = (session) => {
    const currentSeconds = timerRunning
        ? calculateTimerSeconds()
        : timerSeconds;

    const updatedEvents = events.map((event) =>
        event.id === session.id
            ? {
                  ...event,
                  completed: true,
                  status: "completed",
                  completed_at: new Date().toISOString(),
                  actual_seconds: currentSeconds,
                  updated_at: new Date().toISOString(),
              }
            : event
    );

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);

    setTimerRunning(false);
    setActiveSectionId(null);
    setSelectedSessionModal(null);

    toast.success("Session completed.");
};
const saveEditedSession = () => {
    if (!editingSessionModal) return;

    const updatedEvents = events.map((event) =>
        event.id === editingSessionModal.id
            ? {
                  ...event,
                  title: editingSessionTitle.trim() || "Untitled Session",
                  description: editingSessionDescription,
                  materials_needed: editingSessionMaterials,
                  scheduled_start_time: editingSessionStartTime,
                  scheduled_end_time: editingSessionEndTime,
                  start_time: editingSessionStartTime,
                  end_time: editingSessionEndTime,
                  updated_at: new Date().toISOString(),
              }
            : event
    );

    rememberWeekScroll();

saveShadowGroveEvents(updatedEvents);
setEvents(updatedEvents);

restoreWeekScroll();

setSelectedSessionModal((prev) =>
        prev && prev.id === editingSessionModal.id
            ? updatedEvents.find((event) => event.id === editingSessionModal.id)
            : prev
    );

    setEditingSessionModal(null);
    toast.success("Session updated.");
};
    const deleteSingleSession = (session) => {
    const updatedEvents = events.filter((event) => event.id !== session.id);

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);

    setSelectedSessionModal(null);
    setDeleteSessionModal(null);

    toast.success("Session deleted.");
};

const deleteSessionFlow = (session) => {
    const updatedEvents = events.filter((event) => {
        if (!event.is_calendar_session && !event.is_timed_session) return true;

        return !(
            event.child_id === session.child_id &&
            event.subject_id === session.subject_id &&
            event.date_start === session.date_start &&
            event.created_from === session.created_from
        );
    });

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);

    setSelectedSessionModal(null);
    setDeleteSessionModal(null);

    toast.success("Session flow deleted.");
};
    const [editingQuestModal, setEditingQuestModal] = React.useState(null);
    const [copiedQuestForPaste, setCopiedQuestForPaste] = React.useState(null);
    const [copyQuestModal, setCopyQuestModal] = React.useState(null);
    const [editingQuestStartTime, setEditingQuestStartTime] = React.useState("");
    const [editingQuestEndTime, setEditingQuestEndTime] = React.useState("");
    const [editingQuestAllDay, setEditingQuestAllDay] = React.useState(false);
    const [questCreatedPopup, setQuestCreatedPopup] = React.useState(false);
    const [linkedLessonId, setLinkedLessonId] = React.useState("");
    const [linkedLessonTitle, setLinkedLessonTitle] = React.useState("");
    const [linkedChapterId, setLinkedChapterId] = React.useState("");
    const [linkedChapterTitle, setLinkedChapterTitle] = React.useState("");
    const questNotebookPagesStorageKey =
    `liberiCuriosiNotebookPages_${childId}_dashboard`;

    const questLinkableLessons = React.useMemo(() => {
        try {
            const savedPages = JSON.parse(
                localStorage.getItem(
                    questNotebookPagesStorageKey
                ) || "[]"
            );

            return Array.isArray(savedPages)
                ? savedPages
                : [];
        } catch {
            return [];
        }
    }, [questNotebookPagesStorageKey]);
    const [gradingQuestModal, setGradingQuestModal] = React.useState(null);
    const [parentGradeValue, setParentGradeValue] = React.useState("A");
    const [parentExtraMerits, setParentExtraMerits] = React.useState(0);
    React.useEffect(() => { setParentExtraMerits(0); }, [gradingQuestModal?.id, gradingQuestModal?.event_id]);
    const [parentGradeMethod, setParentGradeMethod] = React.useState("letter");
    const [parentEarnedPoints, setParentEarnedPoints] = React.useState("");
    const [parentPercentage, setParentPercentage] = React.useState("");
    const [parentPassFail, setParentPassFail] = React.useState("");
    const [gradeSaving, setGradeSaving] = React.useState(false);
    const [parentGradeNote, setParentGradeNote] = React.useState("");
    const [draggedQuestId, setDraggedQuestId] = React.useState(null);
   
    const [pendingMoveModal, setPendingMoveModal] = React.useState(null);
    
    const [pendingQuestMoveModal, setPendingQuestMoveModal] = React.useState(null);
    const [pendingQuestDeleteModal, setPendingQuestDeleteModal] = React.useState(null);
    const [pendingQuestEditModal, setPendingQuestEditModal] = React.useState(null);
    const [dragPreviewSlot, setDragPreviewSlot] = React.useState(null);
    const [calendarWarning, setCalendarWarning] = React.useState("");
    
    const [showWeekBlockEditor, setShowWeekBlockEditor] = React.useState(false);
    const [zoomToClassTime, setZoomToClassTime] = React.useState(true);
    const [showOnlyClassDaysInWeekZoom, setShowOnlyClassDaysInWeekZoom] =
    React.useState(false);
    const [zoomDayToClassTime, setZoomDayToClassTime] = React.useState(true);
    const plannerViewModeKey =
    `shadowGrovePlannerViewMode_${subjectId}`;

const [plannerViewMode, setPlannerViewMode] = React.useState(
    () =>
        localStorage.getItem(plannerViewModeKey) ||
        ""
);
    const [selectedPlannerDate, setSelectedPlannerDate] = React.useState(
    formatLocalDate(new Date())

    
    
);
const [calendarCursorDate, setCalendarCursorDate] = React.useState(
    formatLocalDate(new Date())
);
const moveSelectedPlannerDate = (days) => {
    const current = parseLocalDate(selectedPlannerDate);
    current.setDate(current.getDate() + days);
    setSelectedPlannerDate(formatLocalDate(current));
};
const moveCalendarMonth = (direction) => {
    const current = parseLocalDate(calendarCursorDate);
    current.setMonth(current.getMonth() + direction);
    setCalendarCursorDate(formatLocalDate(current));
};

const moveCalendarWeek = (direction) => {
    const current = parseLocalDate(calendarCursorDate);
    current.setDate(current.getDate() + direction * 7);
    setCalendarCursorDate(formatLocalDate(current));
};
        const getNextValidSchoolDay = (startDate) => {
    const next = parseLocalDate(startDate);

    for (let i = 0; i < 30; i++) {
        next.setDate(next.getDate() + 1);

        const nextDateString = formatLocalDate(next);

        if (isValidSchoolDay(nextDateString)) {
            return nextDateString;
        }
    }

    return startDate;
};

const snapToValidSchoolDay = (targetDate) => {
    if (isValidSchoolDay(targetDate)) {
        return targetDate;
    }

    return getNextValidSchoolDay(targetDate);
};
const lightenHexColor = (hex, amount = 35) => {
    const cleanHex = hex.replace("#", "");

    const num = parseInt(cleanHex, 16);

    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 255) + amount;
    let b = (num & 255) + amount;

    r = Math.min(255, r);
    g = Math.min(255, g);
    b = Math.min(255, b);

    return `#${(b | (g << 8) | (r << 16))
        .toString(16)
        .padStart(6, "0")}`;
};
const getPreviousValidSchoolDay = (startDate) => {
    const previous = parseLocalDate(startDate);

    for (let i = 0; i < 30; i++) {
        previous.setDate(previous.getDate() - 1);

        const previousDateString = formatLocalDate(previous);

        if (isValidSchoolDay(previousDateString)) {
            return previousDateString;
        }
    }

    return startDate;
};

    const moveQuestPriority = (eventId, direction) => {
    const dayEvents = events
        .filter((event) => event.date_start === selectedPlannerDate)
        .sort((a, b) => (a.priority || 0) - (b.priority || 0));

    const currentIndex = dayEvents.findIndex(
        (event) => event.id === eventId
    );

    if (currentIndex === -1) return;

    const newIndex = currentIndex + direction;

    if (newIndex < 0 || newIndex >= dayEvents.length) return;

    const reorderedDayEvents = [...dayEvents];

    const [movedEvent] = reorderedDayEvents.splice(currentIndex, 1);
    reorderedDayEvents.splice(newIndex, 0, movedEvent);

    const priorityMap = {};

    reorderedDayEvents.forEach((event, index) => {
        priorityMap[event.id] = index + 1;
    });

    const updatedEvents = events.map((event) =>
        priorityMap[event.id]
            ? {
                  ...event,
                  priority: priorityMap[event.id],
              }
            : event
    );

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);
};
    
    const getItemDate = (item) =>
    item.date_start || item.date || item.due_date || item.date_end || "";

const getCalendarItemsForDate = (dateString) => {
    return [
        ...groupLessonCalendarEvents(classroomEvents, {collapseAssignments: true}).filter((event) => classroomEventOnDate(event, dateString)).map((event) => ({ ...event, ...event.assignment_time_slots?.[dateString] })),
        ...getBreakdownStepsForDate(dateString),
    ].sort((a, b) => {
        const aTime = getSessionStartMinutes(a) ?? timeToMinutes(a.start_time) ?? 9999;
        const bTime = getSessionStartMinutes(b) ?? timeToMinutes(b.start_time) ?? 9999;

        if (aTime !== bTime) return aTime - bTime;

        return (a.priority || 0) - (b.priority || 0);
    });
};
    const classroomEvents = events.filter(
    (event) =>
        String(event.subject_id) === String(subjectId) &&
        String(event.child_id) === String(childId) &&
        !event.archived
);
    const openClassroomEvents = classroomEvents.filter(
    (event) =>
        !event.status ||
        event.status === "open"
);

const pendingApprovalEvents = classroomEvents.filter(
    (event) => event.status === "pending_approval"
);

const completedClassroomEvents = classroomEvents.filter(
    (event) => ["completed", "graded"].includes(event.status)
);
    const weekBlockScheduleKey = `shadowGroveWeekBlocks_${childId}_${subjectId}`;

const [weekBlocks, setWeekBlocks] = React.useState(() =>
    JSON.parse(localStorage.getItem(weekBlockScheduleKey) || "null") || [
        { id: "morning", label: "Morning", start: "07:00", end: "08:30" },
        { id: "core", label: "Core", start: "08:30", end: "11:30" },
        { id: "independence", label: "Independence", start: "11:30", end: "12:30" },
        { id: "lunch", label: "Lunch Break", start: "12:30", end: "13:30" },
        { id: "skills", label: "Skills", start: "13:30", end: "15:00" },
        { id: "family", label: "Family", start: "15:00", end: "18:00" },
        { id: "evening", label: "Evening", start: "18:00", end: "20:00" },
    ]
);

const saveWeekBlocks = () => {
    localStorage.setItem(weekBlockScheduleKey, JSON.stringify(weekBlocks));
    toast.success("Week block schedule saved.");
};
const buildTimeRows = (startTime, endTime, interval = 15) => {
    const start = timeToMinutes(startTime);
    const end = timeToMinutes(endTime);

    if (start == null || end == null || end <= start) return [];

    const rows = [];

    for (let minute = start; minute <= end; minute += interval) {
        rows.push(minute);
    }

    return rows;
};
const getCalendarItemStart = (item) =>
    getSessionStartMinutes(item) ??
    timeToMinutes(item.start_time) ??
    timeToMinutes(item.due_time);
const isDueOnlyItem = (item) => {
    return Boolean(
        item?.due_time &&
        !item?.start_time &&
        !item?.end_time &&
        !item?.scheduled_start_time &&
        !item?.scheduled_end_time &&
        !item?.is_calendar_session &&
        !item?.is_timed_session
    );
};

const getCalendarItemEnd = (item) => {
    const start = getCalendarItemStart(item);

    if (start == null) return null;

    if (isDueOnlyItem(item)) {
    return start + 10;
}

    const explicitEnd =
        getSessionEndMinutes(item) ??
        timeToMinutes(item.end_time) ??
        timeToMinutes(item.scheduled_end_time) ??
        timeToMinutes(item.endTime) ??
        timeToMinutes(item.scheduledEndTime);

    if (explicitEnd != null) return explicitEnd;

    const sectionMinutes =
        Number(item.plannedMinutes) ||
        Number(item.planned_minutes) ||
        Number(item.duration_minutes) ||
        Number(item.durationMinutes) ||
        15;

    return start + Math.max(5, sectionMinutes);
};

const WeekBlockSchedule = ({ weekDates, onOpenQuest }) => {
const [draggingWeekItemId, setDraggingWeekItemId] = React.useState(null);
const [draggingWeekDate, setDraggingWeekDate] = React.useState(null);
const [weekDragPreview, setWeekDragPreview] = React.useState(null);
const [didDragWeekItem, setDidDragWeekItem] = React.useState(false);
const weekDragHoldTimerRef = React.useRef(null);
    const visibleWeekDates =
    showOnlyClassDaysInWeekZoom && zoomToClassTime
        ? weekDates.filter((dateString) => isValidSchoolDay(dateString))
        : weekDates;
const isDueOnlyItem = (item) => {
    return Boolean(
        item?.due_time &&
        !item?.start_time &&
        !item?.end_time &&
        !item?.scheduled_start_time &&
        !item?.scheduled_end_time &&
        !item?.is_calendar_session &&
        !item?.is_timed_session
    );
};

const getCalendarItemEnd = (item) => {
    const start = getCalendarItemStart(item);

    if (start == null) return null;

    if (isDueOnlyItem(item)) {
        return start + 10;
    }

    const explicitEnd =
        getSessionEndMinutes(item) ??
        timeToMinutes(item.end_time) ??
        timeToMinutes(item.scheduled_end_time);

    if (explicitEnd != null) return explicitEnd;

    return start + 15;
};
const weekZoomColumnTemplate = `135px repeat(${visibleWeekDates.length}, minmax(160px, 1fr))`;
    const getItemsForBlock = (dateString, block) => {
        const blockStart = timeToMinutes(block.start);
        const blockEnd = timeToMinutes(block.end);

        return getCalendarItemsForDate(dateString).filter((item) => {
            const start =
                getSessionStartMinutes(item) ??
                timeToMinutes(item.start_time) ??
                timeToMinutes(item.due_time);

            if (start == null) return block.label === "Morning";

            if (block.isTimeRow) {
    return start >= blockStart && start < blockEnd;
}

return start < blockEnd && getSessionEndMinutes(item) > blockStart;
        });
    };


const getWeekTimedItemsForDate = (dateString) => {
    const sorted = getCalendarItemsForDate(dateString)
        .map((item) => ({
            item,
            start: getCalendarItemStart(item),
            end: getCalendarItemEnd(item),
        }))
        .filter((entry) => entry.start != null && entry.end != null)
        .sort((a, b) => a.start - b.start);

    const groups = [];

    sorted.forEach((entry) => {
        const matchingGroup = groups.find((group) =>
            group.some((existing) => {
                return (
                    entry.start < existing.end &&
                    entry.end > existing.start
                );
            })
        );

        if (matchingGroup) {
            matchingGroup.push(entry);
        } else {
            groups.push([entry]);
        }
    });

    return groups.flatMap((group) => {
    const columns = [];

    const positioned = group.map((entry) => {
        let columnIndex = columns.findIndex(
            (columnEnd) => columnEnd <= entry.start
        );

        if (columnIndex === -1) {
            columnIndex = columns.length;
            columns.push(entry.end);
        } else {
            columns[columnIndex] = entry.end;
        }

        return {
            ...entry,
            overlapIndex: columnIndex,
        };
    });

    const finalOverlapCount = Math.max(1, columns.length);

    return positioned.map((entry) => ({
        ...entry,
        overlapCount: finalOverlapCount,
    }));
});
};
const earliestWeekItemStart = Math.min(
    ...visibleWeekDates
        .flatMap((dateString) =>
            getCalendarItemsForDate(dateString)
                .map((item) => getCalendarItemStart(item))
                .filter((minute) => minute != null)
        ),
    classSchedule.startTime ? timeToMinutes(classSchedule.startTime) : 7 * 60
);

const weekZoomStart = Number.isFinite(earliestWeekItemStart)
    ? earliestWeekItemStart
    : classSchedule.startTime
    ? timeToMinutes(classSchedule.startTime)
    : 7 * 60;

const weekZoomEnd = classSchedule.endTime
    ? timeToMinutes(classSchedule.endTime)
    : 20 * 60;

const safeWeekZoomStart = weekZoomStart ?? 7 * 60;
const safeWeekZoomEnd = weekZoomEnd ?? 20 * 60;

const weekZoomRows = buildTimeRows(
    classSchedule.startTime || "07:00",
    classSchedule.endTime || "20:00",
    5
);

const WEEK_ZOOM_PIXELS_PER_MINUTE = 2.4;

const weekZoomHeight =
    (safeWeekZoomEnd - safeWeekZoomStart) * WEEK_ZOOM_PIXELS_PER_MINUTE;

const getWeekZoomTop = (minute) =>
    Math.max(0, (minute - safeWeekZoomStart) * WEEK_ZOOM_PIXELS_PER_MINUTE);

const getWeekZoomHeight = (start, end, item = null) => {
    if (isDueOnlyItem(item)) {
        return Math.max(
            26,
            10 * WEEK_ZOOM_PIXELS_PER_MINUTE
        );
    }

    return Math.max(
        26,
        (end - start) * WEEK_ZOOM_PIXELS_PER_MINUTE - 2
    );
};
    const updateWeekDragPreview = (e, dateString) => {
    e.preventDefault();

    const itemId = draggedQuestId;
    if (!itemId) return;

    const draggedItem = events.find((event) => event.id === itemId);
    if (!draggedItem) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;

    const rawMinutes = safeWeekZoomStart + Math.round(y / WEEK_ZOOM_PIXELS_PER_MINUTE);
    const snappedMinutes = Math.round(rawMinutes / 5) * 5;

    const oldStart = getCalendarItemStart(draggedItem);
    const oldEnd = getCalendarItemEnd(draggedItem);

    const duration =
        oldStart != null && oldEnd != null
            ? Math.max(5, oldEnd - oldStart)
            : 30;

    const previewStart = Math.max(
        safeWeekZoomStart,
        Math.min(snappedMinutes, safeWeekZoomEnd - duration)
    );

    setWeekDragPreview({
        item: draggedItem,
        date: dateString,
        start: previewStart,
        end: previewStart + duration,
    });
};
        return (
    <div className="rounded-[32px] border border-[#ffd166]/40 bg-black p-4 shadow-[0_0_35px_rgba(255,209,102,0.12)]">
       <div className="mb-4 rounded-2xl border border-[#ffd166]/35 bg-black p-4 shadow-[0_0_22px_rgba(255,209,102,0.12)]">
    <div className="flex flex-wrap items-center justify-center gap-3">
        <button
            type="button"
            onClick={() => setShowWeekBlockEditor((prev) => !prev)}
            className={purpleActionBtn}
        >
            {showWeekBlockEditor ? "Hide Week Time Block Editor" : "Edit Week Time Blocks"}
        </button>

        <button
            type="button"
            onClick={() => setZoomToClassTime((prev) => !prev)}
            className={purpleActionBtn}
        >
            {zoomToClassTime ? "Show Full Week Blocks" : "Zoom To Class Time Block"}
        </button>

        {zoomToClassTime && (
            <button
                type="button"
                onClick={() => setShowOnlyClassDaysInWeekZoom((prev) => !prev)}
                className={`${purpleActionBtn} ${
                    showOnlyClassDaysInWeekZoom
                        ? "!border-[#ffd166] !bg-[#ffd166]/10 !text-[#ffd166] !shadow-[0_0_22px_rgba(255,209,102,0.35)]"
                        : ""
                }`}
            >
                {showOnlyClassDaysInWeekZoom ? "Showing Class Days Only" : "Show Class Days Only"}
            </button>
        )}
    </div>

    {showWeekBlockEditor && (
        <div className="mt-4 space-y-2 rounded-2xl border border-[#ffd166]/20 bg-black p-4">
            {weekBlocks.map((block, index) => (
                <div key={block.id} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <input
                        defaultValue={block.label}
                        onBlur={(e) => {
                            const updated = [...weekBlocks];
                            updated[index] = { ...updated[index], label: e.target.value };
                            setWeekBlocks(updated);
                        }}
                        className={inputClass}
                    />

                    <input
                        type="time"
                        defaultValue={block.start}
                        onBlur={(e) => {
                            const updated = [...weekBlocks];
                            updated[index] = { ...updated[index], start: e.target.value };
                            setWeekBlocks(updated);
                        }}
                        className={inputClass}
                    />

                    <input
                        type="time"
                        defaultValue={block.end}
                        onBlur={(e) => {
                            const updated = [...weekBlocks];
                            updated[index] = { ...updated[index], end: e.target.value };
                            setWeekBlocks(updated);
                        }}
                        className={inputClass}
                    />

                    <button
                        type="button"
                        onClick={() =>
                            setWeekBlocks((prev) =>
                                prev.length > 1
                                    ? prev.filter((_, i) => i !== index)
                                    : prev
                            )
                        }
                        className={purpleActionBtn}
                    >
                        Remove
                    </button>
                </div>
            ))}

            <div className="flex flex-wrap justify-center gap-2 pt-3">
                <button
                    type="button"
                    onClick={() =>
                        setWeekBlocks((prev) => [
                            ...prev,
                            {
                                id: `${Date.now()}-${Math.random()}`,
                                label: "New Block",
                                start: "09:00",
                                end: "10:00",
                            },
                        ])
                    }
                    className={purpleActionBtn}
                >
                    Add Time Block
                </button>

                <button
                    type="button"
                    onClick={() => {
                        alert("Create Lesson clicked");
                        saveLessonEvent();
                    }}
                    className={purpleActionBtn}
                >
                    Save Week Blocks
                </button>
            </div>
        </div>
    )}
</div>


<div
    ref={weekScrollRef}
    onScroll={(e) => {
    const nextLeft = e.currentTarget.scrollLeft;

    if (nextLeft > 0) {
        weekScrollLeftRef.current = nextLeft;
    }
}}
    className="w-full overflow-x-auto rounded-3xl border border-[#ffd166]/25 bg-black/50 p-3 relative"
>
    <div className="min-w-[1120px]">
        <div
    className="grid gap-2 mb-1"
    style={{
        gridTemplateColumns: zoomToClassTime
            ? weekZoomColumnTemplate
            : "135px repeat(7, minmax(120px, 1fr))",
    }}
>
            <div className="sticky left-0 z-20 rounded-2xl border border-[#ffd166]/30 bg-black p-3 text-center text-xs uppercase tracking-[0.18em] text-[#ffd166] shadow-[0_0_18px_rgba(255,209,102,0.18)]">
                Time
                </div>

            {visibleWeekDates.map((dateString) => (
                <div
                    key={dateString}
                    className={`rounded-2xl border p-1 text-center shadow-[0_0_14px_rgba(255,209,102,0.10)] ${
    isValidSchoolDay(dateString)
        ? "border-[#ffd166]/35 bg-black/60"
        : isVacationDay(dateString)
        ? "border-red-400/30 bg-red-950/20 text-red-200 opacity-70"
        : "border-white/10 bg-black text-[#6c7a9c] opacity-45"
}`}
                >
                    <p className="font-display text-sm text-[#ffd166] leading-none">
                        {parseLocalDate(dateString).toLocaleDateString("en-US", {
                            weekday: "short",
                        })}
                    </p>
                    <p className="text-[9px] text-[#b0b8d6] leading-none mt-1">
                        {dateString}
                    </p>
                </div>
            ))}
        </div>
            {zoomToClassTime && <div className="mb-2 grid gap-2" style={{ gridTemplateColumns: weekZoomColumnTemplate }}>
                <div className="rounded-2xl border border-[#ffd166]/25 bg-black p-3 text-xs text-[#ffd166]">All-day / unscheduled</div>
                {visibleWeekDates.map((dateString) => <div key={`all-day-${dateString}`} className="space-y-2 rounded-2xl border border-[#ffd166]/20 bg-black p-2">{getUnscheduledItemsForDate(getCalendarItemsForDate(dateString), dateString).map((item) => <button key={item.id} type="button" className="w-full rounded-xl border p-2 text-left text-xs text-[#fff3c4]" style={classroomCalendarStyle(item, subject?.color)} onClick={() => onOpenQuest(item)}>{item.title}</button>)}</div>)}
            </div>}
            {zoomToClassTime && (
    <div
    className="grid gap-2 mb-1"
    style={{
        gridTemplateColumns: weekZoomColumnTemplate,
    }}
>
        <div
   className="sticky left-0 z-20 relative rounded-2xl border border-[#ffd166]/30 bg-black shadow-[0_0_18px_rgba(255,209,102,0.18)] overflow-hidden"
    style={{ height: `${weekZoomHeight}px` }}
>
            {weekZoomRows.map((minute) => (
    <div
        key={minute}
        className="absolute left-0 right-0 border-t border-white/10 px-2 leading-none"
        style={{ top: `${getWeekZoomTop(minute)}px` }}
    >
        {minute % 30 === 0 && (
            <span className="text-[10px] text-[#ffd166] bg-black px-1">
                {minutesToTimeLabel(minute)}
            </span>
        )}
    </div>
))}
   
        </div>

        {visibleWeekDates.map((dateString) => (
            <div
    key={dateString}
    onDragOver={(e) => updateWeekDragPreview(e, dateString)}
    onDrop={(e) => handleWeekTimedDrop(e, dateString)}
    className={`relative rounded-2xl border ${
    isValidSchoolDay(dateString)
        ? "border-[#ffd166]/35 bg-black/70"
        : isVacationDay(dateString)
        ? "border-red-400/30 bg-red-950/20 text-red-200 opacity-70"
        : "border-white/10 bg-black text-[#6c7a9c] opacity-45"
}`}
    style={{ height: `${weekZoomHeight}px` }}
>
                {weekZoomRows.map((minute) => (
                    <div
                        key={`${dateString}-${minute}`}
                        className="absolute left-0 right-0 border-t border-white/10"
                        style={{ top: `${getWeekZoomTop(minute)}px` }}
                    />
                ))}
{weekDragPreview?.date === dateString && (
    <div
        className="absolute left-2 right-2 z-[900] rounded-xl border border-[#ffd166]/70 bg-black/90 px-2 py-1 text-[10px] text-[#ffd166] shadow-[0_0_18px_rgba(255,209,102,0.35)] pointer-events-none"
        style={{
            top: `${getWeekZoomTop(weekDragPreview.start)}px`,
            height: `${getWeekZoomHeight(
                weekDragPreview.start,
                weekDragPreview.end,
                weekDragPreview.item
            )}px`,
        }}
    >
        <div className="truncate font-bold">
            {weekDragPreview.item.title || "Moving item"}
        </div>
        <div className="text-[9px] text-[#7cc1ff]">
            {minutesToTimeLabel(weekDragPreview.start)} - {minutesToTimeLabel(weekDragPreview.end)}
        </div>
    </div>
)}
{getWeekTimedItemsForDate(dateString).map(
    ({ item, start, end, overlapIndex, overlapCount }) => (
        <div
    key={item.id}
    role="button"
    tabIndex={0}
    draggable
   onDragStart={(e) => {
    e.stopPropagation();
    e.dataTransfer.setData("text/plain", item.id);
    e.dataTransfer.effectAllowed = "move";
    setDraggedQuestId(item.id);
    
}}
    onDragEnd={() => setDraggedQuestId(null)}
    onClick={() => onOpenQuest(item)}
            className="absolute flex flex-col items-center justify-center rounded-xl border px-2 py-0.5 text-center text-[10px] text-[#fff3c4] overflow-hidden"
           style={{
    top: `${getWeekZoomTop(start)}px`,
    height: `${getWeekZoomHeight(start, end, item)}px`,
    left: `calc(${overlapIndex} * (100% / ${overlapCount}) + 0.15rem)`,
    width: `calc((100% / ${overlapCount}) - 0.3rem)`,
    borderColor: getCalendarItemColor(item),
    backgroundColor: classroomCalendarStyle(item, subject?.color).backgroundColor,
    boxShadow: `0 0 18px ${getCalendarItemColor(item)}88`,
}}
        >                   
        {isDueOnlyItem(item) ? (
    <>
        <p className="block w-full overflow-hidden text-center text-[12px] font-extrabold leading-none text-red-400">
            DUE
        </p>

        <p className="block w-full overflow-hidden text-center text-[10px] font-bold leading-none text-[#7cc1ff]">
            {formatDisplayTime(item.due_time)}
        </p>
    </>
) : (
    <>
        <p className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-center text-[11px] font-extrabold text-[#ffd166]">
            {String(item.title || "Untitled")
                .replace(/([a-z])([A-Z])/g, "$1 $2")
                .replace(/(Lecture)(Vocabulary)/g, "$1 • $2")}
        </p>

        <p className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-center text-[9px] font-bold text-[#7cc1ff]">
            {formatDisplayTime(item.scheduled_start_time || item.start_time)} -{" "}
            {formatDisplayTime(item.scheduled_end_time || item.end_time)}
        </p>
    </>
)}
                              
                       </div>
                    )
                )}
            </div>
        ))}
    </div>
)}
        {!zoomToClassTime && weekBlocks.map((block) => (
                <div
                    key={block.id || block.label}
                    className="grid grid-cols-[135px_repeat(7,minmax(120px,1fr))] gap-2 mb-3"
                >
                    <div className="sticky left-0 z-20 rounded-2xl border border-[#ffd166]/35 bg-black p-3 text-center shadow-[0_0_18px_rgba(255,209,102,0.18)] overflow-hidden">
                        <p className="font-display text-sm text-[#ffd166]">
                    {block.label}
                    </p>

                    {!block.isTimeRow && (
                     <p className="text-[10px] text-[#b0b8d6]">
                     {formatDisplayTime(block.start)} - {formatDisplayTime(block.end)}
                        </p>
)}
                    </div>

                    {weekDates.map((dateString) => {
                        const items = getItemsForBlock(dateString, block);
                        const visibleItems = items.slice(0, 3);

                        return (
                            <div
                                key={`${dateString}-${block.id || block.label}`}
                                className={`min-h-[96px] max-h-[150px] overflow-y-auto rounded-2xl border p-2 ${
                                    isValidSchoolDay(dateString)
                                        ? "border-[#ffd166]/25 bg-black/70"
                                        : "border-white/10 bg-black/70 opacity-45"
                                }`}
                            >
                                {visibleItems.length === 0 ? (
                                    <p className="text-[10px] text-[#6c7a9c]">
                                        —
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {visibleItems.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                onClick={() => onOpenQuest(item)}
                                                className="w-full rounded-xl border bg-black/70 px-2 py-1 text-left text-[10px] text-[#fff3c4] shadow-[0_0_10px_rgba(255,209,102,0.08)]"
                                                style={{
                                                    ...classroomCalendarStyle(item, subject?.color),
                                                }}
                                            >
                                                <p className="truncate text-[#ffd166]">
                                                    {String(item.title || "Untitled")
                                                    .replace(/([a-z])([A-Z])/g, "$1 $2")
                                                    .replace(/(Lecture)(Vocabulary)/g, "$1 • $2")}
                                                </p>

                                                {(item.scheduled_start_time || item.start_time || item.due_time) && (
                                                    <p className="text-[9px] text-[#b0b8d6]">
                                                        {formatDisplayTime(
                                                            item.scheduled_start_time ||
                                                                item.start_time ||
                                                                item.due_time
                                                        )}
                                                    </p>
                                                )}
                                            </button>
                                        ))}

                                      {items.length > 3 && (
    <button
        type="button"
        onClick={() =>
            setCalendarMoreModal({
                date: dateString,
                blockLabel: block.label,
                items,
            })
        }
        className="text-[9px] text-[#ffd166] hover:underline"
    >
        +{items.length - 3} more
    </button>
)}  
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}
                 </div>
        </div>
    </div>
    );
};
const handleMoveTimedDayItem = (itemId, targetDate, newStartMinutes) => {
    const allEvents = JSON.parse(localStorage.getItem("shadowGroveEvents") || "[]");
    

    const foundItem = allEvents.find((item) => item.id === itemId);

    if (!foundItem) {
        toast.error("Could not find that item to move.");
        return;
    }

    const oldStart = getCalendarItemStart(foundItem);
    const oldEnd = getCalendarItemEnd(foundItem);
    const duration = oldStart != null && oldEnd != null
        ? Math.max(10, oldEnd - oldStart)
        : 30;

    const newEndMinutes = newStartMinutes + duration;

    const startTime = minutesToInputTime(newStartMinutes);
    const endTime = minutesToInputTime(newEndMinutes);

    const updateTimedFields = (item) => {
        if (item.id !== itemId) return item;

        return {
            ...item,
            date: targetDate,
            due_date: targetDate,
            scheduled_date: targetDate,
            start_time: startTime,
            end_time: endTime,
            scheduled_start_time: startTime,
            scheduled_end_time: endTime,
            due_time: startTime,
            updated_at: new Date().toISOString(),
        };
    };

    const updatedEvents = allEvents.map(updateTimedFields);


    saveShadowGroveEvents(updatedEvents);


    setEvents(updatedEvents);
    

    toast.success("Moved item on Day schedule.");
};
const handleMoveDayItemToAllDay = (itemId, targetDate) => {
    const updatedEvents = events.map((item) =>
        item.id === itemId
            ? {
                  ...item,
                  date: targetDate,
                  date_start: targetDate,
                  date_end: targetDate,
                  due_date: targetDate,
                  all_day: true,
                  start_time: "",
                  end_time: "",
                  scheduled_start_time: "",
                  scheduled_end_time: "",
                  due_time: "",
                  updated_at: new Date().toISOString(),
              }
            : item
    );

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);
    toast.success("Moved to All-Day.");
};
const DayBlockSchedule = ({ selectedDate, events, onOpenQuest, onMoveTimedItem, onMoveToAllDay }) => {
const [draggingTimedItemId, setDraggingTimedItemId] = React.useState(null);
const [didDragTimedItem, setDidDragTimedItem] = React.useState(false);
const dragHoldTimerRef = React.useRef(null);
const dayScheduleGridRef = React.useRef(null);
const [dragPreviewMinutes, setDragPreviewMinutes] = React.useState(null);
const [hoverPreviewMinutes, setHoverPreviewMinutes] = React.useState(null);
    const timedSessions = events
    .filter((item) => {
        const start = getCalendarItemStart(item);
        const end = getCalendarItemEnd(item);

        return start != null && end != null;
    })
    .sort(
        (a, b) =>
            (getCalendarItemStart(a) ?? 9999) -
            (getCalendarItemStart(b) ?? 9999)
    );
    const unscheduledItems = getUnscheduledItemsForDate(events, selectedDate);

   const earliestDayItemStart = Math.min(
    ...events
        .map((item) => getCalendarItemStart(item))
        .filter((minute) => minute != null),
    classSchedule.startTime ? timeToMinutes(classSchedule.startTime) : BLOCK_DAY_START
);

const visibleStart = Number.isFinite(earliestDayItemStart)
    ? earliestDayItemStart
    : classSchedule.startTime
    ? timeToMinutes(classSchedule.startTime)
    : BLOCK_DAY_START;

const visibleEnd = classSchedule.endTime
    ? timeToMinutes(classSchedule.endTime)
    : BLOCK_DAY_END;
const safeVisibleStart = visibleStart ?? BLOCK_DAY_START;
const safeVisibleEnd = visibleEnd ?? BLOCK_DAY_END;
const effectivePixelsPerMinute = 4;
React.useEffect(() => {
    if (!draggingTimedItemId) return;

    const getMinutesFromPointer = (clientY) => {
        const rect = dayScheduleGridRef.current?.getBoundingClientRect();

        if (!rect) return safeVisibleStart;

        const rawY = clientY - rect.top - 36;
        const droppedMinutes =
            safeVisibleStart + Math.round(rawY / effectivePixelsPerMinute);

        const snappedMinutes = Math.round(droppedMinutes / 5) * 5;

        return Math.max(
            safeVisibleStart,
            Math.min(snappedMinutes, safeVisibleEnd)
        );
    };

    const handlePointerMove = (e) => {
    const previewMinutes = getMinutesFromPointer(e.clientY);

    setDidDragTimedItem(true);
    setDragPreviewMinutes(previewMinutes);
    setHoverPreviewMinutes(previewMinutes);
};

    const handlePointerUp = (e) => {
        const newMinutes = getMinutesFromPointer(e.clientY);

        onMoveTimedItem?.(draggingTimedItemId, selectedDate, newMinutes);

        setDraggingTimedItemId(null);
        setDragPreviewMinutes(null);
        setHoverPreviewMinutes(null);

        setTimeout(() => {
            setDidDragTimedItem(false);
        }, 0);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
    };
}, [
    draggingTimedItemId,
    selectedDate,
    safeVisibleStart,
    safeVisibleEnd,
    effectivePixelsPerMinute,
    onMoveTimedItem,
]);
const totalHeight =
    (safeVisibleEnd - safeVisibleStart) * effectivePixelsPerMinute;

const timeRows = [];

for (
    let t = safeVisibleStart;
    t <= safeVisibleEnd;
    t += 5
) {
    timeRows.push(t);
}

const getVisibleBlockTop = (startMinutes) => {
    return Math.max(
        0,
        (startMinutes - safeVisibleStart) * effectivePixelsPerMinute
    );
};

const getVisibleBlockHeight = (
    startMinutes,
    endMinutes,
    item = null
) => {
    const isDueOnly = isDueOnlyItem(item);
        item?.due_time &&
        !item?.start_time &&
        !item?.end_time &&
        !item?.scheduled_start_time &&
        !item?.scheduled_end_time;

    if (isDueOnly) {
    return Math.max(
        26,
        10 * effectivePixelsPerMinute
    );
}

    const duration = Math.max(5, endMinutes - startMinutes);

    return Math.max(
    26,
    duration * effectivePixelsPerMinute - 2
);
};
    const layoutTimedSessions = (() => {
    const sorted = timedSessions
        .map((session) => ({
            session,
            start: getCalendarItemStart(session),
            end: getCalendarItemEnd(session),
        }))
        .filter((item) => item.start != null && item.end != null)
        .sort((a, b) => a.start - b.start);

    const groups = [];

    sorted.forEach((item) => {
        const matchingGroup = groups.find((group) =>
            group.some((existing) => {
                return (
                    item.start < existing.end &&
                    item.end > existing.start
                );
            })
        );

        if (matchingGroup) {
            matchingGroup.push(item);
        } else {
            groups.push([item]);
        }
    });

   return groups.flatMap((group) => {
    const columns = [];

    const positioned = group.map((entry) => {
        let columnIndex = columns.findIndex(
            (columnEnd) => columnEnd <= entry.start
        );

        if (columnIndex === -1) {
            columnIndex = columns.length;
            columns.push(entry.end);
        } else {
            columns[columnIndex] = entry.end;
        }

        return {
            ...entry,
            overlapIndex: columnIndex,
        };
    });

    const finalOverlapCount = Math.max(1, columns.length);

    return positioned.map((entry) => ({
        ...entry,
        overlapCount: finalOverlapCount,
    }));
});

    const finalOverlapCount = Math.max(1, columns.length);

    return positioned.map((entry) => ({
        ...entry,
        overlapCount: finalOverlapCount,
    }));

})();
    return (
        <section className={shadowCalendarShell}>
            <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="font-display text-3xl text-[#ffd166]">
                    Day Block Schedule
                </h3>

               
            </div>

           <div className="mb-4 rounded-2xl border border-[#ffd166]/30 bg-black p-3">
    <p className="text-xs uppercase tracking-[0.18em] text-[#ffd166] mb-2">
        All-Day / Unscheduled
    </p>

    <div
    className="flex flex-wrap gap-2 min-h-[44px] rounded-xl border border-[#ffd166]/20 bg-black/40 p-2"
    onPointerUp={() => {
        if (!draggingTimedItemId) return;

        onMoveToAllDay?.(draggingTimedItemId, selectedDate);

        setDraggingTimedItemId(null);
        setDragPreviewMinutes(null);
        setHoverPreviewMinutes(null);

        setTimeout(() => {
            setDidDragTimedItem(false);
        }, 0);
    }}
>
        {unscheduledItems.length === 0 ? (
            <p className="text-xs text-[#6c7a9c]">
                No all-day or unscheduled items.
            </p>
        ) : (
            unscheduledItems.map((item) => (
                <button
    key={item.id}
    type="button"
    draggable
    onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", item.id);
        e.dataTransfer.effectAllowed = "move";
    }}
    onClick={() => onOpenQuest?.(item)}
    className="rounded-xl border px-3 py-1 text-xs text-[#ffd166]" style={classroomCalendarStyle(item, subject?.color)}
>
                    {item.title}
                </button>
            ))
        )}
       </div>
</div>

<div className="max-h-[680px] overflow-y-auto rounded-3xl border border-[#ffd166]/25 bg-black shadow-[0_0_18px_rgba(255,209,102,0.08)]">
        <div
    ref={dayScheduleGridRef}
    className="relative overflow-hidden min-w-[720px]"
    style={{ height: `${totalHeight + 36}px`, paddingTop: "36px" }}
    onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    }}
    onDrop={(e) => {
        e.preventDefault();

        const itemId = e.dataTransfer.getData("text/plain");
        if (!itemId && !copiedQuestForPaste) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const rawY = e.clientY - rect.top - 36;
        const droppedMinutes =
            safeVisibleStart + Math.round(rawY / effectivePixelsPerMinute);

        const snappedMinutes = Math.round(droppedMinutes / 5) * 5;

        const clampedMinutes = Math.max(
            safeVisibleStart,
            Math.min(snappedMinutes, safeVisibleEnd)
        );

        if (copiedQuestForPaste && !itemId) {
    const startTime = minutesToInputTime(clampedMinutes);
    const endTime = minutesToInputTime(clampedMinutes + 30);

    const copiedQuest = {
        ...copiedQuestForPaste,
        id: `${Date.now()}-${Math.random()}`,
        date: selectedDate,
        date_start: selectedDate,
        date_end: selectedDate,
        due_date: selectedDate,
        all_day: false,
        start_time: startTime,
        end_time: endTime,
        scheduled_start_time: startTime,
        scheduled_end_time: endTime,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
    };

    const updatedEvents = [...events, copiedQuest];

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);
    setCopiedQuestForPaste(null);

    toast.success("Quest pasted.");
    return;
}

onMoveTimedItem?.(itemId, selectedDate, clampedMinutes);
    }}
>
                {timeRows.map((minute) => (
                    <div
                        key={minute}
                        className="absolute left-0 right-0 border-t border-white/10"
                        style={{ top: `${getVisibleBlockTop(minute) + 36}px` }}
                    >
                       {minute % 30 === 0 && (
    <span className="absolute left-3 -top-2 text-[10px] text-[#6c7a9c] bg-[#05070d] px-1">
        {minutesToTimeLabel(minute)}
    </span>
)}
                    </div>
                ))}
            {draggingTimedItemId && hoverPreviewMinutes != null && (
    <div
        className="absolute left-20 z-[1000] rounded-xl border border-[#ffd166]/70 bg-black px-3 py-1 text-xs font-bold text-[#ffd166] shadow-[0_0_18px_rgba(255,209,102,0.35)] pointer-events-none"
        style={{
            top: `${getVisibleBlockTop(hoverPreviewMinutes) + 36}px`,
        }}
    >
        Drop at {minutesToTimeLabel(hoverPreviewMinutes)}
    </div>
)}
                {layoutTimedSessions.map(({ session, start, end, overlapIndex, overlapCount }) => {
                   

                    if (start == null || end == null) return null;

                    const isBreak = session.is_break || session.type === "break" || session.type === "lunch";
                    const sessionColor =
                        getCalendarItemColor(session);
                    const isDraggingThisItem = draggingTimedItemId === session.id;
const displayStart =
    isDraggingThisItem && dragPreviewMinutes != null
        ? dragPreviewMinutes
        : start;

const displayEnd =
    isDraggingThisItem && dragPreviewMinutes != null
        ? dragPreviewMinutes + Math.max(10, end - start)
        : end;
                    return (
    <div
    key={session.id}
    role="button"
    tabIndex={0}
    onPointerDown={(e) => {
    e.stopPropagation();

    dragHoldTimerRef.current = setTimeout(() => {
        setDraggingTimedItemId(session.id);
        setDidDragTimedItem(false);
    }, 400);
}}
    onPointerUp={() => {
    if (dragHoldTimerRef.current) {
        clearTimeout(dragHoldTimerRef.current);
    }
}}
    onClick={(e) => {
        e.stopPropagation();

        if (didDragTimedItem) return;

        onOpenQuest?.(session);
    }}
   
                            className={`
                            absolute rounded-xl border px-3 py-2 text-left overflow-hidden cursor-grab active:cursor-grabbing
                                transition hover:scale-[1.01]
                                ${
                                    isBreak
                                        ? "border-[#ffd166]/35 shadow-[0_0_18px_rgba(255,209,102,0.12)]"
                                        : "border-[#ffd166]/45 shadow-[0_0_18px_rgba(255,209,102,0.12)]"
                                }
                            `}
                           style={{
    top: `${getVisibleBlockTop(displayStart) + 36}px`,
    height: `${getVisibleBlockHeight(
    displayStart,
    displayEnd,
    session
    )}px`,
    left: `calc(4.75rem + ${overlapIndex} * ((100% - 5.25rem) / ${overlapCount}))`,
    width: `calc((100% - 5.25rem) / ${overlapCount} - 0.35rem)`,
    borderColor: sessionColor,
    backgroundColor: classroomCalendarStyle(session, subject?.color).backgroundColor,
    boxShadow: `0 0 18px ${sessionColor}88`,
    zIndex: isDraggingThisItem ? 999 : 10 + overlapIndex,
}}

                       >
   
   





<div
    className={
    isDueOnlyItem(session)
        ? "h-full w-full flex items-center justify-center overflow-hidden"
        : "h-full w-full overflow-y-auto scrollbar-none px-2"
}
>
   {isDueOnlyItem(session) ? (
    <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden text-center text-[10px] font-bold leading-tight">
        <div className="w-full text-center text-red-400 text-[12px] font-extrabold leading-none">
            DUE
        </div>

        <div className="min-w-0 max-w-full truncate text-[11px] font-extrabold text-[#fff3c4]">
            {session.title || session.name || session.quest_title || "Untitled Quest"}
        </div>

        <div className="text-[9px] text-[#7cc1ff]">
            {minutesToTimeLabel(start)}
        </div>
    </div>
) : (
    <div className="flex h-full w-full flex-col items-center justify-center overflow-hidden text-center">
        <div className="w-full truncate text-center text-[12px] font-extrabold text-[#ffd166] leading-tight">
            {session.title ||
                session.name ||
                session.quest_title ||
                session.parentQuestTitle ||
                "Untitled Block"}
        </div>

        <div className="w-full truncate text-center text-[10px] font-bold text-[#7cc1ff] leading-tight">
            {minutesToTimeLabel(start)} - {minutesToTimeLabel(end)}
        </div>
    </div>
)}
{(session.description || session.notes) && end - start >= 25 && (
        <div className="mt-1 text-[9px] text-[#d8dee9] leading-snug break-words">
            {session.description || session.notes}
        </div>
    )}

    {(session.subject_name || session.subject) && end - start >= 25 && (
        <div className="mt-1 text-[9px] text-[#b0b8d6] leading-tight break-words">
            {session.subject_name || session.subject}
        </div>
    )}
</div>
                            {session.is_instructional_time === false && (
                                <p className="text-[10px] text-[#7cc1ff] mt-1">
                                    Does not count as instructional time
                                </p>
                            )}
                        </div>
                    );
                         })}
            </div>
        </div>
        </section>
    );
};
        const getBreakdownStepsForDate = (dateString) => {
    return classroomEvents.flatMap((event) => {
        const parentColor =
            itemTypeColors[event.type] ||
            event.color ||
            "#ffd166";

        const stepColor = lightenHexColor(parentColor, 35);

        return Array.isArray(event.quest_breakdown_steps)
            ? event.quest_breakdown_steps
                  .filter((step) => step.due_date === dateString)
                  .map((step) => ({
                      ...step,
                      title: step.title,
                      description: step.notes || "",
                      date_start: step.due_date,
                      date_end: step.due_date,
                      color: stepColor,
                      parentQuestId: event.id,
                      parentQuestTitle: event.title,
                      parentQuestType: event.type,
                      parentQuestColor: parentColor,
                      isBreakdownStep: true,
                  }))
            : [];
    });
};

   const saveClassroomImage = (file, field) => {
        if (!file) return;


        const reader = new FileReader();


        reader.onload = () => {
            const updatedStyle = {
                ...classroomStyle,
                [field]: reader.result,
            };


            try {
                localStorage.setItem(
                    classroomStyleKey,
                    JSON.stringify(updatedStyle)
                );


                setClassroomStyle(updatedStyle);
            } catch {
                alert("Image is too large. Try a smaller image.");
            }
        };


        reader.readAsDataURL(file);
    };

    const buildBaseLessonEvent = ({
    id,
    title,
    type,
    dateStart,
    dateEnd,
    batchId = null,
}) => {
    return {
        id,
        batch_id: batchId || null,
        type,
        child_id: childId,
        subject_id: subjectId,
        subject_name:
    subject?.name || "Class",
        child_name: child.name,
        color:
            itemTypeColors[type] ||
            subject.color ||
            "#7c3aed",
        grading_mode: itemGradingMode,
        grading_type: assignmentGradeType || ({ "A-F": "letter", credit: "pass_fail", points: "points", percentage: "percentage" }[gradingSettings.gradingScale] || "letter"),
        max_points: Number(assignmentMaxPoints),

        title,
        linked_lesson_id: linkedLessonId || null,
        linked_lesson_title: linkedLessonTitle || "",
        linked_chapter_id: linkedChapterId || null,
        linked_chapter_title: linkedChapterTitle || "",
        description: lessonDescription.trim(),
        ...assignmentAttachments,
        ...assignmentTiming,
        materials: lessonMaterials.filter((item) => item.name.trim() !== ""),
        end_of_day_notes: lessonEndOfDayNotes.trim(),
        quest_breakdown_enabled: useQuestBreakdown,
        quest_breakdown_steps: useQuestBreakdown
        
        ? questBreakdownSteps.filter((step) => step.title.trim() !== "")
        : [],
rubric_enabled: useRubric,
rubric_mode: rubricMode,
custom_scale_max: Number(customScaleMax) || 4,
rubric_checklist_scoring_mode: rubricChecklistScoringMode,
rubric_total_points: Number(rubricTotalPoints) || getRubricMaxTotal(rubricRows) || 100,
rubric_rows: useRubric ? normalizeRubricRows(rubricRows) : [],
        attached_to_session:
            questSessionAttachmentMode === "attached" &&
            Boolean(selectedQuestSessionId),

        session_event_id:
            questSessionAttachmentMode === "attached"
                ? selectedQuestSessionId || null
                : null,

        session_flow_id:
            questSessionAttachmentMode === "attached"
                ? selectedQuestSessionFlowId || null
                : null,

        session_section_id:
            questSessionAttachmentMode === "attached"
                ? selectedQuestSessionSectionId || null
                : null,

        session_section_order:
            questSessionAttachmentMode === "attached"
                ? selectedQuestSessionSectionOrder
                : null,

        session_occurrence_date:
            questSessionAttachmentMode === "attached"
                ? (
                    selectedQuestSessionSection?.date_start ||
                    selectedQuestSessionSection?.date ||
                    selectedQuestSessionSection?.due_date ||
                    null
                )
                : null,
        schedule_type: scheduleType,
        date_start: dateStart,
        date_end: dateEnd,
        due_date: questDueDate,
        due_time: questDueTime,
        all_day: lessonAllDay,
        start_time: lessonStartTime,
        end_time: lessonEndTime,

        repeat: lessonRepeat,
        repeat_days: lessonRepeatDays,

        section_count: useSectionTitles
    ? sectionTitles.filter((title) => title.trim() !== "").length || 1
    : 1,

section_titles_enabled: useSectionTitles,

section_titles: useSectionTitles
    ? sectionTitles.filter((title) => title.trim() !== "")
    : [],



graded: isGraded,

        questions:
            type === "quiz" || type === "test"
                ? quizQuestions
                : [],

        link:
            lessonLinkTitle.trim() || lessonLinkUrl.trim()
                ? {
                      title: lessonLinkTitle.trim() || "Lesson Link",
                      url: lessonLinkUrl.trim(),
                  }
                : null,

        attachment: lessonFile
            ? {
                  fileName: lessonFile.name,
                  fileUrl: URL.createObjectURL(lessonFile),
              }
            : null,

        planner_mode: plannerMode,
        status: "open",
        archived: false,
        created_at: new Date().toISOString(),
    };
};

    const todayString = formatLocalDate(new Date());

const isClassroomQuest = (event) => questsForClass([event], childId, subjectId).length > 0;

const getClassroomQuestStatus = (event) =>
    String(
        event?.status || "open"
    ).toLowerCase();

const isCompletedClassroomQuest = (event) =>
    [
        "completed",
        "graded",
        "approved",
        "archived",
    ].includes(
        getClassroomQuestStatus(event)
    );

const isPendingClassroomQuest = (event) =>
    [
        "pending",
        "submitted",
        "pending_approval",
        "awaiting_approval",
    ].includes(
        getClassroomQuestStatus(event)
    );

const getClassroomQuestDueDate = (event) =>
    event?.due_date ||
    event?.date_end ||
    event?.date_start ||
    event?.date ||
    "";

const getClassroomQuestStartDate = (event) =>
    event?.date_start ||
    event?.date ||
    "";

const getClassroomQuestCompletedDate = (event) => {
    const completedValue =
        event?.completed_at ||
        event?.completion_date ||
        event?.approved_at ||
        "";

    return completedValue
        ? String(completedValue).slice(0, 10)
        : "";
};

const dueTodayQuests = classroomEvents
    .filter((event) => {
        if (!isClassroomQuest(event)) {
            return false;
        }

        if (
            isCompletedClassroomQuest(event) ||
            isPendingClassroomQuest(event)
        ) {
            return false;
        }

        return (
            getClassroomQuestDueDate(event) ===
            todayString
        );
    })
    .sort((a, b) =>
        String(
            a.due_time || "23:59"
        ).localeCompare(
            String(
                b.due_time || "23:59"
            )
        )
    );

const toDoTodayQuests = classToDoForDay(classroomEvents, childId, subjectId, todayString)
    .sort((a, b) => {
        const aDueDate =
            getClassroomQuestDueDate(a) ||
            "9999-12-31";

        const bDueDate =
            getClassroomQuestDueDate(b) ||
            "9999-12-31";

        if (aDueDate !== bDueDate) {
            return aDueDate.localeCompare(
                bDueDate
            );
        }

        return String(
            a.due_time || "23:59"
        ).localeCompare(
            String(
                b.due_time || "23:59"
            )
        );
    });

const isQuestLate = (event) => {
    if (!isClassroomQuest(event)) {
        return false;
    }

    if (
        isCompletedClassroomQuest(event) ||
        isPendingClassroomQuest(event) ||
        event?.excused
    ) {
        return false;
    }

    const dueDate =
        getClassroomQuestDueDate(event);

    if (!dueDate) {
        return false;
    }

    return dueDate < todayString;
};

const lateQuests = classroomEvents
    .filter(isQuestLate)
    .sort((a, b) =>
        getClassroomQuestDueDate(a).localeCompare(
            getClassroomQuestDueDate(b)
        )
    );

const completedTodayQuests = classroomEvents.filter(
    (event) => {
        if (!isClassroomQuest(event)) {
            return false;
        }

        if (
            !isCompletedClassroomQuest(event)
        ) {
            return false;
        }

        return (
            getClassroomQuestCompletedDate(
                event
            ) === todayString
        );
    }
);

const pendingApprovalQuests = classroomEvents.filter(
    (event) =>
        isClassroomQuest(event) &&
        isPendingClassroomQuest(event)
);

const comingWeekEnd = new Date();

comingWeekEnd.setDate(
    comingWeekEnd.getDate() + 7
);

const comingWeekEndString =
    formatLocalDate(comingWeekEnd);

const comingUpThisWeekQuests = classroomEvents
    .filter((event) => {
        if (!isClassroomQuest(event)) {
            return false;
        }

        if (
            isCompletedClassroomQuest(event) ||
            isPendingClassroomQuest(event)
        ) {
            return false;
        }

        const questDate =
            getClassroomQuestDueDate(event);

        if (!questDate) {
            return false;
        }

        return (
            questDate > todayString &&
            questDate <=
                comingWeekEndString
        );
    })
    .sort((a, b) =>
        getClassroomQuestDueDate(a).localeCompare(
            getClassroomQuestDueDate(b)
        )
    );

const nextTestQuiz = events
    .filter((event) => {
        const eventType =
            String(
                event?.type ||
                    event?.quest_type ||
                    ""
            ).toLowerCase();

        if (
            !["test", "quiz"].includes(
                eventType
            )
        ) {
            return false;
        }

        if (
            isCompletedClassroomQuest(event)
        ) {
            return false;
        }

        const questDate =
            getClassroomQuestDueDate(event);

        return (
            questDate &&
            questDate >= todayString
        );
    })
    .sort((a, b) =>
        getClassroomQuestDueDate(a).localeCompare(
            getClassroomQuestDueDate(b)
        )
    )[0] || null;
const getLateDays = (event) => {
    if (!isQuestLate(event)) return 0;

    const today = parseLocalDate(
        formatLocalDate(new Date())
    );

    const dueDate = parseLocalDate(
        event.date_start
    );

    const diffTime = today - dueDate;

    return Math.floor(
        diffTime / (1000 * 60 * 60 * 24)
    );
};

    const saveLessonEvent = () => {
        if (!validAssignmentTiming(assignmentTiming)) { toast.error("Use whole minutes between 1 and 1440."); return; }
        if (assignmentAttachmentBusy) { toast.error("Wait for the picture upload to finish."); return; }
        if (plannerMode === "curriculum") {
            if (curriculumUploading) { toast.error("Wait for the picture upload to finish."); return; }
            if (!curriculumPreview?.length || curriculumPreview.some((item) => !item.title.trim() || !item.date_start)) { toast.error("Generate and review the assignments first. Each needs a title and date."); return; }
            if (itemGradingMode === "graded" && (!Number.isFinite(Number(assignmentMaxPoints)) || Number(assignmentMaxPoints) <= 0)) { toast.error("Points possible must be greater than zero."); return; }
            const template = buildBaseLessonEvent({ id: "template", title: lessonTitle, type: itemType, dateStart: lessonDateStart, dateEnd: lessonDateStart });
            const generated = prepareAssignmentCopies(template, curriculumPreview);
            const latest = getShadowGroveEvents();
            const ids = new Set(generated.map((item) => item.id));
            const next = [...latest.filter((item) => !ids.has(item.id)), ...generated];
            saveShadowGroveEvents(next); setEvents(next); setCurriculumPreview(null); setQuestCreatedPopup(true);
            toast.success(`${generated.length} curriculum assignments saved.`);
            return;
        }
        if (!lessonDateStart) { toast.error("Choose a start date so this quest appears on the classroom list, dashboard, and calendars."); return; }
        if (questDueDate && questDueDate < lessonDateStart) { toast.error("The due date cannot be before the start date."); return; }
        if (lessonDateEnd && lessonDateEnd < lessonDateStart) { toast.error("The end date cannot be before the start date."); return; }
        if (itemGradingMode === "graded" && (!Number.isFinite(Number(assignmentMaxPoints)) || Number(assignmentMaxPoints) <= 0)) {
            toast.error("Enter an assignment point total greater than zero."); return;
        }
        console.log("CREATE LESSON START", {
        lessonTitle,
        child: Boolean(child),
        subject: Boolean(subject),
        questSessionAttachmentMode,
        selectedQuestSessionSection,
        lessonDateStart,
        lessonDateEnd,
        plannerMode,
        itemType,
        startDateIsValid:
            lessonDateStart
                ? isValidSchoolDay(
                    lessonDateStart
                )
                : "no start date",
        endDateIsValid:
            lessonDateEnd
                ? isValidSchoolDay(
                    lessonDateEnd
                )
                : "no end date",
    });
        if (!lessonTitle.trim()) {
            toast.error("Add a lesson title before creating this lesson.");
            return;
        }

        if (!child || !subject) {
            toast.error("Child or subject information is missing.");
            console.log("CREATE LESSON BLOCKED:", {
                child,
                subject,
                childId,
                subjectId,
            });
            return;
        }
        if (
            questSessionAttachmentMode === "attached" &&
            !selectedQuestSessionSection
        ) {
            toast.error("Choose a valid session section before creating this quest.");
            return;
        }
        if (
            itemType === "lesson" &&
            lessonDateStart &&
            plannerMode !== "date_range" &&
            !isValidSchoolDay(lessonDateStart)
        ) {
            console.log(
                "CREATE LESSON STOPPED: invalid start date",
                lessonDateStart
            );

            setCalendarWarning(
                "That date is not a valid school/class day for this class. Please choose a scheduled class day or update the class schedule first."
            );

            return;
        }

        if (
            plannerMode === "one_time" &&
            lessonDateEnd &&
            !isValidSchoolDay(lessonDateEnd)
        ) {
            console.log(
                "CREATE LESSON STOPPED: invalid end date",
                lessonDateEnd
            );
            setCalendarWarning(
                "The end date is not a valid school/class day. Please choose a scheduled class day or update the class schedule first."
            );
            return;
        }


        const lessonAttachment = lessonFile
            ? {
                  fileName: lessonFile.name,
                  fileUrl: URL.createObjectURL(lessonFile),
              }
            : null;

            if (plannerMode === "date_range") {
    if (!lessonDateStart || !lessonDateEnd) {
        alert("Choose a start date and end date.");
        return;
    }

    const generatedEvents = [];
    const batchId = `${Date.now()}-date-batch`;
    let current = parseLocalDate(lessonDateStart);
    const end = parseLocalDate(lessonDateEnd);
    let count = 1;

    while (current <= end) {
        const dateString = formatLocalDate(current);

        if (isValidSchoolDay(dateString)) {
            generatedEvents.push(
                buildBaseLessonEvent({
                    id: `${Date.now()}-date-${count}`,
                    title: `${lessonTitle.trim()} ${count}`,
                    type: itemType,
                    dateStart: dateString,
                    dateEnd: dateString,
                    batchId,
                })
            );

            count += 1;
        }

        current.setDate(current.getDate() + 1);
    }

    const updatedEvents = [...generatedEvents, ...events];

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);

    alert(`${generatedEvents.length} date range lessons created.`);
    return;
}

if (plannerMode === "lesson_count") {
    const totalLessons = Number(lessonCount) || 1;

    const generatedDates = generateSequentialDates(
        lessonDateStart,
        totalLessons
    );

    const batchId = `${Date.now()}-count-batch`;

    const generatedEvents = generatedDates.map((date, index) =>
        buildBaseLessonEvent({
            id: `${Date.now()}-count-${index}`,
            title: `${lessonTitle.trim()} ${index + 1}`,
            type: itemType,
            dateStart: date,
            dateEnd: date,
            batchId,
        })
    );

    const updatedEvents = [...generatedEvents, ...events];

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);

    alert(`${generatedEvents.length} counted lessons created.`);
    return;
}

if (plannerMode === "custom_pattern") {
    const cleanPatternSteps = customPatternSteps.filter(
        (step) => step.trim() !== ""
    );

    if (cleanPatternSteps.length === 0) {
        alert("Add at least one custom pattern step.");
        return;
    }

    const generatedDates = generateSequentialDates(
        lessonDateStart,
        cleanPatternSteps.length
    );

    const batchId = `${Date.now()}-pattern-batch`;

    const generatedEvents = cleanPatternSteps.map((step, index) =>
        buildBaseLessonEvent({
            id: `${Date.now()}-pattern-${index}`,
            title: `${step.trim()}: ${lessonTitle.trim()}`,
            type: itemType,
            dateStart: generatedDates[index],
            dateEnd: generatedDates[index],
            batchId,
        })
    );

    const updatedEvents = [...generatedEvents, ...events];

    
    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);

    alert(`${generatedEvents.length} custom pattern lessons created.`);
    return;
}

       
                if (plannerMode === "loop") {
    const cleanSections = sectionTitles.filter(
        (title) => title.trim() !== ""
    );

    const cleanSteps = loopSteps.filter(
        (step) => step.label.trim() !== ""
    );

    if (cleanSections.length === 0) {
        alert("Add at least one section title.");
        return;
    }

    if (cleanSteps.length === 0) {
        alert("Add at least one loop step.");
        return;
    }

    const generatedEvents = [];
    const batchId = `${Date.now()}-loop-batch`;
    const generatedDates = generateSequentialDates(
    lessonDateStart,
    cleanSections.length * cleanSteps.length
);

let dateIndex = 0;

    cleanSections.forEach((sectionTitle, sectionIndex) => {
        cleanSteps.forEach((step, stepIndex) => {
            generatedEvents.push(
                buildBaseLessonEvent({
                    id: `${Date.now()}-${sectionIndex}-${stepIndex}`,
                    title: `${step.label}: ${sectionTitle.trim()}`,
                    type: step.type,
                    dateStart: generatedDates[dateIndex],
                    dateEnd: generatedDates[dateIndex],
                    batchId,
                })
            );
            dateIndex += 1;
        });
    });

    const updatedEvents = [...generatedEvents, ...events];

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);

    alert(`${generatedEvents.length} loop lessons created.`);
    return;
}
       const baseEvent = buildBaseLessonEvent({
    id: editingLessonId || Date.now().toString(),
    title: lessonTitle.trim(),
    type: itemType,
    dateStart: lessonDateStart,
    dateEnd: lessonDateEnd || lessonDateStart,
    batchId: null,
});

const newEvent = {
    ...baseEvent,
    linked_lesson_id: linkedLessonId || null,
    linked_lesson_title: linkedLessonTitle || "",
    linked_chapter_id: linkedChapterId || null,
    linked_chapter_title: linkedChapterTitle || "",
    attachment: lessonFile
        ? {
              fileName: lessonFile.name,
              fileUrl: URL.createObjectURL(lessonFile),
          }
        : null,
};
console.log(
    "CREATED EVENT DEBUG",
    newEvent
);

let updatedEvents;

if (editingLessonId) {
    const existingEvents = events.some((event) => String(event.id) === String(editingLessonId)) ? events : [...events, { ...initialDraft, id: editingLessonId }];
    updatedEvents = existingEvents.map((event) =>
        event.id === editingLessonId
            ? {
                  ...event,
                  ...newEvent,
                  status: event.status || "open",
                  attachment: newEvent.attachment || event.attachment || null,
                  link: newEvent.link || event.link || null,
              }
            : event
    );
} else if (lessonRepeat && lessonRepeat !== "none") {
    const repeatDates = generateRepeatQuestDates(
        lessonDateStart,
        lessonRepeat,
        lessonRepeatDays,
        12
    );

    const batchId = `${Date.now()}-repeat-batch`;

    const repeatedEvents = repeatDates.map((date, index) => ({
        ...buildBaseLessonEvent({
            id: `${Date.now()}-repeat-${index}`,
            title: lessonTitle.trim(),
            type: itemType,
            dateStart: date,
            dateEnd: date,
            batchId,
        }),
        repeat: lessonRepeat,
        repeat_days: lessonRepeatDays,
        repeat_index: index + 1,
        repeat_total: repeatDates.length,
        attachment: lessonFile
            ? {
                  fileName: lessonFile.name,
                  fileUrl: URL.createObjectURL(lessonFile),
              }
            : null,
    }));

    updatedEvents = [...repeatedEvents, ...events];

    toast.success(`${repeatedEvents.length} repeating quests created.`);
} else {
    updatedEvents = [newEvent, ...events];
}

saveShadowGroveEvents(updatedEvents);
setEvents(updatedEvents);


        setLessonTitle("");
        setLessonDescription("");
        setAssignmentAttachments({ links: [], pictures: [] });
        setLessonMaterials([
    { id: Date.now().toString(), name: "", checked: false },
]);
        setLessonEndOfDayNotes("");
        setUseQuestBreakdown(false);
        setUseRubric(false);
        setRubricMode("points");
        setCustomScaleMax(4);
        setRubricTotalPoints(100);
        setRubricTemplateName("");
        setRubricRows([createEmptyRubricRow()]);
setQuestBreakdownSteps([
    {
        id: Date.now().toString(),
        title: "",
        notes: "",
        due_date: "",
        completed: false,
    },
]);
        setLessonDateStart(nextClassDate(subject, undefined, schoolCalendar));
        setLessonDateEnd("");
        setQuestDueDate("");
        setQuestDueTime("");
        setQuestSessionAttachmentMode("standalone");
        setSelectedQuestSessionId("");
        setSelectedQuestSessionFlowId("");
        setSelectedQuestSessionSectionId("");
        setSelectedQuestSessionSectionOrder(null);
        setLessonAllDay(true);
        setLessonStartTime("");
        setLessonEndTime("");
        setScheduleType("one_time");
        setLessonRepeat("none");
        setLessonRepeatDays([]);
        setLessonSections(1);
        setItemType("lesson");
        setIsGraded(true);
        setUseSectionTitles(false);
setSectionTitles([""]);

    setQuizQuestions([
    {
        id: Date.now().toString(),
        question: "",
        choices: ["", "", "", ""],
        correctAnswer: 0,
    },
]);

        setLessonLinkTitle("");
        setLessonLinkUrl("");
        setShowCreator(true);
        window.scrollTo({
        top: 0,
        behavior: "smooth",
});
        setEditingLessonId(null);
        setItemType("lesson");
       setProjectSize(10);
       setQuestCreatedPopup(true);
        setItemGradingMode("not_graded");
        setAssignmentMaxPoints(100);
        setAssignmentGradeType("");
    };


    const loadLessonForEdit = (event) => {
    setShowCreator(true);
    setActivePanel("creator");
    setEditingLessonId(event.id);

    setLessonTitle(event.title || "");
    setLessonDescription(event.description || "");
    setAssignmentAttachments({ links: event.links || (event.link?.url ? [event.link] : []), pictures: event.pictures || [] });
    setAssignmentTiming({ assignment_timing: event.assignment_timing || { mode: "auto", minutes: 15 }, include_in_class_timer: Boolean(event.include_in_class_timer) });

    setLessonMaterials(
        Array.isArray(event.materials) && event.materials.length
            ? event.materials
            : [{ id: Date.now().toString(), name: "", checked: false }]
    );

    setLessonEndOfDayNotes(event.end_of_day_notes || "");

    setUseQuestBreakdown(event.quest_breakdown_enabled || false);
    setQuestBreakdownSteps(
        Array.isArray(event.quest_breakdown_steps) &&
            event.quest_breakdown_steps.length
            ? event.quest_breakdown_steps
            : [
                  {
                      id: Date.now().toString(),
                      title: "",
                      notes: "",
                      due_date: "",
                      completed: false,
                  },
              ]
    );

    setUseRubric(event.rubric_enabled || false);
    setRubricMode(event.rubric_mode || "points");
    setRubricTotalPoints(event.rubric_total_points || 100);
    setCustomScaleMax(event.custom_scale_max || 4);
    setRubricChecklistScoringMode(event.rubric_checklist_scoring_mode || "point_per_item");
    setRubricMatrixAssignmentPoints(event.rubric_matrix_assignment_points || event.rubric_total_points || 100);
    setRubricMatrixColumns(
        Array.isArray(event.rubric_matrix_columns) && event.rubric_matrix_columns.length
            ? event.rubric_matrix_columns
            : DEFAULT_MATRIX_COLUMNS
    );
    setRubricRows(
        Array.isArray(event.rubric_rows) && event.rubric_rows.length
            ? normalizeRubricRows(event.rubric_rows)
            : [createEmptyRubricRow()]
    );

    setLessonDateStart(event.date_start || "");
    setLessonDateEnd(event.date_end || "");
    setQuestDueDate(event.due_date || "");
    setQuestDueTime(event.due_time || "");
    setLessonAllDay(event.all_day ?? !event.start_time);
    setLessonStartTime(event.start_time || event.scheduled_start_time || event.due_time || "");
    setLessonEndTime(event.end_time || event.scheduled_end_time || "");
    setEditingQuestAllDay(event.all_day ?? !event.start_time);
    setEditingQuestStartTime(event.start_time || event.scheduled_start_time || event.due_time || "");
    setEditingQuestEndTime(event.end_time || event.scheduled_end_time || "");
    setLessonRepeat(event.repeat || "none");
    setLessonRepeatDays(event.repeat_days || []);
    setLessonSections(event.section_count || 1);
    setItemType(event.type || "lesson");
    setIsGraded(event.graded ?? true);
    setItemGradingMode(event.grading_mode || "not_graded");
    setAssignmentMaxPoints(event.max_points ?? 100);
    setAssignmentGradeType(event.grading_type || "");

    setQuizQuestions(
        event.questions?.length
            ? event.questions
            : [
                  {
                      id: Date.now().toString(),
                      question: "",
                      choices: ["", "", "", ""],
                      correctAnswer: 0,
                  },
              ]
    );

    setLessonLinkTitle(event.link?.title || "");
    setLessonLinkUrl(event.link?.url || "");
    setLessonFile(null);

    window.scrollTo({
        top: 0,
        behavior: "smooth",
    });
};

  const deleteLesson = (id) => {
    const target = events.find((event) => event.id === id);

    if (!target) return;

    if (isRecurringQuestItem(target)) {
        setPendingQuestDeleteModal(target);
        return;
    }

   const updatedEvents = events.filter((event) => event.id !== id);

saveShadowGroveEvents(updatedEvents);
setEvents(updatedEvents);
setSelectedPlannerEvent(null);
setEditingQuestModal(null);

toast.success("Quest deleted.");
};
    



    const clearCurrentClassQuests = () => {
        const confirmClear = window.confirm(
            "This removes every quest for this child and class from localStorage, including old generated/orphaned quests. Continue?"
        );

        if (!confirmClear) return;

        const storedEvents = JSON.parse(
            localStorage.getItem(eventsKey) || "[]"
        );

        const matchesThisClass = (event) => {
            const childMatches =
                event.child_id === childId ||
                event.child_name === child?.name;

            const subjectMatches =
                event.subject_id === subjectId ||
                event.subject_name === subject?.name;

            return childMatches && subjectMatches;
        };

        const updatedEvents = storedEvents.filter(
            (event) => !matchesThisClass(event)
        );

        saveShadowGroveEvents(updatedEvents);
        setEvents(updatedEvents);
        setSelectedPlannerEvent(null);
        setEditingQuestModal(null);
        setGradingQuestModal(null);
        alert("Old quests cleared for this child and class.");
    };
    const saveClassSessionRecord = () => {
    if (!completedSessionSummary) return;

    const savedSessions = JSON.parse(
        localStorage.getItem("shadowGroveClassSessions") || "[]"
    );

    const newRecord = {
        id: `${Date.now()}-${Math.random()}`,
        child_id: childId,
        subject_id: subjectId,
        subject_name: subject?.name || "Class",
        date: formatLocalDate(new Date()),
        started_at: classSessionStartedAt,
        ended_at: completedSessionSummary.endedAt,
        total_seconds: completedSessionSummary.totalSeconds,
        sections: completedSessionSummary.sections.map((section) => ({
        id: section.id,
        title: section.title,
        description: section.description || "",
        materials: Array.isArray(section.materials)
            ? section.materials
            : [],
        plannedMinutes: section.plannedMinutes,
        actualSeconds: section.actualSeconds,
        completed: section.completed,
    })),
    };

    localStorage.setItem(
        "shadowGroveClassSessions",
        JSON.stringify([...savedSessions, newRecord])
    );

    setShowSessionCompleteModal(false);
    setCompletedSessionSummary(null);
    setClassSessionStartedAt(null);

    toast.success("Class session saved.");
};
   const getSessionFlowMatches = (session) => {
    if (!session) return [];

    const sessionDate =
        session.date_start ||
        session.date ||
        session.due_date ||
        session.scheduled_date;

    return events.filter((event) => {
        const eventDate =
            event.date_start ||
            event.date ||
            event.due_date ||
            event.scheduled_date;

        const sameChild = event.child_id === session.child_id;
        const sameSubject = event.subject_id === session.subject_id;
        const sameDate = eventDate === sessionDate;

        const sameBatch =
            session.batch_id && event.batch_id === session.batch_id;

        const sameFlow =
            session.session_flow_id &&
            event.session_flow_id === session.session_flow_id;

        const sameCreatedFrom =
            session.created_from &&
            event.created_from === session.created_from;

        const bothSessions =
            isSessionItem(event) && isSessionItem(session);

        return (
            bothSessions &&
            sameChild &&
            sameSubject &&
            sameDate &&
            (sameBatch || sameFlow || sameCreatedFrom)
        );
    });
};

const moveSingleCalendarItemToDate = (itemId, newDate) => {
    const updatedEvents = events.map((event) =>
        event.id === itemId
            ?{
    ...event,
    date: newDate,
    date_start: newDate,
    date_end: newDate,
    due_date: newDate,
    updated_at: new Date().toISOString(),
}
            : event
    );

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);
    setDraggedQuestId(null);
    setPendingQuestMoveModal(null);

toast.success("Moved.");
};

const moveSessionFlowToDate = (session, newDate) => {
    const flowItems = getSessionFlowMatches(session);

    if (flowItems.length === 0) {
        moveSingleCalendarItemToDate(session.id, newDate);
        return;
    }

    const updatedEvents = events.map((event) =>
        flowItems.some((item) => item.id === event.id)
            ? {
                  ...event,
                  date: newDate,
                  date_start: newDate,
                  date_end: newDate,
                  due_date: newDate,
                  updated_at: new Date().toISOString(),
              }
            : event
    );

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);
    setDraggedQuestId(null);
    setPendingMoveModal(null);

    toast.success("Session flow moved.");
};
const isSessionItem = (item) => {
    return Boolean(
        item?.created_from === "class_timer_lesson_flow" ||
        item?.is_calendar_session === true ||
        item?.is_timed_session === true ||
        item?.session_order != null ||
        item?.session_flow_id ||
        item?.batch_id ||
        (
            item?.scheduled_start_time &&
            item?.scheduled_end_time &&
            item?.subject_id
        )
    );
};
const isBreakOrLunchItem = (item) => {
    return Boolean(
        item?.is_break === true ||
        item?.type === "break" ||
        item?.type === "lunch" ||
        item?.title?.toLowerCase?.().includes("break") ||
        item?.title?.toLowerCase?.().includes("lunch")
    );
};

const doesItemOverlapBreakOrLunch = (targetItem, dateString, allItems = []) => {
    const targetStart = getCalendarItemStart(targetItem);
    const targetEnd = getCalendarItemEnd(targetItem);

    if (targetStart == null || targetEnd == null) return false;

    const itemsForDate = allItems.filter((item) => {
        const itemDate =
            item.date ||
            item.due_date ||
            item.scheduled_date ||
            item.start_date;

        return itemDate === dateString;
    });

    return itemsForDate.some((item) => {
        if (!isBreakOrLunchItem(item)) return false;

        const breakStart = getCalendarItemStart(item);
        const breakEnd = getCalendarItemEnd(item);

        if (breakStart == null || breakEnd == null) return false;

        return targetStart < breakEnd && targetEnd > breakStart;
    });
};
const isRecurringQuestItem = (item) => {
    return Boolean(
        !item?.is_calendar_session &&
        !item?.is_timed_session &&
        item?.created_from !== "class_timer_lesson_flow" &&
        item?.session_order == null &&
        !item?.session_flow_id &&
        (item?.batch_id || (item?.repeat && item.repeat !== "none"))
    );
};

const handleCalendarDropToDate = (dateString) => {
    const droppedItemId =
    draggedQuestId || e.dataTransfer.getData("text/plain");

    if (!droppedItemId) return;

    if (!isValidSchoolDay(dateString)) {
        setCalendarWarning("Choose a valid class day or change the class schedule.");
        setDraggedQuestId(null);
        return;
    }

    const draggedItem = events.find((event) => event.id === droppedItemId);

if (!draggedItem) {
    return;
}

if (isSessionItem(draggedItem)) {
    setPendingMoveModal({
        item: draggedItem,
        targetDate: dateString,
    });
    return;
}
if (isRecurringQuestItem(draggedItem)) {
    setPendingQuestMoveModal({
        item: draggedItem,
        targetDate: dateString,
    });
    return;
}
moveSingleCalendarItemToDate(draggedItem.id, dateString);
};

   const handleWeekTimedDrop = (e, dateString) => {
    e.preventDefault();

    const droppedItemId =
        draggedQuestId || e.dataTransfer.getData("text/plain");

    if (!droppedItemId) return;

    if (!isValidSchoolDay(dateString)) {
        setCalendarWarning("This is not a valid class day.");
        setDraggedQuestId(null);
        return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;

    const weekZoomStartMinutes = classSchedule.startTime
        ? timeToMinutes(classSchedule.startTime)
        : 7 * 60;

    const weekZoomEndMinutes = classSchedule.endTime
        ? timeToMinutes(classSchedule.endTime)
        : 20 * 60;

    const safeStart = weekZoomStartMinutes ?? 7 * 60;
    const safeEnd = weekZoomEndMinutes ?? 20 * 60;
    const pixelsPerMinute = 2.4;

    const rawMinutes = safeStart + Math.round(y / pixelsPerMinute);
    const snappedMinutes = Math.round(rawMinutes / 5) * 5;

    const draggedItem = events.find((event) => event.id === droppedItemId);

if (!draggedItem && copiedQuestForPaste) {
    const copiedQuest = {
        ...copiedQuestForPaste,
        id: `${Date.now()}-${Math.random()}`,
        date: dateString,
        date_start: dateString,
        date_end: dateString,
        due_date: dateString,
        start_time: startTime,
        end_time: endTime,
        scheduled_start_time: startTime,
        scheduled_end_time: endTime,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
    };

  const updatedEvents = [...events, copiedQuest];

saveShadowGroveEvents(updatedEvents);
setEvents(updatedEvents);

setCopiedQuestForPaste(null);

toast.success("Quest pasted.");

return;
}

if (!draggedItem) {
    setDraggedQuestId(null);
    return;
}

    const oldStart = getCalendarItemStart(draggedItem);
    const oldEnd = getCalendarItemEnd(draggedItem);

    const duration =
        oldStart != null && oldEnd != null
            ? Math.max(5, oldEnd - oldStart)
            : 30;

    const newStart = Math.max(
        safeStart,
        Math.min(snappedMinutes, safeEnd - duration)
    );

    const newEnd = Math.min(safeEnd, newStart + duration);

    const startTime = minutesToInputTime(newStart);
    const movingDueOnlyItem = Boolean(
    draggedItem?.due_time &&
    (draggedItem?.all_day ||
        (!draggedItem?.start_time &&
            !draggedItem?.end_time &&
            !draggedItem?.scheduled_start_time &&
            !draggedItem?.scheduled_end_time))
);
    const endTime = minutesToInputTime(newEnd);

    const updatedDraggedItem = {
    ...draggedItem,
    date: dateString,
    date_start: dateString,
    date_end: dateString,
    due_date: dateString,
    ...(movingDueOnlyItem
        ? {
              all_day: true,
              due_time: startTime,
              start_time: "",
              end_time: "",
              scheduled_start_time: "",
              scheduled_end_time: "",
          }
        : {
              all_day: false,
              start_time: startTime,
              end_time: endTime,
              scheduled_start_time: startTime,
              scheduled_end_time: endTime,
          }),
    updated_at: new Date().toISOString(),
};
    if (doesItemOverlapBreakOrLunch(updatedDraggedItem, dateString, events)) {
        toast.error("Quests and sessions cannot be placed during lunch or break.");
        setDraggedQuestId(null);
        return;
    }

    if (isRecurringQuestItem(draggedItem)) {
    setPendingQuestMoveModal({
        item: updatedDraggedItem,
        originalItem: draggedItem,
        targetDate: dateString,
        targetStartTime: startTime,
        targetEndTime: endTime,
    });

    setDraggedQuestId(null);
    return;
}
   const updatedEvents = events.map((event) =>
    event.id === droppedItemId ? updatedDraggedItem : event
);

rememberWeekScroll();

saveShadowGroveEvents(updatedEvents);
setEvents(updatedEvents);

restoreWeekScroll();

setDraggedQuestId(null);

toast.success("Moved to new time.");
};
   const moveLessonToDate = (event, newDate) => {
    if (!newDate) return;

    const updatedEvents = events.map((item) =>
        item.id === event.id
            ? {
                  ...item,
                  date: newDate,
                  date_start: newDate,
                  date_end: newDate,
                  due_date: item.due_date ? newDate : item.due_date,
                  updated_at: new Date().toISOString(),
              }
            : item
    );

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);
};

const seededGenerator = React.useRef(false);
React.useEffect(() => {
    if (!generatorOnly || !initialDraft || seededGenerator.current) return;
    seededGenerator.current = true;
    loadLessonForEdit(initialDraft);
    setEditingLessonId(editExisting ? initialDraft.event_id || initialDraft.id : null);
    // The embedded creator starts with the dashboard's draft once per popup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [generatorOnly, initialDraft, editExisting]);

if (classroomDataLoading) {
    return (
        <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center px-4">
            <div className="w-full max-w-lg rounded-[32px] border border-[#ffd166]/40 bg-black p-8 text-center shadow-[0_0_40px_rgba(255,209,102,0.18)]">
                <h2 className="font-display text-3xl text-[#ffd166]">
                    Loading Classroom
                </h2>

                <p className="mt-3 text-sm text-[#b0b8d6]">
                    Retrieving the student and subject records...
                </p>
            </div>
        </div>
    );
}

if (
    classroomDataError ||
    !backendChild ||
    !backendSubject
) {
    return (
        <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center px-4">
            <div className="w-full max-w-lg rounded-[32px] border border-red-400/40 bg-black p-8 text-center shadow-[0_0_40px_rgba(248,113,113,0.18)]">
                <h2 className="font-display text-3xl text-red-300">
                    Classroom Not Found
                </h2>

                <p className="mt-3 text-sm text-[#b0b8d6]">
                    {classroomDataError ||
                        "This classroom is no longer available."}
                </p>

                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className={`${purpleActionBtn} mt-6`}
                >
                    Go Back
                </button>
            </div>
        </div>
    );
}
const creatorPanel = (
<section
    className="
        rounded-[32px]
        border border-[#ffd166]/40
        bg-black
        p-6
        shadow-[0_0_35px_rgba(255,209,102,0.10)]
        backdrop-blur-sm
    "
>
                    <FileText className="text-[#ffd166] mb-3" />
                    <AttachmentEditor {...assignmentAttachments} title={lessonTitle} inputClass={inputClass} onUploading={setAssignmentAttachmentBusy} onChange={(patch) => setAssignmentAttachments((current) => ({ ...current, ...patch }))} />
                    {plannerMode !== "curriculum" && <AssignmentTimingOptions value={assignmentTiming} onChange={setAssignmentTiming} subject={subject} inputClass={inputClass} />}


                    <h3 className="font-display text-3xl text-[#ffd166]">
                        Quest Creator
                    </h3>

                     <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5">


    <div>
        <label className="text-sm text-[#b0b8d6]">
            Create Type
        </label>


        <select
            value={itemType}
            onChange={(e) =>
                setItemType(e.target.value)
            }
            className={inputClass}
        >
            <option value="lesson">
                Lesson
            </option>


            <option value="assignment">
                Assignment
            </option>


            <option value="quiz">
                Quiz
            </option>


            <option value="test">
                Test
            </option>


            <option value="project">
                Project
            </option>
        </select>
    </div>


    {(itemType === "quiz" ||
        itemType === "test") && (



            <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-[#b0b8d6]">
                    <input
                        type="checkbox"
                        checked={isGraded}
                        onChange={(e) =>
                            setIsGraded(
                                e.target.checked
                            )
                        }
                    />


                    Graded
                </label>
            </div>
    )}


</div>
   <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4 shadow-[0_0_18px_rgba(255,209,102,0.12)]">
    <div>
        <label className="text-sm text-[#b0b8d6]">Item Type</label>
        <select
            value={itemType}
            onChange={(e) => setItemType(e.target.value)}
            className={inputClass}
        >
            <option value="lesson">Lesson</option>
            <option value="assignment">Assignment</option>
            <option value="quiz">Quiz</option>
            <option value="test">Test</option>
            <option value="project">Project</option>
            <option value="practice">Practice</option>
            <option value="field_trip">Field Trip</option>
        </select>
    </div>

    {itemType === "project" && (
        <div>
            <label className="text-sm text-[#b0b8d6]">
                Project Size
            </label>
            <select
                value={projectSize}
                onChange={(e) => setProjectSize(e.target.value)}
                className={inputClass}
            >
                <option value={5}>Small · 5 points / 3 coins</option>
                <option value={10}>Medium · 10 points / 5 coins</option>
                <option value={20}>Large · 20 points / 10 coins</option>
            </select>
        </div>
    )}


    <div>
        <label className="text-sm text-[#b0b8d6]">Grading Mode</label>
        <select
            value={itemGradingMode}
            onChange={(e) => setItemGradingMode(e.target.value)}
            className={inputClass}
        >
            <option value="not_graded">Not Graded</option>
            <option value="graded">Graded</option>
            <option value="practice">Practice</option>
        </select>
    </div>
    {itemGradingMode === "graded" && <>
        <label className="text-sm text-[#b0b8d6]">Grade format<select className={inputClass} value={assignmentGradeType} onChange={(e) => setAssignmentGradeType(e.target.value)}><option value="">Use classroom grading scale</option><option value="points">Points</option><option value="percentage">Number grade (0–100%)</option><option value="letter">Letter grade (A–F)</option><option value="pass_fail">Pass / No pass</option></select></label>
        <label className="text-sm text-[#b0b8d6]">Assignment points possible<input type="number" min="0.01" step="0.01" className={inputClass} value={assignmentMaxPoints} onChange={(e) => setAssignmentMaxPoints(e.target.value)} /></label>
    </>}
</div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                        <div className="md:col-span-2">
                            <label className="text-sm text-[#b0b8d6]">
                                Assignment / Lesson Title
                            </label>


                            <textarea rows={2}
                                value={lessonTitle}
                                onChange={(e) => setLessonTitle(e.target.value)}
                                placeholder="Fractions, Ancient Egypt..."
                                className={`${inputClass} min-h-[84px] resize-y`}
                            />
                        </div>




                               <div>
    <label className="text-sm text-[#b0b8d6]">
        Planner Mode
    </label>

                <select
    value={plannerMode}
    onChange={(e) => { setPlannerMode(e.target.value); setCurriculumPreview(null); setLessonRepeat("none"); }}
    className={inputClass}
>
    <option value="one_time">One Time</option>
    <option value="date_range">Date Range Generator</option>
    <option value="curriculum">Curriculum repeat / loop generator</option>
</select>
</div>
{plannerMode === "curriculum" && <div className="md:col-span-2 space-y-4"><AssignmentSeriesEditor event={buildBaseLessonEvent({ id: "template", title: lessonTitle, type: itemType, dateStart: lessonDateStart, dateEnd: lessonDateStart })} date={lessonDateStart} subject={subject} calendar={schoolCalendarForChild(schoolCalendar, childId)} inputClass={inputClass} buttonClass={purpleActionBtn} timing={assignmentTiming} onTimingChange={setAssignmentTiming} onPreview={setCurriculumPreview} />{curriculumPreview && <AssignmentSeriesPreview items={curriculumPreview} onChange={setCurriculumPreview} pictures={assignmentAttachments.pictures} inputClass={inputClass} buttonClass={purpleActionBtn} onUploading={setCurriculumUploading} />}</div>}

                <div className="md:col-span-2">
    <button
        type="button"
        onClick={() => setUseSectionTitles((prev) => !prev)}
        className={`${purpleActionBtn} ${
            useSectionTitles
                ? "!border-[#ffd166] !bg-[#ffd166]/20 !text-[#fff3c4]"
                : ""
        }`}
    >
        {useSectionTitles ? "Hide Sections" : "Add Sections"}
    </button>
</div>

{useSectionTitles && (
    <div className="md:col-span-2 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4">
        <p className="text-sm text-[#ffd166] mb-3">
            Section Titles
        </p>

        <div className="space-y-2">
            {sectionTitles.map((title, index) => (
                <div key={index} className="flex items-center gap-2">
                    <input
                        value={title}
                        onChange={(e) => {
                            const updated = [...sectionTitles];
                            updated[index] = e.target.value;
                            setSectionTitles(updated);
                        }}
                        placeholder={`Section ${index + 1} title`}
                        className={inputClass}
                    />

                    <button
                        type="button"
                        onClick={() =>
                            setSectionTitles((prev) =>
                                prev.length > 1
                                    ? prev.filter((_, i) => i !== index)
                                    : [""]
                            )
                        }
                        className={purpleActionBtn}
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>

        <button
            type="button"
            onClick={() =>
                setSectionTitles((prev) => [...prev, ""])
            }
            className={`${purpleActionBtn} mt-3`}
        >
            Add Section
        </button>
    </div>
)}

            {plannerMode === "lesson_count" && (
    <div className="md:col-span-2 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4">
        <label className="text-sm text-[#ffd166]">
            Number of Lessons to Generate
        </label>

        <input
            type="number"
            value={lessonCount}
            onChange={(e) => setLessonCount(e.target.value)}
            className={inputClass}
        />
    </div>
)}

{plannerMode === "custom_pattern" && (
    <div className="md:col-span-2 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4">
        <p className="text-sm text-[#ffd166] mb-3">
            Custom Pattern Steps
        </p>

        <div className="space-y-2">
            {customPatternSteps.map((step, index) => (
                <input
                    key={index}
                    value={step}
                    onChange={(e) => {
                        const updated = [...customPatternSteps];
                        updated[index] = e.target.value;
                        setCustomPatternSteps(updated);
                    }}
                    className={inputClass}
                />
            ))}
        </div>

        <button
            type="button"
            onClick={() =>
                setCustomPatternSteps((prev) => [...prev, ""])
            }
            className={`${purpleActionBtn} mt-3`}
        >
            Add Pattern Step
        </button>
    </div>
)}
                    {plannerMode === "loop" && (
    <div className="md:col-span-2 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4">
        <p className="text-sm text-[#ffd166] mb-3">
            Loop Generator
        </p>

        <div className="mb-4">
            <label className="text-sm text-[#b0b8d6]">
                Loop Name
            </label>

            <input
                value={loopName}
                onChange={(e) => setLoopName(e.target.value)}
                placeholder="Read, Practice, Quiz"
                className={inputClass}
            />
        </div>

        {savedLoopTemplates.length > 0 && (
            <div className="mb-4">
                <label className="text-sm text-[#b0b8d6]">
                    Saved Loops
                </label>

                <div className="space-y-2 mt-2">
                    {savedLoopTemplates.map((template) => (
                        <div
                            key={template.id}
                            className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4 shadow-[0_0_18px_rgba(255,209,102,0.08)]"
                        >
                            <div>
                                <p className="text-sm text-[#ffd166]">
                                    {template.name}
                                </p>

                                <p className="text-xs text-[#b0b8d6]">
                                    {template.steps
                                        .map((step) => step.label)
                                        .join(" → ")}
                                </p>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => loadLoopTemplate(template)}
                                    className={purpleActionBtn}
                                >
                                    Use
                                </button>

                                <button
                                    type="button"
                                    onClick={() => deleteLoopTemplate(template.id)}
                                    className={purpleActionBtn}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        <div className="space-y-3">
            {loopSteps.map((step, index) => (
                <div
                    key={step.id}
                    className="grid grid-cols-1 md:grid-cols-3 gap-3"
                >
                    <select
                        value={step.type}
                        onChange={(e) => {
                            const updated = [...loopSteps];
                            updated[index].type = e.target.value;
                            setLoopSteps(updated);
                        }}
                        className={inputClass}
                    >
                        <option value="lesson">Lesson</option>
                        <option value="assignment">Assignment</option>
                        <option value="practice">Practice</option>
                        <option value="quiz">Quiz</option>
                        <option value="test">Test</option>
                        <option value="project">Project</option>
                    </select>

                    <input
                        value={step.label}
                        onChange={(e) => {
                            const updated = [...loopSteps];
                            updated[index].label = e.target.value;
                            setLoopSteps(updated);
                        }}
                        placeholder="Read, Practice, Quiz..."
                        className={inputClass}
                    />

                    <button
                        type="button"
                        onClick={() =>
                            setLoopSteps((prev) =>
                                prev.filter((_, i) => i !== index)
                            )
                        }
                        className={purpleActionBtn}
                    >
                        Remove Step
                    </button>
                </div>
            ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
            <button
                type="button"
                onClick={() =>
                    setLoopSteps((prev) => [
                        ...prev,
                        {
                            id: Date.now().toString(),
                            label: "",
                            type: "assignment",
                        },
                    ])
                }
                className={purpleActionBtn}
            >
                Add Loop Step
            </button>

            <button
                type="button"
                onClick={saveLoopTemplate}
                className={purpleActionBtn}
            >
                Save Loop
            </button>
        </div>
    </div>
)}
        <div className="md:col-span-4 rounded-[28px] border border-[#ffd166]/25 bg-black/60 p-4 shadow-[0_0_18px_rgba(255,209,102,0.08)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h4 className="font-display text-xl text-[#ffd166]">
                        Session Placement
                    </h4>

                    <p className="mt-1 text-xs text-[#8f98b5]">
                        Keep this quest standalone or attach it to a real session section for this child and class.
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            setQuestSessionAttachmentMode("standalone");
                            setSelectedQuestSessionId("");
                            setSelectedQuestSessionFlowId("");
                            setSelectedQuestSessionSectionId("");
                            setSelectedQuestSessionSectionOrder(null);
                        }}
                        className={`${purpleActionBtn} ${
                            questSessionAttachmentMode === "standalone"
                                ? "!border-[#ffd166] !bg-[#ffd166]/10 !text-[#ffd166]"
                                : ""
                        }`}
                    >
                        Standalone Quest
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setQuestSessionAttachmentMode("attached");
                        }}
                        className={`${purpleActionBtn} ${
                            questSessionAttachmentMode === "attached"
                                ? "!border-[#a855f7] !bg-[#a855f7]/15 !text-[#e9d5ff]"
                                : ""
                        }`}
                    >
                        Attach to Session
                    </button>
                </div>
            </div>

            {questSessionAttachmentMode === "attached" && (
                <div className="mt-4">
                    {availableQuestSessionSections.length === 0 ? (
                        <div className="rounded-2xl border border-[#ef4444]/30 bg-[#ef4444]/5 p-4">
                            <p className="text-sm text-[#fca5a5]">
                                No open session sections are available for this child and class.
                            </p>

                            <p className="mt-1 text-xs text-[#8f98b5]">
                                Create and save a lesson flow to the calendar first, then return here.
                            </p>
                        </div>
                    ) : (
                        <>
                            <label className="text-sm text-[#b0b8d6]">
                                Choose Session Section
                            </label>

                            <select
                                value={selectedQuestSessionId}
                                onChange={(e) => {
                                    const nextId = e.target.value;

                                    setSelectedQuestSessionId(nextId);

                                    const selectedSession =
                                        availableQuestSessionSections.find(
                                            (session) => session.id === nextId
                                        ) || null;

                                    if (!selectedSession) {
                                        setSelectedQuestSessionFlowId("");
                                        setSelectedQuestSessionSectionId("");
                                        setSelectedQuestSessionSectionOrder(null);
                                        return;
                                    }

                                    const selectedDate =
                                        selectedSession.date_start ||
                                        selectedSession.date ||
                                        selectedSession.due_date ||
                                        "";

                                    setSelectedQuestSessionFlowId(
                                        selectedSession.session_flow_id || ""
                                    );

                                    setSelectedQuestSessionSectionId(
                                        selectedSession.id
                                    );

                                    setSelectedQuestSessionSectionOrder(
                                        Number(selectedSession.session_order) || null
                                    );

                                    if (selectedDate) {
                                        setLessonDateStart(selectedDate);
                                        setLessonDateEnd(selectedDate);
                                    }
                                }}
                                className={inputClass}
                            >
                                <option value="">
                                    Choose a session section
                                </option>

                                {availableQuestSessionSections.map((session) => {
                                    const sessionDate =
                                        session.date_start ||
                                        session.date ||
                                        session.due_date ||
                                        "No date";

                                    const startLabel =
                                        session.scheduled_start_time ||
                                        session.start_time ||
                                        "";

                                    return (
                                        <option
                                            key={session.id}
                                            value={session.id}
                                        >
                                            {sessionDate}
                                            {" — "}
                                            {session.title || "Untitled Session"}
                                            {startLabel
                                                ? ` — ${formatDisplayTime(startLabel)}`
                                                : " — Flexible"}
                                        </option>
                                    );
                                })}
                            </select>

                            {selectedQuestSessionSection && (
                                <div className="mt-3 rounded-2xl border border-[#a855f7]/30 bg-[#a855f7]/5 p-4">
                                    <p className="text-sm font-semibold text-[#e9d5ff]">
                                        {selectedQuestSessionSection.title}
                                    </p>

                                    <p className="mt-1 text-xs text-[#b0b8d6]">
                                        {selectedQuestSessionSection.date_start ||
                                            selectedQuestSessionSection.date ||
                                            selectedQuestSessionSection.due_date}
                                        {" · "}
                                        Section{" "}
                                        {selectedQuestSessionSection.session_order || "—"}
                                        {" · "}
                                        {selectedQuestSessionSection.scheduled_start_time
                                            ? `${formatDisplayTime(
                                                selectedQuestSessionSection.scheduled_start_time
                                            )}–${formatDisplayTime(
                                                selectedQuestSessionSection.scheduled_end_time
                                            )}`
                                            : "Flexible / Unscheduled"}
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
</div>
                        <div>
                            <label className="text-sm text-[#b0b8d6]">
                                Start Date
                            </label>


                            <input
                                type="date"
                                value={lessonDateStart}
                                onChange={(e) =>
                                    { setLessonDateStart(e.target.value); setCurriculumPreview(null); }
                                }
                                className={inputClass}
                            />
                        </div>

                    <div>
    <label className="text-sm text-[#b0b8d6]">
        Due Date
    </label>

    <input
        type="date"
        value={questDueDate}
        onChange={(e) => setQuestDueDate(e.target.value)}
        className={inputClass}
    />
</div>

<div>
    <label className="text-sm text-[#b0b8d6]">
        Due Time
    </label>

    <input
        type="time"
        value={questDueTime}
        onChange={(e) => setQuestDueTime(e.target.value)}
        className={inputClass}
    />
</div>
<div>
    <label className="text-sm text-[#b0b8d6]">
        Linked Lesson
    </label>

    <select
        value={linkedLessonId}
        onChange={(e) => {
            const selectedId =
                e.target.value;

            setLinkedLessonId(
                selectedId
            );

            if (!selectedId) {
                setLinkedLessonTitle(
                    ""
                );
                setLinkedChapterId(
                    ""
                );
                setLinkedChapterTitle(
                    ""
                );

                return;
            }

            const selectedLesson =
                questLinkableLessons.find(
                    (page) =>
                        page.id ===
                        selectedId
                );

            setLinkedLessonTitle(
                selectedLesson?.title ||
                    ""
            );

            setLinkedChapterId(
                selectedLesson?.chapterId ||
                    ""
            );

            setLinkedChapterTitle(
                selectedLesson?.chapterTitle ||
                    ""
            );
        }}
        className={inputClass}
    >
        <option value="">
            No linked lesson
        </option>

        {questLinkableLessons.map(
            (page) => (
                <option
                    key={page.id}
                    value={page.id}
                >
                    {`${
                        page.chapterTitle ||
                        "Unassigned"
                    } — ${
                        page.lessonNumber
                            ? `Lesson ${page.lessonNumber}: `
                            : ""
                    }${
                        page.title ||
                        "Untitled Lesson"
                    }`}
                </option>
            )
        )}
    </select>

    <p className="mt-1 text-xs text-[#6c7a9c]">
        Link this quest to a Liberi Curiosi lesson.
    </p>
</div>
    {plannerMode === "date_range" && (
    <div>
        <label className="text-sm text-[#b0b8d6]">
            End Date
        </label>

        <input
            type="date"
            value={lessonDateEnd}
            onChange={(e) =>
                setLessonDateEnd(e.target.value)
            }
            className={inputClass}
        />
    </div>
)}



                        <div hidden={plannerMode === "curriculum"}>
                            <label className="text-sm text-[#b0b8d6]">
                                Repeat
                            </label>


                            <select
                                value={lessonRepeat}
                                onChange={(e) => setLessonRepeat(e.target.value)}
                                className={inputClass}
                            >
                                <option value="none">Does Not Repeat</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="biweekly">Biweekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="custom">Custom Days</option>
                            </select>
                        </div>


                        <div className="flex items-end">
                            <label className="flex items-center gap-2 text-sm text-[#b0b8d6]">
                                <input
                                    type="checkbox"
                                    checked={lessonAllDay}
                                    onChange={(e) =>
                                        setLessonAllDay(e.target.checked)
                                    }
                                />
                                All Day / Flexible Block
                            </label>
                        </div>

                    {!lessonAllDay && (
    <>
        <div>
            <label className="text-sm text-[#b0b8d6]">
                Start Time
            </label>

            <input
                type="time"
                step="300"
                value={lessonStartTime}
                onChange={(e) => {
                    setLessonStartTime(e.target.value);
                    setEditingQuestStartTime(e.target.value);
                }}
                className={inputClass}
            />
        </div>

        <div>
            <label className="text-sm text-[#b0b8d6]">
                End Time
            </label>

            <input
                type="time"
                step="300"
                value={lessonEndTime}
                onChange={(e) => {
                    setLessonEndTime(e.target.value);
                    setEditingQuestEndTime(e.target.value);
                }}
                className={inputClass}
            />
        </div>
    </>
)}



                        {plannerMode !== "curriculum" && lessonRepeat === "custom" && (
                            <div className="md:col-span-2 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4">
                                <p className="text-sm text-[#ffd166] mb-3">
                                    Choose repeat days
                                </p>


                                <div className="flex flex-wrap gap-2">
                                    {DAYS.map((day) => (
                                        <button
                                            key={day}
                                            type="button"
                                            onClick={() => {
                                                setLessonRepeatDays((prev) =>
                                                    prev.includes(day)
                                                        ? prev.filter(
                                                              (d) => d !== day
                                                          )
                                                        : [...prev, day]
                                                );
                                            }}
                                            className={`${purpleActionBtn} ${
                                                lessonRepeatDays.includes(day)
                                                ? "!border-[#38bdf8] !bg-[#0ea5e9]/20 !text-[#e0f2fe] !shadow-[0_0_22px_rgba(56,189,248,0.75)]"
                                                 : ""
                                            }`}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>


                   <div className="mt-4">
                        <label className="text-sm text-[#b0b8d6]">
                            Description
                        </label>


                        <textarea
                            value={lessonDescription}
                            onChange={(e) =>
                                setLessonDescription(e.target.value)
                            }
                            placeholder="Lesson overview, objectives, instructions..."
                            className={`${textareaClass} min-h-[240px] resize-y`}
                        />
                    </div>


                            <div className="mt-4 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4">
    <label className="text-sm text-[#ffd166]">
        Materials Checklist
    </label>

    <div className="mt-3">
        <label className="text-sm text-[#b0b8d6]">
            Materials List Name
        </label>

        <input
            value={materialListName}
            onChange={(e) => setMaterialListName(e.target.value)}
            placeholder="Singapore Math Supplies, Science Lab Kit..."
            className={inputClass}
        />
    </div>

    {savedMaterialLists.length > 0 && (
        <div className="mt-4">
            <p className="text-sm text-[#b0b8d6]">
                Saved Material Lists
            </p>

            <div className="space-y-2 mt-2">
                {savedMaterialLists.map((list) => (
                    <div
                        key={list.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4 shadow-[0_0_18px_rgba(255,209,102,0.08)]"
                    >
                        <div>
                            <p className="text-sm text-[#ffd166]">
                                {list.name}
                            </p>

                            <p className="text-xs text-[#b0b8d6]">
                                {list.materials
                                    .map((item) => item.name)
                                    .join(", ")}
                            </p>
                        </div>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => loadMaterialList(list)}
                                className="
    rounded-2xl
    border border-[#ffd166]/40
    bg-black
    px-3 py-2
    text-xs text-[#ffd166]
    shadow-[0_0_14px_rgba(255,209,102,0.10)]
    hover:bg-[#ffd166]/[0.06]
    hover:shadow-[0_0_20px_rgba(255,209,102,0.20)]
    transition-all
    duration-200
"
                            >
                                Use
                            </button>

                            <button
                                type="button"
                                onClick={() => deleteMaterialList(list.id)}
                                className="
                                 rounded-2xl
                                border border-[#ffd166]/40
                                 bg-black
                                 px-3 py-2
                                 text-xs text-[#ffd166]
                                shadow-[0_0_14px_rgba(255,209,102,0.10)]
                                  hover:bg-[#ffd166]/[0.06]
                                    hover:shadow-[0_0_20px_rgba(255,209,102,0.20)]
                                 transition-all
                                duration-200
                                "
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )}

    <div className="space-y-2 mt-4">
        {lessonMaterials.map((material, index) => (
            <div
                key={material.id}
                className="flex items-center gap-2"
            >
                <input
                    type="checkbox"
                    checked={material.checked}
                    onChange={(e) => {
                        const updated = [...lessonMaterials];
                        updated[index].checked = e.target.checked;
                        setLessonMaterials(updated);
                    }}
                />

                <input
                    value={material.name}
                    onChange={(e) => {
                        const updated = [...lessonMaterials];
                        updated[index].name = e.target.value;
                        setLessonMaterials(updated);
                    }}
                    placeholder={`Item ${index + 1}`}
                    className={inputClass}
                />

                <button
                    type="button"
                    onClick={() =>
                        setLessonMaterials((prev) =>
                            prev.length > 1
                                ? prev.filter((_, i) => i !== index)
                                : [
                                      {
                                          id: Date.now().toString(),
                                          name: "",
                                          checked: false,
                                      },
                                  ]
                        )
                    }
                    className={purpleActionBtn}
                >
                    Remove
                </button>
            </div>
        ))}
    </div>

    <div className="flex flex-wrap gap-2 mt-3">
        <button
            type="button"
            onClick={() =>
                setLessonMaterials((prev) => [
                    ...prev,
                    {
                        id: Date.now().toString(),
                        name: "",
                        checked: false,
                    },
                ])
            }
            className={purpleActionBtn}
        >
            Add Item
        </button>

        <button
            type="button"
            onClick={saveMaterialList}
            className={purpleActionBtn}
        >
            Save Materials List

        </button>

    </div>
</div>

<div className="mt-4 rounded-[32px] border border-[#ffd166]/40 bg-black p-5 shadow-[0_0_35px_rgba(255,209,102,0.10)]">
    <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
            <h4 className="font-display text-2xl text-[#ffd166]">
                Grading Rubric
            </h4>

            <p className="text-sm text-[#b0b8d6] mt-1">
                Add criteria so the child can see how this quest will be graded.
            </p>
        </div>

        <button
            type="button"
            onClick={() => setUseRubric((prev) => !prev)}
            className={`${purpleActionBtn} ${
                useRubric
                    ? "!border-[#ffd166] !bg-[#ffd166]/20 !text-[#fff3c4] !shadow-[0_0_18px_rgba(255,209,102,0.55)]"
                    : ""
            }`}
        >
            {useRubric ? "Rubric Enabled" : "Enable Rubric"}
        </button>
    </div>

    {useRubric && (
        <div className="mt-5 space-y-4">
            <div className="rounded-2xl border border-[#ffd166]/40 bg-[#080b14]/90 p-4 shadow-[0_0_18px_rgba(255,209,102,0.12)]">
                <label className="text-sm text-[#b0b8d6]">
                    Rubric Type
                </label>

                <select
                    value={rubricMode}
                    onChange={(e) => {
                        const nextMode = e.target.value;
                        setRubricMode(nextMode);

                        if (nextMode === "custom") {
                            setRubricRows((prev) =>
                                prev.map((row) => ({
                                    ...row,
                                    scaleLevels:
                                        row.scaleLevels?.length
                                            ? row.scaleLevels
                                            : createCustomScaleLevels(customScaleMax),
                                }))
                            );
                        }
                    }}
                    className={inputClass}
                >
                    <option value="points">Points Based Rubric</option>
                    <option value="scale">1-4 Scale Rubric</option>
                    <option value="custom">Custom Scale Rubric</option>
                    <option value="matrix">Rubric Matrix</option>
                    <option value="checklist">Simple Checklist</option>
                </select>

                {rubricMode === "points" && (
                    <div className="mt-4">
                        <label className="text-sm text-[#b0b8d6]">
                            Total Possible Points
                        </label>

                        <input
                            type="number"
                            value={rubricTotalPoints}
                            onChange={(e) =>
                                setRubricTotalPoints(e.target.value)
                            }
                            className={inputClass}
                        />
                    </div>
                )}

                {rubricMode === "custom" && (
                    <div className="mt-4">
                        <label className="text-sm text-[#b0b8d6]">
                            Custom Scale Highest Number
                        </label>

                        <input
                            type="number"
                            min="2"
                            value={customScaleMax}
                            onChange={(e) => {
                                const nextMax = Math.max(
                                    2,
                                    Number(e.target.value) || 4
                                );

                                setCustomScaleMax(nextMax);

                                setRubricRows((prev) =>
                                    prev.map((row) => {
                                        const existingLevels =
                                            Array.isArray(row.scaleLevels) &&
                                            row.scaleLevels.length
                                                ? row.scaleLevels
                                                : createCustomScaleLevels(4);

                                        let nextLevels = existingLevels.slice(
                                            0,
                                            nextMax
                                        );

                                        while (nextLevels.length < nextMax) {
                                            const nextScore =
                                                nextLevels.length + 1;
                                            nextLevels.push({
                                                id: String(nextScore),
                                                score: nextScore,
                                                label: `Score ${nextScore}`,
                                                description: "",
                                                color:
                                                    nextScore === 1
                                                        ? "#ef4444"
                                                        : nextScore === nextMax
                                                        ? "#4ade80"
                                                        : "#facc15",
                                            });
                                        }

                                        return {
                                            ...row,
                                            scaleLevels: nextLevels.map(
                                                (level, levelIndex) => ({
                                                    ...level,
                                                    score: levelIndex + 1,
                                                    id: String(levelIndex + 1),
                                                })
                                            ),
                                        };
                                    })
                                );
                            }}
                            className={inputClass}
                        />
                    </div>
                )}

                {rubricMode === "custom" && (
                    <p className="text-xs text-[#b0b8d6] mt-2">
                        You can lower the highest number to remove top levels, or use each level's Remove button. Existing labels/descriptions are preserved when possible.
                    </p>
                )}

                {rubricMode === "checklist" && (
                    <div className="mt-4 rounded-2xl border border-[#ffd166]/30 bg-black p-4 shadow-[0_0_14px_rgba(255,209,102,0.08)]">
                        <h5 className="font-display text-xl text-[#ffd166]">
                            Simple Checklist Setup
                        </h5>

                        <p className="text-xs text-[#b0b8d6] mt-2 leading-relaxed">
                            Use this only for yes/no completion requirements. For scored rows like Grammar 5/4/3/2/1, choose Rubric Matrix instead.
                        </p>

                        <div className="mt-4">
                            <label className="text-sm text-[#b0b8d6]">
                                Checklist Scoring Logic
                            </label>

                            <select
                                value={rubricChecklistScoringMode}
                                onChange={(e) =>
                                    setRubricChecklistScoringMode(e.target.value)
                                }
                                className={inputClass}
                            >
                                <option value="point_per_item">
                                    Point per item: each checked requirement equals 1 point
                                </option>

                                <option value="weighted_sections">
                                    Weighted sections: completing a category unlocks section points
                                </option>
                            </select>
                        </div>
                    </div>
                )}

                {rubricMode === "matrix" && (
                    <div className="mt-4 rounded-2xl border border-[#ffd166]/30 bg-black p-4 shadow-[0_0_14px_rgba(255,209,102,0.08)]">
                        <h5 className="font-display text-xl text-[#ffd166]">
                            Rubric Matrix Setup
                        </h5>

                        <p className="text-xs text-[#b0b8d6] mt-2 leading-relaxed">
                            Build a colored scoring table. Each row is a category, each column is a score level, and the parent checks one score per row during grading.
                        </p>

                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm text-[#b0b8d6]">
                                    Assignment Worth Points
                                </label>
                                <input
                                    type="number"
                                    value={rubricMatrixAssignmentPoints}
                                    onChange={(e) => setRubricMatrixAssignmentPoints(e.target.value)}
                                    className={inputClass}
                                />
                            </div>
                        </div>

                        <div className="mt-4 overflow-x-auto rounded-2xl border border-[#ffd166]/20">
                            <table className="w-full min-w-[760px] border-collapse text-xs">
                                <thead className="bg-[#080b14] text-[#ffd166]">
                                    <tr>
                                        <th className="border border-[#ffd166]/20 p-2 text-left">Score</th>
                                        {rubricMatrixColumns.map((column, columnIndex) => (
                                            <th key={column.id} className="border border-[#ffd166]/20 p-2">
                                                <div className="space-y-2">
                                                    <input
                                                        type="number"
                                                        value={column.score}
                                                        onChange={(e) => {
                                                            const updated = [...rubricMatrixColumns];
                                                            updated[columnIndex] = { ...updated[columnIndex], score: Number(e.target.value) || 0, id: String(e.target.value || column.score) };
                                                            setRubricMatrixColumns(updated);
                                                        }}
                                                        className={inputClass}
                                                    />
                                                    <input
                                                        value={column.label}
                                                        onChange={(e) => {
                                                            const updated = [...rubricMatrixColumns];
                                                            updated[columnIndex] = { ...updated[columnIndex], label: e.target.value };
                                                            setRubricMatrixColumns(updated);
                                                        }}
                                                        className={inputClass}
                                                    />
                                                    <input
                                                        type="color"
                                                        value={column.color || "#ffd166"}
                                                        onChange={(e) => {
                                                            const updated = [...rubricMatrixColumns];
                                                            updated[columnIndex] = { ...updated[columnIndex], color: e.target.value };
                                                            setRubricMatrixColumns(updated);
                                                        }}
                                                        className="h-8 w-12 rounded bg-transparent"
                                                    />
                                                    {rubricMatrixColumns.length > 2 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setRubricMatrixColumns((prev) => prev.filter((_, i) => i !== columnIndex))}
                                                            className="rounded-lg border border-red-400/40 px-2 py-1 text-[10px] text-red-200 hover:bg-red-950/30"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                            </table>
                        </div>

                        <button
                            type="button"
                            onClick={() =>
                                setRubricMatrixColumns((prev) => [
                                    ...prev,
                                    {
                                        id: `${Date.now()}-${Math.random()}`,
                                        score: prev.length ? Math.max(...prev.map((col) => Number(col.score) || 0)) + 1 : 1,
                                        label: "New Score",
                                        color: "#ffd166",
                                    },
                                ])
                            }
                            className={`${purpleActionBtn} mt-3`}
                        >
                            Add Matrix Column
                        </button>
                    </div>
                )}
            </div>

            {savedRubricTemplates.length > 0 && (
                <div className="rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4">
                    <p className="text-sm text-[#ffd166] mb-3">
                        Saved Rubric Templates
                    </p>

                    <div className="space-y-2">
                        {savedRubricTemplates.map((template) => (
                            <div
                                key={template.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-[#ffd166]/20 bg-black p-3"
                            >
                                <div>
                                    <p className="text-sm text-[#ffd166]">
                                        {template.name}
                                    </p>

                                    <p className="text-xs text-[#b0b8d6]">
                                        {getRubricModeLabel(
                                            template.rubric_mode
                                        )}
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            loadRubricTemplate(template)
                                        }
                                        className={purpleActionBtn}
                                    >
                                        Use
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            deleteRubricTemplate(template.id)
                                        }
                                        className={purpleActionBtn}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {rubricMode !== "matrix" && rubricRows.map((row, index) => (
                <div
                    key={row.id}
                    className="rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4 shadow-[0_0_18px_rgba(255,209,102,0.08)]"
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex-1 min-w-[220px]">
                            <label className="text-sm text-[#b0b8d6]">
                                Criteria
                            </label>

                            <input
                                value={row.criteria}
                                onChange={(e) => {
                                    const updated = [...rubricRows];
                                    updated[index] = {
                                        ...updated[index],
                                        criteria: e.target.value,
                                    };
                                    setRubricRows(updated);
                                }}
                                placeholder="Accuracy, effort, organization..."
                                className={inputClass}
                            />
                        </div>

                        <button
                            type="button"
                            onClick={() =>
                                setRubricRows((prev) =>
                                    prev.length > 1
                                        ? prev.filter((_, i) => i !== index)
                                        : [createEmptyRubricRow()]
                                )
                            }
                            className={purpleActionBtn}
                        >
                            Remove Criteria
                        </button>
                    </div>

                    {rubricMode === "points" && (
                        <div className="mt-3">
                            <label className="text-sm text-[#b0b8d6]">
                                Max Points
                            </label>

                            <input
                                type="number"
                                value={row.maxPoints}
                                onChange={(e) => {
                                    const updated = [...rubricRows];
                                    updated[index] = {
                                        ...updated[index],
                                        maxPoints: e.target.value,
                                    };

                                    setRubricRows(updated);
                                }}
                                className={inputClass}
                            />
                        </div>
                    )}

                    {rubricMode === "checklist" && (
                        <div className="mt-4 rounded-2xl border border-[#ffd166]/20 bg-black p-4 shadow-[0_0_14px_rgba(255,209,102,0.08)]">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <label className="flex items-center gap-2 text-sm text-[#b0b8d6]">
                                    <input
                                        type="checkbox"
                                        checked={row.checklistRequired ?? true}
                                        onChange={(e) => {
                                            const updated = [...rubricRows];
                                            updated[index] = {
                                                ...updated[index],
                                                checklistRequired:
                                                    e.target.checked,
                                            };

                                            setRubricRows(updated);
                                        }}
                                    />
                                    Required item
                                </label>

                                <div>
                                    <label className="text-sm text-[#b0b8d6]">
                                        Category / Section
                                    </label>

                                    <input
                                        value={row.checklistCategory || ""}
                                        onChange={(e) => {
                                            const updated = [...rubricRows];
                                            updated[index] = {
                                                ...updated[index],
                                                checklistCategory: e.target.value,
                                            };

                                            setRubricRows(updated);
                                        }}
                                        placeholder="Content & Ideas"
                                        className={inputClass}
                                    />
                                </div>

                                {rubricChecklistScoringMode === "weighted_sections" && (
                                    <div>
                                        <label className="text-sm text-[#b0b8d6]">
                                            Section Points
                                        </label>

                                        <input
                                            type="number"
                                            value={row.checklistSectionPoints || 5}
                                            onChange={(e) => {
                                                const updated = [...rubricRows];
                                                updated[index] = {
                                                    ...updated[index],
                                                    checklistSectionPoints: e.target.value,
                                                };

                                                setRubricRows(updated);
                                            }}
                                            className={inputClass}
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="text-sm text-[#b0b8d6]">
                                        Complete Color
                                    </label>

                                    <input
                                        type="color"
                                        value={
                                            row.checklistColor ||
                                            CHECKLIST_COMPLETE_COLOR
                                        }
                                        onChange={(e) => {
                                            const updated = [...rubricRows];
                                            updated[index] = {
                                                ...updated[index],
                                                checklistColor: e.target.value,
                                            };

                                            setRubricRows(updated);
                                        }}
                                        className="mt-2 h-9 w-14 rounded bg-transparent"
                                    />
                                </div>

                                <div className="md:col-span-3 overflow-hidden rounded-2xl border border-[#ffd166]/30 bg-black shadow-[0_0_14px_rgba(255,209,102,0.08)]">
                                    <div className="grid grid-cols-12 border-b border-[#ffd166]/20 bg-[#080b14]/90 text-[11px] text-[#ffd166]">
                                        <div className="col-span-2 px-3 py-2 border-r border-[#ffd166]/20">
                                            Done
                                        </div>
                                        <div className="col-span-3 px-3 py-2 border-r border-[#ffd166]/20">
                                            Category
                                        </div>
                                        <div className="col-span-5 px-3 py-2 border-r border-[#ffd166]/20">
                                            Requirement
                                        </div>
                                        <div className="col-span-2 px-3 py-2">
                                            Points
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-12 text-xs text-[#d8dee9]">
                                        <div
                                            className="col-span-2 px-3 py-2 border-r border-[#ffd166]/10 font-bold"
                                            style={{
                                                color:
                                                    row.checklistColor ||
                                                    CHECKLIST_COMPLETE_COLOR,
                                            }}
                                        >
                                            ✓
                                        </div>
                                        <div className="col-span-3 px-3 py-2 border-r border-[#ffd166]/10">
                                            {row.checklistCategory || "Category"}
                                        </div>
                                        <div className="col-span-5 px-3 py-2 border-r border-[#ffd166]/10">
                                            {row.criteria || "Concrete yes/no requirement"}
                                        </div>
                                        <div className="col-span-2 px-3 py-2">
                                            {rubricChecklistScoringMode === "weighted_sections"
                                                ? row.checklistSectionPoints || 5
                                                : row.checklistRequired === false
                                                ? "Optional"
                                                : 1}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-3">
                                <label className="text-sm text-[#b0b8d6]">
                                    Checklist note / success requirement
                                </label>

                                <textarea
                                    value={row.checklistNote || ""}
                                    onChange={(e) => {
                                        const updated = [...rubricRows];
                                        updated[index] = {
                                            ...updated[index],
                                            checklistNote: e.target.value,
                                        };

                                        setRubricRows(updated);
                                    }}
                                    placeholder="Example: Must include a thesis statement, three body paragraphs, and conclusion."
                                    className={`${textareaClass} min-h-[70px]`}
                                />
                            </div>
                        </div>
                    )}

                    {(rubricMode === "scale" || rubricMode === "custom") && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                            {(row.scaleLevels?.length
                                ? row.scaleLevels
                                : DEFAULT_SCALE_LEVELS
                            ).map((level, levelIndex) => (
                                <div
                                    key={level.id || level.score}
                                    className="rounded-2xl border bg-black p-3"
                                    style={{
                                        borderColor:
                                            level.color || "#ffd166",
                                        boxShadow: `0 0 14px ${
                                            level.color || "#ffd166"
                                        }55`,
                                    }}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <p
                                            className="text-sm font-bold"
                                            style={{
                                                color:
                                                    level.color ||
                                                    "#ffd166",
                                            }}
                                        >
                                            Score {level.score}
                                        </p>

                                        {rubricMode === "custom" && (
                                            <input
                                                type="color"
                                                value={
                                                    level.color ||
                                                    "#ffd166"
                                                }
                                                onChange={(e) => {
                                                    const updated = [
                                                        ...rubricRows,
                                                    ];

                                                    const scaleLevels = [
                                                        ...(updated[index]
                                                            .scaleLevels ||
                                                            []),
                                                    ];

                                                    scaleLevels[levelIndex] = {
                                                        ...scaleLevels[
                                                            levelIndex
                                                        ],
                                                        color: e.target.value,
                                                    };

                                                    updated[index] = {
                                                        ...updated[index],
                                                        scaleLevels,
                                                    };

                                                    setRubricRows(updated);
                                                }}
                                                className="h-8 w-10 rounded bg-transparent"
                                            />
                                        )}

                                        {rubricMode === "custom" &&
                                            (row.scaleLevels || []).length > 2 && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const updated = [
                                                            ...rubricRows,
                                                        ];

                                                        const scaleLevels = [
                                                            ...(updated[index]
                                                                .scaleLevels || []),
                                                        ].filter(
                                                            (_, i) =>
                                                                i !== levelIndex
                                                        );

                                                        updated[index] = {
                                                            ...updated[index],
                                                            scaleLevels: scaleLevels.map(
                                                                (item, itemIndex) => ({
                                                                    ...item,
                                                                    score: itemIndex + 1,
                                                                    id: String(itemIndex + 1),
                                                                })
                                                            ),
                                                        };

                                                        setCustomScaleMax(
                                                            scaleLevels.length
                                                        );
                                                        setRubricRows(updated);
                                                    }}
                                                    className="rounded-lg border border-red-400/40 px-2 py-1 text-[10px] text-red-200 hover:bg-red-950/30"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                    </div>

                                    {rubricMode === "custom" && (
                                        <input
                                            value={level.label || ""}
                                            onChange={(e) => {
                                                const updated = [
                                                    ...rubricRows,
                                                ];

                                                const scaleLevels = [
                                                    ...(updated[index]
                                                        .scaleLevels || []),
                                                ];

                                                scaleLevels[levelIndex] = {
                                                    ...scaleLevels[levelIndex],
                                                    label: e.target.value,
                                                };

                                                updated[index] = {
                                                    ...updated[index],
                                                    scaleLevels,
                                                };

                                                setRubricRows(updated);
                                            }}
                                            placeholder="Level label..."
                                            className={inputClass}
                                        />
                                    )}

                                    <textarea
                                        value={level.description || ""}
                                        onChange={(e) => {
                                            const updated = [...rubricRows];

                                            const scaleLevels = [
                                                ...(updated[index]
                                                    .scaleLevels || []),
                                            ];

                                            scaleLevels[levelIndex] = {
                                                ...scaleLevels[levelIndex],
                                                description: e.target.value,
                                            };

                                            updated[index] = {
                                                ...updated[index],
                                                scaleLevels,
                                            };

                                            setRubricRows(updated);
                                        }}
                                        placeholder={`Describe what a ${level.score} means...`}
                                        className={`${textareaClass} min-h-[80px] mt-2`}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {rubricMode === "custom" && (
                        <button
                            type="button"
                            onClick={() => {
                                const nextMax = Number(customScaleMax) + 1;
                                setCustomScaleMax(nextMax);
                                setRubricRows((prev) =>
                                    prev.map((item, itemIndex) => {
                                        if (itemIndex !== index) return item;

                                        const nextScore =
                                            (item.scaleLevels || []).length + 1;

                                        return {
                                            ...item,
                                            scaleLevels: [
                                                ...(item.scaleLevels || []),
                                                {
                                                    id: String(nextScore),
                                                    score: nextScore,
                                                    label: `Score ${nextScore}`,
                                                    description: "",
                                                    color: "#4ade80",
                                                },
                                            ],
                                        };
                                    })
                                );
                            }}
                            className={`${purpleActionBtn} mt-3`}
                        >
                            Add Scale Level
                        </button>
                    )}
                </div>
            ))}

            {rubricMode === "matrix" && (
                <div className="overflow-x-auto rounded-2xl border border-[#ffd166]/30 bg-black shadow-[0_0_18px_rgba(255,209,102,0.08)]">
                    <table className="w-full min-w-[900px] border-collapse text-xs">
                        <thead className="bg-[#080b14] text-[#ffd166]">
                            <tr>
                                <th className="border border-[#ffd166]/20 p-3 text-left w-[180px]">Category</th>
                                {rubricMatrixColumns.map((column) => (
                                    <th
                                        key={column.id}
                                        className="border border-[#ffd166]/20 p-3 text-center"
                                        style={{ color: column.color || "#ffd166" }}
                                    >
                                        <div className="font-bold">{column.score}</div>
                                        <div>{column.label}</div>
                                    </th>
                                ))}
                                <th className="border border-[#ffd166]/20 p-3 text-center w-[90px]">Remove</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rubricRows.map((row, rowIndex) => (
                                <tr key={row.id}>
                                    <td className="border border-[#ffd166]/20 p-2 align-top">
                                        <input
                                            value={row.criteria}
                                            onChange={(e) => {
                                                const updated = [...rubricRows];
                                                updated[rowIndex] = { ...updated[rowIndex], criteria: e.target.value };
                                                setRubricRows(updated);
                                            }}
                                            placeholder="Grammar, spelling, creativity..."
                                            className={inputClass}
                                        />
                                    </td>
                                    {rubricMatrixColumns.map((column) => (
                                        <td
                                            key={column.id}
                                            className="border border-[#ffd166]/20 p-2 align-top"
                                            style={{ backgroundColor: `${column.color || "#ffd166"}12` }}
                                        >
                                            <textarea
                                                value={(row.matrixCells || {})[column.score] || ""}
                                                onChange={(e) => {
                                                    const updated = [...rubricRows];
                                                    updated[rowIndex] = {
                                                        ...updated[rowIndex],
                                                        matrixCells: {
                                                            ...(updated[rowIndex].matrixCells || {}),
                                                            [column.score]: e.target.value,
                                                        },
                                                    };
                                                    setRubricRows(updated);
                                                }}
                                                placeholder={`Describe ${column.label}...`}
                                                className={`${textareaClass} min-h-[90px]`}
                                            />
                                        </td>
                                    ))}
                                    <td className="border border-[#ffd166]/20 p-2 text-center align-middle">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setRubricRows((prev) =>
                                                    prev.length > 1
                                                        ? prev.filter((_, i) => i !== rowIndex)
                                                        : [createEmptyRubricRow()]
                                                )
                                            }
                                            className={purpleActionBtn}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={() =>
                        setRubricRows((prev) => [
                            ...prev,
                            createEmptyRubricRow(),
                        ])
                    }
                    className={purpleActionBtn}
                >
                    Add Rubric Criteria
                </button>

                <div className="flex-1 min-w-[220px]">
                    <input
                        value={rubricTemplateName}
                        onChange={(e) =>
                            setRubricTemplateName(e.target.value)
                        }
                        placeholder="Template name, like Essay Rubric..."
                        className={inputClass}
                    />
                </div>

                <button
                    type="button"
                    onClick={saveRubricTemplate}
                    className={purpleActionBtn}
                >
                    Save Rubric Template
                </button>
            </div>
        </div>
    )}
</div>


                        <div className="mt-4 rounded-[32px] border border-[#ffd166]/40 bg-black p-5 shadow-[0_0_35px_rgba(255,209,102,0.10)]">
    <div className="flex flex-wrap items-center justify-between gap-3">
        <div>

            <h4 className="font-display text-2xl text-[#ffd166]">

                Quest Breakdown
            </h4>

            <p className="text-sm text-[#b0b8d6] mt-1">
                Add steps to Quest
            </p>

        </div>

        <label className="flex items-center gap-2 text-sm text-[#ffd166]">
            <input
                type="checkbox"
                checked={useQuestBreakdown}
                onChange={(e) => setUseQuestBreakdown(e.target.checked)}
            />

            Enable Checklist
        </label>
    </div>

    {useQuestBreakdown && (
        <div className="mt-5 space-y-3">
            {questBreakdownSteps.map((step, index) => (
                <div
                    key={step.id}
                    className="rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4 shadow-[0_0_18px_rgba(255,209,102,0.08)]"
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                            <label className="text-sm text-[#b0b8d6]">
                                Step Title
                            </label>

                            <input
                                value={step.title}
                                onChange={(e) => {
                                    const updated = [...questBreakdownSteps];
                                    updated[index].title = e.target.value;
                                    setQuestBreakdownSteps(updated);
                                }}
                                placeholder={`Step ${index + 1}`}
                                className={inputClass}
                            />
                        </div>

                        <div>
                            <label className="text-sm text-[#b0b8d6]">
                                Step Due Date
                            </label>

                            <input
                                type="date"
                                value={step.due_date}
                                onChange={(e) => {
                                    const updated = [...questBreakdownSteps];
                                    updated[index].due_date = e.target.value;
                                    setQuestBreakdownSteps(updated);
                                }}
                                className={inputClass}
                            />
                        </div>

                        <div className="flex items-end">

                            <button
                                type="button"
                                onClick={() =>
                                    setQuestBreakdownSteps((prev) =>
                                        prev.length > 1
                                            ? prev.filter((_, i) => i !== index)
                                            : [
                                                  {
                                                      id: Date.now().toString(),
                                                      title: "",
                                                      notes: "",
                                                      due_date: "",
                                                      completed: false,
                                                  },
                                              ]
                                    )
                                }
                                className="
                                    rounded-2xl
                                    border border-[#ffd166]/40
                                    bg-black
                                    px-3 py-2
                                    text-xs text-[#ffd166]
                                    shadow-[0_0_14px_rgba(255,209,102,0.10)]
                                    hover:bg-[#ffd166]/[0.06]
                                    hover:shadow-[0_0_20px_rgba(255,209,102,0.20)]
                                    transition-all
                                    duration-200
                                "
                            >
                                Remove Step
                            </button>

                        </div>
                    </div>

                    <div className="mt-3">
                        <label className="text-sm text-[#b0b8d6]">
                            Step Notes
                        </label>

                        <textarea
                            value={step.notes}
                            onChange={(e) => {
                                const updated = [...questBreakdownSteps];
                                updated[index].notes = e.target.value;
                                setQuestBreakdownSteps(updated);
                            }}
                            placeholder="Optional instructions for this step..."
                            className={`${textareaClass} min-h-[70px]`}
                        />
                    </div>
                </div>
            ))}

            <button
                type="button"
                onClick={() =>
                    setQuestBreakdownSteps((prev) => [
                        ...prev,
                        {
                            id: Date.now().toString(),
                            title: "",
                            notes: "",
                            due_date: "",
                            completed: false,
                        },
                    ])
                }
                className="
                    rounded-2xl
                    border border-[#ffd166]/40
                    bg-black
                    px-3 py-2
                    text-xs text-[#ffd166]
                    shadow-[0_0_14px_rgba(255,209,102,0.10)]
                    hover:bg-[#ffd166]/[0.06]
                    hover:shadow-[0_0_20px_rgba(255,209,102,0.20)]
                    transition-all
                    duration-200
                "
            >
                Add Breakdown Step
            </button>
        </div>
    )}
</div>
                    <div className="mt-4">
                        <label className="text-sm text-[#b0b8d6]">
                            End of Day Notes
                        </label>


                        <textarea
                            value={lessonEndOfDayNotes}
                            onChange={(e) =>
                                setLessonEndOfDayNotes(e.target.value)
                            }
                            placeholder="What happened? What needs review tomorrow?"
                            className={`${textareaClass} min-h-[80px]`}
                        />
                    </div>


                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                            <label className="text-sm text-[#b0b8d6]">
                                Link Title
                            </label>


                            <input
                                value={lessonLinkTitle}
                                onChange={(e) =>
                                    setLessonLinkTitle(e.target.value)
                                }
                                placeholder="Khan video, worksheet link..."
                                className={inputClass}
                            />
                        </div>


                        <div>
                            <label className="text-sm text-[#b0b8d6]">
                                Link URL
                            </label>


                            <input
                                value={lessonLinkUrl}
                                onChange={(e) =>
                                    setLessonLinkUrl(e.target.value)
                                }
                                placeholder="https://..."
                                className={inputClass}
                            />
                        </div>


                        <div>
                            <label className="text-sm text-[#b0b8d6]">
                                File Upload
                            </label>


                            <input
                                type="file"
                                onChange={(e) =>
                                    setLessonFile(e.target.files?.[0] || null)
                                }
                                className="mt-1 w-full text-sm text-[#b0b8d6] file:mr-3 file:rounded-full file:border-0 file:bg-[#7c3aed] file:text-white file:px-3 file:py-1.5"
                            />
                        </div>
                    </div>


                    <div className="mt-5">
                        {(itemType === "quiz" ||
    itemType === "test") && (
    <div className="mt-6 rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-5">


        <h4 className="font-display text-2xl text-[#ffd166] mb-4">
            Questions
        </h4>


        <div className="space-y-5">


            {quizQuestions.map(
                (questionItem, questionIndex) => (
                    <div
                        key={questionItem.id}
                        className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/70 p-4"
                    >


                        <div className="flex items-center justify-between mb-3">


                            <h5 className="font-display text-lg text-[#ffd166]">
                                Question {questionIndex + 1}
                            </h5>


                            <button
                                type="button"
                                onClick={() => {
                                    setQuizQuestions((prev) =>
                                        prev.filter(
                                            (_, i) =>
                                                i !== questionIndex
                                        )
                                    );
                                }}
                                className={purpleActionBtn}
                            >
                                Delete
                            </button>


                        </div>


                        <textarea
                            value={questionItem.question}
                            onChange={(e) => {
                                const updated =
                                    [...quizQuestions];


                                updated[
                                    questionIndex
                                ].question =
                                    e.target.value;


                                setQuizQuestions(updated);
                            }}
                            placeholder="Question..."
                            className={`${textareaClass} min-h-[80px]`}
                        />


                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">


                            {questionItem.choices.map(
                                (
                                    choice,
                                    choiceIndex
                                ) => (
                                    <div
                                        key={choiceIndex}
                                        className="rounded-xl border border-[#1f2942] p-3 bg-[#080b14]"
                                    >


                                        <div className="flex items-center gap-2 mb-2">


                                            <input
                                                type="radio"
                                                checked={
                                                    questionItem.correctAnswer ===
                                                    choiceIndex
                                                }
                                                onChange={() => {
                                                    const updated =
                                                        [...quizQuestions];


                                                    updated[
                                                        questionIndex
                                                    ].correctAnswer =
                                                        choiceIndex;


                                                    setQuizQuestions(
                                                        updated
                                                    );
                                                }}
                                            />


                                            <span className="text-xs text-[#ffd166]">
                                                Correct Answer
                                            </span>


                                        </div>


                                        <input
                                            value={choice}
                                            onChange={(e) => {
                                                const updated =
                                                    [...quizQuestions];


                                                updated[
                                                    questionIndex
                                                ].choices[
                                                    choiceIndex
                                                ] =
                                                    e.target.value;


                                                setQuizQuestions(
                                                    updated
                                                );
                                            }}
                                            placeholder={`Choice ${
                                                choiceIndex + 1
                                            }`}
                                            className={inputClass}
                                        />


                                    </div>
                                )
                            )}


                        </div>


                    </div>
                )
            )}


        </div>


        <button
            type="button"
            onClick={() => {
                setQuizQuestions((prev) => [
                    ...prev,
                    {
                        id: Date.now().toString(),
                        question: "",
                        choices: [
                            "",
                            "",
                            "",
                            "",
                        ],
                        correctAnswer: 0,
                    },
                ]);
            }}
            className={`${purpleActionBtn} mt-5`}
        >
            Add Question
        </button>


    </div>
)}

                        <button
                            type="button"
                            onClick={saveLessonEvent}
                            disabled={assignmentAttachmentBusy || curriculumUploading}
                            className={purpleActionBtn}
                        >
                            {plannerMode === "curriculum" ? "Save generated assignments" : editingLessonId
                                ? "Save Lesson Edit"
                                : "Create Lesson"}
                        </button>
                    </div>
                               </section>
);
if (generatorOnly) return <div className="text-white">{questCreatedPopup && <p role="status" className="mb-3 rounded-xl border border-green-300/30 p-3 text-sm text-green-200">Assignment saved to this classroom. Dashboard cards and calendars refresh automatically.</p>}{creatorPanel}</div>;
        return (
    <div className="
            min-h-screen
            text-white
            bg-[#05070d]
            bg-[radial-gradient(circle_at_top,rgba(124,58,237,0.10),transparent_35%)]
        "
    >

        {pendingMoveModal && (
            <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 px-4">
                <div className="w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-[#05070d] p-6 shadow-[0_0_40px_rgba(255,209,102,0.22)]">
                    <h3 className="font-display text-3xl text-[#ffd166] mb-4">
                        Move Session
                    </h3>

                    <p className="text-sm text-[#d8dee9]">
                        Do you want to move only this section, or the entire session flow?
                    </p>

                    <div className="mt-6 flex flex-col gap-3">
                    <button
    type="button"
    onClick={() => {
        const updatedEvents = events.map((event) =>
            event.id === pendingMoveModal.item.id
                ? {
                      ...event,
                      date: pendingMoveModal.targetDate,
                      date_start: pendingMoveModal.targetDate,
                      date_end: pendingMoveModal.targetDate,
                      due_date: pendingMoveModal.targetDate,
                      start_time: pendingMoveModal.targetStartTime,
                      end_time: pendingMoveModal.targetEndTime,
                      scheduled_start_time: pendingMoveModal.targetStartTime,
                      scheduled_end_time: pendingMoveModal.targetEndTime,
                      updated_at: new Date().toISOString(),
                  }
                : event
        );

    rememberWeekScroll();

saveShadowGroveEvents(updatedEvents);
setEvents(updatedEvents);

restoreWeekScroll();

setDraggedQuestId(null);
setPendingMoveModal(null);

toast.success("Moved section.");
    }}
    className={purpleActionBtn}
>
    Move Only This Section
</button>

                        <button
                            type="button"
                            onClick={() =>
                        moveSessionFlowToDate(
                            pendingMoveModal.item,
                            pendingMoveModal.targetDate,
                            pendingMoveModal.targetStartTime
                                )
                            }
                            className={purpleActionBtn}
                        >
                            Move Entire Session Flow
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setPendingMoveModal(null);
                                setDraggedQuestId(null);
                            }}
                            className={purpleActionBtn}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
       )}

{pendingQuestMoveModal && (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 px-4">
        <div className="w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-[#05070d] p-6 shadow-[0_0_40px_rgba(255,209,102,0.22)]">
            <h3 className="font-display text-3xl text-[#ffd166] mb-4">
                Move Recurring Quest
            </h3>

            <p className="text-sm text-[#d8dee9]">
                Do you want to move only this quest, or the entire recurring quest group?
            </p>

            <div className="mt-6 flex flex-col gap-3">
                <button
                    type="button"
                   onClick={() => {
    const target = pendingQuestMoveModal.item;

    const updatedEvents = events.map((event) =>
        event.id === target.id
            ? {
                  ...event,
                  ...target,
                  updated_at: new Date().toISOString(),
              }
            : event
    );

    rememberWeekScroll();

saveShadowGroveEvents(updatedEvents);
setEvents(updatedEvents);

restoreWeekScroll();

setDraggedQuestId(null);
setPendingQuestMoveModal(null);

toast.success("Quest moved.");
}}
                    className={purpleActionBtn}
                >
                    Move Only This Quest
                </button>

                <button
                    type="button"
                    onClick={() => {
    const target = pendingQuestMoveModal.item;

    if (!target.batch_id) {
        toast.error("This quest is not connected to a recurring group.");
        return;
    }

    const originalDate =
    pendingQuestMoveModal.originalItem?.date_start ||
    pendingQuestMoveModal.originalItem?.date ||
    pendingQuestMoveModal.originalItem?.due_date;
    const newDate = pendingQuestMoveModal.targetDate;

    const dayDifference =
        Math.round(
            (parseLocalDate(newDate) - parseLocalDate(originalDate)) /
                (1000 * 60 * 60 * 24)
        );

    const updatedEvents = events.map((event) => {
        if (event.batch_id !== target.batch_id) return event;

        const eventDate = event.date_start || event.date || event.due_date;

        if (!eventDate) return event;

        const shiftedDate = parseLocalDate(eventDate);
        shiftedDate.setDate(shiftedDate.getDate() + dayDifference);

        const finalDate = formatLocalDate(shiftedDate);

        return {
    ...event,
    date: finalDate,
    date_start: finalDate,
    date_end: finalDate,
    due_date: finalDate,
    ...(isDueOnlyItem(target)
        ? {
              due_time: pendingQuestMoveModal.targetStartTime,
              start_time: "",
              end_time: "",
              scheduled_start_time: "",
              scheduled_end_time: "",
              all_day: true,
          }
        : {
              start_time: pendingQuestMoveModal.targetStartTime,
              end_time: pendingQuestMoveModal.targetEndTime,
              scheduled_start_time: pendingQuestMoveModal.targetStartTime,
              scheduled_end_time: pendingQuestMoveModal.targetEndTime,
              all_day: false,
          }),
    updated_at: new Date().toISOString(),
};
    });

   rememberWeekScroll();

saveShadowGroveEvents(updatedEvents);
setEvents(updatedEvents);

restoreWeekScroll();

setDraggedQuestId(null);
setPendingQuestMoveModal(null);
setPendingMoveModal(null);

toast.success("Recurring quest group moved.");
}}
                    className={purpleActionBtn}
                >
                    Move All Future Quest Occurrences
                </button>

                <button
                    type="button"
                    onClick={() => {
                        setPendingQuestMoveModal(null);
                        setDraggedQuestId(null);
                    }}
                    className={purpleActionBtn}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}

{pendingQuestEditModal && (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 px-4">
        <div className="w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-[#05070d] p-6 shadow-[0_0_40px_rgba(255,209,102,0.22)]">
            <h3 className="font-display text-3xl text-[#ffd166] mb-4">
                Update Recurring Quest
            </h3>

            <p className="text-sm text-[#d8dee9]">
                Do you want to move only this quest, or all future quest occurrences?
            </p>

            <div className="mt-6 flex flex-col gap-3">
                <button
                    type="button"
                    onClick={() => {
                        const target = pendingQuestEditModal.item;

                        const updatedEvents = events.map((event) =>
    event.id === target.id
        ? {
              ...event,
              ...target,
              date: target.date_start || target.date || target.due_date,
              date_start: target.date_start || target.date || target.due_date,
              date_end: target.date_end || target.date_start || target.date || target.due_date,
              due_date: target.due_date || target.date_start || target.date,
              start_time: target.all_day ? "" : target.start_time,
              end_time: target.all_day ? "" : target.end_time,
              scheduled_start_time: target.all_day ? "" : target.scheduled_start_time,
              scheduled_end_time: target.all_day ? "" : target.scheduled_end_time,
              due_time: target.due_time || "",
              updated_at: new Date().toISOString(),
          }
        : event
);
            saveShadowGroveEvents(updatedEvents);
            setEvents(updatedEvents);

                        setPendingQuestEditModal(null);
                        setEditingQuestModal(null);

                        toast.success("Quest updated.");
                    }}
                    className={purpleActionBtn}
                >
                    Update Only This Quest
                </button>

                <button
                    type="button"
                    onClick={() => {
                        const target = pendingQuestEditModal.item;

                        const targetDate =
                            target.date_start ||
                            target.date ||
                            target.due_date;

                        const updatedEvents = events.map((event) => {
                            if (event.batch_id !== target.batch_id) {
                                return event;
                            }

                            const eventDate =
                                event.date_start ||
                                event.date ||
                                event.due_date;

                            if (
                                eventDate &&
                                targetDate &&
                                parseLocalDate(eventDate) < parseLocalDate(targetDate)
                            ) {
                                return event;
                            }

                            return {
                                ...event,
                               child_id: childId,
                                subject_id: subjectId,
                                subject_name:
                                    subject?.name || "Class",
                                title: target.title,
                                description: target.description,
                                materials: target.materials,
                                due_time: target.due_time,
                                start_time: target.start_time,
                                end_time: target.end_time,
                                scheduled_start_time: target.scheduled_start_time,
                                scheduled_end_time: target.scheduled_end_time,
                                all_day: target.all_day,
                                updated_at: new Date().toISOString(),
                            };
                        });

                        saveShadowGroveEvents(updatedEvents);
                        setEvents(updatedEvents);

                        setPendingQuestEditModal(null);
                        setEditingQuestModal(null);

                        toast.success("Future quest occurrences updated.");
                    }}
                    className={purpleActionBtn}
                >
                    Update All Future Quest Occurrences
                </button>

                <button
                    type="button"
                    onClick={() => setPendingQuestEditModal(null)}
                    className={purpleActionBtn}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}

{pendingQuestDeleteModal && <DeleteAssignmentDialog event={pendingQuestDeleteModal} onClose={() => setPendingQuestDeleteModal(null)} onDelete={async (scope) => {
    const updated = await deleteSharedAssignment(pendingQuestDeleteModal, scope);
    setEvents(updated); setSelectedPlannerEvent(null); setEditingQuestModal(null);
    toast.success(scope === "series" ? "All assignments in this loop deleted." : "Assignment deleted.");
    return true;
}} />}

{shiftScheduleOpen && (
    <div className="fixed inset-0 z-[99999] flex items-start justify-center overflow-y-auto bg-black/90 px-4 py-6">
        <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-[32px] border border-[#ffd166]/50 bg-[#05070d] shadow-[0_0_45px_rgba(255,209,102,0.22)]">
            <div className="border-b border-[#ffd166]/20 p-5">
                <h3 className="font-display text-3xl text-[#ffd166] drop-shadow-[0_0_12px_rgba(255,209,102,0.4)]">
                    Shift Schedule
                </h3>
                <p className="mt-2 text-sm text-[#d8dee9]">
                    Move future quests and sessions forward or backward for sickness, emergencies, or absences.
                </p>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-5 [scrollbar-width:thin] [scrollbar-color:#ffd166_#05070d]">
                <div className="grid gap-4">
                    <div className="rounded-3xl border border-[#ffd166]/20 bg-black/60 p-4">
                        <label className="text-sm font-semibold text-[#ffd166]">
                            Start shifting from
                        </label>
                        <input
                            type="date"
                            value={shiftScheduleForm.startDate}
                            onChange={(event) =>
                                setShiftScheduleForm((prev) => ({
                                    ...prev,
                                    startDate: event.target.value,
                                }))
                            }
                            className={inputClass}
                        />
                    </div>

                    <div className="rounded-3xl border border-[#ffd166]/20 bg-black/60 p-4">
                        <label className="text-sm font-semibold text-[#ffd166]">
                            Shift by days
                        </label>

                        <input
                            type="number"
                            min="1"
                            value={shiftScheduleForm.dayOffset}
                            onChange={(event) =>
                                setShiftScheduleForm((prev) => ({
                                    ...prev,
                                    dayOffset: event.target.value,
                                }))
                            }
                            className={inputClass}
                        />
                    </div>

                    <div className="rounded-3xl border border-[#ffd166]/30 bg-black p-4 text-sm text-[#fff3c4]">
                        Preview: <span className="font-bold text-[#ffd166]">{getShiftPreviewCount()}</span> item(s) will shift.
                    </div>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-[#ffd166]/20 p-5">
                <button type="button" onClick={applyShiftSchedule} className={purpleActionBtn}>
                    Apply Shift
                </button>

                <button type="button" onClick={() => setShiftScheduleOpen(false)} className={purpleActionBtn}>
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}


<header className="relative z-10 border-b border-[#ffd166]/20 bg-black/60 backdrop-blur-sm">
            <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className={purpleActionBtn}
                >
                    <ArrowLeft size={16} />
                    Back
                </button>



                    <h1 className="font-display text-2xl flex items-center gap-2 text-[#ffd166]">
                    <BookOpen size={22} />
                    {subject.name}
                    </h1>



                </div>
            </header>


            <main
                className="
                    relative z-10
                    w-full
                    max-w-none
                    px-4
                    md:px-6
                    xl:px-8
                    py-8
                    space-y-6
                "
            >

                            {activePanel !== "lessonStage" && (
<section
    className="
        relative overflow-hidden
        rounded-[32px]
        border border-[#ffd166]/40
        bg-black
        p-6
        shadow-[0_0_35px_rgba(255,209,102,0.10)]
        backdrop-blur-sm
    "
    style={{

    backgroundColor: "#080b14",
}}
                >



                    <div className="w-full">
    <div
        className="w-full rounded-3xl border px-8 py-6 text-center transition-all duration-300"
        style={{
            color: getActiveSectionColor(),
            borderColor: getActiveSectionColor(),
            boxShadow: `0 0 28px ${getActiveSectionColor()}66`,
            backgroundColor: "#00000088",
        }}
    >
        <div className="text-xs uppercase tracking-[0.25em] text-[#b0b8d6]">
    {timerRunning
        ? "Class In Session"
        : "Class Not In Session"}
</div>

        <div className="font-display text-6xl mt-3">
            {formatTimer(timerSeconds)}
        </div>
            <div
    className="font-display text-2xl mt-3 tracking-wide"
    style={{
        color: getActiveSectionColor(),
        textShadow: `0 0 14px ${getActiveSectionColor()}88`,
    }}
>
    {getActiveSection()?.title || "No Active Section"}
</div>

{activeSectionId && (
    <div
        className="text-sm mt-2 flex flex-wrap items-center justify-center gap-2"
        style={{
            color: getActiveSectionColor(),
        }}
    >
        <span>
            Remaining: {formatTimer(getActiveSectionRemainingSeconds())}
        </span>

        {getNextSection() && (
            <span className="rounded-full border border-[#ffd166]/40 bg-black px-3 py-1 text-xs text-[#ffd166] shadow-[0_0_12px_rgba(255,209,102,0.16)]">
                Next: {getNextSection().title || "Untitled Section"}
            </span>
        )}

        <button
            type="button"
            onClick={() => setShowLessonFlowModal(true)}
            className="rounded-full border border-[#ffd166]/40 bg-black px-3 py-1 text-xs text-[#ffd166] shadow-[0_0_12px_rgba(255,209,102,0.16)] hover:bg-[#ffd166]/[0.06] transition-all duration-200"
            title="View Full Lesson Flow"
        >
            ⋯
        </button>
    </div>
)}
{getActiveSection() && (
    <div className="mt-5 w-full max-w-xl mx-auto">
        <div className="h-5 rounded-full border border-[#ffd166]/50 bg-black shadow-[0_0_14px_rgba(255,209,102,0.18)] overflow-hidden">
            <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                    width: `${Math.min(
                        100,
                        Math.round(
                            ((Number(getActiveSection().actualSeconds) || 0) /
                                ((Number(getActiveSection().plannedMinutes) || 1) * 60)) *
                                100
                        )
                    )}%`,
                    backgroundColor: getActiveSectionColor(),
                    boxShadow: `0 0 18px ${getActiveSectionColor()}99`,
                }}
            />
        </div>

     <div className="mt-3 w-full overflow-hidden py-2">
    <div
        className="whitespace-nowrap animate-pulse"
        style={{
            color: getActiveSectionColor(),
            transform: `translateX(-${(timerSeconds * 2) % 300}%)`,
            transition: "transform 1s linear",
        }}
    >
        {Array(10)
    .fill(
        getActiveSection()?.description ||
            "No session description yet."
    )
    .join("   •   ")}
     </div>
</div>
</div>
)}
{classTimerSections.length > 0 && (
    <div className="mt-5 flex flex-col items-center">
        <div className="flex items-center justify-center gap-4">

            <button
                type="button"
                onClick={() => adjustClassTimerSeconds(-10)}
                className="text-[#ffd166] text-2xl leading-none hover:scale-110 transition-transform duration-200"
                title="Back 10 Seconds"
            >
                ↺
            </button>
        <div className="relative h-24 w-24 rounded-full border border-[#ffd166]/60 bg-black overflow-hidden shadow-[0_0_22px_rgba(255,209,102,0.25)]">
            <img
    src="/images/moon.gif"
    alt="Moon progress background"
    className="absolute inset-0 h-full w-full rounded-full object-cover"
/>

            <div
                className="absolute inset-0 rounded-full"
               style={{
    maskImage: `conic-gradient(
        from 0deg,
        black 0deg,
        black ${getOverallClassProgress() * 3.6}deg,
        transparent ${getOverallClassProgress() * 3.6}deg,
        transparent 360deg
    )`,
    WebkitMaskImage: `conic-gradient(
        from 0deg,
        black 0deg,
        black ${getOverallClassProgress() * 3.6}deg,
        transparent ${getOverallClassProgress() * 3.6}deg,
        transparent 360deg
    )`,
}}
            >
             <img
    src="/images/sun.gif"
    alt="Sun progress reveal"
    className="absolute inset-0 h-full w-full rounded-full object-cover scale-125"
/>
            </div>

            <div className="absolute inset-0 rounded-full border border-[#ffd166]/40 shadow-[inset_0_0_18px_rgba(255,209,102,0.22)]" />
        </div>
                    <button
                type="button"
                onClick={() => adjustClassTimerSeconds(10)}
                className="text-[#ffd166] text-2xl leading-none hover:scale-110 transition-transform duration-200"
                title="Forward 10 Seconds"
            >
                ↻
            </button>

        </div>
        <p className="mt-2 text-[10px] uppercase tracking-[0.18em] text-[#ffd166]">
            Today's Journey
        </p>

        <p className="text-[10px] text-[#b0b8d6] mt-1">
            {getOverallClassProgress()}% complete
        </p>
    </div>
)}

<div className="mt-3 text-center">
    <p className="text-xs uppercase tracking-[0.18em] text-[#ffd166]">
        Today&apos;s Schedule
    </p>

    <p className="mt-1 text-sm text-[#d8b4fe]">
        {sessionLessonName?.trim() || "No session flow scheduled for today"}
    </p>

</div>
<div className="flex justify-center gap-2 mt-5">
    <button
        type="button"
        onClick={startClassSession}
        className={purpleActionBtn}
    >
        Start Session
    </button>

    <button
        type="button"
        onClick={handleTimerStartPause}
        className={purpleActionBtn}
    >
        {timerRunning ? "Pause" : "Start"}
    </button>

    <button
        type="button"
        onClick={handleTimerReset}
        className={purpleActionBtn}
    >
        Reset
    </button>

    <button
        type="button"
        onClick={startNextClassTimerSection}
        className={purpleActionBtn}
        disabled={!activeSectionId}
    >
        Next Section
    </button>

    <button
        type="button"
        onClick={async () => {
            const currentSeconds = timerRunning
                ? calculateTimerSeconds()
                : timerSeconds;

            const completedSections =
                classTimerSections.map(
                    (section) => ({
                        ...section,
                        running: false,
                        completed: true,
                    })
                );

            const sharedCompletedSteps =
                completedSections.map(
                    (section) => ({
                        step_id: String(section.id),
                        title:
                            section.title ||
                            "Untitled Section",
                        step_type:
                            section.type ||
                            "lesson",
                        description:
                            section.description ||
                            "",
                        materials:
                            Array.isArray(
                                section.materials
                            )
                                ? section.materials
                                : [],
                        planned_minutes:
                            Math.max(
                                0,
                                Number(
                                    section.plannedMinutes
                                ) || 0
                            ),
                        actual_seconds:
                            Math.max(
                                0,
                                Number(
                                    section.actualSeconds
                                ) || 0
                            ),
                        running: false,
                        completed: true,
                        skipped: Boolean(
                            section.skipped
                        ),
                        quest_id:
                            section.questId ||
                            null,
                    })
                );

            setTimerRunning(false);
            setTimerSeconds(currentSeconds);
            setClassTimerSections(
                completedSections
            );

            localStorage.setItem(
                classTimerKey,
                JSON.stringify({
                    running: false,
                    base_seconds:
                        currentSeconds,
                    started_at: null,
                    child_id: childId,
                    subject_id: subjectId,
                    subject_name:
                        subject?.name ||
                        "Class",
                    updated_at:
                        new Date().toISOString(),
                })

            );

            try {
                await saveSharedSessionSnapshot({
                    status: "completed",
                    base_seconds:
                        currentSeconds,
                    started_at: null,
                    active_step_id: null,
                    active_step_started_at:
                        null,
                    steps:
                        sharedCompletedSteps,
                });
            } catch (error) {
                console.error(
                    "Unable to synchronize completed class session:",
                    error
                );

                toast.error(
                    "The class ended here, but the child classroom did not synchronize."
                );
            }

            setCompletedSessionSummary({
                totalSeconds:
                    currentSeconds,
                sections:
                    completedSections,
                endedAt:
                    new Date().toISOString(),
            });

            setShowSessionCompleteModal(
                true
            );
        }}
        className={purpleActionBtn}
    >
        End Class
    </button>
</div>

<div className="mt-5 rounded-[24px] border border-[#ffd166]/30 bg-black/70 p-4 shadow-[0_0_22px_rgba(255,209,102,0.10)]">
    <div className="text-center">
        <h3 className="font-display text-xl text-[#ffd166]">
            Child Session Permissions
        </h3>

        <p className="mt-1 text-xs text-[#b0b8d6]">
            Choose which controls the child may use while working independently.
        </p>
    </div>

    <div className="mt-3 flex flex-wrap justify-center gap-2">
        {[
            {
                key: "child_can_start",
                label: "Start Session",
            },
            {
                key: "child_can_pause",
                label: "Pause / Resume",
            },
            {
                key: "child_can_skip",
                label: "Next Section",
            },
            {
                key: "child_can_reorder",
                label: "Reorder Sections",
            },
            {
                key: "child_can_end",
                label: "End Session",
            },
        ].map((permission) => {
            const allowed = Boolean(
                childSessionPermissions[
                    permission.key
                ]
            );

            return (
                <button
                    key={permission.key}
                    type="button"
                    onClick={() =>
                        updateChildSessionPermission(
                            permission.key,
                            !allowed
                        )
                    }
                    className={`
                        rounded-xl
                        border
                        px-3
                        py-1.5
                        text-[11px]
                        font-semibold
                        leading-tight
                        transition-all
                        duration-200
                        ${
                             allowed
                                ? "border-[#ffd166] bg-[#ffd166]/20 text-[#ffd166] ring-1 ring-[#ffd166]/40 shadow-[0_0_20px_rgba(255,209,102,0.70),inset_0_0_10px_rgba(255,209,102,0.12)]"
                                : "border-[#7c3aed]/35 bg-[#2b1548]/40 text-[#d8b4fe] hover:border-[#ffd166]/50 hover:text-[#ffd166]"
                        }
                    `}
                >
                    <span className="block">
                        {permission.label}
                    </span>

                    <span className="mt-0.5 block text-[8px] uppercase tracking-[0.14em]">
                        {allowed
                            ? "Allowed"
                            : "Parent Only"}
                    </span>
                </button>
            );
        })}
    </div>
</div>

<div>
</div>
                                              </div>
                    </div>

</section>
)}
<div
    className="
        relative z-[70]
        mb-8
        w-full
        rounded-[32px]
        border border-[#ffd166]/30
        bg-black
        px-6 py-5
        shadow-[0_0_28px_rgba(255,209,102,0.06)]
    "
>
        <div
            className="
                flex
                w-full
                flex-row
                items-center
                justify-between
                gap-6
            "
        >
        <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.22em] text-[#d8b4fe]">
                Classroom Navigation
            </p>

            <h3 className="mt-1 font-display text-2xl text-[#ffd166]">
                {subject?.name || "Classroom"}
            </h3>
        </div>

        <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={classroomTabClass(activePanel === "bulletin")} onClick={() => selectClassroomPanel("bulletin")}>Bulletin Board</button>
    <button
        type="button"
        onClick={() => selectClassroomPanel("lessonStage")}
        className={compactClassroomButtonClass(
            activePanel === "lessonStage"
        )}
    >
        <BookOpen size={16} />
        Lesson Stage
    </button>
        <button
            type="button"
            onClick={() => selectClassroomPanel("today")}
            className={classroomTabClass(
                activePanel === "today"
            )}
        >
            <Clock size={16} />
            Today
        </button>
        <div className="relative">
            <button
                type="button"
                onClick={() => toggleClassroomMenu("quests")}
                className={classroomTabClass(
                    ["creator", "openQuests", "completed"].includes(
                        activePanel
                    )
                )}
                aria-expanded={openClassroomMenu === "quests"}
            >
                <BookOpen size={16} />
                Quests
                <span className="text-xs">
                    {openClassroomMenu === "quests" ? "▴" : "▾"}
                </span>
            </button>

            {openClassroomMenu === "quests" && (
                <div
                    className="
                        absolute left-0 top-full z-[90]
                        mt-2 w-64
                        max-h-[70vh] overflow-y-auto
                        rounded-2xl
                        border border-[#ffd166]/35
                        bg-black
                        p-2
                        shadow-[0_0_30px_rgba(255,209,102,0.18)]
                    "
                >
                    <p className="px-3 pb-2 pt-1 text-[10px] uppercase tracking-[0.22em] text-[#ffd166]/70">
                        Quest Tools
                    </p>

                    <button
                        type="button"
                        onClick={() => selectClassroomPanel("creator")}
                        className={compactDropdownItemClass(
                            activePanel === "creator"
                        )}
                    >
                        <FileText size={15} />
                        Quest Creator
                    </button>

                    <button
                        type="button"
                        onClick={() => selectClassroomPanel("openQuests")}
                        className={compactDropdownItemClass(
                            activePanel === "openQuests"
                        )}
                    >
                        <BookOpen size={15} />
                        Open Quests
                    </button>

                    <button
                        type="button"
                        onClick={() => selectClassroomPanel("completed")}
                        className={compactDropdownItemClass(
                            activePanel === "completed"
                        )}
                    >
                        <BookOpen size={15} />
                        Completed Quests
                    </button>

                    <div className="my-2 border-t border-[#ffd166]/15" />

                    <p className="px-3 py-2 text-xs leading-5 text-[#8f98b5]">
                        Day, week, month, all-quest, and archived filters
                        will be connected after the navigation layout is
                        confirmed.
                    </p>
                </div>
            )}
        </div>

        <div className="relative">
            <button
                type="button"
                onClick={() => toggleClassroomMenu("calendar")}
                className={classroomTabClass(
                    ["calendar", "schedule", "classTimer"].includes(
                        activePanel
                    )
                )}
                aria-expanded={openClassroomMenu === "calendar"}
            >
                <CalendarDays size={16} />
                Quest Calendar
                <span className="text-xs">
                    {openClassroomMenu === "calendar" ? "▴" : "▾"}
                </span>
            </button>

            {openClassroomMenu === "calendar" && (
                <div
                    className="
                        absolute left-0 top-full z-[90]
                        mt-2 w-64
                        max-h-[70vh] overflow-y-auto
                        rounded-2xl
                        border border-[#ffd166]/35
                        bg-black
                        p-2
                        shadow-[0_0_30px_rgba(255,209,102,0.18)]
                    "
                >
                    <p className="px-3 pb-2 pt-1 text-[10px] uppercase tracking-[0.22em] text-[#ffd166]/70">
                        Calendar & Schedule
                    </p>

                    <button
                        type="button"
                        onClick={() => selectClassroomPanel("calendar")}
                        className={compactDropdownItemClass(
                            activePanel === "calendar"
                        )}
                    >
                        <CalendarDays size={15} />
                        Open Quest Calendar
                    </button>

                    <button
                        type="button"
                        onClick={() => selectClassroomPanel("schedule")}
                        className={compactDropdownItemClass(
                            activePanel === "schedule"
                        )}
                    >
                        <CalendarDays size={15} />
                        Class Schedule
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setOpenClassroomMenu(null);
                            setShiftScheduleOpen(true);
                        }}
                        className={compactDropdownItemClass(false)}
                    >
                        <CalendarDays size={15} />
                        Shift Schedule
                    </button>

                    <button
                        type="button"
                        onClick={() => selectClassroomPanel("lessonStage")}
                        className={compactDropdownItemClass(
                            activePanel === "classTimer"
                        )}
                    >
                        <Clock size={15} />
                        Class Timer
                    </button>
                </div>
            )}
        </div>

        <button
            type="button"
            onClick={() => selectClassroomPanel("pending")}
            className={classroomTabClass(
                activePanel === "pending"
            )}
        >
            <Clock size={16} />
            Pending Approval
        </button>

        <div className="relative">
            <button
                type="button"
                onClick={() => toggleClassroomMenu("gradingResources")}
                className={classroomTabClass(
                    ["grading", "fieldTrip", "resources"].includes(
                        activePanel
                    )
                )}
                aria-expanded={
                    openClassroomMenu === "gradingResources"
                }
            >
                <FileText size={16} />
                Grading & Resources
                <span className="text-xs">
                    {openClassroomMenu === "gradingResources"
                        ? "▴"
                        : "▾"}
                </span>
            </button>

            {openClassroomMenu === "gradingResources" && (
                <div
                    className="
                        absolute right-0 top-full z-[90]
                        mt-2 w-72
                        max-h-[70vh] overflow-y-auto
                        rounded-2xl
                        border border-[#ffd166]/35
                        bg-black
                        p-2
                        shadow-[0_0_30px_rgba(255,209,102,0.18)]
                    "
                >
                    <p className="px-3 pb-2 pt-1 text-[10px] uppercase tracking-[0.22em] text-[#ffd166]/70">
                        Grading & Resources
                    </p>

                    <button
                        type="button"
                        onClick={() => selectClassroomPanel("grading")}
                        className={compactDropdownItemClass(
                            activePanel === "grading"
                        )}
                    >
                        <FileText size={15} />
                        Grading & Reports
                    </button>

                    <button
                        type="button"
                        onClick={() => selectClassroomPanel("fieldTrip")}
                        className={compactDropdownItemClass(
                            activePanel === "fieldTrip"
                        )}
                    >
                        <FileText size={15} />
                        Field Trips & Weekly Reports
                    </button>

                    <button
                        type="button"
                        onClick={() => selectClassroomPanel("resources")}
                        className={compactDropdownItemClass(
                            activePanel === "resources"
                        )}
                    >
                        <BookOpen size={15} />
                        Resources & Books
                    </button>
                </div>
            )}
        </div>
        <button type="button" className={classroomTabClass(activePanel === "messages")} onClick={() => selectClassroomPanel("messages")}>Messages</button>
    </div>
</div>
</div>
<div
    className="grid items-start gap-0"
        style={
        isClassroomNotesOpen
            ? {
                gridTemplateColumns: `minmax(0, 1fr) 10px ${layout.workspaceWidth}px`,
            }
            : {
                gridTemplateColumns: "minmax(0, 1fr) auto",
            }
    }
>
    <div className="min-w-0">



{activePanel === "lessonStage" && <LessonStagePanel role="parent" childId={childId} subjectId={subjectId} child={child} />}
{activePanel === "messages" && child && <section className={shadowCalendarShell}><HomeroomMessages key={childId} role="parent" children={[child]} /></section>}
{activePanel === "today" && <section className={shadowCalendarShell}><h3 className="font-display text-3xl text-[#ffd166]">Today’s Classroom</h3><p className="mt-2 text-sm text-[#b0b8d6]">{subject?.name} · lesson workspace and class tools.</p><div className="mt-4 flex flex-wrap gap-3"><button type="button" className={purpleActionBtn} onClick={() => selectClassroomPanel("lessonStage")}>Lesson Stage</button><button type="button" className={purpleActionBtn} onClick={() => selectClassroomPanel("lessonStage")}>Class Timer</button></div></section>}
{activePanel === "bulletin" && (
    <section
        className="
            mb-6
            rounded-[32px]
            border border-[#ffd166]/40
            bg-black
            p-6
            shadow-[0_0_35px_rgba(255,209,102,0.10)]
        "
    >
        <div className="flex items-start justify-between gap-4">
            <div>
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#d8b4fe]">
                    Classroom Dashboard
                </p>

                <h3 className="mt-1 font-display text-3xl text-[#ffd166]">
                    Bulletin Board
                </h3>

                <p className="mt-1 text-sm text-[#b0b8d6]">
                    To do, due today, late work, completions, and pending approvals.
                </p>
            </div>

            <button
                type="button"
                onClick={() => setActivePanel(null)}
                className="
                    flex h-9 w-9 items-center justify-center
                    rounded-xl
                    border border-[#ffd166]/35
                    bg-black
                    text-lg text-[#ffd166]
                    transition
                    hover:border-[#c084fc]/70
                    hover:text-[#e9d5ff]
                    hover:shadow-[0_0_18px_rgba(192,132,252,0.35)]
                "
                aria-label="Close Bulletin Board"
            >
                ×
            </button>
        </div>
    <div className="rounded-2xl border border-[#c084fc]/30 bg-[#160d26]/45 p-4">
        <div className="flex items-center justify-between gap-3">
            <h4 className="font-display text-xl text-[#e9d5ff]">
                To Do
            </h4>

            {toDoTodayQuests.length > 0 && (
                <span className="rounded-full border border-[#c084fc]/40 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#e9d5ff]">
                    {toDoTodayQuests.length}
                </span>
            )}
        </div>

        {toDoTodayQuests.length === 0 ? (
            <p className="mt-2 text-sm text-[#b0b8d6]">
                No active quests for today.
            </p>
        ) : (
            <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                {toDoTodayQuests.map((quest) => (
                    <button
                        key={quest.id}
                        type="button"
                        onClick={() =>
                            setSelectedPlannerEvent(
                                quest
                            )
                        }
                        className="
                            block
                            w-full
                            rounded-xl
                            border
                            border-[#c084fc]/20
                            bg-black/40
                            p-3
                            text-left
                            transition
                            hover:border-[#c084fc]/60
                            hover:bg-[#2e1065]/25
                            hover:shadow-[0_0_18px_rgba(192,132,252,0.12)]
                        "
                    >
                        <p className="text-sm font-semibold text-[#fff3c4]">
                            {quest.title ||
                                "Untitled Quest"}
                        </p>

                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[#9da6c3]">
                            <span>
                                Starts:{" "}
                                {quest.date_start ||
                                    quest.date ||
                                    "No start date"}
                            </span>

                            {quest.due_date && (
                                <span className="text-[#ffd166]">
                                    Due: {quest.due_date}
                                    {quest.due_time
                                        ? ` at ${formatDisplayTime(
                                            quest.due_time
                                        )}`
                                        : ""}
                                </span>
                            )}
                        </div>

                        {quest.attached_to_session && (
                            <p className="mt-1 text-[10px] text-[#d8b4fe]">
                                Attached to session
                            </p>
                        )}
                    </button>
                ))}
            </div>
        )}
    </div>
    <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#ffd166]/25 bg-[#080b14]/80 p-4">
            <div className="flex items-center justify-between gap-3">
                <h4 className="font-display text-xl text-[#ffd166]">
                    Due Today
                </h4>

                {dueTodayQuests.length > 0 && (
                    <span className="rounded-full border border-[#ffd166]/35 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#ffd166]">
                        {dueTodayQuests.length}
                    </span>
                )}
            </div>

            {dueTodayQuests.length === 0 ? (
                <p className="mt-2 text-sm text-[#b0b8d6]">
                    No open quests are due today.
                </p>
            ) : (
                <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                    {dueTodayQuests.map((quest) => (
                        <button
                            key={
                                quest.id ??
                                quest.quest_id ??
                                `${quest.title}-${quest.due_date}`
                            }
                            type="button"
                            onClick={() =>
                                setSelectedPlannerEvent(
                                    quest
                                )
                            }
                            className="
                                block
                                w-full
                                rounded-xl
                                border
                                border-[#ffd166]/15
                                bg-black/40
                                p-3
                                text-left
                                transition
                                hover:border-[#ffd166]/45
                                hover:bg-[#ffd166]/5
                                hover:shadow-[0_0_18px_rgba(255,209,102,0.10)]
                            "
                        >
                            <p className="text-sm font-semibold text-[#fff3c4]">
                                {quest.title ||
                                    "Untitled Quest"}
                            </p>

                            {quest.linked_lesson_title && (
                                <p className="mt-1 text-xs text-[#d8b4fe]">
                                    Linked Lesson:{" "}
                                    {quest.linked_lesson_title}
                                </p>
                            )}

                            {quest.attached_to_session && (
                                <p className="mt-1 text-xs text-[#9da6c3]">
                                    Attached to session
                                </p>
                            )}

                            {quest.due_time && (
                                <p className="mt-1 text-xs text-[#ffd166]">
                                    Due:{" "}
                                    {formatDisplayTime(
                                        quest.due_time
                                    )}
                                </p>
                            )}
                        </button>
                    ))}
                </div>
            )}

            <button
                type="button"
                onClick={() =>
                    selectClassroomPanel("openQuests")
                }
                className={`${purpleActionBtn} mt-4`}
            >
                View Open Quests
            </button>
        </div>

        <div className="rounded-2xl border border-red-400/30 bg-red-950/20 p-4">
            <div className="flex items-center justify-between gap-3">
                <h4 className="font-display text-xl text-red-200">
                    Late
                </h4>

                {lateQuests.length > 0 && (
                    <span className="rounded-full border border-red-300/35 bg-black px-2.5 py-1 text-[10px] font-semibold text-red-200">
                        {lateQuests.length}
                    </span>
                )}
            </div>

            {lateQuests.length === 0 ? (
                <p className="mt-2 text-sm text-[#c4b5d8]">
                    No late work.
                </p>
            ) : (
                <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                    {lateQuests.map((quest) => (
                        <button
                            key={
                                quest.id ??
                                quest.quest_id ??
                                `${quest.title}-${getClassroomQuestDueDate(
                                    quest
                                )}`
                            }
                            type="button"
                            onClick={() =>
                                setSelectedPlannerEvent(
                                    quest
                                )
                            }
                            className="
                                block
                                w-full
                                rounded-xl
                                border
                                border-red-300/15
                                bg-black/40
                                p-3
                                text-left
                                transition
                                hover:border-red-300/45
                                hover:bg-red-400/5
                            "
                        >
                            <p className="text-sm font-semibold text-[#fff3c4]">
                                {quest.title ||
                                    "Untitled Quest"}
                            </p>

                            <p className="mt-1 text-xs text-red-200">
                                Due:{" "}
                                {getClassroomQuestDueDate(
                                    quest
                                )}
                            </p>
                        </button>
                    ))}
                </div>
            )}

            <button
                type="button"
                onClick={() =>
                    selectClassroomPanel("openQuests")
                }
                className={`${purpleActionBtn} mt-4`}
            >
                View Late Work
            </button>
        </div>
    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#ffd166]/25 bg-[#080b14]/80 p-4">
            <div className="flex items-center justify-between gap-3">
                <h4 className="font-display text-xl text-[#ffd166]">
                    Completed Today
                </h4>

                {completedTodayQuests.length > 0 && (
                    <span className="rounded-full border border-[#ffd166]/35 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#ffd166]">
                        {completedTodayQuests.length}
                    </span>
                )}
            </div>

            {completedTodayQuests.length === 0 ? (
                <p className="mt-2 text-sm text-[#b0b8d6]">
                    No quests completed today.
                </p>
            ) : (
                <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                    {completedTodayQuests.map((quest) => (
                        <button
                            key={
                                quest.id ??
                                quest.quest_id ??
                                `${quest.title}-${getClassroomQuestCompletedDate(
                                    quest
                                )}`
                            }
                            type="button"
                            onClick={() =>
                                setSelectedPlannerEvent(
                                    quest
                                )
                            }
                            className="
                                block
                                w-full
                                rounded-xl
                                border
                                border-[#ffd166]/15
                                bg-black/40
                                p-3
                                text-left
                                transition
                                hover:border-[#ffd166]/45
                                hover:bg-[#ffd166]/5
                                hover:shadow-[0_0_18px_rgba(255,209,102,0.10)]
                            "
                        >
                            <p className="text-sm font-semibold text-[#fff3c4]">
                                {quest.title ||
                                    "Untitled Quest"}
                            </p>

                            <p className="mt-1 text-xs text-[#b0b8d6]">
                                Completed:{" "}
                                {getClassroomQuestCompletedDate(
                                    quest
                                )}
                            </p>
                        </button>
                    ))}
                </div>
            )}

            <button
                type="button"
                onClick={() =>
                    selectClassroomPanel("completed")
                }
                className={`${purpleActionBtn} mt-4`}
            >
                View Completed Quests
            </button>
        </div>

        <div className="rounded-2xl border border-[#c084fc]/30 bg-[#160d26]/45 p-4">
            <div className="flex items-center justify-between gap-3">
                <h4 className="font-display text-xl text-[#e9d5ff]">
                    Pending Approval
                </h4>

                {pendingApprovalQuests.length > 0 && (
                    <span className="rounded-full border border-[#c084fc]/40 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#e9d5ff]">
                        {pendingApprovalQuests.length}
                    </span>
                )}
            </div>

            {pendingApprovalQuests.length === 0 ? (
                <p className="mt-2 text-sm text-[#c4b5d8]">
                    No work is waiting for approval.
                </p>
            ) : (
                <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                    {pendingApprovalQuests.map((quest) => (
                        <button
                            key={
                                quest.id ??
                                quest.quest_id ??
                                `${quest.title}-${quest.status}`
                            }
                            type="button"
                            onClick={() =>
                                setSelectedPlannerEvent(
                                    quest
                                )
                            }
                            className="
                                block
                                w-full
                                rounded-xl
                                border
                                border-[#c084fc]/20
                                bg-black/40
                                p-3
                                text-left
                                transition
                                hover:border-[#c084fc]/60
                                hover:bg-[#2e1065]/25
                                hover:shadow-[0_0_18px_rgba(192,132,252,0.12)]
                            "
                        >
                            <p className="text-sm font-semibold text-[#fff3c4]">
                                {quest.title ||
                                    "Untitled Quest"}
                            </p>

                            <p className="mt-1 text-xs text-[#d8b4fe]">
                                Status:{" "}
                                {quest.status ||
                                    "Pending"}
                            </p>
                        </button>
                    ))}
                </div>
            )}

            <button
                type="button"
                onClick={() =>
                    selectClassroomPanel("pending")
                }
                className={`${purpleActionBtn} mt-4`}
            >
                Review Pending Work
            </button>
        </div>
    </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-[#ffd166]/25 bg-[#080b14]/80 p-4">
                <div className="flex items-center justify-between gap-3">
                    <h4 className="font-display text-xl text-[#ffd166]">
                        Coming Up This Week
                    </h4>

                    {comingUpThisWeekQuests.length > 0 && (
                        <span className="rounded-full border border-[#ffd166]/35 bg-black px-2.5 py-1 text-[10px] font-semibold text-[#ffd166]">
                            {comingUpThisWeekQuests.length}
                        </span>
                    )}
                </div>

                {comingUpThisWeekQuests.length === 0 ? (
                    <p className="mt-2 text-sm text-[#b0b8d6]">
                        No upcoming quests this week.
                    </p>
                ) : (
                    <div className="mt-3 max-h-[240px] space-y-2 overflow-y-auto pr-1">
                        {comingUpThisWeekQuests.map((quest) => (
                            <button
                                key={
                                    quest.id ??
                                    quest.quest_id ??
                                    `${quest.title}-${getClassroomQuestDueDate(
                                        quest
                                    )}`
                                }
                                type="button"
                                onClick={() =>
                                    setSelectedPlannerEvent(
                                        quest
                                    )
                                }
                                className="
                                    block
                                    w-full
                                    rounded-xl
                                    border
                                    border-[#ffd166]/15
                                    bg-black/40
                                    p-3
                                    text-left
                                    transition
                                    hover:border-[#ffd166]/45
                                    hover:bg-[#ffd166]/5
                                    hover:shadow-[0_0_18px_rgba(255,209,102,0.10)]
                                "
                            >
                                <p className="text-sm font-semibold text-[#fff3c4]">
                                    {quest.title ||
                                        "Untitled Quest"}
                                </p>

                                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#b0b8d6]">
                                    <span>
                                        {String(
                                            quest.type ||
                                                quest.quest_type ||
                                                "Quest"
                                        )}
                                    </span>

                                    <span className="text-[#ffd166]">
                                        {getClassroomQuestDueDate(
                                            quest
                                        )}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                <button
                    type="button"
                    onClick={() =>
                        selectClassroomPanel("openQuests")
                    }
                    className={`${purpleActionBtn} mt-4`}
                >
                    View Open Quests
                </button>
            </div>

            <div className="rounded-2xl border border-[#c084fc]/30 bg-[#160d26]/45 p-4">
                <h4 className="font-display text-xl text-[#e9d5ff]">
                    Next Test / Quiz
                </h4>

                {!nextTestQuiz ? (
                    <p className="mt-2 text-sm text-[#c4b5d8]">
                        No upcoming test or quiz is scheduled.
                    </p>
                ) : (
                    <button
                        type="button"
                        onClick={() =>
                            setSelectedPlannerEvent(
                                nextTestQuiz
                            )
                        }
                        className="
                            mt-3
                            block
                            w-full
                            rounded-xl
                            border
                            border-[#c084fc]/20
                            bg-black/40
                            p-3
                            text-left
                            transition
                            hover:border-[#c084fc]/60
                            hover:bg-[#2e1065]/25
                            hover:shadow-[0_0_18px_rgba(192,132,252,0.12)]
                        "
                    >
                        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d8b4fe]">
                            {String(
                                nextTestQuiz.type ||
                                    nextTestQuiz.quest_type ||
                                    "Test"
                            )}
                        </p>

                        <p className="mt-1 text-sm font-semibold text-[#fff3c4]">
                            {nextTestQuiz.title ||
                                "Untitled Test / Quiz"}
                        </p>

                        <p className="mt-2 text-xs text-[#ffd166]">
                            {getClassroomQuestDueDate(
                                nextTestQuiz
                            )}
                            {nextTestQuiz.due_time
                                ? ` at ${formatDisplayTime(
                                    nextTestQuiz.due_time
                                )}`
                                : ""}
                        </p>
                    </button>
                )}
            </div>
        </div>
    <div className="rounded-2xl border border-[#ffd166]/25 bg-[#080b14]/80 p-4">
        <div className="flex items-center justify-between gap-3">
            <div>
                <h4 className="font-display text-xl text-[#ffd166]">
                    Gadgets
                </h4>

                <p className="mt-1 text-sm text-[#b0b8d6]">
                    Add classroom tools such as a To-Do List
                    and Notes.
                </p>
            </div>

            <button
                type="button"
                className={compactClassroomButtonClass(
                    false
                )}
            >
                + Add Gadget
            </button>
        </div>
    </div>


</div>
    </section>
)}
{["classTimer", "lessonStage"].includes(activePanel) && (
    <details className="mt-4" open={activePanel === "classTimer" || undefined}>
    <summary className={`${purpleActionBtn} cursor-pointer`}>Class timer generator ▾</summary>
    <section
        className="
            rounded-[32px]
            border border-[#ffd166]/40
            bg-black
            p-6
            shadow-[0_0_35px_rgba(255,209,102,0.10)]
            backdrop-blur-sm
        "
    >

        <div className="mt-0 border-t border-[#ffd166]/20 pt-6">
    <div className="space-y-4 mb-4">
    <div className="w-full">
            <div className="mb-5 rounded-2xl border border-[#ffd166]/25 bg-black/60 p-4">
    <p className="text-xs uppercase tracking-[0.2em] text-[#ffd166] mb-2">
        Class Hours Summary
    </p>

    <p className="text-sm text-[#b0b8d6]">
        Total saved hours for this class:
        <span className="ml-2 text-[#ffd166]">
            {getSessionHoursTotal(getCurrentClassSessions())} hrs
        </span>
    </p>

    <p className="text-xs text-[#6c7a9c] mt-1">
        Saved sessions: {getCurrentClassSessions().length}
    </p>
</div>
            <h4 className="font-display text-2xl text-[#ffd166]">
                Lesson Sections
            </h4>

            <p className="text-sm text-[#b0b8d6] mt-1">
                Add timed parts of the class, like lecture, practice, review, or quiz.
            </p>
            <div className="mt-4">
    <div className="flex flex-wrap gap-3">
        <button
            type="button"
            onClick={() => setShowSessionFlowBuilder((prev) => !prev)}
            className={purpleActionBtn}
        >
            {showSessionFlowBuilder ? "Hide Builder" : "Show Builder"}
        </button>

        <button
            type="button"
            onClick={() => setShowSavedSessionLessons((prev) => !prev)}
            className={purpleActionBtn}
        >
            {showSavedSessionLessons
                ? `Hide Saved Session Lessons (${savedSessionLessons.length})`
                : `Show Saved Session Lessons (${savedSessionLessons.length})`}
        </button>
    </div>
</div>

<div className="mt-4 rounded-2xl border border-[#ffd166]/25 bg-black/60 p-4">


    <label className="text-sm text-[#b0b8d6]">
        Session Date
    </label>

    <input
        type="date"
        value={sessionCalendarDate}
        onChange={(e) => setSessionCalendarDate(e.target.value)}
        className={inputClass}
    />

    <p className="text-xs text-[#6c7a9c] mt-2">
        This date is only for timed class sessions. It does not create or change quests.
    </p>
    <div className="mt-4 space-y-3">
        <label className="block text-sm text-[#b0b8d6]">Session start time<input type="time" className={inputClass} value={sessionFlowStartTime || classSchedule.startTime || ""} onChange={event => {setSessionFlowStartTime(event.target.value); sessionOutsideConfirmedRef.current = false;}} /></label>
        <p className="text-xs text-[#b0b8d6]">Class time: {classSchedule.startTime || "Unscheduled"}–{classSchedule.endTime || "Unscheduled"}. Session length follows its section minutes.</p>
        <label className="block text-sm text-[#b0b8d6]">Session days<select className={inputClass} value={sessionDayMode} onChange={event => setSessionDayMode(event.target.value)}><option value="all">All class days</option><option value="custom">Choose class days</option></select></label>
        {sessionDayMode === "custom" && <div className="flex flex-wrap gap-2">{(classSchedule.days?.length ? classSchedule.days : defaultClassDays).map(day => <button type="button" key={day} aria-pressed={sessionSelectedDays.includes(day)} className={purpleActionBtn} onClick={() => setSessionSelectedDays(previous => previous.includes(day) ? previous.filter(saved => saved !== day) : [...previous, day])}>{day}</button>)}</div>}
    </div>

<div className="mt-5 border-t border-[#ffd166]/20 pt-4">
    <p className="text-xs uppercase tracking-[0.2em] text-[#ffd166] mb-3">
        Repeat Session Flow
    </p>

    <div className="flex flex-wrap gap-2">
        {[
            { value: "none", label: "Does Not Repeat" },
            { value: "daily", label: "Every Day" },
            { value: "weekly", label: "Weekly" },
            { value: "monthly", label: "Monthly" },
            { value: "custom", label: "Custom" },
        ].map((option) => (
            <button
                key={option.value}
                type="button"
                onClick={() => setSessionRepeatMode(option.value)}
                className={`${purpleActionBtn} ${
                    sessionRepeatMode === option.value
                        ? "!border-[#a855f7] !bg-[#a855f7]/15 !text-[#e9d5ff] !shadow-[0_0_24px_rgba(168,85,247,0.45)]"
                        : ""
                }`}
            >
                {option.label}
            </button>
        ))}
    </div>

    {sessionRepeatMode !== "none" && (
        <div className="mt-4">
            <label className="text-sm text-[#b0b8d6]">
                Repeat Until
            </label>

            <input
                type="date"
                value={sessionRepeatEndDate}
                min={sessionCalendarDate}
                onChange={(e) => setSessionRepeatEndDate(e.target.value)}
                className={inputClass}
            />
        </div>
    )}

    {sessionRepeatMode === "custom" && (
        <div className="mt-4 space-y-4">
            <div>
                <label className="text-sm text-[#b0b8d6]">
                    Repeat Every
                </label>

                <div className="mt-2 flex items-center gap-3">
                    <input
                        type="number"
                        min="1"
                        max="52"
                        value={sessionRepeatInterval}
                        onChange={(e) =>
                            setSessionRepeatInterval(
                                Math.max(1, Number(e.target.value) || 1)
                            )
                        }
                        className={inputClass}
                    />

                    <span className="text-sm text-[#fff3c4]">
                        week{Number(sessionRepeatInterval) === 1 ? "" : "s"}
                    </span>
                </div>
            </div>

            <p className="text-xs text-[#b0b8d6]">Repeats use the class days selected above.</p>
        </div>
    )}
</div>
</div>
            <div className="mt-4 rounded-2xl border border-[#ffd166]/25 bg-black/60 p-4">
            <label className="text-sm text-[#b0b8d6]">
    Session Date
</label>
   <p className="text-xs uppercase tracking-[0.2em] text-[#ffd166] mb-3">
    Saved Session Lessons
</p>
<div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
    <input
        value={sessionLessonName}
        onChange={(e) => setSessionLessonName(e.target.value)}
        placeholder="Example: English Reading Block"
        className={inputClass}
    />

    <div className="flex flex-wrap gap-3 mt-4 md:mt-0">
        <button
            type="button"
            onClick={saveSessionLessonTemplate}
            className={purpleActionBtn}
        >
            Save Current Lesson Flow
        </button>
        <button
    type="button"
    onClick={updateActiveSessionLessonTemplate}
    className={purpleActionBtn}
    disabled={!activeSessionLessonId}
>
    Update Saved Lesson Flow
</button>
        <button
    type="button"
    onClick={createNewSessionFlow}
    className={purpleActionBtn}
>
    Create New Session
</button>

    </div>
</div>

{showSavedSessionLessons &&

    savedSessionLessons.length > 0 && (
    <div className="mt-4 space-y-3">
        {savedSessionLessons.map((template) => (
            <div
                key={template.id}
             onClick={() => {
    if (activeSessionLessonId === template.id) {
        createNewSessionFlow();
        toast.info(`${template.name} unloaded.`);
        return;
    }

    loadSessionLessonTemplate(template);
}}
                className={`
                    rounded-2xl
                    cursor-pointer
                    border
                    p-4
                    backdrop-blur-sm
                    transition-all
                    duration-200
                    hover:bg-[#101522]
                    ${
                        activeSessionLessonId === template.id
                            ? "border-[#ffd166]/70 bg-[#ffd166]/10 shadow-[0_0_28px_rgba(255,209,102,0.32)]"
                            : "border-[#ffd166]/30 bg-[#080b14]/90 shadow-[0_0_14px_rgba(255,209,102,0.08)]"
                    }
                `}
            >
                <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-start">
                    <div>
                        <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm text-[#ffd166]">
                                {template.name}
                            </p>

                            {activeSessionLessonId === template.id && (
                                <span className="inline-flex items-center rounded-full border border-[#ffd166]/60 bg-[#ffd166]/10 px-3 py-1 text-[10px] uppercase tracking-[0.18em] text-[#ffd166] shadow-[0_0_14px_rgba(255,209,102,0.25)]">
                                    ★ Active Lesson
                                </span>
                            )}

                        </div>

                        <p className="text-xs text-[#b0b8d6] mt-2 leading-relaxed">
                            {template.sections
                                .map(
                                    (section) =>
                                        `${section.title} (${section.plannedMinutes}m)`
                                )
                                .join(" → ")}
                        </p>
                    </div>

                    <div className="flex flex-wrap md:flex-nowrap gap-2 justify-start md:justify-end">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                openRenameSessionLessonModal(template);
                            }}
                            className={purpleActionBtn}
                        >
                            Rename
                        </button>

                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                deleteSessionLessonTemplate(template.id);
                            }}
                            className={purpleActionBtn}
                        >
                            Delete
                        </button>
                    </div>

                </div>
            </div>
        ))}
    </div>
)}
</div>
            <div className="mt-4 rounded-2xl border border-[#ffd166]/25 bg-black/60 p-4">
    <p className="text-xs uppercase tracking-[0.2em] text-[#ffd166] mb-3">
        Lesson Flow Preview
    </p>

    <div className="flex flex-wrap gap-2">
        {classTimerSections.map((section, index) => (
            <div
                key={section.id}
                className="rounded-xl border border-[#ffd166]/30 bg-[#080b14] px-3 py-2 text-xs text-[#fff3c4]"
            >
                {index + 1}. {section.title || "Untitled Section"} ·{" "}
                {section.plannedMinutes || 0} min
            </div>
        ))}

    </div>
</div>

        </div>
        <div className="mt-4 rounded-2xl border border-[#ffd166]/25 bg-black/60 p-4">
        {activePanel === "lessonStage" ? <p className="text-sm text-[#b0b8d6]">Choose whether to pause for more-time requests or move on in the lesson editor above.</p> : <>
        <label className="flex items-center gap-2 text-sm text-[#fff3c4]">
    <input
        type="checkbox"
        checked={autoStartNextSection}
        onChange={(e) => setAutoStartNextSection(e.target.checked)}
    />
    Auto-start next section when time runs out
</label>

<label className="mt-3 flex items-center gap-2 text-sm text-[#fff3c4]">
    <input
        type="checkbox"
        checked={autoEndClassAfterLastSection}
        onChange={(e) =>
            setAutoEndClassAfterLastSection(e.target.checked)
        }
    />
    Auto-end class when final section ends
</label>

<p className="text-xs text-[#b0b8d6] mt-2">
    If enabled, the next lesson section will automatically begin when the current section reaches its target time.
</p>
        </>}
</div>
    </div>

    {showSessionFlowBuilder && (
<div className="space-y-3">
        {classTimerSections.map((section, index) => (
            <div
    key={section.id}
    className="rounded-2xl border bg-[#080b14]/90 p-4 transition-all duration-300"
    style={{
        borderColor: getSectionProgressColor(section),
        boxShadow: `0 0 18px ${getSectionProgressColor(section)}55`,
    }}

>

<div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[#ffd166]/20 pb-3">
    <h4 className="font-display text-xl text-[#ffd166]">
        Session Section {index + 1}
    </h4>

    <button
        type="button"
        onClick={() =>
            removeClassTimerSection(
                section.id
            )
        }
        disabled={
            classTimerSections.length === 1
        }
        title={
            classTimerSections.length === 1
                ? "At least one session section is required."
                : `Delete Session Section ${index + 1}`
        }
        className={`
            rounded-xl
            border
            px-4
            py-2
            text-sm
            font-semibold
            transition
            ${
                classTimerSections.length === 1
                    ? "cursor-not-allowed border-red-400/20 text-red-200/40 opacity-50"
                    : "border-red-400/60 bg-red-500/10 text-red-200 hover:bg-red-500/20 hover:shadow-[0_0_18px_rgba(248,113,113,0.30)]"
            }
        `}
    >
        Delete Section
    </button>
</div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>

    <label className="text-sm text-[#b0b8d6]">
        Session Type
    </label>

    <select
        value={section.type || "lesson"}
        onChange={(e) =>
            updateClassTimerSection(
                section.id,
                "type",
                e.target.value
            )
        }
        className={inputClass}
    >
        <option value="lesson">Lesson</option>
        <option value="practice">Practice</option>
        <option value="quiz">Quiz</option>
        <option value="test">Test</option>
        <option value="assignment">Assignment</option>
        <option value="project">Project</option>
        <option value="reading">Reading</option>
        <option value="break">Break</option>
        <option value="lunch">Lunch</option>
    </select>

    <label className="text-sm text-[#b0b8d6]">
        Section {index + 1} Name
    </label>

                        <input
                            value={section.title}
                            onChange={(e) =>
                                updateClassTimerSection(
                                    section.id,
                                    "title",
                                    e.target.value
                                )
                            }
                            placeholder="Lecture, Practice, Review..."
                            className={inputClass}
                        />
                    </div>
                       <div className="md:col-span-4">
    <label className="text-sm text-[#b0b8d6]">
        Session Description
    </label>

    <textarea
        value={section.description || ""}
        onChange={(e) =>
            updateClassTimerSection(
                section.id,
                "description",
                e.target.value
            )
        }
        placeholder="What will happen during this session?"
        className={`${textareaClass} min-h-[70px]`}
    />
</div>

<div className="md:col-span-4">
    <label className="text-sm text-[#b0b8d6]">
        Materials Needed
    </label>

    <div className="mt-2 space-y-2">
        {(Array.isArray(section.materials)
            ? section.materials
            : [{ id: Date.now().toString(), name: section.materials || "", checked: false }]
        ).map((material, materialIndex) => (
            <div key={material.id || materialIndex} className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={!!material.checked}
                    onChange={(e) => {
                        const currentMaterials = Array.isArray(section.materials)
                            ? section.materials
                            : [{ id: Date.now().toString(), name: section.materials || "", checked: false }];

                        const updatedMaterials = currentMaterials.map((item, index) =>
                            index === materialIndex
                                ? { ...item, checked: e.target.checked }
                                : item
                        );

                        updateClassTimerSection(section.id, "materials", updatedMaterials);
                    }}
                />

                <input
                    value={material.name || ""}
                    onChange={(e) => {
                        const currentMaterials = Array.isArray(section.materials)
                            ? section.materials
                            : [{ id: Date.now().toString(), name: section.materials || "", checked: false }];

                        const updatedMaterials = currentMaterials.map((item, index) =>
                            index === materialIndex
                                ? { ...item, name: e.target.value }
                                : item
                        );

                        updateClassTimerSection(section.id, "materials", updatedMaterials);
                    }}
                    placeholder={`Material ${materialIndex + 1}`}
                    className={inputClass}
                />

                <button
                    type="button"
                    onClick={() => {
                        const currentMaterials = Array.isArray(section.materials)
                            ? section.materials
                            : [{ id: Date.now().toString(), name: section.materials || "", checked: false }];

                        const updatedMaterials =
                            currentMaterials.length > 1
                                ? currentMaterials.filter((_, index) => index !== materialIndex)
                                : [{ id: Date.now().toString(), name: "", checked: false }];

                        updateClassTimerSection(section.id, "materials", updatedMaterials);
                    }}
                    className={purpleActionBtn}
                >
                    Remove
                </button>
            </div>
        ))}
    </div>

    <button
        type="button"
        onClick={() => {
            const currentMaterials = Array.isArray(section.materials)
                ? section.materials
                : [{ id: Date.now().toString(), name: section.materials || "", checked: false }];

            updateClassTimerSection(section.id, "materials", [
                ...currentMaterials,
                {
                    id: `${Date.now()}-${Math.random()}`,
                    name: "",
                    checked: false,
                },
            ]);
        }}
        className={`${purpleActionBtn} mt-3`}
    >
        Add Material
    </button>
    <button
    type="button"
    onClick={() => saveSessionMaterialList(section)}
    className={`${purpleActionBtn} mt-3 ml-2`}
>
    Save Materials
</button>
</div>
                    <div>
                        <label className="text-sm text-[#b0b8d6]">
                            Target Minutes
                        </label>

                        <input
                            type="number"
                            min="1"
                            value={section.plannedMinutes}
                            onChange={(e) =>
                                updateClassTimerSection(
                                    section.id,
                                    "plannedMinutes",
                                    e.target.value
                                )
                            }
                            className={inputClass}
                        />
                    </div>

                    <div className="flex items-end">
                        <div className="flex flex-wrap gap-2">
    <button
        type="button"
        onClick={() => moveClassTimerSection(section.id, -1)}
        className={purpleActionBtn}
        disabled={index === 0}
    >
        ↑
    </button>

    <button
        type="button"
        onClick={() => moveClassTimerSection(section.id, 1)}
        className={purpleActionBtn}
        disabled={index === classTimerSections.length - 1}
    >
        ↓
    </button>

    <button
        type="button"
        onClick={() => removeClassTimerSection(section.id)}
        className={purpleActionBtn}
    >
        Remove
    </button>
</div>
                    </div>
                </div>

                <div className="mt-3 text-xs text-[#ffd166]">
                    Actual Time: {formatTimer(section.actualSeconds || 0)}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">

    <button
        type="button"
        onClick={() => startClassTimerSection(section.id)}
        className={purpleActionBtn}
    >
        Start Section
    </button>

    <button
        type="button"
        onClick={pauseClassTimerSection}
        className={purpleActionBtn}
    >
        Pause Section
    </button>

    <button
        type="button"
        onClick={() => completeClassTimerSection(section.id)}
        className={purpleActionBtn}
    >
        Complete Section
    </button>
    <button
    type="button"
    onClick={() =>
        updateClassTimerSection(section.id, "actualSeconds", 0)
    }
    className={purpleActionBtn}
>
    Reset Section
</button>
</div>

<div className="mt-2 text-xs text-[#b0b8d6]">
    Status:{" "}
    {section.completed
        ? "Completed"
        : section.running
        ? "Running"
        : "Not Started"}
</div>
            </div>
          ))}
    </div>
)}
        <div className="mt-5 flex flex-wrap gap-3">
            <button
                type="button"
                onClick={addClassTimerSection}
                className={purpleActionBtn}
            >
                Add Section
            </button>

            <button type="button" className={purpleActionBtn} onClick={() => saveSessionToLessonStage()}>Save today's session to Lesson Stage</button>
            <button
                type="button"
                onClick={createCalendarSessionsFromLessonFlow}
                className={purpleActionBtn}
            >
                Send Lesson Flow to Calendar
            </button>
        </div>


</div>
</section>
    </details>
)}
{activePanel === "schedule" && (
    <section
        className="
            rounded-[32px]
            border border-[#ffd166]/40
            bg-black
            p-6
            shadow-[0_0_35px_rgba(255,209,102,0.10)]
            backdrop-blur-sm
        "
    >
        <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
                <p className="text-xs uppercase tracking-[0.22em] text-[#d8b4fe]">
                    Class Schedule
                </p>

                <h3 className="mt-1 font-display text-2xl text-[#ffd166]">
                    {subject?.name || "Class"}
                </h3>

                <p className="mt-2 text-sm text-[#b0b8d6]">
                    This schedule is configured with the class settings in the parent profile.
                </p>
            </div>
        </div>

        {!subject?.class_schedule ? (
            <div className="mt-6 rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/70 p-5">
                <p className="text-sm text-[#b0b8d6]">
                    No class schedule has been configured yet.
                </p>

                <p className="mt-2 text-xs text-[#8f98b5]">
                    Edit this class from the Parent Profile to choose class days,
                    optional times, or a block schedule.
                </p>
            </div>
        ) : (
            <div className="mt-6">
                {(
                    subject.class_schedule.mode === "days" ||
                    subject.class_schedule.mode === "scheduled"
                ) && (
                    <div className="space-y-5">
                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-[#d8b4fe]">
                                Class Days
                            </p>

                            <div className="mt-3 flex flex-wrap gap-2">
                                {Array.isArray(
                                    subject.class_schedule.days
                                ) &&
                                subject.class_schedule.days.length > 0 ? (
                                    subject.class_schedule.days.map(
                                        (day) => (
                                            <div
                                                key={day}
                                                className="
                                                    rounded-xl
                                                    border border-[#ffd166]/35
                                                    bg-[#ffd166]/10
                                                    px-3 py-2
                                                    text-sm font-semibold
                                                    text-[#fff3c4]
                                                "
                                            >
                                                {day}
                                            </div>
                                        )
                                    )
                                ) : (
                                    <p className="text-sm text-[#8f98b5]">
                                        No regular class days selected.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-[#d8b4fe]">
                                Class Time
                            </p>

                            {subject.class_schedule.start_time ||
                            subject.class_schedule.end_time ? (
                                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                                    <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/70 p-4">
                                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8f98b5]">
                                            Start
                                        </p>

                                        <p className="mt-1 text-lg font-semibold text-[#fff3c4]">
                                            {subject.class_schedule.start_time
                                                ? formatDisplayTime(
                                                      subject.class_schedule.start_time
                                                  )
                                                : "Not set"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/70 p-4">
                                        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8f98b5]">
                                            End
                                        </p>

                                        <p className="mt-1 text-lg font-semibold text-[#fff3c4]">
                                            {subject.class_schedule.end_time
                                                ? formatDisplayTime(
                                                      subject.class_schedule.end_time
                                                  )
                                                : "Not set"}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-3 rounded-2xl border border-[#c084fc]/20 bg-[#160d26]/30 p-4">
                                    <p className="text-sm text-[#d8b4fe]">
                                        Flexible time
                                    </p>

                                    <p className="mt-1 text-xs text-[#8f98b5]">
                                        This class has regular class days but no fixed clock time.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {(
                    subject.class_schedule.mode === "block" ||
                    subject.class_schedule.mode === "flexible"
                ) && (
                    <div className="space-y-4">
                        <div className="rounded-2xl border border-[#c084fc]/25 bg-[#160d26]/30 p-5">
                            <p className="text-xs uppercase tracking-[0.18em] text-[#d8b4fe]">
                                Block Schedule
                            </p>

                            <p className="mt-2 font-display text-xl text-[#ffd166]">
                                {subject.class_schedule.preferred_block ===
                                "custom"
                                    ? subject.class_schedule
                                          .custom_block_label ||
                                      "Custom Block"
                                    : subject.class_schedule
                                          .preferred_block
                                    ? subject.class_schedule.preferred_block
                                          .charAt(0)
                                          .toUpperCase() +
                                      subject.class_schedule.preferred_block.slice(
                                          1
                                      )
                                    : "Flexible Block"}
                            </p>
                        </div>

                        <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/70 p-4">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-[#8f98b5]">
                                Estimated Weekly Hours
                            </p>

                            <p className="mt-1 text-lg font-semibold text-[#fff3c4]">
                                {subject.class_schedule.weekly_hours
                                    ? `${subject.class_schedule.weekly_hours} hours`
                                    : "Not specified"}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        )}
    </section>
)}

            {activePanel === "calendar" && !plannerViewMode && (
    <div className="rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/70 p-5 text-sm text-[#b0b8d6]">
        Choose Month, Week, or Day view to open the Quest Calendar. Your choice will be saved as the default view for this class.
    </div>
)}

            {activePanel === "calendar" && (
    <section className={shadowCalendarShell}>
        <h4 className="font-display text-2xl text-[#ffd166] mb-3">
            Quest Calendar
        </h4>

        <div className={`${shadowCalendarToolbar} flex flex-wrap gap-2 mb-4`}>
            <button
                type="button"
                onClick={() => {
                    setPlannerViewMode("month");
                    localStorage.setItem(plannerViewModeKey, "month");
                }}
                className={purpleActionBtn}
            >
                Month
            </button>

            <button
                type="button"
                onClick={() => {
                    setPlannerViewMode("week");
                    localStorage.setItem(plannerViewModeKey, "week");
                }}
                className={purpleActionBtn}
            >
                Week
            </button>

            <button
                type="button"
                onClick={() => {
                    setPlannerViewMode("day");
                    localStorage.setItem(plannerViewModeKey, "day");
                }}
                className={purpleActionBtn}
            >
                Day
            </button>
        </div>

       <div className={shadowCalendarShell}>
       {plannerViewMode === "month" && (
    <div className="flex items-center justify-between mb-4">
        <button
            type="button"
            onClick={() => moveCalendarMonth(-1)}
            className={purpleActionBtn}
        >
            Previous Month
        </button>

        <div className="text-center">
            <p className="text-[#ffd166] font-display text-xl">
                {parseLocalDate(calendarCursorDate).toLocaleDateString(
                    "en-US",
                    {
                        month: "long",
                        year: "numeric",
                    }
                )}
            </p>
        </div>

        <button
            type="button"
            onClick={() => moveCalendarMonth(1)}
            className={purpleActionBtn}
        >
            Next Month
        </button>
    </div>
)}
        {plannerViewMode === "month" && (
        <div className={`${shadowCalendarGrid} grid-cols-7`}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day} className="text-[#ffd166] font-semibold">
                    {day}
                </div>
            ))}


            {Array.from({
    length:
        new Date(parseLocalDate(calendarCursorDate).getFullYear(), parseLocalDate(calendarCursorDate).getMonth() + 1, 0).getDate() +
        new Date(parseLocalDate(calendarCursorDate).getFullYear(), parseLocalDate(calendarCursorDate).getMonth(), 1).getDay(),
}).map((_, index) => {
    const currentMonthDate = parseLocalDate(calendarCursorDate);

const firstDayOffset = parseLocalDate(
    formatLocalDate(
        new Date(
            currentMonthDate.getFullYear(),
            currentMonthDate.getMonth(),
            1
        )
    )
).getDay();


    if (index < firstDayOffset) {
        return <div key={`blank-${index}`} />;
    }


    const previewDate = new Date(
    currentMonthDate.getFullYear(),
    currentMonthDate.getMonth(),
    index - firstDayOffset + 1
);


                const dateString = formatLocalDate(previewDate);
                const valid = isValidSchoolDay(dateString);
                const vacation = isVacationDay(dateString);


                return (
                    <div
    key={dateString}
    onDragOver={(e) => e.preventDefault()}
    onDrop={() => handleCalendarDropToDate(dateString)}
            className={`rounded-2xl border p-3 min-h-[150px] max-h-[230px] overflow-y-auto flex flex-col items-start justify-start ${
    valid
        ? "border-[#ffd166]/35 bg-[#ffd166]/[0.03]"
        : vacation
        ? "border-red-400/30 bg-red-950/20 text-red-200 opacity-70"
        : "border-white/10 bg-black text-[#6c7a9c] opacity-45"
}`}
                    >
                        <span className="font-semibold">
                            {previewDate.getDate()}
                        </span>


                        <span className="text-[10px] mt-1">
                            {valid ? "Class" : vacation ? "Vacation" : "Skip"}
                        </span>
                        <div className="mt-1 space-y-1 w-full max-h-[110px] overflow-y-auto pr-1">
  {
    getCalendarItemsForDate(dateString)
    .map((event) => (
           <div
    key={event.id}
    draggable={!event.isBreakdownStep}
    onDragStart={() => {
    if (event.isBreakdownStep) return;
    setDraggedQuestId(event.id);
}}
    onDragEnd={() => {
    setDraggedQuestId(null);

}}
    onClick={() => {
    if (event.is_calendar_session || event.is_timed_session) {
        openSessionModalWithoutScrollJump(event);
        return;
    }

    setSelectedPlannerEvent(event);
}}
    className="w-full cursor-pointer hover:bg-[#ffd166]/10 text-[10px] rounded-lg bg-black px-2 py-1 text-left leading-tight border"
style={{ ...classroomCalendarStyle(event, subject?.color), borderWidth: "2px" }}
>
                {event.isBreakdownStep
    ? `☐ ${event.title} · ${event.parentQuestTitle}`
    : event.title}
            </div>
        ))}


</div>
                    </div>
                );
                     })}
                </div>

)}
{plannerViewMode === "week" && (
    <div>
        <div className="flex items-center justify-between mb-4">
            <button
                type="button"
                onClick={() => moveCalendarWeek(-1)}
                className={purpleActionBtn}
            >
                Previous Week
            </button>

            <p className="text-[#ffd166] font-display text-xl">
                Week Of {parseLocalDate(calendarCursorDate).toLocaleDateString()}
            </p>

            <button
                type="button"
                onClick={() => moveCalendarWeek(1)}
                className={purpleActionBtn}
            >
                Next Week
            </button>
        </div>

        <WeekBlockSchedule
            weekDates={Array.from({ length: 7 }).map((_, index) => {
                const selectedDate = parseLocalDate(calendarCursorDate);
                const dayOfWeek = selectedDate.getDay();
                const weekDate = new Date(selectedDate);

                weekDate.setDate(selectedDate.getDate() - dayOfWeek + index);

                return formatLocalDate(weekDate);
            })}
            onOpenQuest={(item) => {
                if (item.is_calendar_session || item.is_timed_session) {
                    openSessionModalWithoutScrollJump(item);
                    return;
                }

             openPlannerEventModalWithoutWeekJump(item);
            }}
        />
    </div>
)}

  {plannerViewMode === "day" && (
    <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
            <button
                type="button"
                onClick={() => moveSelectedPlannerDate(-1)}
                className={purpleActionBtn}
            >
                Previous Day
            </button>

            <div className="text-center">
                <p className="text-sm text-[#ffd166]">
                    {selectedPlannerDate}
                </p>

                <p className="text-xs text-[#b0b8d6]">
                    {isValidSchoolDay(selectedPlannerDate)
                        ? "Class Day"
                        : "No Class / Skipped Day"}
                </p>
            </div>

            <button
                type="button"
                onClick={() => moveSelectedPlannerDate(1)}
                className={purpleActionBtn}
            >
                Next Day
            </button>
        </div>

       <DayBlockSchedule
    selectedDate={selectedPlannerDate}
    events={getCalendarItemsForDate(selectedPlannerDate)}
    onMoveTimedItem={handleMoveTimedDayItem}
    onMoveToAllDay={handleMoveDayItemToAllDay}
    onOpenQuest={(item) => {
                if (item.is_calendar_session || item.is_timed_session) {
                    openSessionModalWithoutScrollJump(item);
                    return;
                }

                setSelectedPlannerEvent(item);
            }}
        />
    </div>
)}

        </div>
    </section>
)}

{activePanel === "grading" && (
    <section
        className="
            rounded-[32px]
            border border-[#ffd166]/40
            bg-black
            p-6
            shadow-[0_0_35px_rgba(255,209,102,0.10)]
            backdrop-blur-sm
        "
    >
        <h3 className="font-display text-2xl text-[#ffd166]">
            Grading System & Weights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
    <div>
        <label className="text-sm text-[#b0b8d6]">
            Grading Scale
        </label>

        <select
            value={gradingSettings.gradingScale}
            onChange={(e) =>
                setGradingSettings((prev) => ({
                    ...prev,
                    gradingScale: e.target.value,
                }))
            }
            className={inputClass}
        >
            <option value="A-F">
                Traditional A-F
            </option>

            <option value="points">Points earned / possible</option>
            <option value="percentage">Number grade (0–100%)</option>
            <option value="standards">
                Standards Based
            </option>

            <option value="credit">
                Credit / No Credit
            </option>
        </select>
    </div>
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
    <div>
        <label className="text-sm text-[#b0b8d6]">
            Assignment Weight %
        </label>

        <input
            type="number"
            value={gradingSettings.assignmentWeight}
            onChange={(e) =>
                setGradingSettings((prev) => ({
                    ...prev,
                    assignmentWeight: e.target.value,
                }))
            }
            className={inputClass}
        />
    </div>

    <div>
        <label className="text-sm text-[#b0b8d6]">
            Quiz Weight %
        </label>

        <input
            type="number"
            value={gradingSettings.quizWeight}
            onChange={(e) =>
                setGradingSettings((prev) => ({
                    ...prev,
                    quizWeight: e.target.value,
                }))
            }
            className={inputClass}
        />
    </div>

    <div>
        <label className="text-sm text-[#b0b8d6]">
            Test Weight %
        </label>

        <input
            type="number"
            value={gradingSettings.testWeight}
            onChange={(e) =>
                setGradingSettings((prev) => ({
                    ...prev,
                    testWeight: e.target.value,
                }))
            }
            className={inputClass}
        />
    </div>

    <div>
        <label className="text-sm text-[#b0b8d6]">
            Project Weight %
        </label>

        <input
            type="number"
            value={gradingSettings.projectWeight}
            onChange={(e) =>
                setGradingSettings((prev) => ({
                    ...prev,
                    projectWeight: e.target.value,
                }))
            }
            className={inputClass}
        />
    </div>
</div>
<button
    type="button"
    onClick={() => {
        localStorage.setItem(
            gradingSettingsKey,
            JSON.stringify(gradingSettings)
        );
        queueGradingSettingsSync(childId, subjectId, gradingSettings);

        alert("Grading system saved.");
    }}
    className={`${purpleActionBtn} mt-5`}
>
    Save Grading System
</button>
<div className="mt-8 border-t border-[#ffd166]/20 pt-6">
    <h3 className="font-display text-2xl text-[#ffd166]">
        Planner Item Colors
    </h3>

    <p className="text-sm text-[#b0b8d6] mt-2">
        Choose glow colors for each type of classroom quest.
    </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
    {Object.keys(itemTypeColors).map((type) => (
        <div
            key={type}
            className="
                rounded-2xl
                border border-[#ffd166]/30
                bg-[#080b14]/90
                p-4
                shadow-[0_0_18px_rgba(255,209,102,0.08)]
                transition-all
                duration-200
                hover:bg-[#101522]
            "
        >
            <label className="text-sm text-[#b0b8d6] capitalize">
                {type.replace("_", " ")}
            </label>

            <div className="flex items-center gap-3 mt-2">
                <input
                    type="color"
                    value={itemTypeColors[type]}
                    onChange={(e) =>
                        setItemTypeColors((prev) => ({
                            ...prev,
                            [type]: e.target.value,
                        }))
                    }
                    className="h-10 w-14 rounded-lg bg-transparent"
                />

                <div
                    className="flex-1 rounded-xl border px-3 py-2 text-sm text-white"
                    style={{
                        borderColor: itemTypeColors[type],
                        boxShadow: `0 0 14px ${itemTypeColors[type]}99`,
                    }}
                >
                    {itemTypeColors[type]}
                </div>
            </div>
        </div>
    ))}
</div>

    <button
        type="button"
        onClick={saveItemTypeColors}
        className="
    mt-5
    rounded-2xl
    border border-[#ffd166]/40
    bg-black
    px-3 py-2
    text-xs text-[#ffd166]
    shadow-[0_0_14px_rgba(255,209,102,0.10)]
    hover:bg-[#ffd166]/[0.06]
    hover:shadow-[0_0_20px_rgba(255,209,102,0.20)]
    transition-all
    duration-200
"
    >
        Save Planner Colors
    </button>
</div>

<div className="mt-8 border-t border-[#ffd166]/20 pt-6">
    <h3 className="font-display text-2xl text-[#ffd166]">
        Progress Reports & Report Cards
    </h3>

    <p className="text-sm text-[#b0b8d6] mt-2">
        This section will hold strengths, weaknesses, parent notes, progress tracking, and report card exports.
    </p>
</div>
    </section>
)}



{activePanel === "resources" && (
    <section
    className="
        rounded-[32px]
        border border-[#ffd166]/40
        bg-black
        p-6
        shadow-[0_0_35px_rgba(255,209,102,0.10)]
        backdrop-blur-sm
    "
>
<div className="flex flex-wrap items-start justify-between gap-4">
    <div>
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#d8b4fe]">
            Liberi Curiosi
        </p>

        <h3 className="mt-1 font-display text-2xl text-[#ffd166]">
            Archivum
        </h3>

        <p className="mt-2 text-sm text-[#b0b8d6]">
            Organize lesson videos, Tabula presentations, voice recordings,
            worksheets, files, links, and quest resources for this class.
        </p>
    </div>

    <div className="flex w-full flex-col gap-2 sm:w-auto sm:min-w-[320px]">
        <label className="text-xs font-semibold uppercase tracking-[0.16em] text-[#ffd166]/80">
            Nova Tabularium
        </label>

        <div className="flex gap-2">
            <input
                type="text"
                value={newArchivumFolderName}
                onChange={(event) =>
                    setNewArchivumFolderName(
                        event.target.value
                    )
                }
                onKeyDown={(event) => {
                    if (event.key === "Enter") {
                        addArchivumFolder();
                    }
                }}
                placeholder="Example: Section II"
                className={inputClass}
            />

            <button
                type="button"
                onClick={addArchivumFolder}
                className={purpleActionBtn}
                title="Create folder"
            >
                <FolderPlus size={18} />
            </button>
        </div>
    </div>
</div>

<div className="mt-6">
    {selectedArchivumFolder ? (
        <div className="space-y-5">
            <button
                type="button"
                onClick={() =>
                    setOpenArchivumFolderId(
                        selectedArchivumFolder
                            .parent_folder_id ||
                            null
                    )
                }
                className={purpleActionBtn}
            >
                <ArrowLeft size={17} />
                Back to{" "}
                {selectedArchivumFolder
                    .parent_folder_id
                    ? "Parent Folder"
                    : "Archivum"}
            </button>

            <div
                className="
                    rounded-[28px]
                    border
                    border-[#ffd166]/35
                    bg-[#080b14]
                    p-5
                    shadow-[0_0_25px_rgba(255,209,102,0.08)]
                "
            >
                <div className="flex items-start gap-4">
                    <div
                        className="
                            flex
                            h-14
                            w-14
                            shrink-0
                            items-center
                            justify-center
                            rounded-2xl
                            border
                            border-[#ffd166]/35
                            bg-black
                            text-[#ffd166]
                        "
                    >
                        <Folder size={27} />
                    </div>

                    <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#d8b4fe]">
                            Archivum Folder
                        </p>

                        <h4 className="mt-1 font-display text-2xl text-[#fff3c4]">
                            {
                                selectedArchivumFolder.name
                            }
                        </h4>

                        {selectedArchivumFolder.description ? (
                            <p className="mt-2 whitespace-pre-wrap text-sm text-[#b0b8d6]">
                                {
                                    selectedArchivumFolder.description
                                }
                            </p>
                        ) : (
                            <p className="mt-2 text-sm italic text-[#7f88a8]">
                                No folder description has
                                been added yet.
                            </p>
                        )}
                    </div>
                </div>
            </div>


<div className="space-y-5">
    <div
        className="
            rounded-[28px]
            border
            border-[#ffd166]/35
            bg-[#080b14]
            p-5
            shadow-[0_0_25px_rgba(255,209,102,0.08)]
        "
    >
        <div className="mb-4">
            <h5 className="font-display text-xl text-[#ffd166]">
                {editingArchivumInstructionId
                    ? "Edit Parent Instructions"
                    : "Add Parent Instructions"}
            </h5>

            <p className="mt-1 text-sm text-[#b0b8d6]">
                These instructions are saved to this folder and shown to the child.
            </p>
        </div>

        <div className="grid gap-4">
            <label className="grid gap-2">
                <span className="text-sm font-semibold text-[#fff3c4]">
                    Instruction Title
                </span>

                <input
                    type="text"
                    value={
                        archivumInstructionTitle
                    }
                    onChange={(event) =>
                        setArchivumInstructionTitle(
                            event.target.value
                        )
                    }
                    className={inputClass}
                />
            </label>

            <label className="grid gap-2">
                <span className="text-sm font-semibold text-[#fff3c4]">
                    Instructions
                </span>

                <textarea
                    value={
                        archivumInstructionBody
                    }
                    onChange={(event) =>
                        setArchivumInstructionBody(
                            event.target.value
                        )
                    }
                    rows={6}
                    className={`${inputClass} min-h-[150px] resize-y`}
                />
            </label>

            <label className="grid gap-2">
                <span className="text-sm font-semibold text-[#fff3c4]">
                    Note Style
                </span>

                <select
                    value={
                        archivumInstructionColor
                    }
                    onChange={(event) =>
                        setArchivumInstructionColor(
                            event.target.value
                        )
                    }
                    className={inputClass}
                >
                    <option value="parchment">
                        Parchment
                    </option>

                    <option value="gold">
                        Gold
                    </option>

                    <option value="purple">
                        Imperial Purple
                    </option>
                </select>
            </label>

            <div className="flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={
                        saveArchivumInstruction
                    }
                    disabled={
                        isSavingArchivumInstruction
                    }
                    className={purpleActionBtn}
                >
                    {isSavingArchivumInstruction
                        ? "Saving..."
                        : editingArchivumInstructionId
                        ? "Update Instructions"
                        : "Save Instructions"}
                </button>

                {editingArchivumInstructionId && (
                    <button
                        type="button"
                        onClick={
                            resetArchivumInstructionForm
                        }
                        className="
                            rounded-full
                            border
                            border-[#ffd166]/35
                            bg-black
                            px-5
                            py-2.5
                            font-semibold
                            text-[#fff3c4]
                            transition
                            hover:border-[#ffd166]/70
                        "
                    >
                        Cancel Editing
                    </button>
                )}
            </div>
        </div>
    </div>

    <div>
        <div className="mb-3 flex items-center justify-between gap-3">
            <h5 className="font-display text-xl text-[#ffd166]">
                Parent Instructions
            </h5>

            <span className="text-xs text-[#b0b8d6]">
                {
                    (
                        selectedArchivumFolder
                            .sticky_notes || []
                    ).length
                }{" "}
                saved
            </span>
        </div>

        {(
            selectedArchivumFolder
                .sticky_notes || []
        ).length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
                {(
                    selectedArchivumFolder
                        .sticky_notes || []
                )
                    .slice()
                    .sort(
                        (
                            firstNote,
                            secondNote
                        ) =>
                            (
                                firstNote.sort_order ||
                                0
                            ) -
                            (
                                secondNote.sort_order ||
                                0
                            )
                    )
                    .map((note) => (
                        <div
                            key={note.note_id}
                            className={`
                                rounded-[24px]
                                border
                                p-5
                                shadow-[0_8px_25px_rgba(0,0,0,0.24)]
                                ${
                                    note.note_color ===
                                    "purple"
                                        ? "border-[#c084fc]/50 bg-[#2a123d]"
                                        : note.note_color ===
                                          "gold"
                                        ? "border-[#ffd166]/60 bg-[#2a2108]"
                                        : "border-[#d8c7a0]/50 bg-[#211b12]"
                                }
                            `}
                        >
                            <div className="flex items-start gap-3">
                                <FileText
                                    size={20}
                                    className="mt-0.5 shrink-0 text-[#ffd166]"
                                />

                                <div className="min-w-0 flex-1">
                                    <h6 className="font-semibold text-[#fff3c4]">
                                        {note.title}
                                    </h6>

                                    <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#f4ead0]">
                                        {note.body}
                                    </p>

                                    <div className="mt-4 flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                startEditingArchivumInstruction(
                                                    note
                                                )
                                            }
                                            className="
                                                rounded-full
                                                border
                                                border-[#ffd166]/40
                                                bg-black/40
                                                px-4
                                                py-2
                                                text-xs
                                                font-semibold
                                                text-[#ffd166]
                                                transition
                                                hover:border-[#ffd166]/75
                                            "
                                        >
                                            Edit
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                deleteArchivumInstruction(
                                                    note.note_id
                                                )
                                            }
                                            className="
                                                rounded-full
                                                border
                                                border-red-400/40
                                                bg-black/40
                                                px-4
                                                py-2
                                                text-xs
                                                font-semibold
                                                text-red-300
                                                transition
                                                hover:border-red-400/75
                                            "
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
            </div>
        ) : (
            <div
                className="
                    rounded-[24px]
                    border
                    border-dashed
                    border-[#ffd166]/25
                    bg-black/50
                    px-5
                    py-6
                    text-center
                "
            >
                <p className="font-semibold text-[#fff3c4]">
                    No instructions saved
                </p>

                <p className="mt-1 text-sm text-[#b0b8d6]">
                    Enter the actual instructions above and press Save Instructions.
                </p>
            </div>
        )}
    </div>
</div>

            <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                    <h5 className="font-display text-xl text-[#ffd166]">
                        Folders
                    </h5>

                    <span className="text-xs text-[#b0b8d6]">
                        {
                            visibleArchivumFolders.length
                        }{" "}
                        inside
                    </span>
                </div>

                {visibleArchivumFolders.length >
                0 ? (
                    <div className="grid gap-3 md:grid-cols-2">
                        {visibleArchivumFolders.map(
                            (folder) => {
                                const childFolderCount =
                                    (
                                        classArchivum.folders ||
                                        []
                                    ).filter(
                                        (
                                            childFolder
                                        ) =>
                                            (
                                                childFolder.parent_folder_id ||
                                                null
                                            ) ===
                                            folder.id
                                    ).length;

                                return (
                                    <button
                                        key={
                                            folder.id
                                        }
                                        type="button"
                                        onClick={() =>
                                            setOpenArchivumFolderId(
                                                folder.id
                                            )
                                        }
                                        className="
                                            flex
                                            items-center
                                            justify-between
                                            gap-4
                                            rounded-[24px]
                                            border
                                            border-[#ffd166]/30
                                            bg-[#080b14]
                                            px-5
                                            py-4
                                            text-left
                                            transition
                                            hover:border-[#ffd166]/60
                                            hover:bg-[#ffd166]/5
                                        "
                                    >
                                        <span className="flex min-w-0 items-center gap-3">
                                            <span
                                                className="
                                                    flex
                                                    h-11
                                                    w-11
                                                    shrink-0
                                                    items-center
                                                    justify-center
                                                    rounded-xl
                                                    border
                                                    border-[#ffd166]/30
                                                    bg-black
                                                    text-[#ffd166]
                                                "
                                            >
                                                <Folder
                                                    size={
                                                        21
                                                    }
                                                />
                                            </span>

                                            <span className="min-w-0">
                                                <span className="block truncate font-semibold text-[#fff3c4]">
                                                    {
                                                        folder.name
                                                    }
                                                </span>

                                                <span className="mt-1 block text-xs text-[#b0b8d6]">
                                                    {
                                                        childFolderCount
                                                    }{" "}
                                                    folders,{" "}
                                                    {
                                                        (
                                                            folder.resources ||
                                                            []
                                                        )
                                                            .length
                                                    }{" "}
                                                    resources
                                                </span>

                                                {folder.description && (
                                                    <span className="mt-2 line-clamp-2 block text-xs text-[#8f99b8]">
                                                        {
                                                            folder.description
                                                        }
                                                    </span>
                                                )}
                                            </span>
                                        </span>

                                        <ChevronRight
                                            size={19}
                                            className="shrink-0 text-[#ffd166]"
                                        />
                                    </button>
                                );
                            }
                        )}
                    </div>
                ) : (
                    <div
                        className="
                            rounded-[24px]
                            border
                            border-dashed
                            border-[#ffd166]/25
                            bg-black/50
                            px-5
                            py-6
                            text-center
                        "
                    >
                        <p className="font-semibold text-[#fff3c4]">
                            No folders inside this
                            folder
                        </p>

                        <p className="mt-1 text-sm text-[#b0b8d6]">
                            Use the folder creator
                            above to add one here.
                        </p>
                    </div>
                )}
            </div>

            <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                    <h5 className="font-display text-xl text-[#ffd166]">
                        Resources
                    </h5>

                    <span className="text-xs text-[#b0b8d6]">
                        {
                            (
                                selectedArchivumFolder.resources ||
                                []
                            ).length
                        }{" "}
                        available
                    </span>
                </div>

                {(
                    selectedArchivumFolder.resources ||
                    []
                ).length > 0 ? (
                    <div className="grid gap-3 md:grid-cols-2">
                        {(
                            selectedArchivumFolder.resources ||
                            []
                        ).map((resource) => (
                            <button
                                key={
                                    resource.resource_id
                                }
                                type="button"
                                onClick={() => {
                                    if (
                                        resource.url
                                    ) {
                                        window.open(
                                            resource.url,
                                            "_blank",
                                            "noopener,noreferrer"
                                        );

                                        return;
                                    }

                                    if (
                                        resource.resource_id
                                    ) {
                                        const resourceUrl =
                                            `${api.defaults.baseURL}` +
                                            `/children/${childId}` +
                                            `/classes/${subjectId}` +
                                            `/archivum/resources/` +
                                            resource.resource_id;

                                        window.open(
                                            resourceUrl,
                                            "_blank",
                                            "noopener,noreferrer"
                                        );
                                    }
                                }}
                                className="
                                    flex
                                    items-start
                                    gap-4
                                    rounded-[24px]
                                    border
                                    border-[#7c3aed]/45
                                    bg-[#10091c]
                                    p-5
                                    text-left
                                    transition
                                    hover:border-[#ffd166]/60
                                    hover:bg-[#180d2a]
                                "
                            >
                                <span
                                    className="
                                        flex
                                        h-11
                                        w-11
                                        shrink-0
                                        items-center
                                        justify-center
                                        rounded-xl
                                        border
                                        border-[#ffd166]/30
                                        bg-black
                                        text-[#ffd166]
                                    "
                                >
                                    {resource.resource_type ===
                                        "video" ||
                                    resource.resource_type ===
                                        "video_link" ? (
                                        <Video
                                            size={21}
                                        />
                                    ) : resource.resource_type ===
                                          "audio" ||
                                      resource.resource_type ===
                                          "audio_link" ? (
                                        <Mic
                                            size={21}
                                        />
                                    ) : resource.resource_type ===
                                          "presentation" ? (
                                        <Presentation
                                            size={21}
                                        />
                                    ) : resource.url ? (
                                        <LinkIcon
                                            size={21}
                                        />
                                    ) : (
                                        <FileText
                                            size={21}
                                        />
                                    )}
                                </span>

                                <span className="min-w-0">
                                    <span className="block font-semibold text-[#fff3c4]">
                                        {
                                            resource.title
                                        }
                                    </span>

                                    <span className="mt-1 block text-xs uppercase tracking-[0.12em] text-[#c4a7ff]">
                                        {
                                            resource.resource_type
                                        }
                                    </span>

                                    {resource.description && (
                                        <span className="mt-2 line-clamp-3 block text-sm text-[#b0b8d6]">
                                            {
                                                resource.description
                                            }
                                        </span>
                                    )}

                                    {resource.file_name && (
                                        <span className="mt-2 block truncate text-xs text-[#7f88a8]">
                                            {
                                                resource.file_name
                                            }
                                        </span>
                                    )}
                                </span>
                            </button>
                        ))}
                    </div>
                ) : (
                    <div
                        className="
                            rounded-[24px]
                            border
                            border-dashed
                            border-[#ffd166]/25
                            bg-black/50
                            px-5
                            py-8
                            text-center
                        "
                    >
                        <FileText
                            size={28}
                            className="mx-auto text-[#ffd166]/70"
                        />

                        <p className="mt-3 font-semibold text-[#fff3c4]">
                            This folder has no
                            resources
                        </p>

                        <p className="mt-1 text-sm text-[#b0b8d6]">
                            Files, videos, links,
                            recordings, and Tabula
                            lessons will appear here.
                        </p>
                    </div>
                )}
            </div>
        </div>
    ) : (
        <div>
            <div className="mb-3 flex items-center justify-between gap-3">
                <h5 className="font-display text-xl text-[#ffd166]">
                    Archivum Folders
                </h5>

                <span className="text-xs text-[#b0b8d6]">
                    {
                        visibleArchivumFolders.length
                    }{" "}
                    folders
                </span>
            </div>

            {visibleArchivumFolders.length >
            0 ? (
                <div className="grid gap-3 md:grid-cols-2">
                    {visibleArchivumFolders.map(
                        (folder) => {
                            const childFolderCount =
                                (
                                    classArchivum.folders ||
                                    []
                                ).filter(
                                    (
                                        childFolder
                                    ) =>
                                        (
                                            childFolder.parent_folder_id ||
                                            null
                                        ) === folder.id
                                ).length;

                            return (
                                <button
                                    key={folder.id}
                                    type="button"
                                    onClick={() =>
                                        setOpenArchivumFolderId(
                                            folder.id
                                        )
                                    }
                                    className="
                                        flex
                                        items-center
                                        justify-between
                                        gap-4
                                        rounded-[24px]
                                        border
                                        border-[#ffd166]/30
                                        bg-[#080b14]
                                        px-5
                                        py-5
                                        text-left
                                        transition
                                        hover:border-[#ffd166]/60
                                        hover:bg-[#ffd166]/5
                                    "
                                >
                                    <span className="flex min-w-0 items-center gap-3">
                                        <span
                                            className="
                                                flex
                                                h-12
                                                w-12
                                                shrink-0
                                                items-center
                                                justify-center
                                                rounded-xl
                                                border
                                                border-[#ffd166]/30
                                                bg-black
                                                text-[#ffd166]
                                            "
                                        >
                                            <Folder
                                                size={
                                                    23
                                                }
                                            />
                                        </span>

                                        <span className="min-w-0">
                                            <span className="block truncate font-semibold text-[#fff3c4]">
                                                {
                                                    folder.name
                                                }
                                            </span>

                                            <span className="mt-1 block text-xs text-[#b0b8d6]">
                                                {
                                                    childFolderCount
                                                }{" "}
                                                folders,{" "}
                                                {
                                                    (
                                                        folder.resources ||
                                                        []
                                                    )
                                                        .length
                                                }{" "}
                                                resources
                                            </span>

                                            {folder.description && (
                                                <span className="mt-2 line-clamp-2 block text-xs text-[#8f99b8]">
                                                    {
                                                        folder.description
                                                    }
                                                </span>
                                            )}
                                        </span>
                                    </span>

                                    <ChevronRight
                                        size={19}
                                        className="shrink-0 text-[#ffd166]"
                                    />
                                </button>
                            );
                        }
                    )}
                </div>
            ) : (
                <div
                    className="
                        rounded-[24px]
                        border
                        border-dashed
                        border-[#ffd166]/25
                        bg-black/50
                        px-5
                        py-8
                        text-center
                    "
                >
                    <Folder
                        size={30}
                        className="mx-auto text-[#ffd166]/70"
                    />

                    <p className="mt-3 font-semibold text-[#fff3c4]">
                        No Archivum folders yet
                    </p>
                </div>
            )}
        </div>
    )}
</div>
    </section>
)}



{activePanel === "fieldTrip" && (
    <section
    className="
        rounded-[32px]
        border border-[#ffd166]/40
        bg-black
        p-6
        shadow-[0_0_35px_rgba(255,209,102,0.10)]
        backdrop-blur-sm
    "
>
        <h3 className="font-display text-2xl text-[#ffd166]">
            Field Trip
        </h3>
        <p className="text-sm text-[#b0b8d6] mt-2">
            Field trip notes, location, subjects, photos, and reports will go here.
        </p>
        <div className="mt-8 border-t border-[#ffd166]/20 pt-6">
    <h3 className="font-display text-2xl text-[#ffd166]">
        Weekly Reflections
    </h3>

    <p className="text-sm text-[#b0b8d6] mt-2">
        Child weekly reports, parent observations, reflections, and end-of-week summaries will go here.
    </p>
</div>
    </section>
)}




                                               {activePanel === "creator" && creatorPanel}

{activePanel === "openQuests" && (
<section
    className="
        rounded-[32px]
        border
        border-[#ffd166]/40
        bg-black
        p-6
        shadow-[0_0_35px_rgba(255,209,102,0.10)]
        backdrop-blur-sm
    "
>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-3xl text-[#ffd166]">
            Open Lessons
        </h3>

        <button
            type="button"
            onClick={clearCurrentClassQuests}
            className={purpleActionBtn}
        >
            Clear Old Quests For This Class
        </button>
    </div>


                    {openClassroomEvents.length === 0 ? (
                        <p className="text-sm text-[#6c7a9c]">
                            No lessons created yet.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {openClassroomEvents.map((event) => (
                                <div
                                    key={event.id}
                                    className="
    rounded-2xl
    border
    bg-[#080b14]/90
    p-4
    backdrop-blur-sm
    transition-all
    duration-200
    hover:bg-[#101522]
"
style={{
    borderColor:
        itemTypeColors[event.type] ||
        event.color ||
        "#ffd166",

    borderWidth: "2px",

    boxShadow: `0 0 18px ${
        itemTypeColors[event.type] ||
        event.color ||
        "#ffd166"
    }55`,
}}
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-4">
                                        <div>
                                            <h4 className="font-display text-xl text-[#ffd166]">
                                                {event.title}
                                                
                                            </h4>


                                            <p className="text-xs text-[#d8b4fe] mt-1">
                                                {event.date_start || "No date"}{" "}
                                                {event.all_day
                                                    ? "· Flexible Block"
                                                    : `· ${event.start_time || "No start"} to ${event.end_time || "No end"}`}
                                                {" · "}
                                                {event.repeat || "none"}
                                                {event.repeat === "custom" &&
                                                    event.repeat_days?.length > 0 &&
                                                    `: ${event.repeat_days.join(", ")}`}
                                            </p>
                                               {(event.due_date || event.due_time) && (
                                                <p className="text-xs text-[#ffd166] mt-1">
                                                 Due: {event.due_date || "No due date"}
                                                {event.due_time
                                                  ? ` at ${formatDisplayTime(event.due_time)}`
                                                      : ""}
                                                    </p>
                                            )}

                                            <p className="text-xs text-[#ffd166] mt-1">
                                                Sections: {event.section_count || 1}
                                                {event.section_titles_enabled && event.section_titles?.length > 0 && (
    <p className="text-xs text-[#b0b8d6] mt-1">
        {event.section_titles.join(" → ")}
    </p>
)}

    </p>

    {isQuestLate(event) && (
        <div className="mt-2">
            <div className="inline-flex rounded-full border border-red-500 bg-red-950/40 px-2 py-1 text-xs text-red-200">
                Late · {getLateDays(event)} day
                {getLateDays(event) === 1 ? "" : "s"}
            </div>
        </div>
    )}


                                            {event.description && (
                                                <p className="text-sm text-[#b0b8d6] mt-3">
                                                    {event.description}
                                                </p>
                                            )}


                                            {Array.isArray(event.materials) && event.materials.length > 0 && (
    <div className="mt-3">
        <p className="text-xs text-[#ffd166] mb-1">
            Materials:
        </p>

        <ul className="space-y-1">
            {event.materials.map((material) => (
                <li
                    key={material.id}
                    className="text-xs text-[#b0b8d6]"
                >
                    {material.checked ? "☑" : "☐"} {material.name}
                </li>
            ))}
        </ul>
    </div>
)}


                                            {event.end_of_day_notes && (
                                                <p className="text-xs text-[#7dffb2] mt-2">
                                                    End of Day: {event.end_of_day_notes}
                                                </p>
                                            )}


                                            {event.link?.url && (
                                                <a
                                                    href={event.link.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="block text-xs text-[#9fd0ff] underline break-all mt-2"
                                                >
                                                    <LinkIcon size={12} className="inline mr-1" />
                                                    {event.link.title || event.link.url}
                                                </a>
                                            )}


                                            {event.attachment?.fileName && (
                                                <a
                                                    href={event.attachment.fileUrl}
                                                    download={event.attachment.fileName}
                                                    className="block text-xs text-[#ffd166] underline break-all mt-2"
                                                >
                                                    <Upload size={12} className="inline mr-1" />
                                                    Download {event.attachment.fileName}
                                                </a>
                                            )}
                                        </div>


                                        <div className="flex flex-wrap gap-2">
                                            <button
    type="button"
    onClick={() => {
        const updatedEvents = events.map((q) =>
            q.id === event.id
                ? {
                      ...q,
                      status: "pending_approval",
                      submitted_at: new Date().toISOString(),
                  }
                : q
        );

        saveShadowGroveEvents(updatedEvents);

        setEvents(updatedEvents);
    }}
    className="
        rounded-2xl
        border border-[#ffd166]/40
        bg-black
        px-3 py-2
        text-xs text-[#ffd166]
        shadow-[0_0_14px_rgba(255,209,102,0.10)]
        hover:bg-[#ffd166]/[0.06]
        hover:shadow-[0_0_20px_rgba(255,209,102,0.20)]
        transition-all
        duration-200
    "
>
    Submit for Approval
</button>
                                            <button
                                                type="button"
                                                onClick={() => setEditingQuestModal(event)}
                                                className={purpleActionBtn}
                                            >
                                                Edit
                                            </button>


                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const newDate = window.prompt(
                                                        "Move to date YYYY-MM-DD:",
                                                        event.date_start || ""
                                                    );


                                                    moveLessonToDate(event, newDate);
                                                }}
                                                className={purpleActionBtn}
                                            >
                                                Move Date
                                            </button>


                                            <button
                                                type="button"
                                                onClick={() => deleteLesson(event.id)}
                                                className={purpleActionBtn}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                
                           </section>
                           )}
{activePanel === "pending" && (

<section
    className="
        rounded-[32px]
        border border-[#ffd166]/40
        bg-black
        p-6
        shadow-[0_0_35px_rgba(255,209,102,0.10)]
        backdrop-blur-sm
    "
>
    <h3 className="font-display text-3xl text-[#ffd166] mb-4">
        Pending Approval
    </h3>


    {pendingApprovalEvents.length === 0 ? (
        <p className="text-sm text-[#6c7a9c]">
            No submitted quests awaiting approval.
        </p>
    ) : (
        <div className="space-y-3">
            {pendingApprovalEvents.map((event) => (
                <div
                    key={event.id}
                    className="
                        rounded-2xl
                        border border-[#ffd166]/40
                        bg-[#080b14]/90
                        p-4
                        shadow-[0_0_18px_rgba(255,209,102,0.08)]
                    "
                >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <h4 className="font-display text-xl text-[#ffd166]">
                                {event.title}
                            </h4>


                            <p className="text-xs text-[#d8b4fe] mt-1">
                                Submitted for approval
                            </p>


                            {event.description && (
                                <p className="text-sm text-[#b0b8d6] mt-3">
                                    {event.description}
                                </p>
                            )}
                        </div>


                        <div className="flex flex-wrap gap-2">
                          <button
                                    type="button"
                                     onClick={() => {
                                    setGradingQuestModal(event);
                                    setParentGradeMethod(event.parent_review?.grading_type || event.grading_type || ({ "A-F": "letter", points: "points", percentage: "percentage", credit: "pass_fail" }[gradingSettings.gradingScale] || "letter"));
                                    setParentEarnedPoints(event.parent_review?.earned_points ?? "");
                                    setParentPercentage(event.parent_review?.percentage ?? "");
                                    setParentPassFail(event.parent_review?.pass_fail || "");
                                    setParentGradeValue(getSuggestedGradeForRubric(event));
                                    setParentGradeNote("");
                                            }}
                                    className={purpleActionBtn}
>
                                      Grade / Review
</button>


                            <button
                                type="button"
                                onClick={() => {
                                    const updatedEvents = events.map((q) =>
                                        q.id === event.id
                                            ? {
                                                  ...q,
                                                  status: "open",
                                                  submitted_at: null,
                                              }
                                            : q
                                    );


                                    saveShadowGroveEvents(updatedEvents);


                                    setEvents(updatedEvents);
                                }}
                                className={purpleActionBtn}
                            >
                                Send Back
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )}
</section>
)}


{activePanel === "completed" && (
<section
    className="
        rounded-[32px]
        border border-[#ffd166]/40
        bg-black
        p-6
        shadow-[0_0_35px_rgba(255,209,102,0.10)]
        backdrop-blur-sm
    "
>
    <h3 className="font-display text-3xl text-[#ffd166] mb-4">
        Completed Lessons
    </h3>


    {completedClassroomEvents.length === 0 ? (
        <p className="text-sm text-[#6c7a9c]">
            No completed quests yet.
        </p>
    ) : (
        <div className="space-y-3">
            {completedClassroomEvents.map((event) => (
                <div
                    key={event.id}
                    className="
                        rounded-2xl
                        border border-[#7dffb2]/40
                        bg-[#080b14]/90
                        p-4
                        shadow-[0_0_18px_rgba(125,255,178,0.10)]
                    "
                >
                    <h4 className="font-display text-xl text-[#7dffb2]">
                        {event.title}
                    </h4>


                    <p className="text-xs text-[#7dffb2] mt-1">
                        Approved and completed
                    </p>
                </div>
            ))}
        </div>
    )}
</section>
)}



{questCreatedPopup && (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm">
        <div className="my-6 w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-black p-5 shadow-[0_0_45px_rgba(255,209,102,0.22)]">
            <h3 className="font-display text-3xl text-[#ffd166] text-center">
                ✨ Quest Created
            </h3>

            <p className="mt-3 text-center text-sm text-[#b0b8d6]">
                Your quest was added to the calendar and Open Lessons.
            </p>

            <div className="mt-6 flex flex-wrap justify-center gap-2">
                <button
                    type="button"
                    onClick={() => {
                        setQuestCreatedPopup(false);
                        setShowCreator(true);
                        setActivePanel("today");
                    }}
                    className={purpleActionBtn}
                >
                    Create Another Quest
                </button>

                <button
                    type="button"
                    onClick={() => {
                        setQuestCreatedPopup(false);
                        setShowCreator(false);
                        setActivePanel("today");
                    }}
                    className={purpleActionBtn}
                >
                    Done Creating Quests
                </button>
            </div>
        </div>
    </div>
)}
    {copyQuestModal && (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 px-4 py-6">
        <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-[32px] border border-[#ffd166]/45 bg-[#05070d] p-5 shadow-[0_0_45px_rgba(255,209,102,0.22)] [scrollbar-width:thin] [scrollbar-color:#ffd166_#05070d]">
            <div className="flex items-start justify-between gap-4 border-b border-[#ffd166]/20 pb-5">
                <div>
                    <h3 className="font-display text-2xl text-[#ffd166]">
                        Copy Quest
                    </h3>
                    <p className="text-sm text-[#b0b8d6] mt-1">
                        Edit the copied quest before creating it.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => setCopyQuestModal(null)}
                    className={purpleActionBtn}
                >
                    Close
                </button>
            </div>

            <div className="mt-5 space-y-4">
                <input
                    value={copyQuestModal.title || ""}
                    onChange={(e) =>
                        setCopyQuestModal((prev) => ({
                            ...prev,
                            title: e.target.value,
                        }))
                    }
                    className={inputClass}
                />

                <textarea
                    value={copyQuestModal.description || ""}
                    onChange={(e) =>
                        setCopyQuestModal((prev) => ({
                            ...prev,
                            description: e.target.value,
                        }))
                    }
                    className={`${textareaClass} min-h-[100px]`}
                />
                <div className="rounded-3xl border border-[#ffd166]/20 bg-black/60 p-4 shadow-[0_0_16px_rgba(255,209,102,0.08)]">
    <p className="mb-3 text-sm font-semibold text-[#ffd166]">
        Materials
    </p>

    <div className="space-y-2">
        {(Array.isArray(copyQuestModal.materials) && copyQuestModal.materials.length
            ? copyQuestModal.materials
            : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }]
        ).map((material, index) => (
            <div key={material.id || index} className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={Boolean(material.checked)}
                    onChange={(e) => {
                        const currentMaterials =
                            Array.isArray(copyQuestModal.materials) && copyQuestModal.materials.length
                                ? copyQuestModal.materials
                                : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }];

                        const updatedMaterials = currentMaterials.map((item, materialIndex) =>
                            materialIndex === index
                                ? { ...item, checked: e.target.checked }
                                : item
                        );

                        setCopyQuestModal((prev) => ({
                            ...prev,
                            materials: updatedMaterials,
                        }));
                    }}
                />

                <input
                    value={material.name || ""}
                    onChange={(e) => {
                        const currentMaterials =
                            Array.isArray(copyQuestModal.materials) && copyQuestModal.materials.length
                                ? copyQuestModal.materials
                                : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }];

                        const updatedMaterials = currentMaterials.map((item, materialIndex) =>
                            materialIndex === index
                                ? { ...item, name: e.target.value }
                                : item
                        );

                        setCopyQuestModal((prev) => ({
                            ...prev,
                            materials: updatedMaterials,
                        }));
                    }}
                    placeholder={`Material ${index + 1}`}
                    className={inputClass}
                />

                <button
                    type="button"
                    onClick={() => {
                        const currentMaterials =
                            Array.isArray(copyQuestModal.materials) && copyQuestModal.materials.length
                                ? copyQuestModal.materials
                                : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }];

                        const updatedMaterials =
                            currentMaterials.length > 1
                                ? currentMaterials.filter((_, materialIndex) => materialIndex !== index)
                                : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }];

                        setCopyQuestModal((prev) => ({
                            ...prev,
                            materials: updatedMaterials,
                        }));
                    }}
                    className={purpleActionBtn}
                >
                    Remove
                </button>
            </div>
        ))}
    </div>

    <button
        type="button"
        onClick={() =>
            setCopyQuestModal((prev) => ({
                ...prev,
                materials: [
                    ...(Array.isArray(prev.materials) ? prev.materials : []),
                    {
                        id: `${Date.now()}-${Math.random()}`,
                        name: "",
                        checked: false,
                    },
                ],
            }))
        }
        className={`${purpleActionBtn} mt-3`}
    >
        Add Material
    </button>
</div>
                <div>
    <label className="text-sm text-[#b0b8d6]">
        Repeat
    </label>

    <select
        value={copyQuestModal.repeat || "none"}
        onChange={(e) =>
            setCopyQuestModal((prev) => ({
                ...prev,
                repeat: e.target.value,
            }))
        }
        className={`${inputClass} h-11`}
    >
        <option value="none">Does Not Repeat</option>
<option value="daily">Daily</option>
<option value="weekly">Weekly</option>
<option value="biweekly">Biweekly</option>
<option value="monthly">Monthly</option>
<option value="custom">Custom Days</option>
    </select>
</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                        type="date"
                        value={copyQuestModal.date_start || ""}
                        onChange={(e) =>
                            setCopyQuestModal((prev) => ({
                                ...prev,
                                date_start: e.target.value,
                                date: e.target.value,
                                due_date: e.target.value,
                            }))
                        }
                        className={inputClass}
                    />

                    <input
                        type="time"
                        value={copyQuestModal.due_time || ""}
                        onChange={(e) =>
                            setCopyQuestModal((prev) => ({
                                ...prev,
                                due_time: e.target.value,
                            }))
                        }
                        className={inputClass}
                    />
                </div>
                <div className="rounded-3xl border border-[#ffd166]/20 bg-black/60 p-4 shadow-[0_0_16px_rgba(255,209,102,0.08)]">
    <label className="flex items-center gap-2 text-sm text-[#b0b8d6]">
        <input
            type="checkbox"
            checked={copyQuestModal.all_day ?? true}
            onChange={(e) =>
                setCopyQuestModal((prev) => ({
                    ...prev,
                    all_day: e.target.checked,
                }))
            }
        />
        All Day / Flexible Block
    </label>

    {!copyQuestModal.all_day && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
                <label className="text-sm text-[#b0b8d6]">
                    Start Time
                </label>

                <input
                    type="time"
                    step="300"
                    value={copyQuestModal.start_time || ""}
                    onChange={(e) =>
                        setCopyQuestModal((prev) => ({
                            ...prev,
                            start_time: e.target.value,
                            scheduled_start_time: e.target.value,
                        }))
                    }
                    className={inputClass}
                />
            </div>

            <div>
                <label className="text-sm text-[#b0b8d6]">
                    End Time
                </label>

                <input
                    type="time"
                    step="300"
                    value={copyQuestModal.end_time || ""}
                    onChange={(e) =>
                        setCopyQuestModal((prev) => ({
                            ...prev,
                            end_time: e.target.value,
                            scheduled_end_time: e.target.value,
                        }))
                    }
                    className={inputClass}
                />
            </div>
        </div>
    )}
</div>
                    {copyQuestModal.repeat && copyQuestModal.repeat !== "none" && (
    <div>
        <label className="text-sm text-[#b0b8d6]">
            Repeat Until
        </label>

        <input
            type="date"
            value={copyQuestModal.repeat_end_date || copyQuestModal.date_end || ""}
            onChange={(e) =>
                setCopyQuestModal((prev) => ({
                    ...prev,
                    repeat_end_date: e.target.value,
                    date_end: e.target.value,
                }))
            }
            className={inputClass}
        />
    </div>
)}
            {copyQuestModal.repeat === "custom" && (
    <div className="rounded-3xl border border-[#ffd166]/20 bg-black/60 p-4 shadow-[0_0_16px_rgba(255,209,102,0.08)]">
        <label className="block text-sm text-[#b0b8d6] mb-3">
            Repeat On
        </label>

        <div className="flex flex-wrap gap-2">
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((day) => (
                <button
                    key={day}
                    type="button"
                    onClick={() => {
                        const current = copyQuestModal.custom_days || [];

                        setCopyQuestModal((prev) => ({
                            ...prev,
                            custom_days: current.includes(day)
                                ? current.filter((d) => d !== day)
                                : [...current, day],
                        }));
                    }}
                    className={
                        (copyQuestModal.custom_days || []).includes(day)
                            ? purpleActionBtn
                            : "rounded-2xl border border-[#ffd166]/20 bg-black px-3 py-2 text-[#b0b8d6]"
                    }
                >
                    {day}
                </button>
            ))}
        </div>
    </div>
)}
                <button
                    type="button"
                    onClick={() => {
    const repeatType = copyQuestModal.repeat || "none";
    const startDate = copyQuestModal.date_start || copyQuestModal.date || copyQuestModal.due_date;
    const endDate = copyQuestModal.repeat_end_date || copyQuestModal.date_end;

    if (!startDate) {
        toast.error("Choose a start date for the copied quest.");
        return;
    }

    if (repeatType !== "none" && !endDate) {
        toast.error("Choose a Repeat Until date.");
        return;
    }

    const batchId = `${Date.now()}-${Math.random()}`;
    const createdAt = new Date().toISOString();

    const makeQuestCopy = (dateString, index = 0) => ({
        ...copyQuestModal,

        id: `${Date.now()}-${Math.random()}-${index}`,

        // Always bind copied quests to the current canonical class.
        child_id: childId,
        subject_id: subjectId,
        subject_name:
            subject?.name || "Class",

        batch_id:
            repeatType !== "none"
                ? batchId
                : copyQuestModal.batch_id || "",

        repeat: repeatType,

        date: dateString,
        date_start: dateString,
        date_end: dateString,
        due_date: dateString,

        created_at: createdAt,
        updated_at: createdAt,
    });

    const newQuests = [];

    if (repeatType === "none") {
        newQuests.push(makeQuestCopy(startDate, 0));
    } else {
        const cursor = parseLocalDate(startDate);
        const finalDate = parseLocalDate(endDate);
        let index = 0;

        while (cursor <= finalDate && index < 370) {
            const dayName = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][cursor.getDay()];
            const shouldCreate =
                repeatType === "daily" ||
                (repeatType === "weekly" && index % 7 === 0) ||
                (repeatType === "biweekly" && index % 14 === 0) ||
                (repeatType === "monthly" && cursor.getDate() === parseLocalDate(startDate).getDate()) ||
                (repeatType === "custom" &&
                    Array.isArray(copyQuestModal.custom_days) &&
                    copyQuestModal.custom_days.includes(dayName));

            if (shouldCreate) {
                newQuests.push(makeQuestCopy(formatLocalDate(cursor), newQuests.length));
            }

            cursor.setDate(cursor.getDate() + 1);
            index += 1;
        }
    }

    const updatedEvents = [...events, ...newQuests];

    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);
    setCopyQuestModal(null);

    toast.success(
        newQuests.length === 1
            ? "Copied quest created."
            : `${newQuests.length} copied quests created.`
    );
}}
                    className={purpleActionBtn}
                >
                    Create Copied Quest
                </button>
            </div>
        </div>
    </div>
)}
{editingQuestModal && (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 px-4 py-6">
        <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-[32px] border border-[#ffd166]/45 bg-[#05070d] p-5 shadow-[0_0_45px_rgba(255,209,102,0.22)] [scrollbar-width:thin] [scrollbar-color:#ffd166_#05070d]">
                    <div className="max-h-[78vh] overflow-y-auto p-4 text-sm [scrollbar-width:thin] [scrollbar-color:#ffd166_#05070d]"></div>
            <div className="flex items-start justify-between gap-4 border-b border-[#ffd166]/20 pb-5">
                <div>
                    <h3 className="font-display text-2xl text-[#ffd166] drop-shadow-[0_0_12px_rgba(255,209,102,0.4)]">
                        Edit Quest
                    </h3>

                    <p className="text-sm text-[#b0b8d6] mt-1">
                        Update this quest without reopening the Quest Creator.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => setEditingQuestModal(null)}
                    className={purpleActionBtn}
                >
                    Close
                </button>
            </div>

            <div className="mt-5 space-y-4">
                <div>
                    <label className="text-sm text-[#b0b8d6]">
                        Quest Title
                    </label>

                    <input
                        value={editingQuestModal.title || ""}
                        onChange={(e) =>
                            setEditingQuestModal((prev) => ({
                                ...prev,
                                title: e.target.value,
                            }))
                        }
                        className={inputClass}
                    />
                </div>

                <div>
                    <label className="text-sm text-[#b0b8d6]">
                        Description
                    </label>

                    <textarea
                        value={editingQuestModal.description || ""}
                        onChange={(e) =>
                            setEditingQuestModal((prev) => ({
                                ...prev,
                                description: e.target.value,
                            }))
                        }
                        className={`${textareaClass} min-h-[120px]`}
                    />
                </div>
                    <div className="rounded-3xl border border-[#ffd166]/20 bg-black/60 p-4 shadow-[0_0_16px_rgba(255,209,102,0.08)]">
    <p className="mb-3 text-sm font-semibold text-[#ffd166]">
        Materials
    </p>

    <div className="space-y-2">
        {(Array.isArray(editingQuestModal.materials) && editingQuestModal.materials.length
            ? editingQuestModal.materials
            : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }]
        ).map((material, index) => (
            <div key={material.id || index} className="flex items-center gap-2">
                <input
                    type="checkbox"
                    checked={Boolean(material.checked)}
                    onChange={(e) => {
                        const currentMaterials =
                            Array.isArray(editingQuestModal.materials) && editingQuestModal.materials.length
                                ? editingQuestModal.materials
                                : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }];

                        const updatedMaterials = currentMaterials.map((item, materialIndex) =>
                            materialIndex === index
                                ? { ...item, checked: e.target.checked }
                                : item
                        );

                        setEditingQuestModal((prev) => ({
                            ...prev,
                            materials: updatedMaterials,
                        }));
                    }}
                />

                <input
                    value={material.name || ""}
                    onChange={(e) => {
                        const currentMaterials =
                            Array.isArray(editingQuestModal.materials) && editingQuestModal.materials.length
                                ? editingQuestModal.materials
                                : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }];

                        const updatedMaterials = currentMaterials.map((item, materialIndex) =>
                            materialIndex === index
                                ? { ...item, name: e.target.value }
                                : item
                        );

                        setEditingQuestModal((prev) => ({
                            ...prev,
                            materials: updatedMaterials,
                        }));
                    }}
                    placeholder={`Material ${index + 1}`}
                    className={inputClass}
                />

                <button
                    type="button"
                    onClick={() => {
                        const currentMaterials =
                            Array.isArray(editingQuestModal.materials) && editingQuestModal.materials.length
                                ? editingQuestModal.materials
                                : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }];

                        const updatedMaterials =
                            currentMaterials.length > 1
                                ? currentMaterials.filter((_, materialIndex) => materialIndex !== index)
                                : [{ id: `${Date.now()}-${Math.random()}`, name: "", checked: false }];

                        setEditingQuestModal((prev) => ({
                            ...prev,
                            materials: updatedMaterials,
                        }));
                    }}
                    className={purpleActionBtn}
                >
                    Remove
                </button>
            </div>
        ))}
    </div>

    <button
        type="button"
        onClick={() =>
            setEditingQuestModal((prev) => ({
                ...prev,
                materials: [
                    ...(Array.isArray(prev.materials) ? prev.materials : []),
                    {
                        id: `${Date.now()}-${Math.random()}`,
                        name: "",
                        checked: false,
                    },
                ],
            }))
        }
        className={`${purpleActionBtn} mt-3`}
    >
        Add Material
    </button>
</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                       <label className="text-sm text-[#b0b8d6]">
    {plannerMode === "date_range"
        ? "Start Date"
        : plannerMode === "lesson_count"
        ? "First Quest Date"
        : lessonRepeat !== "none"
        ? "First Repeat Date"
        : "Quest Date"}
</label>

                        <input
                            type="date"
                            value={editingQuestModal.date_start || ""}
                            onChange={(e) =>
                                setEditingQuestModal((prev) => ({
                                    ...prev,
                                    date_start: e.target.value,
                                }))
                            }
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className="text-sm text-[#b0b8d6]">
                            End Date
                        </label>

                        <input
                            type="date"
                            value={editingQuestModal.date_end || ""}
                            onChange={(e) =>
                                setEditingQuestModal((prev) => ({
                                    ...prev,
                                    date_end: e.target.value,
                                }))
                            }
                            className={inputClass}
                        />
                    </div>
                                            </div>

                <div className="rounded-3xl border border-[#ffd166]/20 bg-black/60 p-4 shadow-[0_0_16px_rgba(255,209,102,0.08)]">
                    <label className="flex items-center gap-2 text-sm text-[#b0b8d6]">
                        <input
                            type="checkbox"
                            checked={editingQuestModal.all_day ?? !editingQuestModal.start_time}
                            onChange={(e) =>
                                setEditingQuestModal((prev) => ({
                                    ...prev,
                                    all_day: e.target.checked,
                                    start_time: e.target.checked ? "" : prev.start_time,
                                    end_time: e.target.checked ? "" : prev.end_time,
                                    scheduled_start_time: e.target.checked ? "" : prev.scheduled_start_time,
                                    scheduled_end_time: e.target.checked ? "" : prev.scheduled_end_time,
                                }))
                            }
                        />
                        All Day / Flexible Block
                    </label>

                    {!(editingQuestModal.all_day ?? !editingQuestModal.start_time) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                <label className="text-sm text-[#b0b8d6]">
                                    Start Time
                                </label>

                                <input
                                    type="time"
                                    step="300"
                                    value={
                                        editingQuestModal.start_time ||
                                        editingQuestModal.scheduled_start_time ||
                                        editingQuestModal.due_time ||
                                        ""
                                    }
                                    onChange={(e) =>
                                        setEditingQuestModal((prev) => ({
                                            ...prev,
                                            all_day: false,
                                            start_time: e.target.value,
                                            scheduled_start_time: e.target.value,
                                            due_time: e.target.value,
                                        }))
                                    }
                                    className={inputClass}
                                />
                            </div>

                            <div>
                                <label className="text-sm text-[#b0b8d6]">
                                    End Time
                                </label>

                                <input
                                    type="time"
                                    step="300"
                                    value={
                                        editingQuestModal.end_time ||
                                        editingQuestModal.scheduled_end_time ||
                                        ""
                                    }
                                    onChange={(e) =>
                                        setEditingQuestModal((prev) => ({
                                            ...prev,
                                            all_day: false,
                                            end_time: e.target.value,
                                            scheduled_end_time: e.target.value,
                                        }))
                                    }
                                    className={inputClass}
                                />
                            </div>
                        </div>
                    )}

                    <div className="mt-4">
                        <label className="text-sm text-[#b0b8d6]">
                            Due Time
                        </label>

                        <input
                            type="time"
                            step="300"
                            value={editingQuestModal.due_time || ""}
                            onChange={(e) =>
                                setEditingQuestModal((prev) => ({
                                    ...prev,
                                    due_time: e.target.value,
                                }))
                            }
                            className={inputClass}
                        />
                    </div>
                </div>
                    <button
    type="button"
    onClick={() => {
        const eventToEdit = editingQuestModal;
        setEditingQuestModal(null);
        loadLessonForEdit(eventToEdit);
    }}
    className={purpleActionBtn}
>
    Edit in Full Quest Creator
</button>
                <button
                    type="button"
                   onClick={() => {
    if (isRecurringQuestItem(editingQuestModal)) {
        setPendingQuestEditModal({
            item: editingQuestModal,
        });
        return;
    }

    const savedWeekScrollLeft =
        weekScrollRef.current?.scrollLeft || weekScrollLeftRef.current;

    const updatedEvents = events.map((event) =>
        event.id === editingQuestModal.id
            ? {
                ...event,
                ...editingQuestModal,

                // Always normalize edited quests
                // to the current canonical class.
                child_id: childId,
                subject_id: subjectId,
                subject_name:
                    subject?.name || "Class",

                start_time:
                    editingQuestModal.all_day
                        ? ""
                        : editingQuestModal.start_time,

                end_time:
                    editingQuestModal.all_day
                        ? ""
                        : editingQuestModal.end_time,

                scheduled_start_time:
                    editingQuestModal.all_day
                        ? ""
                        : editingQuestModal.scheduled_start_time,

                scheduled_end_time:
                    editingQuestModal.all_day
                        ? ""
                        : editingQuestModal.scheduled_end_time,

                due_time:
                    editingQuestModal.due_time || "",

                updated_at:
                    new Date().toISOString(),
            }
            : event
    );
    saveShadowGroveEvents(updatedEvents);
    setEvents(updatedEvents);
    setEditingQuestModal(null);

    requestAnimationFrame(() => {
        if (weekScrollRef.current) {
            weekScrollRef.current.scrollLeft = savedWeekScrollLeft;
        }

        setTimeout(() => {
            if (weekScrollRef.current) {
                weekScrollRef.current.scrollLeft = savedWeekScrollLeft;
            }
        }, 50);
    });

    toast.success("Quest updated.");
}}

                   
                     
                    className={purpleActionBtn}
                >
                    Save Quest Changes
                </button>
                                </div>
            </div>
        </div>
    
)}

{gradingQuestModal && (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl rounded-[32px] border border-[#ffd166]/50 bg-black p-6 shadow-[0_0_35px_rgba(255,209,102,0.22)] max-h-[90vh] overflow-y-auto">

            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-display text-3xl text-[#ffd166]">
                        Grade Quest
                    </h3>

                    <p className="text-sm text-[#b0b8d6] mt-1">
                        Review submitted work and send feedback.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => setGradingQuestModal(null)}
                    className={purpleActionBtn}
                >
                    Close
                </button>
            </div>

            <div className="mt-6 space-y-5">
            {gradingQuestModal.rubric_enabled && (
        <div className="rounded-2xl border border-[#ffd166]/30 bg-[#080b14]/90 p-4">
            <h4 className="font-display text-2xl text-[#ffd166] mb-3">
                Rubric Matrix
            </h4>

            <p className="text-sm text-[#b0b8d6] mb-4">
                {getRubricModeLabel(gradingQuestModal.rubric_mode)}
            </p>

            <div className="space-y-3">
                {gradingQuestModal.rubric_rows?.map((row, index) => (
                    <div
                        key={row.id}
                        className="rounded-2xl border border-[#ffd166]/20 bg-black p-4"
                    >
                        <p className="text-sm text-[#ffd166]">
                            {row.criteria}
                        </p>

                        {gradingQuestModal.rubric_mode === "points" && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                                <div>
                                    <p className="text-xs text-[#6c7a9c]">
                                        Max: {row.maxPoints} pts
                                    </p>
                                </div>

                                <div>
                                    <label className="text-sm text-[#b0b8d6]">
                                        Earned Points
                                    </label>

                                    <input
                                        type="number"
                                        min="0"
                                        max={row.maxPoints}
                                        value={row.earnedPoints || 0}
                                        onChange={(e) => {
                                            const updatedRows = [
                                                ...(gradingQuestModal.rubric_rows ||
                                                    []),
                                            ];

                                            updatedRows[index] = {
                                                ...updatedRows[index],
                                                earnedPoints:
                                                    Number(e.target.value) || 0,
                                            };

                                            setGradingQuestModal((prev) => ({
                                                ...prev,
                                                rubric_rows: updatedRows,
                                            }));
                                        }}
                                        className={inputClass}
                                    />
                                </div>

                                <div className="flex items-end">
                                    <p className="text-sm text-[#7dffb2]">
                                        {row.earnedPoints || 0} /{" "}
                                        {row.maxPoints}
                                    </p>
                                </div>
                            </div>
                        )}

                        {gradingQuestModal.rubric_mode === "matrix" && (
                            <div className="mt-4 overflow-x-auto rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/60">
                                <table className="w-full min-w-[780px] border-collapse text-xs">
                                    <thead className="text-[#ffd166]">
                                        <tr>
                                            <th className="border border-[#ffd166]/20 p-2 text-left">Category</th>
                                            {(gradingQuestModal.rubric_matrix_columns || DEFAULT_MATRIX_COLUMNS).map((column) => (
                                                <th key={column.id} className="border border-[#ffd166]/20 p-2 text-center" style={{ color: column.color || "#ffd166" }}>
                                                    {column.score} · {column.label}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="border border-[#ffd166]/20 p-2 text-[#ffd166]">
                                                {row.criteria}
                                            </td>
                                            {(gradingQuestModal.rubric_matrix_columns || DEFAULT_MATRIX_COLUMNS).map((column) => {
                                                const isSelected = Number(row.selectedMatrixScore) === Number(column.score);
                                                return (
                                                    <td key={column.id} className="border border-[#ffd166]/20 p-2 align-top">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const updatedRows = [...(gradingQuestModal.rubric_rows || [])];
                                                                updatedRows[index] = {
                                                                    ...updatedRows[index],
                                                                    selectedMatrixScore: column.score,
                                                                };
                                                                setGradingQuestModal((prev) => ({
                                                                    ...prev,
                                                                    rubric_rows: updatedRows,
                                                                }));
                                                            }}
                                                            className="w-full rounded-xl border bg-black p-2 text-left transition-all duration-200"
                                                            style={{
                                                                borderColor: column.color || "#ffd166",
                                                                boxShadow: isSelected
                                                                    ? `0 0 20px ${column.color || "#ffd166"}99`
                                                                    : `0 0 10px ${column.color || "#ffd166"}33`,
                                                            }}
                                                        >
                                                            <div className="text-center text-lg font-bold" style={{ color: column.color || "#ffd166" }}>
                                                                {isSelected ? "☑" : "☐"}
                                                            </div>
                                                            <p className="mt-1 text-[11px] text-[#d8dee9]">
                                                                {(row.matrixCells || {})[column.score] || column.label}
                                                            </p>
                                                        </button>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {gradingQuestModal.rubric_mode === "checklist" && (
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const updatedRows = [
                                            ...(gradingQuestModal.rubric_rows ||
                                                []),
                                        ];

                                        updatedRows[index] = {
                                            ...updatedRows[index],
                                            checklistChecked: true,
                                        };

                                        setGradingQuestModal((prev) => ({
                                            ...prev,
                                            rubric_rows: updatedRows,
                                        }));
                                    }}
                                    className="rounded-2xl border bg-black p-3 text-left transition-all duration-200"
                                    style={{
                                        borderColor: row.checklistChecked
                                            ? row.checklistColor ||
                                              CHECKLIST_COMPLETE_COLOR
                                            : "#ffd16655",
                                        boxShadow: row.checklistChecked
                                            ? `0 0 18px ${
                                                  row.checklistColor ||
                                                  CHECKLIST_COMPLETE_COLOR
                                              }88`
                                            : "0 0 10px rgba(255,209,102,0.12)",
                                        color: row.checklistChecked
                                            ? row.checklistColor ||
                                              CHECKLIST_COMPLETE_COLOR
                                            : "#b0b8d6",
                                    }}
                                >
                                    ✓ Complete
                                </button>

                                <button
                                    type="button"
                                    onClick={() => {
                                        const updatedRows = [
                                            ...(gradingQuestModal.rubric_rows ||
                                                []),
                                        ];

                                        updatedRows[index] = {
                                            ...updatedRows[index],
                                            checklistChecked: false,
                                        };

                                        setGradingQuestModal((prev) => ({
                                            ...prev,
                                            rubric_rows: updatedRows,
                                        }));
                                    }}
                                    className="rounded-2xl border bg-black p-3 text-left transition-all duration-200"
                                    style={{
                                        borderColor: !row.checklistChecked
                                            ? CHECKLIST_INCOMPLETE_COLOR
                                            : "#ffd16655",
                                        boxShadow: !row.checklistChecked
                                            ? `0 0 18px ${CHECKLIST_INCOMPLETE_COLOR}66`
                                            : "0 0 10px rgba(255,209,102,0.12)",
                                        color: !row.checklistChecked
                                            ? CHECKLIST_INCOMPLETE_COLOR
                                            : "#b0b8d6",
                                    }}
                                >
                                    ✕ Needs Work
                                </button>

                                {row.checklistNote && (
                                    <p className="md:col-span-2 text-xs text-[#b0b8d6]">
                                        {row.checklistNote}
                                    </p>
                                )}
                            </div>
                        )}

                        {(gradingQuestModal.rubric_mode === "scale" ||
                            gradingQuestModal.rubric_mode === "custom") && (
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                                {(row.scaleLevels || DEFAULT_SCALE_LEVELS).map(
                                    (level) => (
                                        <button
                                            key={level.id || level.score}
                                            type="button"
                                            onClick={() => {
                                                const updatedRows = [
                                                    ...(gradingQuestModal.rubric_rows ||
                                                        []),
                                                ];

                                                updatedRows[index] = {
                                                    ...updatedRows[index],
                                                    selectedScaleScore:
                                                        level.score,
                                                };

                                                setGradingQuestModal(
                                                    (prev) => ({
                                                        ...prev,
                                                        rubric_rows:
                                                            updatedRows,
                                                    })
                                                );
                                            }}
                                            className={`rounded-2xl border bg-black p-3 text-left transition-all duration-200 ${
                                                Number(
                                                    row.selectedScaleScore
                                                ) === Number(level.score)
                                                    ? "scale-105"
                                                    : ""
                                            }`}
                                            style={{
                                                borderColor:
                                                    level.color || "#ffd166",
                                                boxShadow:
                                                    Number(
                                                        row.selectedScaleScore
                                                    ) === Number(level.score)
                                                        ? `0 0 20px ${
                                                              level.color ||
                                                              "#ffd166"
                                                          }99`
                                                        : `0 0 10px ${
                                                              level.color ||
                                                              "#ffd166"
                                                          }44`,
                                            }}
                                        >
                                            <p
                                                className="text-sm font-bold"
                                                style={{
                                                    color:
                                                        level.color ||
                                                        "#ffd166",
                                                }}
                                            >
                                                {level.label ||
                                                    `Score ${level.score}`}
                                            </p>

                                            <p className="text-xs text-[#b0b8d6] mt-1">
                                                Score {level.score}
                                            </p>

                                            {level.description && (
                                                <p className="text-xs text-[#d8dee9] mt-2">
                                                    {level.description}
                                                </p>
                                            )}
                                        </button>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {(() => {
                if (gradingQuestModal.rubric_mode === "matrix") {
                    const matrixSummary = getMatrixScoreSummary(
                        gradingQuestModal.rubric_rows || [],
                        gradingQuestModal.rubric_matrix_columns || DEFAULT_MATRIX_COLUMNS,
                        gradingQuestModal.rubric_matrix_assignment_points || gradingQuestModal.rubric_total_points || 100
                    );
                    const grade = getGradeFromPercentage(matrixSummary.percentage);

                    return (
                        <div className="mt-4 rounded-xl border border-[#ffd166]/20 bg-black p-3">
                            <p className="text-sm text-[#ffd166]">
                                Matrix Score: {matrixSummary.earnedScore} / {matrixSummary.maxScore}
                            </p>

                            <p className="text-sm text-[#ffd166] mt-1">
                                Weighted Points: {matrixSummary.earnedPoints} / {matrixSummary.assignmentPoints}
                            </p>

                            <p
                                className="text-sm font-bold mt-1"
                                style={{ color: getGradeColor(grade) }}
                            >
                                {matrixSummary.percentage}% · Suggested Grade: {grade}
                            </p>
                        </div>
                    );
                }

                if (gradingQuestModal.rubric_mode === "checklist") {
                    const checklistSummary = getChecklistScoreSummary(
                        gradingQuestModal.rubric_rows || [],
                        gradingQuestModal.rubric_checklist_scoring_mode || "point_per_item"
                    );
                    const completed = checklistSummary.completed;
                    const total = checklistSummary.count;
                    const percentage = checklistSummary.percentage;
                    const grade = getGradeFromPercentage(percentage);

                    return (
                        <div className="mt-4 rounded-xl border border-[#ffd166]/20 bg-black p-3">
                            <p className="text-sm text-[#ffd166]">
                                Checklist Score: {completed} / {total}
                            </p>

                            <p
                                className="text-sm font-bold mt-1"
                                style={{ color: getGradeColor(grade) }}
                            >
                                {percentage}% · Suggested Grade: {grade}
                            </p>
                        </div>
                    );
                }

                if (gradingQuestModal.rubric_mode === "points") {
                    const earned = getRubricEarnedTotal(
                        gradingQuestModal.rubric_rows || []
                    );

                    const total =
                        Number(gradingQuestModal.rubric_total_points) ||
                        getRubricMaxTotal(
                            gradingQuestModal.rubric_rows || []
                        ) ||
                        100;

                    const percentage = getRubricPercentage(earned, total);

                    const grade = getGradeFromPercentage(percentage);

                    return (
                        <div className="mt-4 rounded-xl border border-[#ffd166]/20 bg-black p-3">
                            <p className="text-sm text-[#ffd166]">
                                Rubric Score: {earned} / {total}
                            </p>

                            <p
                                className="text-sm font-bold mt-1"
                                style={{ color: getGradeColor(grade) }}
                            >
                                {percentage}% · Suggested Grade: {grade}
                            </p>
                        </div>
                    );
                }

                const average = getScaleAverage(
                    gradingQuestModal.rubric_rows || []
                );
                const maxScore = getScaleMaxScore(
                    gradingQuestModal.rubric_rows || [],
                    gradingQuestModal.custom_scale_max || 4
                );
                const percentage = getRubricPercentage(average, maxScore);
                const grade = getGradeFromPercentage(percentage);

                return (
                    <div className="mt-4 rounded-xl border border-[#ffd166]/20 bg-black p-3">
                        <p className="text-sm text-[#ffd166]">
                            Scale Average: {average || "Not scored yet"} / {maxScore}
                        </p>

                        <p
                            className="text-sm font-bold mt-1"
                            style={{ color: getGradeColor(grade) }}
                        >
                            {percentage}% · Suggested Grade: {grade}
                        </p>

                        <p className="text-xs text-[#b0b8d6] mt-1">
                            Select one scale score for each criteria. The grade box below starts with the suggested grade, but the parent can override it.
                        </p>
                    </div>
                );
            })()}
        </div>
    )}

                <div>
                    <p className="text-[#ffd166] text-sm mb-2">
                        Grade
                    </p>

                    <p className="text-xs text-[#b0b8d6] mb-2">
                        Starts with the rubric's automatic grade. Parent may override.
                    </p>

                    {gradingQuestModal.parent_review?.extra_credit_merits ? <p className="text-sm text-[#ffd166]">{gradingQuestModal.parent_review.extra_credit_merits} extra credit merits already awarded.</p> : <label className="block text-sm text-[#b0b8d6]">Extra credit to add to merits (optional)<input type="number" min="0" max="10000" step="1" className={inputClass} value={parentExtraMerits} onChange={(e) => setParentExtraMerits(e.target.value)} /></label>}
                    <select aria-label="Grade format" className={inputClass} value={parentGradeMethod} onChange={(e) => setParentGradeMethod(e.target.value)}><option value="letter">Letter (A–F)</option><option value="points">Points</option><option value="percentage">Number grade (0–100%)</option><option value="pass_fail">Pass / No pass</option></select>
                    {parentGradeMethod === "points" && <label className="block mt-3 text-sm text-[#b0b8d6]">Points earned / {gradingQuestModal.max_points ?? gradingQuestModal.rubric_total_points ?? 100} possible<input type="number" min="0" max={gradingQuestModal.max_points ?? gradingQuestModal.rubric_total_points ?? 100} step="0.01" className={inputClass} value={parentEarnedPoints} onChange={(e) => setParentEarnedPoints(e.target.value)} /></label>}
                    {parentGradeMethod === "percentage" && <input aria-label="Percentage" type="number" min="0" max="100" step="0.01" className={`${inputClass} mt-3`} value={parentPercentage} onChange={(e) => setParentPercentage(e.target.value)} />}
                    {parentGradeMethod === "pass_fail" && <select aria-label="Pass or no pass" className={`${inputClass} mt-3`} value={parentPassFail} onChange={(e) => setParentPassFail(e.target.value)}><option value="">Select result</option><option value="pass">Pass</option><option value="no_pass">No pass</option></select>}
                    {parentGradeMethod === "letter" && <>                    <select
                        value={parentGradeValue}
                        onChange={(e) =>
                            setParentGradeValue(e.target.value)
                        }
                        className={inputClass}
                    >
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                        <option value="F">F</option>
                    </select></>}
                </div>

                <div>
                    <p className="text-[#ffd166] text-sm mb-2">
                        Parent Note
                    </p>

                    <textarea
                        value={parentGradeNote}
                        onChange={(e) =>
                            setParentGradeNote(e.target.value)
                        }
                        className={`${textareaClass} min-h-[120px]`}
                        placeholder="Excellent work..."
                    />
                </div>

                <button
                    type="button"
                    disabled={gradeSaving}
                    onClick={async () => {
                        const rubricEarned = getRubricEarnedTotal(
    gradingQuestModal.rubric_rows || []
);

const rubricTotal =
    Number(gradingQuestModal.rubric_total_points) ||
    getRubricMaxTotal(gradingQuestModal.rubric_rows || []) ||
    100;

const rubricPercentage = getRubricPercentage(
    rubricEarned,
    rubricTotal
);

const scaleAverage = getScaleAverage(
    gradingQuestModal.rubric_rows || []
);

const checklistPercentage = getChecklistPercentage(
    gradingQuestModal.rubric_rows || [],
    gradingQuestModal.rubric_checklist_scoring_mode || "point_per_item"
);

const matrixSummary = getMatrixScoreSummary(
    gradingQuestModal.rubric_rows || [],
    gradingQuestModal.rubric_matrix_columns || DEFAULT_MATRIX_COLUMNS,
    gradingQuestModal.rubric_matrix_assignment_points || gradingQuestModal.rubric_total_points || 100
);

const scaleMaxScore = getScaleMaxScore(
    gradingQuestModal.rubric_rows || [],
    gradingQuestModal.custom_scale_max || 4
);

const scalePercentage = getRubricPercentage(
    scaleAverage,
    scaleMaxScore
);

const suggestedGrade = getSuggestedGradeForRubric({
    ...gradingQuestModal,
    rubric_rows: gradingQuestModal.rubric_rows || [],
});

const finalGrade = parentGradeValue || suggestedGrade;

                        if ((parentGradeMethod === "points" && parentEarnedPoints === "") || (parentGradeMethod === "percentage" && parentPercentage === "") || (parentGradeMethod === "pass_fail" && !parentPassFail)) { toast.error("Enter a grade before approving."); return; }
                        let savedReview;
                        setGradeSaving(true);
                        try {
                            saveShadowGroveEvents(events.map((q) => q.id === gradingQuestModal.id ? { ...q, rubric_rows: gradingQuestModal.rubric_rows || [] } : q));
                            await waitForClassroomSync();
                            const response = await api.post(`/children/${childId}/classroom-dashboard/events/${gradingQuestModal.event_id ?? gradingQuestModal.id}/review`, {
                                grading_type: parentGradeMethod,
                                extra_credit_merits: Number(parentExtraMerits),
                                letter_grade: parentGradeMethod === "letter" ? finalGrade : null,
                                earned_points: parentGradeMethod === "points" ? Number(parentEarnedPoints) : null,
                                percentage: parentGradeMethod === "percentage" ? Number(parentPercentage) : null,
                                pass_fail: parentGradeMethod === "pass_fail" ? parentPassFail : null,
                                parent_notes: parentGradeNote, show_to_child: Boolean(child?.show_current_grades),
                            });
                            savedReview = response.data.data?.parent_review || response.data.parent_review;
                            publishReviewedAssignment(response.data);
                        } catch (error) { toast.error(error?.response?.data?.detail || "Grade could not be saved."); return; }
                        finally { setGradeSaving(false); }

const isPassing = savedReview?.pass_fail ? savedReview.pass_fail === "pass" : !["D", "F"].includes(savedReview?.letter_grade);

const savedNotifications = JSON.parse(
    localStorage.getItem("shadowGroveNotifications") || "[]"
);

savedNotifications.push({
    id: Date.now().toString(),
    child_id: gradingQuestModal.child_id,
    quest_id: gradingQuestModal.id,
    quest_name: gradingQuestModal.title,
    grade: child?.show_current_grades ? (savedReview?.pass_fail || savedReview?.letter_grade) : null,
    rubric_results: gradingQuestModal.rubric_rows || [],

rubric_total_earned: rubricEarned,

rubric_percentage: rubricPercentage,
rubric_scale_average: scaleAverage,
rubric_scale_percentage: scalePercentage,
rubric_checklist_percentage: checklistPercentage,
rubric_matrix_percentage: matrixSummary.percentage,
rubric_matrix_earned_score: matrixSummary.earnedScore,
rubric_matrix_max_score: matrixSummary.maxScore,
rubric_matrix_earned_points: matrixSummary.earnedPoints,
rubric_matrix_assignment_points: matrixSummary.assignmentPoints,
rubric_matrix_columns: gradingQuestModal.rubric_matrix_columns || DEFAULT_MATRIX_COLUMNS,
rubric_mode: gradingQuestModal.rubric_mode || "points",
    note: child?.show_current_grades ? parentGradeNote : "",
    result: isPassing ? "passed" : "try_again",
    seen: false,
    created_at: new Date().toISOString(),
});

localStorage.setItem(
    "shadowGroveNotifications",
    JSON.stringify(savedNotifications)
);
                        setGradingQuestModal(null);
                    }}
                    className={purpleActionBtn}
                >
                    Submit Grade
                </button>
            </div>
        </div>
    </div>
)}
        {pendingSessionLessonSwitch && (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
        <div className="w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-black p-6 shadow-[0_0_35px_rgba(255,209,102,0.22)]">
            <h3 className="font-display text-3xl text-[#ffd166]">
                Switch Session Lesson?
            </h3>

            <p className="text-sm text-[#b0b8d6] mt-3">
                A class timer is currently running. Choose how you want to switch to{" "}
                <span className="text-[#ffd166]">
                    {pendingSessionLessonSwitch.name}
                </span>.
            </p>

            <div className="mt-6 space-y-3">
                <button
                    type="button"
                    onClick={() => {
                        applySessionLessonTemplate(pendingSessionLessonSwitch, true);
                        setPendingSessionLessonSwitch(null);
                    }}
                    className={purpleActionBtn}
                >
                    Restart Timer With This Lesson
                </button>

                <button
                    type="button"
                    onClick={() => {
                        applySessionLessonTemplate(pendingSessionLessonSwitch, false);
                        setPendingSessionLessonSwitch(null);
                    }}
                    className={purpleActionBtn}
                >
                    Keep Timer, Change Lesson Flow
                </button>

                <button
                    type="button"
                    onClick={() => setPendingSessionLessonSwitch(null)}
                    className={purpleActionBtn}
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>
)}
        {renameSessionLessonModal && (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
        <div className="w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-black p-6 shadow-[0_0_35px_rgba(255,209,102,0.22)]">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-display text-3xl text-[#ffd166]">
                        Rename Session Lesson
                    </h3>

                    <p className="text-sm text-[#b0b8d6] mt-1">
                        Update the saved lesson flow name.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => {
                        setRenameSessionLessonModal(null);
                        setRenameSessionLessonValue("");
                    }}
                    className={purpleActionBtn}
                >
                    Close
                </button>
            </div>

            <div className="mt-5">
                <label className="text-sm text-[#b0b8d6]">
                    Session Lesson Name
                </label>

                <input
                    value={renameSessionLessonValue}
                    onChange={(e) => setRenameSessionLessonValue(e.target.value)}
                    className={inputClass}
                />
            </div>

            <button
                type="button"
                onClick={saveRenamedSessionLesson}
                className={`${purpleActionBtn} mt-5`}
            >
                Save Name
            </button>
        </div>
    </div>
)}
{showLessonFlowModal && (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm">
        <div className="my-6 w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-[32px] border border-[#ffd166]/40 bg-black p-5 shadow-[0_0_45px_rgba(255,209,102,0.22)]">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-display text-3xl text-[#ffd166]">
                        Lesson Flow
                    </h3>

                    <p className="mt-1 text-sm text-[#b0b8d6]">
                        View the full order of this class session.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => setShowLessonFlowModal(false)}
                    className={purpleActionBtn}
                >
                    Close
                </button>
            </div>

            <div className="mt-5 space-y-3">
                {classTimerSections.map((section, index) => {
                    const isActive = section.id === activeSectionId;
                    const isNext = getNextSection()?.id === section.id;

                    return (
                        <div
                            key={section.id}
                            className="rounded-2xl border bg-[#080b14]/90 p-4 shadow-[0_0_14px_rgba(255,209,102,0.08)]"
                            style={{
                                borderColor: isActive
                                    ? getActiveSectionColor()
                                    : isNext
                                    ? "#ffd166"
                                    : "#ffd16655",
                                boxShadow: isActive
                                    ? `0 0 20px ${getActiveSectionColor()}66`
                                    : isNext
                                    ? "0 0 18px rgba(255,209,102,0.22)"
                                    : "0 0 14px rgba(255,209,102,0.08)",
                            }}
                        >
                            <p className="text-sm text-[#ffd166]">
                                {index + 1}. {section.title || "Untitled Section"}
                            </p>

                            <p className="mt-1 text-xs text-[#b0b8d6]">
                                Planned: {section.plannedMinutes || 0} min · Actual:{" "}
                                {formatTimer(section.actualSeconds || 0)}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    </div>
)}
{sessionTimeWarning && <div role="dialog" aria-modal="true" aria-label="Session outside class time" className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/85 p-4"><div className="max-w-lg space-y-4 rounded-[28px] border border-[#ffd166]/40 bg-black p-6"><h3 className="font-display text-2xl text-[#ffd166]">Session is outside class time</h3><p className="text-sm text-[#d8d3e7]">Change the start time or section minutes to fit {classSchedule.startTime}–{classSchedule.endTime}, or proceed for this occurrence only.{sessionTimeWarning.repeating ? " Outside-hours sessions will not repeat." : ""}</p><div className="flex gap-2"><button type="button" className={purpleActionBtn} onClick={() => setSessionTimeWarning(null)}>Change time</button><button type="button" className={purpleActionBtn} onClick={() => {sessionOutsideConfirmedRef.current = true; const action = sessionTimeWarning.action; setSessionTimeWarning(null); if (action === "calendar") createCalendarSessionsFromLessonFlow({confirmOutside: true}); else if (action === "save") void saveSessionToLessonStage({confirmOutside: true}); else startClassSession({confirmOutside: true});}}>Proceed once</button></div></div></div>}
{showSessionCompleteModal && completedSessionSummary && (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm">
    <div
        className="
            my-6
            w-full
            max-w-xl
            max-h-[90vh]
            overflow-y-auto
            rounded-[32px]
            border
            border-[#ffd166]/40
            bg-black
            p-5
            shadow-[0_0_45px_rgba(255,209,102,0.22)]
        "
    >
            <h2 className="font-display text-3xl text-[#ffd166] mb-4">
                ✨ Class Session Complete
            </h2>
        <div className="flex justify-center mb-4">
    <div className="relative h-40 w-40 rounded-full overflow-hidden border border-[#ffd166]/50 shadow-[0_0_20px_rgba(255,209,102,0.22)]">
       <img
    src="/images/sun.gif"
    alt="Journey Complete"
    className="absolute inset-0 h-full w-full object-cover scale-150"
/>
    </div>
</div>

<p className="text-center text-sm text-[#b0b8d6] mb-5">
    Today's learning journey has reached its destination.
</p>
            <div className="space-y-3 text-[#e5e7eb]">
                <p>
                    Total Time:
                    <span className="ml-2 text-[#ffd166]">
                        {formatTimer(
                          completedSessionSummary.totalSeconds
)}
                    </span>
                </p>

                <p>
                    Sections Completed:
                    <span className="ml-2 text-[#ffd166]">
                        {
                            completedSessionSummary.sections.filter(
                                (s) => s.completed
                            ).length
                        }
                    </span>
                </p>
            </div>

            <div className="mt-6 rounded-2xl border border-[#ffd166]/20 p-4">
                <h3 className="text-[#ffd166] mb-3">
                    Session Breakdown
                </h3>

                <div className="space-y-2">
                    {completedSessionSummary.sections.map((section) => (
                        <div
                            key={section.id}
                            className="
                                flex
                                items-center
                                justify-between
                                rounded-xl
                                bg-[#111827]
                                px-4
                                py-2
                            "
                        >
                            <span>{section.title}</span>

                            <span className="text-[#ffd166]">
                                {formatTimer(
                                  section.actualSeconds || 0
                                )}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
                <NextClassButton role="parent" childId={childId} subjectId={subjectId} onLeave={() => setShowSessionCompleteModal(false)} />
                <button
                    className={purpleActionBtn}
                    onClick={() => {
                        setShowSessionCompleteModal(false);

                    }}
                >
                    Close
                </button>

                <button
                    className="
    rounded-2xl
    border border-[#ffd166]/40
    bg-black
    px-3 py-2
    text-xs text-[#ffd166]
    shadow-[0_0_14px_rgba(255,209,102,0.10)]
    hover:bg-[#ffd166]/[0.06]
    hover:shadow-[0_0_20px_rgba(255,209,102,0.20)]
    transition-all
    duration-200
"
                    onClick={saveClassSessionRecord}
                >
                    Save Session
                </button>
            </div>
        </div>
    </div>
)}

    


{showClassCustomize && (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4">
        <div className="w-full max-w-xl rounded-[32px] border border-[#ffd166]/40 bg-black p-6 shadow-[0_0_35px_rgba(255,209,102,0.18)]">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h3 className="font-display text-3xl text-[#ffd166]">
                        Customize Class Banner
                    </h3>

                    <p className="text-sm text-[#b0b8d6] mt-1">
                        Change the banner name, banner image, and class icon.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => setShowClassCustomize(false)}
                    className={purpleActionBtn}
                >
                    Close
                </button>
            </div>

            <div className="mt-5 space-y-4">
                <div>
                    <label className="text-sm text-[#b0b8d6]">
                        Banner Class Name
                    </label>

                    <input
                        value={classDisplayName}
                        onChange={(e) => setClassDisplayName(e.target.value)}
                        placeholder="Alchemy of Numbers..."
                        className={inputClass}
                    />
                </div>

                <div className="flex flex-wrap gap-2">
                    <label className={purpleActionBtn}>
                        Change Banner Image
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) =>
                                saveClassroomImage(
                                    e.target.files?.[0],
                                    "banner"
                                )
                            }
                        />
                    </label>

                    <label className={purpleActionBtn}>
                        Change Class Icon
                        <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) =>
                                saveClassroomImage(
                                    e.target.files?.[0],
                                    "icon"
                                )
                            }
                        />
                    </label>
                </div>

                <button
                    type="button"
                    onClick={() => {
                        saveClassDisplayName();
                        setShowClassCustomize(false);
                    }}
                    className={purpleActionBtn}
                >
                    Save Banner Settings
                </button>
            </div>
        </div>
    </div>
)}

                  {selectedPlannerEvent && (
    <div className="fixed inset-0 z-[99999] flex items-start justify-center overflow-y-auto bg-black/80 px-4 py-6">
        <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-[32px] border border-[#ffd166]/45 bg-[#05070d] shadow-[0_0_45px_rgba(255,209,102,0.22)]">
            <div className="border-b border-[#ffd166]/20 p-5">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h3
                            className="font-display text-3xl text-[#ffd166] drop-shadow-[0_0_12px_rgba(255,209,102,0.4)] leading-tight break-words"
                            style={{
                                color: selectedPlannerEvent.color || "#ffd166",
                            }}
                        >
                            {selectedPlannerEvent.title}
                        </h3>

                        <p className="text-sm text-[#b0b8d6] mt-2">
                            {selectedPlannerEvent.date_start}
                        </p>
                    </div>

                    <div className="flex shrink-0 gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                const eventToEdit = selectedPlannerEvent;
                                setSelectedPlannerEvent(null);
                                setEditingQuestModal(eventToEdit);
                            }}
                            className={purpleActionBtn}
                        >
                            Edit
                        </button>
                    <button
    type="button"
    onClick={() => {
    setCopyQuestModal({
        ...selectedPlannerEvent,
        id: `${Date.now()}-${Math.random()}`,
        title: selectedPlannerEvent.title || "Untitled Quest",
        repeat: selectedPlannerEvent.repeat || "none",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
    });

    closePlannerEventModalWithoutWeekJump();
}}
    className={purpleActionBtn}
>
    Copy Quest
</button>
                        <button
                            type="button"
                            onClick={closePlannerEventModalWithoutWeekJump}
                            className={purpleActionBtn}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

                <div className="max-h-[70vh] overflow-y-auto p-5 [scrollbar-width:thin] [scrollbar-color:#ffd166_#05070d]">
                <div className="space-y-5"></div>
                <div>
                    <p className="text-xs text-[#ffd166] mb-1">
                        Description
                    </p>

                    <div className="rounded-3xl border border-[#ffd166]/20 bg-black/60 p-4 text-sm text-[#d8dee9] shadow-[0_0_16px_rgba(255,209,102,0.08)]">
                        {selectedPlannerEvent.description || "No description"}
                    </div>
                <LessonCalendarDetails event={selectedPlannerEvent} />
                <AssignmentLessonLinks event={selectedPlannerEvent} childId={childId} subjectId={subjectId} />
                </div>

                <div>
                    <p className="text-xs text-[#ffd166] mb-1">
                        Materials
                    </p>

                    <div className="rounded-3xl border border-[#ffd166]/20 bg-black/60 p-4 shadow-[0_0_16px_rgba(255,209,102,0.08)]">
                        {Array.isArray(selectedPlannerEvent.materials) &&
                        selectedPlannerEvent.materials.length > 0 ? (
                            <div className="space-y-2">
                                {selectedPlannerEvent.materials
    .filter((material) => material.checked)
    .map((material) => (
                                    <div
                                        key={material.id}
                                        className="text-sm text-[#d8dee9]"
                                    >
                                        {material.checked ? "☑" : "☐"}{" "}
                                        {material.name}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-[#6c7a9c]">
                             No completed materials
                            </p>
                        )}
                    </div>
                </div>

                {selectedPlannerEvent.rubric_enabled && (
                    <div>
                        <p className="text-xs text-[#ffd166] mb-1">
                            Rubric
                        </p>

                        <div className="rounded-2xl border border-[#ffd166]/20 bg-[#111827] p-4">
                            <p className="text-sm text-[#ffd166]">
                                {getRubricModeLabel(
                                    selectedPlannerEvent.rubric_mode
                                )}
                            </p>

                            <div className="space-y-3 mt-3">
                                {selectedPlannerEvent.rubric_rows?.map(
                                    (row, index) => (
                                        <div
                                            key={row.id || index}
                                            className="rounded-2xl border border-[#ffd166]/20 bg-black p-4"
                                        >
                                            <p className="text-sm text-[#d8dee9]">
                                                {row.criteria}
                                            </p>

                                            {selectedPlannerEvent.rubric_mode ===
                                                "points" && (
                                                <p className="text-xs text-[#ffd166] mt-1">
                                                    {row.maxPoints} pts
                                                </p>
                                            )}

                                            {selectedPlannerEvent.rubric_mode ===
                                                "checklist" && (
                                                <div
                                                    className="mt-2 rounded-xl border bg-black p-2 text-xs"
                                                    style={{
                                                        borderColor:
                                                            row.checklistRequired
                                                                ? row.checklistColor ||
                                                                  CHECKLIST_COMPLETE_COLOR
                                                                : CHECKLIST_OPTIONAL_COLOR,
                                                        color: row.checklistRequired
                                                            ? row.checklistColor ||
                                                              CHECKLIST_COMPLETE_COLOR
                                                            : CHECKLIST_OPTIONAL_COLOR,
                                                    }}
                                                >
                                                    {row.checklistRequired
                                                        ? "Required"
                                                        : "Optional"}

                                                    {row.checklistNote && (
                                                        <p className="text-[#b0b8d6] mt-1">
                                                            {row.checklistNote}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {(selectedPlannerEvent.rubric_mode ===
                                                "scale" ||
                                                selectedPlannerEvent.rubric_mode ===
                                                    "custom") &&
                                                row.scaleLevels &&
                                                row.scaleLevels.length > 0 && (
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                                                        {row.scaleLevels.map(
                                                            (level) => (
                                                                <div
                                                                    key={
                                                                        level.id ||
                                                                        level.score
                                                                    }
                                                                    className="rounded-xl border bg-black p-3 text-[10px] min-h-[90px]"
                                                                    style={{
                                                                        borderColor:
                                                                            level.color ||
                                                                            "#ffd166",
                                                                        boxShadow: `0 0 14px ${
                                                                            level.color ||
                                                                            "#ffd166"
                                                                        }99`,
                                                                    }}
                                                                >
                                                                    <p
                                                                        className="font-bold"
                                                                        style={{
                                                                            color:
                                                                                level.color ||
                                                                                "#ffd166",
                                                                        }}
                                                                    >
                                                                        {level.label ||
                                                                            `Score ${level.score}`}
                                                                    </p>

                                                                    {(level.description ||
                                                                        level.details) && (
                                                                        <p className="text-[#b0b8d6] mt-1">
                                                                            {level.description ||
                                                                                level.details}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                )}

                                            {selectedPlannerEvent.rubric_mode ===
                                                "matrix" && (
                                                <div className="mt-4 overflow-x-auto rounded-2xl border border-[#ffd166]/20 bg-[#080b14]/60">
                                                    <table className="w-full min-w-[780px] border-collapse text-xs">
                                                        <thead className="text-[#ffd166]">
                                                            <tr>
                                                                <th className="border border-[#ffd166]/20 p-2 text-left">
                                                                    Category
                                                                </th>

                                                                {(selectedPlannerEvent.rubric_matrix_columns ||
                                                                    DEFAULT_MATRIX_COLUMNS
                                                                ).map(
                                                                    (column) => (
                                                                        <th
                                                                            key={
                                                                                column.id
                                                                            }
                                                                            className="border border-[#ffd166]/20 p-2 text-center"
                                                                            style={{
                                                                                color:
                                                                                    column.color ||
                                                                                    "#ffd166",
                                                                            }}
                                                                        >
                                                                            {column.score} ·{" "}
                                                                            {column.label}
                                                                        </th>
                                                                    )
                                                                )}
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            <tr>
                                                                <td className="border border-[#ffd166]/20 p-2 text-[#ffd166]">
                                                                    {row.criteria}
                                                                </td>

                                                                {(selectedPlannerEvent.rubric_matrix_columns ||
                                                                    DEFAULT_MATRIX_COLUMNS
                                                                ).map(
                                                                    (column) => (
                                                                        <td
                                                                            key={
                                                                                column.id
                                                                            }
                                                                            className="border border-[#ffd166]/20 p-2 align-top"
                                                                            style={{
                                                                                backgroundColor: `${
                                                                                    column.color ||
                                                                                    "#ffd166"
                                                                                }12`,
                                                                            }}
                                                                        >
                                                                            <p className="text-[11px] text-[#d8dee9]">
                                                                                {(row.matrixCells ||
                                                                                    {})[
                                                                                    column
                                                                                        .score
                                                                                ] ||
                                                                                    column.label}
                                                                            </p>
                                                                        </td>
                                                                    )
                                                                )}
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={() => {
                            setEditingQuestModal(selectedPlannerEvent);
                            setSelectedPlannerEvent(null);
                        }}
                        className={purpleActionBtn}
                    >
                        Edit Quest
                    </button>

                    <button
    type="button"
    onClick={() => deleteLesson(selectedPlannerEvent.id)}
    className="bb-btn-ghost !text-red-400"
>
    Delete Quest
</button>

                    <button
                        type="button"
                        onClick={() => {
                            const nextDay = generateSequentialDates(
                                formatLocalDate(
                                    new Date(
                                        parseLocalDate(
                                            selectedPlannerEvent.date_start
                                        ).getTime() + 86400000
                                    )
                                ),
                                1
                            )[0];

                            moveLessonToDate(selectedPlannerEvent, nextDay);
                            setSelectedPlannerEvent(null);
                        }}
                        className={purpleActionBtn}
                    >
                        Move To Next Day
                    </button>
                </div>
            </div>
        </div>
    </div>
)}
        {pendingSessionFlowConflict && (
            <div className="fixed inset-0 z-[99999] flex items-start justify-center overflow-y-auto bg-black/85 px-4 py-8 backdrop-blur-sm">
                <div className="my-auto w-full max-w-xl rounded-[32px] border border-[#ffd166]/50 bg-[#05070d] p-6 shadow-[0_0_45px_rgba(255,209,102,0.22)]">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <p className="text-xs uppercase tracking-[0.22em] text-[#d8b4fe]">
                                Calendar Conflict
                            </p>

                            <h3 className="mt-2 font-display text-3xl text-[#ffd166]">
                                Session Flow Already Exists
                            </h3>
                        </div>

                        <button
                            type="button"
                            onClick={() =>
                                setPendingSessionFlowConflict(null)
                            }
                            className={purpleActionBtn}
                        >
                            Close
                        </button>
                    </div>

                    <p className="mt-4 text-sm leading-relaxed text-[#b0b8d6]">
                        This child and class already have a session flow on{" "}
                        {pendingSessionFlowConflict.existingFlowDates.length === 1
                            ? "the selected date"
                            : `${pendingSessionFlowConflict.existingFlowDates.length} selected dates`}
                        .
                    </p>

                    <div className="mt-4 rounded-2xl border border-[#ffd166]/25 bg-black/60 p-4">
                        <p className="text-xs uppercase tracking-[0.18em] text-[#ffd166]">
                            Conflicting Dates
                        </p>

                        <div className="mt-3 flex flex-wrap gap-2">
                            {pendingSessionFlowConflict.existingFlowDates.map(
                                (date) => (
                                    <span
                                        key={date}
                                        className="rounded-full border border-[#a855f7]/35 bg-[#a855f7]/10 px-3 py-1 text-xs text-[#e9d5ff]"
                                    >
                                        {new Date(
                                            `${date}T12:00:00`
                                        ).toLocaleDateString(
                                            undefined,
                                            {
                                                month: "long",
                                                day: "numeric",
                                                year: "numeric",
                                            }
                                        )}
                                    </span>
                                )
                            )}
                        </div>
                    </div>

                    <div className="mt-5 space-y-3">
                        <button
                            type="button"
                            onClick={() =>
                                commitCalendarSessionFlow({
                                    newSessions:
                                        pendingSessionFlowConflict.newSessions,
                                    repeatDates:
                                        pendingSessionFlowConflict.repeatDates,
                                    replaceExisting: true,
                                })
                            }
                            className="w-full rounded-2xl border border-[#ffd166]/60 bg-[#ffd166]/10 px-5 py-4 text-left transition-all hover:bg-[#ffd166]/20 hover:shadow-[0_0_24px_rgba(255,209,102,0.25)]"
                        >
                            <span className="block font-semibold text-[#ffd166]">
                                Replace Existing Flow
                            </span>

                            <span className="mt-1 block text-xs text-[#b0b8d6]">
                                Remove the session flows already scheduled on these
                                dates and replace them with this one.
                            </span>
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                commitCalendarSessionFlow({
                                    newSessions:
                                        pendingSessionFlowConflict.newSessions,
                                    repeatDates:
                                        pendingSessionFlowConflict.repeatDates,
                                    replaceExisting: false,
                                })
                            }
                            className="w-full rounded-2xl border border-[#a855f7]/55 bg-[#a855f7]/10 px-5 py-4 text-left transition-all hover:bg-[#a855f7]/20 hover:shadow-[0_0_24px_rgba(168,85,247,0.25)]"
                        >
                            <span className="block font-semibold text-[#e9d5ff]">
                                Add Another Copy
                            </span>

                            <span className="mt-1 block text-xs text-[#b0b8d6]">
                                Keep the existing flow and add this session flow
                                alongside it.
                            </span>
                        </button>

                        <button
                            type="button"
                            onClick={() =>
                                setPendingSessionFlowConflict(null)
                            }
                            className="w-full rounded-2xl border border-[#64748b]/40 bg-black px-5 py-3 text-sm text-[#b0b8d6] transition-all hover:border-[#b0b8d6]/60 hover:text-white"
                        >
                            Cancel Without Saving
                        </button>
                    </div>
                </div>
            </div>
        )}
        {selectedSessionModal && (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/80 px-4 py-6">
        <div className="w-full max-w-xl max-h-[88vh] overflow-y-auto rounded-[32px] border border-[#ffd166]/50 bg-[#05070d] p-5 shadow-[0_0_40px_rgba(255,209,102,0.22)] [scrollbar-width:thin] [scrollbar-color:#ffd166_#05070d]">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-[#7cc1ff]">
                        Timed Session
                    </p>

                    <h3 className="font-display text-3xl text-[#ffd166] mt-1">
                        {selectedSessionModal.title}
                    </h3>

                    <p className="text-sm text-[#b0b8d6] mt-2">
                        {formatDisplayTime(selectedSessionModal.scheduled_start_time)} -{" "}
                        {formatDisplayTime(selectedSessionModal.scheduled_end_time)}
                    </p>
                </div>

                <button
                    type="button"
                    onClick={closeSessionModalWithoutScrollJump}
                    className={purpleActionBtn}
                >
                    Close
                </button>
            </div>

            {selectedSessionModal.description && (
                <div className="mt-5 rounded-2xl border border-[#ffd166]/25 bg-black/60 p-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#ffd166] mb-2">
                        Description
                    </p>

                    <p className="text-sm text-[#d8dee9] leading-relaxed">
                        {selectedSessionModal.description}
                    </p>
                </div>
            )}
            {editingSessionModal && (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/85 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-[32px] border border-[#ffd166]/50 bg-[#05070d] p-5 shadow-[0_0_40px_rgba(255,209,102,0.22)] [scrollbar-width:thin] [scrollbar-color:#ffd166_#05070d]">
    
            <div className="flex items-center justify-between gap-3 mb-6">
                <h3 className="font-display text-3xl text-[#ffd166]">
                    Edit Session
                </h3>

                <button
                    type="button"
                    onClick={() => setEditingSessionModal(null)}
                    className={purpleActionBtn}
                >
                    Close
                </button>
            </div>

            <div className="space-y-4">

                <div>
                    <label className="text-sm text-[#b0b8d6]">
                        Session Title
                    </label>

                    <input
                        value={editingSessionTitle}
                        onChange={(e) =>
                            setEditingSessionTitle(e.target.value)
                        }
                        className={inputClass}
                    />
                </div>

                <div>
                    <label className="text-sm text-[#b0b8d6]">
                        Description
                    </label>

                    <textarea
                        value={editingSessionDescription}
                        onChange={(e) =>
                            setEditingSessionDescription(e.target.value)
                        }
                        className={`${textareaClass} min-h-[100px]`}
                    />
                </div>

                <div>
                    <label className="text-sm text-[#b0b8d6]">
                        Materials
                    </label>

                   <div className="space-y-2 mt-2">
    {editingSessionMaterials.map((material, index) => (
        <div key={material.id} className="flex items-center gap-2">
            <input
                type="checkbox"
                checked={material.checked}
                onChange={(e) => {
                    const updated = [...editingSessionMaterials];
                    updated[index] = {
                        ...updated[index],
                        checked: e.target.checked,
                    };
                    setEditingSessionMaterials(updated);
                }}
            />

            <input
                value={material.name}
                onChange={(e) => {
                    const updated = [...editingSessionMaterials];
                    updated[index] = {
                        ...updated[index],
                        name: e.target.value,
                    };
                    setEditingSessionMaterials(updated);
                }}
                placeholder={`Material ${index + 1}`}
                className={inputClass}
            />

            <button
                type="button"
                onClick={() =>
                    setEditingSessionMaterials((prev) =>
                        prev.length > 1
                            ? prev.filter((_, i) => i !== index)
                            : [
                                  {
                                      id: Date.now().toString(),
                                      name: "",
                                      checked: false,
                                  },
                              ]
                    )
                }
                className={purpleActionBtn}
            >
                Remove
            </button>
        </div>
    ))}

    <button
        type="button"
        onClick={() =>
            setEditingSessionMaterials((prev) => [
                ...prev,
                {
                    id: `${Date.now()}-${Math.random()}`,
                    name: "",
                    checked: false,
                },
            ])
        }
        className={purpleActionBtn}
    >
        Add Material
    </button>
</div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <div>
                        <label className="text-sm text-[#b0b8d6]">
                            Start Time
                        </label>

                        <input
                            type="time"
                            value={editingSessionStartTime}
                            onChange={(e) =>
                                setEditingSessionStartTime(e.target.value)
                            }
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className="text-sm text-[#b0b8d6]">
                            End Time
                        </label>

                        <input
                            type="time"
                            value={editingSessionEndTime}
                            onChange={(e) =>
                                setEditingSessionEndTime(e.target.value)
                            }
                            className={inputClass}
                        />
                    </div>
                                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={saveEditedSession}
                        className={purpleActionBtn}
                    >
                        Save Session Changes
                    </button>

                    <button
                        type="button"
                        onClick={() => setEditingSessionModal(null)}
                        className={purpleActionBtn}
                    >
                        Cancel
                    </button>
                </div>

            </div>
        </div>
    </div>
)}
            {deleteSessionModal && (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 px-4">
        <div className="w-full max-w-xl rounded-[32px] border border-red-500/40 bg-[#05070d] p-6 shadow-[0_0_40px_rgba(220,38,38,0.25)]">

            <h3 className="font-display text-3xl text-red-300 mb-4">
                Delete Session
            </h3>

            <p className="text-sm text-[#d8dee9]">
                Choose how you want to delete this session.
            </p>

            <div className="mt-6 flex flex-col gap-3">

                <button
                    type="button"
                    onClick={() => deleteSingleSession(deleteSessionModal)}
                    className={purpleActionBtn}
                >
                    Delete This Session Only
                </button>

                <button
                    type="button"
                    onClick={() => deleteSessionFlow(deleteSessionModal)}
                    className={purpleActionBtn}
                >
                    Delete Entire Session Flow
                </button>

                <button
                    type="button"
                    onClick={() => setDeleteSessionModal(null)}
                    className={purpleActionBtn}
                >
                    Cancel
                </button>

            </div>
        </div>
    </div>
)}
          

            {selectedSessionModal.materials_needed && (
    <div className="mt-4 rounded-2xl border border-[#ffd166]/25 bg-black/60 p-4">
        <p className="text-xs uppercase tracking-[0.18em] text-[#ffd166] mb-2">
            Materials Needed
        </p>

        {Array.isArray(selectedSessionModal.materials_needed) ? (
            <ul className="space-y-1">
                {selectedSessionModal.materials_needed
                    .filter((item) => item.name?.trim())
                    .map((item) => (
                        <li
                            key={item.id}
                            className="text-sm text-[#d8dee9]"
                        >
                            {item.checked ? "☑" : "☐"} {item.name}
                        </li>
                    ))}
            </ul>
        ) : (
            <p className="text-sm text-[#d8dee9] leading-relaxed">
                {selectedSessionModal.materials_needed}
            </p>
        )}
    </div>
)}

            <div className="mt-4 rounded-2xl border border-[#7cc1ff]/25 bg-black/60 p-4">
                <p className="text-sm text-[#b0b8d6]">
                    {selectedSessionModal.is_instructional_time === false
                        ? "This is a break/non-instructional block."
                        : "This counts as instructional time."}
                </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
                <button
                    type="button"
                    onClick={() => {
                        startTimedSession(selectedSessionModal);
                        setSelectedSessionModal(null);
                    }}
                    className={purpleActionBtn}
                >
                    Start Session
                </button>
                <button
    type="button"
    onClick={() => openEditSessionModal(selectedSessionModal)}
    className={purpleActionBtn}
>
    Edit Session
</button>
                <button
                    type="button"
                    onClick={() => {
                        completeTimedSession(selectedSessionModal);
                        setSelectedSessionModal(null);
                    }}
                    className={purpleActionBtn}
                >
                    Complete Session
                </button>
            </div>
            <button
    type="button"
    onClick={() => setDeleteSessionModal(selectedSessionModal)}
    className={purpleActionBtn}
>
    Delete Session
</button>
        </div>
    </div>

                    )}

{calendarMoreModal && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
        <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-[32px] border border-[#ffd166]/50 bg-[#05070d] p-6 shadow-[0_0_40px_rgba(255,209,102,0.25)]">

            <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                    <h3 className="font-display text-2xl text-[#ffd166]">
                        {calendarMoreModal.blockLabel || "Calendar Items"}
                    </h3>

                    <p className="text-xs text-[#b0b8d6]">
                        {calendarMoreModal.date}
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => setCalendarMoreModal(null)}
                    className={purpleActionBtn}
                >
                    Close
                </button>
            </div>

            <div className="space-y-3">
                {calendarMoreModal.items.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                            setCalendarMoreModal(null);

                            if (item.is_calendar_session || item.is_timed_session) {
                                openSessionModalWithoutScrollJump(item);
                                return;
                            }

                            setSelectedPlannerEvent(item);
                        }}
                        className="w-full rounded-2xl border border-[#ffd166]/30 bg-black p-4 text-left hover:bg-[#101522]"
                    >
                        <p className="text-[#ffd166]">
                            {item.title || "Untitled"}
                        </p>

                        <p className="text-xs text-[#b0b8d6] mt-1">
                            {formatDisplayTime(
                                item.scheduled_start_time ||
                                item.start_time ||
                                item.due_time
                            )}
                        </p>
                    </button>
                ))}
            </div>

        </div>
    </div>
)}

                                    </div>
            {isClassroomNotesOpen && (
                <div
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize classroom workspace"
                    onPointerDown={
                        startClassroomWorkspaceResize
                    }
                    className="
                        hidden
                        md:flex
                        h-full
                        min-h-[480px]
                        cursor-col-resize
                        items-stretch
                        justify-center
                        group
                    "
                >
                    <div
                        className="
                            w-[2px]
                            rounded-full
                            bg-[#ffd166]/20
                            transition-all
                            duration-150
                            group-hover:w-[4px]
                            group-hover:bg-[#ffd166]/70
                            group-hover:shadow-[0_0_14px_rgba(255,209,102,0.45)]
                        "
                    />
                </div>
            )}
            <ClassroomNotesPanel
                role="parent"
                childId={childId}
                subjectId={subjectId}
                subjectName={subject?.name}
                onOpenChange={setIsClassroomNotesOpen}
            />

        </div>

            </main>
        </div>
    );
};

const ParentClassroomRoute = () => {
    const { childId, subjectId } = useParams();
    return <ParentClassroom key={`${childId}:${subjectId}`} />;
};

export default ParentClassroomRoute;
